/**
 * Mock API Client - In-memory implementation for offline development
 * Simulates all endpoints with realistic data and delays
 */

import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';
import { IAPIClient } from './client';
import { MockEstimates } from './mockEstimates';
import { MockConversations } from './mockConversations';
import { MockSearchError, mockGlobalSearch, mockSearchTypes } from './mockSearch';
import { MockMfa, MockMfaError } from './mockMfa';
import { MockSetup, MockSetupError, MockSetupSignals } from './mockSetup';
import { MockStaffNotifications } from './mockStaffNotifications';
import { MockSurveys } from './mockSurveys';
import { MockBundles } from './mockBundles';
import { MockPortalRollout } from './mockPortalRollout';
import { MockStarter, MockStarterError } from './mockStarter';
import { createMockImportExport } from './mockImportExport';
import { descendantIds, effectiveUtm, pathNames } from '../utils/leadSource';
import { SIZE_UNIT_OPTIONS, isSizeUnit } from '../utils/priceChart';
import { DEFAULT_PAYMENT_TERMS_DAYS, addDaysToDateOnly } from '../utils/companySettings';
import { BillingPeriodPattern, billingPeriodAt, billingPeriodContaining, periodsOverlap } from '../utils/billingPeriods';
import { addDays, frequencyConfigError, isDateOnly, listOccurrences } from '../utils/recurrence';
import { centsToDecimal, decimalToCents } from '../utils/cents';
import { checklistTemplateBodyError } from '../utils/checklistTemplate';
import { cleanTechText, formatTechNoteLine, todayInTimeZone } from '../utils/technician';
import { formatArrivalWindow } from '../pages/dispatchUi';
import {
  ATTACHMENT_DESCRIPTION_MAX,
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_FILENAME_MAX,
  ATTACHMENT_MAX_BYTES,
  extensionOf,
  fileTypeForContentType,
  fileTypeForExtension,
} from '../utils/attachment';

/**
 * An error shaped like a failed backend request ({ success, message, code }
 * in the response body), so getErrorCode / getErrorMessage treat mock and
 * real failures the same. Used where the UI branches on the error code.
 */
function mockApiError(status: number, message: string, code?: string, details?: unknown): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = {
    status,
    statusText: '',
    headers: {},
    config,
    data: { success: false, message, ...(code ? { code } : {}), ...(details !== undefined ? { details } : {}) },
  } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

// ============ Mock Data ============

const MOCK_TENANTS: types.Tenant[] = [
  { id: 'tenant-1', name: 'company-a', displayName: 'Company A', slug: 'company-a' },
  { id: 'tenant-2', name: 'company-b', displayName: 'Company B', slug: 'company-b' },
];

// The mock keeps plaintext passwords so login can check them; they never leave
// the mock (toUserListItem / toUserDetail strip them), matching the real DTOs.
type MockUser = types.UserDetail & { password: string; /** A staff first name, shown to customers in the portal (tracker item 45) */ firstName?: string | null };

const MOCK_USER_TIMESTAMP = '2024-01-01T00:00:00.000Z';

function mockUser(
  id: string,
  tenantId: string,
  email: string,
  role: types.UserRole,
  overrides: Partial<MockUser> = {}
): MockUser {
  return {
    id,
    tenantId,
    email,
    role,
    isActive: true,
    mustChangePassword: false,
    password: 'password',
    createdAt: MOCK_USER_TIMESTAMP,
    updatedAt: MOCK_USER_TIMESTAMP,
    ...overrides,
  };
}

const MOCK_USERS: MockUser[] = [
  mockUser('user-1', 'tenant-1', 'alice@company-a.com', types.UserRole.ADMIN),
  mockUser('user-3', 'tenant-1', 'carol@company-a.com', types.UserRole.OFFICE),
  mockUser('user-4', 'tenant-1', 'dave@company-a.com', types.UserRole.OFFICE_MANAGER),
  mockUser('user-5', 'tenant-1', 'tom@company-a.com', types.UserRole.TECHNICIAN),
  mockUser('user-2', 'tenant-2', 'bob@company-b.com', types.UserRole.TECHNICIAN),
];

/** Backend ASSIGNABLE_ROLES: who GET /users/assignable lists and who may call it. */
const ASSIGNABLE_MOCK_ROLES: readonly types.UserRole[] = [
  types.UserRole.ADMIN,
  types.UserRole.OFFICE_MANAGER,
  types.UserRole.TECHNICIAN,
];

function toUserListItem({ password, tenantId, updatedAt, firstName, ...user }: MockUser): types.UserListItem {
  return { ...user };
}

function toUserDetail({ password, firstName, ...user }: MockUser): types.UserDetail {
  return { ...user };
}

/** Same rules as backend UsersBusinessLayer.validatePasswordStrength. */
function validateMockPassword(password: string | undefined): void {
  if (!password || password.length < 8) throw new Error('Password must be at least 8 characters long');
  if (!/[A-Z]/.test(password)) throw new Error('Password must contain at least one uppercase letter');
  if (!/[0-9]/.test(password)) throw new Error('Password must contain at least one number');
}

// The real API does not return tenantId on customers; the mock keeps it
// internally so it can scope data per tenant, and strips it on the way out.
type MockCustomer = types.Customer & { tenantId: string };

function toCustomer({ tenantId, ...customer }: MockCustomer): types.Customer {
  return { ...customer };
}

const MOCK_CUSTOMERS: MockCustomer[] = [
  {
    id: 'cust-1',
    tenantId: 'tenant-1',
    name: 'ACME Corp',
    email: 'acme@example.com',
    phone: '555-0001',
    address: '123 Main St',
    city: 'Springfield',
    state: 'IL',
    zip: '62701',
    taxExempt: false,
    billingMethod: 'CONSOLIDATED',
    status: 'ACTIVE',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'cust-2',
    tenantId: 'tenant-1',
    name: 'Smith Cleaning Services',
    email: 'smith@example.com',
    phone: '555-0002',
    address: '456 Oak Ave',
    city: 'Springfield',
    state: 'IL',
    zip: '62702',
    taxExempt: true,
    billingMethod: 'PER_VISIT',
    status: 'PROSPECT',
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'cust-3',
    tenantId: 'tenant-2',
    name: 'Johnson Landscaping',
    email: 'johnson@example.com',
    phone: '555-0003',
    address: '789 Pine Rd',
    city: 'Springfield',
    state: 'IL',
    zip: '62703',
    taxExempt: false,
    billingMethod: 'PER_VISIT',
    status: 'LEAD',
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const MOCK_PROPERTIES: types.Property[] = [
  {
    id: 'prop-1',
    tenantId: 'tenant-1',
    customerId: 'cust-1',
    billingCustomerId: 'cust-1',
    address: '123 Main St',
    city: 'Springfield',
    state: 'IL',
    zip: '62701',
    taxRateId: 'tax-1',
    sizeValue: null,
    sizeUnit: null,
    createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'prop-2',
    tenantId: 'tenant-1',
    customerId: 'cust-1',
    billingCustomerId: 'cust-1',
    address: '234 Oak Ln',
    city: 'Springfield',
    state: 'IL',
    zip: '62701',
    taxRateId: 'tax-1',
    sizeValue: '8000.00',
    sizeUnit: 'SQFT',
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Items are stored raw (like the DB row: tenant, account/category ids, soft
// delete); the response shape with embedded accounts is built on read.
interface MockItem {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  itemType: types.ItemType;
  unitPrice: string;
  unitCost: string | null;
  unitOfMeasure: string;
  revenueAccountId: string;
  expenseAccountId: string | null;
  categoryId: string | null;
  taxable: boolean;
  quantityOnHand: string;
  /** Low-stock threshold (PRODUCT only); null = none */
  reorderPoint: string | null;
  priceChartId: string | null;
  routeId?: string | null;
  /** Checklist template copied onto jobs (tracker item 44); null = none */
  checklistTemplateId?: string | null;
  /** Offered on the customer portal's request form (SERVICE / LABOR only; tracker item 45) */
  portalRequestable?: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

const MOCK_ITEMS: MockItem[] = [
  {
    id: 'item-1',
    tenantId: 'tenant-1',
    name: 'Office Cleaning',
    description: 'Standard office cleaning service',
    itemType: types.ItemType.SERVICE,
    unitPrice: '150.00',
    unitCost: null,
    unitOfMeasure: 'visit',
    revenueAccountId: 'gl-acc-4001',
    expenseAccountId: null,
    categoryId: 'item-cat-1',
    taxable: true,
    quantityOnHand: '0',
    reorderPoint: null,
    priceChartId: null,
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  },
  {
    id: 'item-2',
    tenantId: 'tenant-1',
    name: 'Supplies',
    description: 'Cleaning supplies',
    itemType: types.ItemType.PRODUCT,
    unitPrice: '50.00',
    unitCost: '20.00',
    unitOfMeasure: 'each',
    revenueAccountId: 'gl-acc-4000',
    expenseAccountId: 'gl-acc-5001',
    categoryId: null,
    taxable: true,
    quantityOnHand: '0',
    reorderPoint: '5.00',
    priceChartId: null,
    createdAt: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  },
  {
    id: 'item-3',
    tenantId: 'tenant-1',
    name: 'Lawn Mowing',
    description: 'Priced by lawn area',
    itemType: types.ItemType.SERVICE,
    unitPrice: '45.00',
    unitCost: null,
    unitOfMeasure: 'visit',
    revenueAccountId: 'gl-acc-4000',
    expenseAccountId: null,
    categoryId: null,
    taxable: true,
    quantityOnHand: '0',
    reorderPoint: null,
    priceChartId: 'chart-1',
    createdAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  },
];

// Price charts are stored raw (tiers inline, no usage counts); the response
// shape with fromSize and usage is built on read, like the backend.
interface MockPriceChartTier {
  id: string;
  upToSize: string;
  price: string;
}

interface MockPriceChart {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  unit: types.SizeUnit;
  pricingType: types.PriceChartPricingType;
  overageRatePerUnit: string;
  active: boolean;
  /** Sorted by upToSize. */
  tiers: MockPriceChartTier[];
  createdAt: string;
  updatedAt: string;
}

const MOCK_PRICE_CHARTS: MockPriceChart[] = [
  {
    id: 'chart-1',
    tenantId: 'tenant-1',
    name: 'Lawn Area',
    description: 'Mowing by lawn size',
    unit: 'SQFT',
    pricingType: 'BAND_FLAT',
    overageRatePerUnit: '0.005',
    active: true,
    tiers: [
      { id: 'tier-1', upToSize: '5000', price: '50' },
      { id: 'tier-2', upToSize: '10000', price: '70' },
      { id: 'tier-3', upToSize: '20000', price: '100' },
    ],
    createdAt: MOCK_USER_TIMESTAMP,
    updatedAt: MOCK_USER_TIMESTAMP,
  },
];

/**
 * Exact size of one unit in m² (AREA) or m (LENGTH), as in backend
 * SIZE_UNIT_DEFINITIONS. Only the mock uses these (to quote prices offline).
 */
const MOCK_SI_FACTORS: Record<types.SizeUnit, number> = {
  SQFT: 0.09290304,
  SQYD: 0.83612736,
  SQM: 1,
  ACRE: 4046.8564224,
  HECTARE: 10000,
  LINEAR_FT: 0.3048,
  METER: 1,
};

function roundTo(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

const MOCK_ITEM_CATEGORIES: (types.ItemCategory & { tenantId: string })[] = [
  {
    id: 'item-cat-1',
    tenantId: 'tenant-1',
    name: 'Cleaning Services',
    description: null,
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

/** Page a list like the backend list endpoints (limit capped at 100). */
function paginate<T>(rows: T[], page: number, limit: number): types.PaginatedResponse<T> {
  const size = Math.min(100, limit);
  const start = (page - 1) * size;
  return {
    success: true,
    data: rows.slice(start, start + size),
    pagination: { page, limit: size, total: rows.length, pages: Math.ceil(rows.length / size) },
  };
}

const MOCK_TAX_RATES: types.TaxRate[] = [
  {
    id: 'tax-1',
    tenantId: 'tenant-1',
    name: 'Illinois Sales Tax',
    rate: '0.0625',
    payableAccount: { id: 'gl-acc-2200', accountNumber: '2200', accountName: 'Sales Tax Payable' },
    propertyCount: 2,
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const MOCK_DISCOUNTS: types.Discount[] = [
  {
    id: 'discount-1',
    tenantId: 'tenant-1',
    name: 'Senior Discount',
    description: 'For customers 65 and older',
    type: types.DiscountType.PERCENTAGE,
    value: '10',
    discountAccount: { id: 'gl-acc-4900', accountNumber: '4900', accountName: 'Sales Discounts' },
    active: true,
    startDate: null,
    endDate: null,
    customerCount: 0,
    propertyCount: 0,
    itemCount: 0,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

// Lead sources are stored raw (as the DB row); paths, inherited UTM values and
// usage counts are computed on read, like the backend.
type MockLeadSourceNode = Omit<
  types.LeadSource,
  | 'effectiveUtm'
  | 'path'
  | 'pathLabel'
  | 'depth'
  | 'childCount'
  | 'customerCount'
  | 'customerHistoryCount'
  | 'propertyCount'
  | 'jobCount'
  | 'inUse'
>;

function mockLeadSource(
  id: string,
  tenantId: string,
  name: string,
  parentId: string | null,
  utm: Partial<types.UtmValues> = {}
): MockLeadSourceNode {
  return {
    id,
    tenantId,
    parentId,
    name,
    description: null,
    active: true,
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmTerm: null,
    utmContent: null,
    ...utm,
    createdAt: MOCK_USER_TIMESTAMP,
    updatedAt: MOCK_USER_TIMESTAMP,
  };
}

const MOCK_LEAD_SOURCES: MockLeadSourceNode[] = [
  mockLeadSource('ls-online', 'tenant-1', 'Online', null),
  mockLeadSource('ls-google', 'tenant-1', 'Google', 'ls-online', { utmSource: 'google', utmMedium: 'GoogleAds' }),
  mockLeadSource('ls-google-ads', 'tenant-1', 'Google Ads', 'ls-google', { utmCampaign: 'brand' }),
  mockLeadSource('ls-facebook', 'tenant-1', 'Facebook', 'ls-online', { utmSource: 'facebook', utmMedium: 'social' }),
  mockLeadSource('ls-referral', 'tenant-1', 'Referral', null),
  mockLeadSource('ls-online-b', 'tenant-2', 'Online', null),
];

type MockLeadSourceHistory = types.CustomerLeadSourceHistoryEntry & { tenantId: string };

/** Blank or null becomes null (so a UTM field inherits), otherwise trimmed. */
function toNullableText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function mockGLAccount(
  id: string,
  accountNumber: string,
  accountName: string,
  accountType: types.GLAccountType,
  normalBalance: 'DEBIT' | 'CREDIT',
  parentId?: string
): types.GLAccount {
  return {
    id,
    tenantId: 'tenant-1',
    accountNumber,
    accountName,
    accountType,
    normalBalance,
    parentId,
    isPostable: true,
    hasJournalLines: false,
    createdAt: new Date().toISOString(),
  };
}

const MOCK_GL_ACCOUNTS: types.GLAccount[] = [
  mockGLAccount('gl-acc-1000', '1000', 'Cash', types.GLAccountType.ASSET, 'DEBIT'),
  mockGLAccount('gl-acc-1200', '1200', 'Accounts Receivable', types.GLAccountType.ASSET, 'DEBIT'),
  mockGLAccount('gl-acc-1300', '1300', 'Inventory - Materials', types.GLAccountType.ASSET, 'DEBIT'),
  mockGLAccount('gl-acc-2000', '2000', 'Accounts Payable', types.GLAccountType.LIABILITY, 'CREDIT'),
  mockGLAccount('gl-acc-2200', '2200', 'Sales Tax Payable', types.GLAccountType.LIABILITY, 'CREDIT'),
  mockGLAccount('gl-acc-4000', '4000', 'Service Revenue', types.GLAccountType.REVENUE, 'CREDIT'),
  mockGLAccount('gl-acc-4001', '4001', 'Office Cleaning Revenue', types.GLAccountType.REVENUE, 'CREDIT', 'gl-acc-4000'),
  // Contra-revenue: REVENUE type with a DEBIT normal balance.
  mockGLAccount('gl-acc-4900', '4900', 'Sales Discounts', types.GLAccountType.REVENUE, 'DEBIT'),
  mockGLAccount('gl-acc-5000', '5000', 'Cost of Goods Sold', types.GLAccountType.EXPENSE, 'DEBIT'),
  mockGLAccount('gl-acc-5001', '5001', 'Cost of Supplies', types.GLAccountType.EXPENSE, 'DEBIT'),
];

/** Journal entries as the mock stores them; reversal links are computed on the way out. */
type MockJournalEntry = Omit<types.JournalEntry, 'reversedByEntryId' | 'reversesEntryId'>;

function mockJournalLine(
  entryId: string,
  index: number,
  account: types.GLAccount,
  debit: string,
  credit: string
): types.JournalEntryLine {
  return {
    id: `${entryId}-l${index}`,
    journalEntryId: entryId,
    glAccountId: account.id,
    accountNumber: account.accountNumber,
    accountName: account.accountName,
    accountType: account.accountType,
    debit,
    credit,
  };
}

const glAccountById = (id: string) => MOCK_GL_ACCOUNTS.find((a) => a.id === id)!;

/** A couple of manual adjustments so /ledger has something to show offline. */
const MOCK_JOURNAL_ENTRIES: MockJournalEntry[] = [
  {
    id: 'je-seed-1',
    tenantId: 'tenant-1',
    entryDate: '2026-09-30',
    memo: 'Supplies bought with petty cash',
    sourceType: 'ADJUSTMENT',
    sourceId: 'manual-seed-1',
    createdBy: 'user-1',
    createdByEmail: 'alice@company-a.com',
    createdAt: '2026-09-30T15:00:00.000Z',
    lines: [
      mockJournalLine('je-seed-1', 1, glAccountById('gl-acc-5001'), '45.00', '0.00'),
      mockJournalLine('je-seed-1', 2, glAccountById('gl-acc-1000'), '0.00', '45.00'),
    ],
  },
  {
    id: 'je-seed-2',
    tenantId: 'tenant-1',
    entryDate: '2026-10-05',
    memo: 'Reclassify office cleaning revenue',
    sourceType: 'ADJUSTMENT',
    sourceId: 'manual-seed-2',
    createdBy: 'user-4',
    createdByEmail: 'dave@company-a.com',
    createdAt: '2026-10-05T16:30:00.000Z',
    lines: [
      mockJournalLine('je-seed-2', 1, glAccountById('gl-acc-4000'), '120.00', '0.00'),
      mockJournalLine('je-seed-2', 2, glAccountById('gl-acc-4001'), '0.00', '120.00'),
    ],
  },
];

/**
 * A few audit rows so Settings > Audit log has something to show offline. The
 * mock does not write rows when it performs actions; these are static.
 */
type MockAuditLog = types.AuditLogEntry & { tenantId: string };

const MOCK_AUDIT_LOGS: MockAuditLog[] = [
  {
    id: 'audit-seed-1', tenantId: 'tenant-1', actorUserId: 'user-1', actorEmail: 'alice@company-a.com',
    action: 'user.role_change', targetUserId: 'user-5', targetEmail: 'tom@company-a.com',
    details: { from: 'OFFICE_MANAGER', to: 'TECHNICIAN' }, createdAt: '2026-09-28T14:05:00.000Z',
  },
  {
    id: 'audit-seed-2', tenantId: 'tenant-1', actorUserId: 'user-1', actorEmail: 'alice@company-a.com',
    action: 'gl.journal_entry_manual', targetUserId: null, targetEmail: null,
    details: { target: { type: 'journal_entry', id: 'je-seed-1', label: 'Supplies bought with petty cash' }, entryDate: '2026-09-30', sourceType: 'ADJUSTMENT', sourceId: 'manual-seed-1' },
    createdAt: '2026-09-30T15:00:00.000Z',
  },
  {
    id: 'audit-seed-3', tenantId: 'tenant-1', actorUserId: 'user-4', actorEmail: 'dave@company-a.com',
    action: 'tax_rate.update', targetUserId: null, targetEmail: null,
    details: { target: { type: 'tax_rate', id: 'tax-1', label: 'State Sales Tax' }, changes: { rate: { from: '0.065', to: '0.07' } } },
    createdAt: '2026-10-02T09:30:00.000Z',
  },
  {
    id: 'audit-seed-4', tenantId: 'tenant-1', actorUserId: 'user-1', actorEmail: 'alice@company-a.com',
    action: 'company_settings.update', targetUserId: null, targetEmail: null,
    details: null, createdAt: '2026-10-06T11:00:00.000Z',
  },
  {
    id: 'audit-seed-5', tenantId: 'tenant-2', actorUserId: 'user-2', actorEmail: 'bob@company-b.com',
    action: 'user.update', targetUserId: 'user-2', targetEmail: 'bob@company-b.com',
    details: { email: { from: 'bob@b.com', to: 'bob@company-b.com' } }, createdAt: '2026-10-01T08:00:00.000Z',
  },
];

/** A stock move as the mock stores it (the backend's inventory_adjustments row). */
type MockInventoryAdjustment = types.InventoryAdjustment & { tenantId: string };

/** Signed quantity / amount with at most 2 decimals, in hundredths; null when invalid. */
function mockHundredths(value: unknown): number | null {
  const text = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  if (typeof text !== 'string' || !/^-?\d+(\.\d{1,2})?$/.test(text.trim())) return null;
  return decimalToCents(text.trim());
}

/** round2 half-up(|qty| x cost), in cents (qty and cost in hundredths). */
function mockStockValueCents(qtyHundredths: number, costCents: number): number {
  return Math.round((Math.abs(qtyHundredths) * costCents) / 100);
}

// ============ Utility Functions ============

/** Work order batch member as stored by the mock (print data is read live from jobs). */
type MockWorkOrderMember = Omit<types.WorkOrderBatchMember, 'job' | 'checklist' | 'reprint' | 'routeName' | 'confirmBlock'>;
type MockWorkOrderBatch = types.WorkOrderBatchSummary & { tenantId: string; members: MockWorkOrderMember[] };

/** A 409 like the backend's, with `details.problems` (tracker item 58). */
function mockWorkOrderError(status: number, message: string, code: string, problems: types.WorkOrderJobProblem[]): AxiosError {
  const error = mockApiError(status, message, code);
  (error.response!.data as Record<string, unknown>).details = { problems };
  return error;
}

async function delay(ms: number): Promise<void> {
  // Tests that exercise many calls turn the simulated latency off: globalThis.__MOCK_API_INSTANT__ = true
  if ((globalThis as { __MOCK_API_INSTANT__?: boolean }).__MOCK_API_INSTANT__) return;
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generateId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

function getCurrentTenantId(token: string): string | null {
  try {
    const decoded = JSON.parse(atob(token));
    return decoded.tenantId || null;
  } catch {
    return null;
  }
}

function getCurrentActor(token: string): { userId: string; email: string } | null {
  try {
    const decoded = JSON.parse(atob(token));
    return decoded.userId && decoded.email ? { userId: decoded.userId, email: decoded.email } : null;
  } catch {
    return null;
  }
}

function filterByTenant<T extends { tenantId: string }>(items: T[], tenantId: string): T[] {
  return items.filter((item) => item.tenantId === tenantId);
}

/**
 * A recurring schedule as the mock stores it: the relations are rebuilt from
 * current data on the way out, and occurrences record which dates have a job.
 */
type MockRecurringSchedule = Omit<types.RecurringSchedule, 'property' | 'customer' | 'templateJob'> & {
  occurrences: { date: string; jobId: string }[];
  deleted: boolean;
};

/** A billed flat contract period (backend recurring_billing_periods). */
interface MockBilledPeriod {
  tenantId: string;
  scheduleId: string;
  start: string;
  end: string;
  invoiceId: string;
  amount: string;
  trigger: 'MANUAL' | 'AUTOMATIC';
  createdAt: string;
}

/** An attachment as the mock stores it (tracker item 47). */
type MockAttachment = types.Attachment & { tenantId: string; blob: Blob; deletedAt: string | null };

/** Checklist template row with its ordered steps (tracker item 44). */
interface MockChecklistTemplate {
  id: string;
  tenantId: string;
  name: string;
  isActive: boolean;
  steps: types.ChecklistTemplateStep[];
  createdAt: string;
  updatedAt: string;
}

/** Customer portal (tracker item 45): a login's registration state and link, kept next to the mock users. */
interface MockPortalAccount {
  userId: string;
  tenantId: string;
  status: 'PENDING_EMAIL' | 'ACTIVE';
  /** The ONE linked customer; null = unlinked */
  customerId: string | null;
  name: string;
  phone: string | null;
  accessRequest: { status: types.PortalAccessRequestStatus; createdAt: string } | null;
  /** How the login got its link, and when (shown to staff on the customer page) */
  linkedVia?: types.CustomerPortalLogin['linkedVia'];
  linkedAt?: string;
}

/** A verified registration that could not be linked automatically (staff Portal access requests). */
interface MockAccessRequest {
  id: string;
  tenantId: string;
  userId: string;
  status: types.PortalAccessRequestStatus;
  flagged: boolean;
  createdAt: string;
  reason: types.PortalReviewReason;
  codeGiven: boolean;
  candidates: types.PortalAccessRequest['candidates'];
  decidedAt: string | null;
  linkedCustomerId: string | null;
  declineReason: string | null;
}

/** Service requests (tracker item 45 phase 2): the shared inbox. */
interface MockServiceRequest {
  id: string;
  tenantId: string;
  customerId: string | null;
  propertyId: string | null;
  source: types.ServiceRequestSource;
  status: types.ServiceRequestStatus;
  requestedItemId: string | null;
  requestedItemName: string | null;
  serviceAddress: string | null;
  preferredDates: string[];
  preferredTimeOfDay: types.ServiceRequestTimeOfDay | null;
  notes: string | null;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  convertedJobId: string | null;
  decidedById: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

/** Reschedule requests (tracker item 45 phase 2). */
interface MockRescheduleRequest {
  id: string;
  tenantId: string;
  jobId: string;
  customerId: string;
  requestedByUserId: string;
  originalDate: string | null;
  originalStartTime: string | null;
  requestedDate: string;
  requestedStartTime: string | null;
  requestedWindow: string | null;
  reason: string | null;
  status: types.RescheduleRequestStatus;
  decidedById: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  approvedDate: string | null;
  approvedStartTime: string | null;
  createdAt: string;
}

interface MockPortalPayload {
  /** Normalised invoice code (no hyphen), or null */
  code: string | null;
  address: string | null;
  phoneLast4: string | null;
  invoiceNumber: string | null;
  invoiceAmount: string | null;
}

interface MockPortalToken {
  token: string;
  purpose: 'VERIFY' | 'RESET' | 'INVITE';
  userId: string;
  tenantId: string;
  expiresAt: number;
  usedAt: number | null;
  payload: MockPortalPayload | null;
}

/** The backend's one answer to a registration (portal.service REGISTER_MESSAGE). */
const MOCK_PORTAL_REGISTER_MESSAGE =
  'Thanks. If this email address can be registered, we have sent a message to it with a link to confirm it. ' +
  'The link works once and expires in 24 hours.';
const MOCK_PORTAL_RESET_MESSAGE =
  'If there is an online account for this email address, we have sent a message to it with a link to choose a new password.';

function formatMockCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/** Case, punctuation, "St" = "Street" and a few more, enough for the mock's address proof. */
function normalizeMockAddress(value: string): string {
  const words: Record<string, string> = { st: 'street', ave: 'avenue', rd: 'road', ln: 'lane', dr: 'drive', ct: 'court', blvd: 'boulevard', n: 'north', s: 'south', e: 'east', w: 'west' };
  return value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean).map((w) => words[w] ?? w).join(' ');
}

// ============ Mock API Client ============

/** A property back in the geocoding queue (tracker item 41). */
const MOCK_GEOCODE_RESET: Pick<types.Property, 'latitude' | 'longitude' | 'geocodeStatus' | 'geocodePrecision'> = {
  latitude: null,
  longitude: null,
  geocodeStatus: 'PENDING',
  geocodePrecision: null,
};

/** A planned (not saved) placement the mock conflict check counts as a job (bulk preview, tracker item 41). */
interface MockPlacement {
  id: string;
  assignedTo: string | null;
  scheduledDate: string | null;
  scheduledStartTime: string;
  duration: number;
}

export class MockAPIClient implements IAPIClient {
  private token: string | null = null;
  private users = MOCK_USERS.map((u) => ({ ...u }));
  private customers = [...MOCK_CUSTOMERS];
  private properties = MOCK_PROPERTIES.map((p) => ({ ...p }));
  private items = MOCK_ITEMS.map((i) => ({ ...i }));
  private priceCharts = MOCK_PRICE_CHARTS.map((c) => ({ ...c, tiers: c.tiers.map((t) => ({ ...t })) }));
  private itemCategories = MOCK_ITEM_CATEGORIES.map((c) => ({ ...c }));
  private taxRates = [...MOCK_TAX_RATES];
  private discounts = MOCK_DISCOUNTS.map((d) => ({ ...d }));
  private customerDiscountLinks: { customerId: string; discountId: string }[] = [];
  private propertyDiscountLinks: { propertyId: string; discountId: string }[] = [];
  private leadSources = MOCK_LEAD_SOURCES.map((l) => ({ ...l }));
  private customerLeadSourceHistory: MockLeadSourceHistory[] = [];
  private glAccounts = [...MOCK_GL_ACCOUNTS];
  private jobs: Array<types.Job & { tenantId: string }> = [];
  private invoices: types.Invoice[] = [];
  private payments: types.Payment[] = [];
  private journalEntries: MockJournalEntry[] = MOCK_JOURNAL_ENTRIES.map((e) => ({ ...e, lines: e.lines.map((l) => ({ ...l })) }));
  /** `${tenantId}:${customerId}:${periodKey}` of every finance charge assessed (one per period). */
  private financeChargePeriods = new Set<string>();
  private recurringSchedules: MockRecurringSchedule[] = [];
  /** Billed flat contract periods (one per schedule per period start) */
  private billedPeriods: MockBilledPeriod[] = [];
  private autoInvoiceRuns: Array<types.AutoInvoiceRunSummary & { tenantId: string }> = [];
  /** Automated dunning (tracker item 36): schedule per tenant, `${invoiceId}:${stepId}` notices, runs */
  private dunningSteps = new Map<string, types.DunningStep[]>();
  private dunningNotices = new Set<string>();
  private dunningRuns: Array<types.DunningRunSummary & { tenantId: string }> = [];
  private auditLogs: MockAuditLog[] = MOCK_AUDIT_LOGS.map((row) => ({ ...row }));
  private inventoryAdjustments: MockInventoryAdjustment[] = [];
  /** One settings document per tenant, created with defaults on first read (like the backend) */
  private companySettings = new Map<string, types.CompanySettings>();
  /** Dispatch (tracker item 40): per-technician hour overrides and time off */
  private workHourOverrides = new Map<string, types.WorkHours>();
  private timeOffEntries: Array<types.TimeOff & { tenantId: string }> = [];
  /** Dispatch routes (tracker item 57), scoped by tenant. */
  private dispatchRoutes: Array<types.DispatchRoute & { tenantId: string }> = [];
  private technicianRouteIds = new Map<string, string[]>();
  private routeAssignments: Array<types.RouteAssignment & { tenantId: string }> = [];
  /** Work order batches (tracker item 58): members keep only ids and copies; print data is always current */
  private workOrderBatches: MockWorkOrderBatch[] = [];
  /** Checklist templates and job checklist steps (tracker item 44), scoped by tenant. */
  private checklistTemplates: MockChecklistTemplate[] = [];
  private jobChecklistSteps: Array<types.JobChecklistStep & { tenantId: string }> = [];
  /** Attachments (tracker item 47): metadata plus the stored bytes; soft-deleted rows are kept. */
  private attachments: MockAttachment[] = [];
  /** Technician time entries (tracker item 42), scoped by tenant. */
  private technicianTimeEntries: Array<types.TechnicianTimeEntry & { tenantId: string }> = [];
  /** Job signatures (tracker item 42), scoped by tenant; the PNG is kept as a Blob. */
  private technicianSignatures: Array<types.JobSignature & { tenantId: string; blob: Blob }> = [];
  /** Estimates (tracker item 39): kept in mockEstimates.ts, over this client's records */
  readonly mockEstimates = new MockEstimates({
    office: () => this.requireOfficeUser(),
    portalCustomer: () => {
      const scope = this.portalCustomerScope();
      return { tenantId: scope.tenantId, customerId: scope.customerId, userId: scope.user.id, name: scope.account.name || scope.user.email };
    },
    customer: (tenantId, id) => this.customers.find((c) => c.id === id && c.tenantId === tenantId),
    property: (tenantId, id) => this.properties.find((p) => p.id === id && p.tenantId === tenantId && !(p as { deletedAt?: string | null }).deletedAt),
    item: (tenantId, id) => this.items.find((i) => i.id === id && i.tenantId === tenantId && !i.deletedAt),
    taxRate: (tenantId, propertyId) => {
      const property = this.properties.find((p) => p.id === propertyId && p.tenantId === tenantId);
      return Number(this.taxRates.find((t) => t.id === property?.taxRateId)?.rate ?? 0);
    },
    companyName: (tenantId) => this.mockCompanySettings(tenantId).companyName,
    createJob: (data) => this.createJob(data),
    addJobLineItem: (jobId, data) => this.addJobLineItem(jobId, data),
    createRecurringSchedule: (data) => this.createRecurringSchedule(data),
    bundleTemplate: (tenantId, bundleId) => this.mockBundles.bundleTemplate(tenantId, bundleId),
    createEnrollmentFromEstimate: (tenantId, input) => this.mockBundles.createFromEstimate(tenantId, input),
  });

  /** Conversation inbox (tracker item 56): kept in mockConversations.ts, over this client's records */
  readonly mockConversations = new MockConversations({
    currentUser: () => this.currentMockUser(),
    inboxAccess: (tenantId) => this.mockCompanySettings(tenantId).inboxAccess ?? 'STAFF',
    customer: (tenantId, id) => this.customers.find((c) => c.id === id && c.tenantId === tenantId),
    customers: (tenantId) => this.customers.filter((c) => c.tenantId === tenantId),
    job: (tenantId, id) => this.jobs.find((j) => j.id === id && j.tenantId === tenantId),
    user: (tenantId, id) => this.users.find((u) => u.id === id && u.tenantId === tenantId),
    portalCustomer: () => {
      const scope = this.portalCustomerScope();
      return { tenantId: scope.tenantId, customerId: scope.customerId, userId: scope.user.id, email: scope.user.email };
    },
  });

  async login(tenantSlug: string, email: string, password: string): Promise<types.LoginResponse> {
    await delay(500); // Simulate network delay

    // Find tenant
    const tenant = MOCK_TENANTS.find((t) => t.slug === tenantSlug);
    if (!tenant) {
      throw new Error('Tenant not found');
    }

    // Find active user in tenant
    const user = this.users.find((u) => u.tenantId === tenant.id && u.email === email && u.isActive);
    if (!user) {
      throw new Error('Invalid email or password');
    }

    // Note: In real app, we'd verify password hash
    if (password !== user.password) {
      throw new Error('Invalid email or password');
    }

    // Customer portal: off for the company, or not yet confirmed (its password is unusable until then)
    if (user.role === types.UserRole.CUSTOMER_PORTAL) {
      if (this.portalOff(tenant.id)) throw mockApiError(403, 'The customer portal is not available', 'PORTAL_DISABLED');
      if (this.portalAccounts.get(user.id)?.status === 'PENDING_EMAIL') throw new Error('Invalid email or password');
    }

    // MFA (tracker item 63): with MFA on a correct password is only a challenge
    const step = this.mfaCall(() => this.mockMfa.loginStep(user));
    if ('challenge' in step) {
      throw new types.MfaChallengeRequiredError(step.challenge.challengeToken, step.challenge.expiresAt);
    }
    return this.mockSession(user, step.enrollmentRequired);
  }

  /** A mock session for `user` (the token is base64 JSON, like a JWT payload). */
  private mockSession(user: MockUser, mfaEnrollmentRequired: boolean): types.LoginResponse {
    const tokenPayload = {
      userId: user.id,
      tenantId: user.tenantId,
      email: user.email,
      role: user.role,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
    };

    const mockToken = btoa(JSON.stringify(tokenPayload));

    return {
      token: mockToken,
      user: {
        id: user.id,
        email: user.email,
        name: user.email.split('@')[0],
        role: user.role,
        tenantId: user.tenantId,
        mustChangePassword: user.mustChangePassword,
        mfaEnrollmentRequired,
      },
    };
  }

  // ---- MFA / TOTP (tracker item 63): see mockMfa.ts. The mock accepts MOCK_TOTP_CODE ("123456").

  /** In-memory MFA state (exposed for tests: `mockMfa.now` moves the clock) */
  readonly mockMfa = new MockMfa();

  private mfaCall<T>(fn: () => T): T {
    try {
      return fn();
    } catch (error) {
      if (error instanceof MockMfaError) throw mockApiError(error.status, error.message, error.code);
      throw error;
    }
  }

  async verifyMfaLogin(challengeToken: string, factor: types.MfaFactor): Promise<types.LoginResponse> {
    await delay(200);
    const user = this.mfaCall(() =>
      this.mockMfa.verifyChallenge(challengeToken, factor, (tenantId, userId) =>
        this.users.find((u) => u.id === userId && u.tenantId === tenantId && u.isActive)
      )
    ) as MockUser;
    return this.mockSession(user, false);
  }

  async getMfaStatus(): Promise<types.MfaStatus> {
    const user = this.currentMockUser();
    return this.mfaCall(() => this.mockMfa.status(user));
  }

  async startMfaEnrollment(password: string): Promise<types.MfaEnrollmentStart> {
    const user = this.currentMockUser();
    const issuer = this.mockCompanySettings(user.tenantId).companyName || 'CinRM';
    return this.mfaCall(() => this.mockMfa.start(user, password, issuer));
  }

  async confirmMfaEnrollment(code: string): Promise<types.MfaEnrollmentConfirm> {
    const user = this.currentMockUser();
    const recoveryCodes = this.mfaCall(() => this.mockMfa.confirm(user, code));
    return { recoveryCodes, token: this.mockSession(user, false).token };
  }

  async disableMfa(reauth: types.MfaReauth): Promise<void> {
    const user = this.currentMockUser();
    this.mfaCall(() => this.mockMfa.disable(user, reauth));
  }

  async regenerateMfaRecoveryCodes(reauth: types.MfaReauth): Promise<string[]> {
    const user = this.currentMockUser();
    return this.mfaCall(() => this.mockMfa.regenerate(user, reauth));
  }

  async getMfaPolicy(): Promise<types.MfaPolicy> {
    const user = this.currentMockUser();
    return this.mfaCall(() => this.mockMfa.getPolicy(user));
  }

  async updateMfaPolicy(requiredRoles: types.MfaEnforceableRole[]): Promise<types.MfaPolicy> {
    const user = this.currentMockUser();
    return this.mfaCall(() => this.mockMfa.setPolicy(user, requiredRoles));
  }

  async listUserMfaStatuses(): Promise<types.UserMfaStatus[]> {
    const user = this.currentMockUser();
    return this.mfaCall(() => this.mockMfa.listStatuses(user));
  }

  async resetUserMfa(userId: string, reauth: types.MfaReauth): Promise<{ hadMfa: boolean }> {
    const user = this.currentMockUser();
    const target = this.users.find((u) => u.id === userId && u.tenantId === user.tenantId);
    return this.mfaCall(() => this.mockMfa.reset(user, target, reauth));
  }

  // ---- Setup checklist (tracker item 75): see mockSetup.ts. ADMIN only. ----

  /** In-memory checklist state (exposed for tests: `mockSetup.show(tenantId)` starts a tenant fresh and visible) */
  readonly mockSetup = new MockSetup((tenantId) => this.setupSignals(tenantId));

  private setupSignals(tenantId: string): MockSetupSignals {
    const company = this.mockCompanySettings(tenantId);
    const items = this.items.filter((i) => i.tenantId === tenantId && !i.deletedAt);
    return {
      company_profile: !!company.companyName.trim() && [company.addressLine1, company.phone, company.email].some((v) => !!v?.trim()),
      tax_rate: this.taxRates.some((r) => r.tenantId === tenantId),
      items: items.length > 0,
      price_chart: this.priceCharts.some((c) => c.tenantId === tenantId && c.active),
      users: this.users.some((u) => u.tenantId === tenantId && u.isActive && ['OFFICE_MANAGER', 'OFFICE', 'TECHNICIAN'].includes(u.role)),
      email_sender: this.notificationProviders.get(tenantId)?.status === 'VERIFIED',
      onlinePayments: this.onlineConnections.get(tenantId)?.status === 'CONNECTED',
      portal: company.portalEnabled !== false && items.some((i) => i.portalRequestable),
    };
  }

  private setupCall<T>(fn: (tenantId: string) => T): T {
    const user = this.currentMockUser();
    if (user.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    try {
      return fn(user.tenantId);
    } catch (error) {
      if (error instanceof MockSetupError) throw mockApiError(error.status, error.message, error.code);
      throw error;
    }
  }

  async getSetup(): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.get(t));
  }

  async skipSetupStep(key: types.SetupStepKey): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.skip(t, key));
  }

  async unskipSetupStep(key: types.SetupStepKey): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.unskip(t, key));
  }

  async markSetupStepDone(key: types.SetupStepKey): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.markDone(t, key));
  }

  async unmarkSetupStepDone(key: types.SetupStepKey): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.unmarkDone(t, key));
  }

  async setSetupDismissed(dismissed: boolean): Promise<types.SetupProgress> {
    return this.setupCall((t) => this.mockSetup.setDismissed(t, dismissed));
  }

  // ---- Staff in-app notification center (tracker item 62): see mockStaffNotifications.ts ----

  /** In-memory alerts and mutes (exposed for tests: `mockStaffNotifications.raise(tenantId, type, input)`) */
  readonly mockStaffNotifications = new MockStaffNotifications({
    currentUser: () => this.currentMockUser(),
    users: (tenantId) => this.users.filter((u) => u.tenantId === tenantId),
  });

  async listStaffNotifications(query?: types.StaffNotificationListQuery) { await delay(50); return this.mockStaffNotifications.list(query); }
  async getStaffNotificationUnreadCount() { await delay(30); return this.mockStaffNotifications.unreadCount(); }
  async markStaffNotificationRead(id: string) { await delay(30); return this.mockStaffNotifications.markRead(id); }
  async markAllStaffNotificationsRead(input?: types.StaffNotificationMarkAllInput) { await delay(30); return this.mockStaffNotifications.markAllRead(input); }
  async getStaffNotificationPreferences() { await delay(30); return this.mockStaffNotifications.preferences(); }
  async setStaffNotificationPreferences(muted: Partial<Record<types.StaffNotificationType, boolean>>) { await delay(30); return this.mockStaffNotifications.setPreferences(muted); }

  // ---- Service bundles (tracker item 85): see mockBundles.ts ----

  /** In-memory bundles, enrollments, renewals and alert routing (visit jobs are ordinary mock jobs) */
  readonly mockBundles: MockBundles = new MockBundles({
    currentUser: () => {
      const user = this.currentMockUser();
      return { id: user.id, tenantId: user.tenantId, role: user.role, email: user.email, isActive: user.isActive !== false, firstName: user.firstName ?? null, lastName: null };
    },
    users: (tenantId) => this.users.filter((u) => u.tenantId === tenantId).map((u) => ({ id: u.id, tenantId: u.tenantId, role: u.role, email: u.email, isActive: u.isActive !== false, firstName: u.firstName ?? null, lastName: null })),
    item: (tenantId, id) => {
      const item = this.items.find((i) => i.id === id && i.tenantId === tenantId);
      return item ? { id: item.id, name: item.name, itemType: item.itemType, isActive: (item as { isActive?: boolean }).isActive !== false, deletedAt: item.deletedAt } : null;
    },
    property: (tenantId, id) => {
      const property = this.properties.find((p) => p.id === id && p.tenantId === tenantId);
      return property ? { id: property.id, customerId: property.customerId, address: property.address, city: property.city, state: property.state } : null;
    },
    customer: (tenantId, id) => {
      const customer = this.customers.find((c) => c.id === id && c.tenantId === tenantId);
      return customer ? { id: customer.id, name: customer.name } : null;
    },
    priceFor: (tenantId, propertyId, itemId) => {
      const item = this.items.find((i) => i.id === itemId && i.tenantId === tenantId);
      const property = this.properties.find((p) => p.id === propertyId && p.tenantId === tenantId);
      if (!item || !property) throw mockApiError(404, 'Item or property not found', 'NOT_FOUND');
      const quote = this.mockPriceItem(tenantId, item, property);
      return { unitPrice: quote.unitPrice, priceSource: quote.source, taxRate: '0' };
    },
    createVisitJob: (tenantId, input) => {
      const property = this.properties.find((p) => p.id === input.propertyId && p.tenantId === tenantId)!;
      const customer = this.customers.find((c) => c.id === property.customerId && c.tenantId === tenantId);
      const item = this.items.find((i) => i.id === input.itemId && i.tenantId === tenantId)!;
      const assignee = this.users.find((u) => u.id === input.assignedTo && u.tenantId === tenantId);
      const now = new Date().toISOString();
      const lineTotal = Number(input.quantity) * Number(input.unitPrice);
      const job: types.Job & { tenantId: string } = {
        id: generateId('job'), tenantId, routeId: property.routeId ?? customer?.routeId ?? null, routeSource: 'DEFAULT',
        customerId: property.customerId, customer: { id: property.customerId, name: customer?.name || '' },
        property: { id: property.id, address: property.address, city: property.city, state: property.state, customerId: property.customerId }, propertyId: property.id,
        assignedTo: input.assignedTo, assignedToUser: assignee ? { id: assignee.id, email: assignee.email } : null,
        scheduledDate: input.scheduledDate, notes: null, leadSourceId: null, invoiceId: null, status: types.JobStatus.SCHEDULED,
        lineItems: [{
          id: generateId('li'), itemId: item.id, item: { id: item.id, name: item.name, description: item.description, itemType: item.itemType },
          quantity: input.quantity, unitPrice: input.unitPrice, priceSource: 'MANUAL', priceChartId: null, sizeSnapshot: null, sizeUnitSnapshot: null,
          pricingModel: 'PER_ITEM', taxRate: '0', lineTotal: String(lineTotal), lineTax: '0', lineAmount: String(lineTotal), createdAt: now,
        }],
        subtotal: String(lineTotal), totalTax: '0', total: String(lineTotal), checklist: { total: 0, completed: 0, requiredIncomplete: 0 },
        enrollmentId: input.enrollment.id, enrollmentStep: input.enrollment.step, enrollmentStepCount: input.enrollment.stepCount, bundleName: input.enrollment.bundleName,
        createdAt: now, updatedAt: now,
      };
      this.jobs.push(job);
      return { id: job.id };
    },
    job: (tenantId, id) => {
      const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
      if (!job) return null;
      const line = job.lineItems[0];
      return {
        id: job.id, status: job.status, scheduledDate: job.scheduledDate, completedDate: job.completedDate ?? null, assignedTo: job.assignedTo,
        invoiceId: job.invoiceId ?? null, unitPrice: line?.unitPrice ?? null, lineTotal: line?.lineTotal ?? null, lineTax: line?.lineTax ?? null,
      };
    },
    cancelJob: (tenantId, id) => {
      const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
      if (job) { job.status = types.JobStatus.CANCELLED; job.updatedAt = new Date().toISOString(); }
    },
    setJobDate: (tenantId, id, date) => {
      const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
      if (job) { job.scheduledDate = date; job.updatedAt = new Date().toISOString(); }
    },
    setJobUnitPrice: (tenantId, id, unitPrice) => {
      const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
      const line = job?.lineItems[0];
      if (!job || !line) return;
      Object.assign(line, this.mockManualPrice(unitPrice));
      line.lineTotal = String(Number(line.quantity) * Number(line.unitPrice));
      line.lineAmount = String(Number(line.lineTotal) + Number(line.lineTax));
      this.updateMockJobTotals(job);
    },
    today: () => this.mockTechnicianToday(this.currentMockUser().tenantId),
    portal: () => {
      const scope = this.portalScope();
      return { tenantId: scope.tenantId, userId: scope.user.id, email: scope.user.email, customerId: scope.account.customerId };
    },
    ownProperties: (tenantId, customerId) =>
      this.properties
        .filter((p) => p.tenantId === tenantId && p.customerId === customerId && !(p as { deletedAt?: string | null }).deletedAt)
        .map((p) => ({ id: p.id, customerId: p.customerId, address: p.address, city: p.city, state: p.state })),
    alert: (tenantId, type, input) =>
      this.mockStaffNotifications.raise(tenantId, type, {
        title: input.title, body: input.body, link: { type: 'ENROLLMENT', id: input.enrollmentId },
        recipientUserIds: input.userIds, routeByRole: false, dedupeKey: `${type}:${input.enrollmentId}`,
      }),
  });

  async listServiceBundles(filters?: types.ServiceBundleListFilters) { await delay(60); return this.mockBundles.listBundles(filters); }
  async getServiceBundle(id: string) { await delay(60); return this.mockBundles.getBundle(id); }
  async createServiceBundle(data: types.CreateServiceBundleRequest) { await delay(80); return this.mockBundles.createBundle(data); }
  async updateServiceBundle(id: string, data: types.UpdateServiceBundleRequest) { await delay(80); return this.mockBundles.updateBundle(id, data); }
  async deleteServiceBundle(id: string) { await delay(60); this.mockBundles.deleteBundle(id); }
  async previewServiceBundle(id: string, data: { propertyId: string; startDate: string }) { await delay(80); return this.mockBundles.previewBundle(id, data); }
  async listEnrollments(filters?: types.EnrollmentListFilters) { await delay(60); return this.mockBundles.listEnrollments(filters); }
  async listPropertyEnrollments(propertyId: string, filters?: types.EnrollmentListFilters) { await delay(60); return this.mockBundles.listPropertyEnrollments(propertyId, filters); }
  async getEnrollment(id: string) { await delay(60); return this.mockBundles.getEnrollment(id); }
  async addBundleToProperty(propertyId: string, data: types.AddBundleRequest) { await delay(120); return this.mockBundles.addBundleToProperty(propertyId, data); }
  async updateEnrollmentAutoRenew(id: string, autoRenew: boolean) { await delay(60); return this.mockBundles.updateAutoRenew(id, autoRenew); }
  async cancelEnrollment(id: string, reason?: string) { await delay(80); return this.mockBundles.cancelEnrollment(id, reason); }
  async updateEnrollmentStepPrice(id: string, step: number, data: types.StepPriceRequest) { await delay(80); return this.mockBundles.updateStepPrice(id, step, data); }
  async getEnrollmentQuote(id: string, startDate?: string) { await delay(60); return this.mockBundles.getQuote(id, startDate); }
  async createRenewal(id: string, startDate?: string) { await delay(80); return this.mockBundles.createRenewal(id, startDate); }
  async createRenewalsBulk(enrollmentIds: string[], startDate?: string) { await delay(100); return this.mockBundles.createRenewalsBulk(enrollmentIds, startDate); }
  async listRenewals(filters?: types.RenewalListFilters) { await delay(60); return this.mockBundles.listRenewals(filters); }
  async exportRenewals(filters?: types.RenewalListFilters) { await delay(60); return this.mockBundles.exportRenewals(filters); }
  async approveEnrollment(id: string, data?: types.ApproveEnrollmentRequest) { await delay(100); return this.mockBundles.approve(id, data); }
  async declineEnrollment(id: string, reason?: string) { await delay(80); return this.mockBundles.decline(id, reason); }
  async listPendingEnrollments(filters?: types.PendingEnrollmentFilters) { await delay(60); return this.mockBundles.listPending(filters); }
  async listPortalRequestableBundles(query?: { propertyId?: string; startDate?: string }) { await delay(80); return this.mockBundles.requestableBundles(query); }
  async createPortalBundleRequest(data: types.PortalBundleRequestInput) {
    await delay(150);
    const { user } = this.portalScope();
    this.portalLimit(user.id, 'request', 30);
    return this.mockBundles.requestBundle(data);
  }
  async listPortalEnrollments(query?: types.PortalEnrollmentListQuery) { await delay(80); return this.mockBundles.listPortalEnrollments(query); }
  async getPortalEnrollment(id: string) { await delay(60); return this.mockBundles.getPortalEnrollment(id); }
  async setPortalEnrollmentAutoRenew(id: string, autoRenew: boolean) { await delay(80); return this.mockBundles.setPortalAutoRenew(id, autoRenew); }
  async getBundleAlertRouting() { await delay(60); return this.mockBundles.getRouting(); }
  async updateBundleAlertRouting(routing: Partial<types.BundleAlertRouting>) { await delay(80); return this.mockBundles.updateRouting(routing); }

  // ---- Customer satisfaction surveys (tracker item 69): see mockSurveys.ts ----

  /** In-memory survey settings, requests and answers (exposed for tests: `mockSurveys.scheduleForJob`, `tokenFor`) */
  readonly mockSurveys = new MockSurveys({
    currentUser: () => this.currentMockUser(),
    users: (tenantId) => this.users.filter((u) => u.tenantId === tenantId),
    job: (tenantId, jobId) => {
      const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
      return job ? { id: job.id, status: job.status, customerId: job.customerId, assignedTo: job.assignedTo, scheduledDate: job.scheduledDate, completedDate: job.completedDate ?? null } : null;
    },
    customer: (tenantId, customerId) => {
      const customer = this.customers.find((c) => c.id === customerId && c.tenantId === tenantId);
      return customer ? { id: customer.id, name: customer.name, email: customer.email, notificationOptOut: customer.notificationOptOut } : null;
    },
    companyName: (tenantId) => this.companySettings.get(tenantId)?.companyName ?? 'Your company',
    notifications: (tenantId) => ({
      enabled: !!this.notificationSettings.get(tenantId)?.enabled,
      templateActive: !!this.mockTemplates(tenantId).find((t) => t.eventKey === 'SURVEY_REQUEST')?.isActive,
    }),
    activateTemplate: (tenantId) => {
      const template = this.mockTemplates(tenantId).find((t) => t.eventKey === 'SURVEY_REQUEST');
      if (template) template.isActive = true;
    },
    alertLowScore: (tenantId, input) =>
      this.mockStaffNotifications.raise(tenantId, 'SURVEY_LOW_SCORE', {
        title: input.title,
        body: input.body,
        link: { type: 'JOB', id: input.jobId },
        dedupeKey: input.responseId,
      }),
  });

  async getSurveySettings() { await delay(60); return this.mockSurveys.getSettings(); }
  async updateSurveySettings(input: types.UpdateSurveySettingsInput) { await delay(80); return this.mockSurveys.updateSettings(input); }
  async listSurveyResponses(query?: types.SurveyListQuery) { await delay(60); return this.mockSurveys.list(query); }
  async getJobSurvey(jobId: string) { await delay(60); return this.mockSurveys.getJobSurvey(jobId); }
  async sendJobSurvey(jobId: string, idempotencyKey?: string) { await delay(80); return this.mockSurveys.sendJobSurvey(jobId, idempotencyKey); }
  async getSatisfactionReport(query?: types.SatisfactionReportQuery) { await delay(80); return this.mockSurveys.report(query); }
  async getPublicSurvey(token: string) { await delay(60); return this.mockSurveys.getPublic(token); }
  async submitPublicSurvey(token: string, answer: types.PublicSurveyAnswer) { await delay(80); return this.mockSurveys.submitPublic(token, answer); }

  // ---- Portal rollout campaigns (tracker item 81): see mockPortalRollout.ts ----

  /** In-memory rollout campaigns (exposed for tests); invitations create the same pending logins as "Invite to portal". */
  readonly mockPortalRollout = new MockPortalRollout({
    currentUser: () => this.currentMockUser(),
    customers: (tenantId) => this.customers.filter((c) => c.tenantId === tenantId) as Array<MockCustomer & { externalRef?: string | null }>,
    hasLogin: (tenantId, customerId) => [...this.portalAccounts.values()].some((a) => a.tenantId === tenantId && a.customerId === customerId),
    emailInUse: (tenantId, email) => this.users.some((u) => u.tenantId === tenantId && u.email.toLowerCase() === email.toLowerCase()),
    readiness: (tenantId) => ({
      portalEnabled: !this.portalOff(tenantId),
      notificationsEnabled: !!this.notificationSettings.get(tenantId)?.enabled,
      eventActive: !!this.mockTemplates(tenantId).find((t) => t.eventKey === 'PORTAL_ROLLOUT_WELCOME')?.isActive,
      connectionVerified: this.notificationProviders.get(tenantId)?.status === 'VERIFIED',
    }),
    invite: (tenantId, customerId, email, existingUserId) => {
      const existing = existingUserId ? this.users.find((u) => u.id === existingUserId) : undefined;
      let user: MockUser;
      if (existing) {
        user = existing;
      } else {
        const customer = this.customers.find((c) => c.id === customerId)!;
        user = this.createPendingPortalUser(tenantId, email.toLowerCase(), customer.name, customer.phone ?? null);
        Object.assign(this.portalAccounts.get(user.id)!, { customerId, linkedVia: 'INVITE', linkedAt: new Date().toISOString() });
      }
      this.issuePortalToken(user, 'INVITE', 7 * 24 * 3600_000, null);
      return user.id;
    },
    loginState: (userId) => {
      const user = this.users.find((u) => u.id === userId);
      const account = this.portalAccounts.get(userId);
      if (!user || !account) return 'MISSING';
      if (!user.isActive) return 'DISABLED';
      return account.status === 'ACTIVE' ? 'ACTIVE' : 'PENDING_EMAIL';
    },
  });

  async getPortalRolloutReadiness() { await delay(60); return this.mockPortalRollout.readiness(); }
  async previewPortalRollout(input: types.RolloutCampaignInput) { await delay(100); return this.mockPortalRollout.preview(input); }
  async createPortalRolloutCampaign(input: types.RolloutCampaignInput) { await delay(100); return this.mockPortalRollout.create(input); }
  async listPortalRolloutCampaigns(query?: types.RolloutCampaignListQuery) { await delay(60); return this.mockPortalRollout.list(query); }
  async getPortalRolloutCampaign(id: string, query?: types.RolloutRecipientQuery) { await delay(60); return this.mockPortalRollout.get(id, query); }
  async startPortalRolloutCampaign(id: string) { await delay(80); return this.mockPortalRollout.start(id); }
  async pausePortalRolloutCampaign(id: string) { await delay(80); return this.mockPortalRollout.pause(id); }
  async resumePortalRolloutCampaign(id: string) { await delay(80); return this.mockPortalRollout.resume(id); }
  async cancelPortalRolloutCampaign(id: string) { await delay(80); return this.mockPortalRollout.cancel(id); }
  async sendPortalRolloutBatch(id: string) { await delay(120); return this.mockPortalRollout.sendBatch(id); }
  async resendPortalRolloutCampaign(id: string) { await delay(100); return this.mockPortalRollout.resendAll(id); }
  async resendPortalRolloutRecipient(id: string, recipientId: string) { await delay(100); return this.mockPortalRollout.resendOne(id, recipientId); }
  async exportPortalRolloutRecipients(id: string) { await delay(100); return this.mockPortalRollout.exportCsv(id); }
  async getCustomerPortalRollout(customerId: string) { await delay(60); return this.mockPortalRollout.customerView(customerId); }

  // ---- Industry starter packs and recurring presets (tracker item 76): see mockStarter.ts ----

  /** Recurring presets, scoped by tenant */
  private recurringPresets: Array<types.RecurringPreset & { tenantId: string }> = [];

  /** In-memory starter packs (exposed for tests) */
  readonly mockStarter = new MockStarter({
    accountId: (tenantId, number) => this.glAccounts.find((a) => a.tenantId === tenantId && a.accountNumber === number)?.id ?? null,
    find: (tenantId, type, name) => {
      switch (type) {
        case 'ITEM_CATEGORY': {
          const c = this.itemCategories.find((x) => x.tenantId === tenantId && x.name === name);
          return c ? { id: c.id, deleted: false } : null;
        }
        case 'ITEM': {
          const i = this.items.find((x) => x.tenantId === tenantId && x.name === name);
          return i ? { id: i.id, deleted: !!i.deletedAt } : null;
        }
        case 'PRICE_CHART': {
          const c = this.priceCharts.find((x) => x.tenantId === tenantId && x.name === name);
          return c ? { id: c.id, deleted: false } : null;
        }
        case 'CHECKLIST_TEMPLATE': {
          const t = this.checklistTemplates.find((x) => x.tenantId === tenantId && x.name === name);
          return t ? { id: t.id, deleted: false } : null;
        }
        case 'LEAD_SOURCE': {
          const l = this.leadSources.find((x) => x.tenantId === tenantId && !x.parentId && x.name === name);
          return l ? { id: l.id, deleted: false } : null;
        }
        case 'RECURRING_PRESET': {
          const p = this.recurringPresets.find((x) => x.tenantId === tenantId && x.name === name);
          return p ? { id: p.id, deleted: false } : null;
        }
        default:
          return null;
      }
    },
    createCategory: (tenantId, name) => {
      const now = new Date().toISOString();
      const id = generateId('cat');
      this.itemCategories.push({ id, tenantId, name, createdAt: now, updatedAt: now } as (typeof this.itemCategories)[number]);
      return id;
    },
    create: (tenantId, type, definition, links) => {
      const now = new Date().toISOString();
      switch (type) {
        case 'CHECKLIST_TEMPLATE': {
          const def = definition as types.StarterPackChecklistTemplate;
          const id = generateId('ctpl');
          this.checklistTemplates.push({
            id, tenantId, name: def.name, isActive: true, createdAt: now, updatedAt: now,
            steps: def.steps.map((s, index) => ({ id: generateId('cts'), text: s.text, required: s.required, sortOrder: index })),
          });
          return { id, updatedAt: now };
        }
        case 'PRICE_CHART': {
          const def = definition as types.StarterPackPriceChart;
          const id = generateId('chart');
          this.priceCharts.push({
            id, tenantId, name: def.name, description: def.description ?? null, unit: def.unit as types.SizeUnit,
            pricingType: def.pricingType as types.PriceChartPricingType, overageRatePerUnit: def.overageRatePerUnit, active: true,
            tiers: def.tiers.map((t) => ({ id: generateId('tier'), upToSize: t.upToSize, price: t.price })), createdAt: now, updatedAt: now,
          });
          return { id, updatedAt: now };
        }
        case 'ITEM': {
          const def = definition as types.StarterPackItem;
          const id = generateId('item');
          this.items.push({
            id, tenantId, name: def.name, description: def.description ?? null, itemType: def.itemType as types.ItemType,
            unitPrice: def.unitPrice, unitCost: def.unitCost ?? null, unitOfMeasure: def.unitOfMeasure,
            revenueAccountId: links.revenueAccountId as string, expenseAccountId: links.expenseAccountId ?? null,
            categoryId: links.categoryId ?? null, taxable: def.taxable, quantityOnHand: '0', reorderPoint: def.reorderPoint ?? null,
            priceChartId: links.priceChartId ?? null, checklistTemplateId: links.checklistTemplateId ?? null, portalRequestable: false,
            createdAt: now, updatedAt: now, deletedAt: null,
          });
          return { id, updatedAt: now };
        }
        case 'LEAD_SOURCE': {
          const def = definition as types.StarterPackLeadSource;
          const id = generateId('ls');
          this.leadSources.push({
            id, tenantId, parentId: null, name: def.name, description: def.description ?? null, active: true,
            utmSource: null, utmMedium: null, utmCampaign: null, utmTerm: null, utmContent: null, createdAt: now, updatedAt: now,
          });
          return { id, updatedAt: now };
        }
        default: {
          const def = definition as types.StarterPackRecurringPreset;
          const id = generateId('rp');
          this.recurringPresets.push({
            id, tenantId, name: def.name, frequency: def.frequency, interval: def.interval, frequencyDetails: def.frequencyDetails ?? null,
            billingModel: def.billingModel as types.RecurringPreset['billingModel'],
            billingFrequency: (def.billingFrequency as types.RecurringPreset['billingFrequency']) ?? null,
            billingInterval: def.billingInterval ?? 1, notes: def.notes ?? null, isActive: true, createdAt: now, updatedAt: now,
          });
          return { id, updatedAt: now };
        }
      }
    },
    restoreItem: (tenantId, id, def, links) => {
      const item = this.items.find((x) => x.id === id && x.tenantId === tenantId) as MockItem;
      const now = new Date().toISOString();
      Object.assign(item, {
        description: def.description ?? null, itemType: def.itemType as types.ItemType, unitPrice: def.unitPrice, unitCost: def.unitCost ?? null,
        unitOfMeasure: def.unitOfMeasure, revenueAccountId: links.revenueAccountId as string, expenseAccountId: links.expenseAccountId ?? null,
        categoryId: links.categoryId ?? null, taxable: def.taxable, reorderPoint: def.reorderPoint ?? null, priceChartId: links.priceChartId ?? null,
        checklistTemplateId: links.checklistTemplateId ?? null, deletedAt: null, updatedAt: now,
      });
      return { id, updatedAt: now };
    },
    state: (tenantId, type, id) => {
      const pick = (row: { name: string; updatedAt: string; deletedAt?: string | null } | undefined) =>
        row && !row.deletedAt ? { name: row.name, updatedAt: row.updatedAt } : null;
      switch (type) {
        case 'ITEM': return pick(this.items.find((x) => x.id === id && x.tenantId === tenantId));
        case 'PRICE_CHART': return pick(this.priceCharts.find((x) => x.id === id && x.tenantId === tenantId));
        case 'CHECKLIST_TEMPLATE': return pick(this.checklistTemplates.find((x) => x.id === id && x.tenantId === tenantId));
        case 'LEAD_SOURCE': return pick(this.leadSources.find((x) => x.id === id && x.tenantId === tenantId));
        case 'RECURRING_PRESET': return pick(this.recurringPresets.find((x) => x.id === id && x.tenantId === tenantId));
        default: return null;
      }
    },
    isUsed: (tenantId, type, id) => {
      switch (type) {
        case 'ITEM':
          return this.jobs.some((j) => j.tenantId === tenantId && j.lineItems.some((l) => l.itemId === id))
            || this.invoices.some((inv) => (inv as { tenantId?: string }).tenantId === tenantId && inv.lineItems?.some((l) => l.itemId === id));
        case 'PRICE_CHART': {
          const chart = this.priceCharts.find((x) => x.id === id);
          const usage = chart ? this.mockPriceChartUsage(chart) : { itemCount: 0, jobLineCount: 0 };
          return usage.itemCount > 0 || usage.jobLineCount > 0;
        }
        case 'CHECKLIST_TEMPLATE': {
          const template = this.checklistTemplates.find((x) => x.id === id);
          return !!template && this.toChecklistTemplate(template).inUse;
        }
        case 'LEAD_SOURCE': {
          const node = this.leadSources.find((x) => x.id === id);
          if (!node) return false;
          const view = this.toMockLeadSource(node);
          return view.inUse || view.childCount > 0;
        }
        default:
          return false;
      }
    },
    remove: (tenantId, type, id) => {
      switch (type) {
        case 'ITEM': {
          const item = this.items.find((x) => x.id === id && x.tenantId === tenantId);
          if (item) item.deletedAt = new Date().toISOString();
          break;
        }
        case 'PRICE_CHART': this.priceCharts = this.priceCharts.filter((x) => x.id !== id); break;
        case 'CHECKLIST_TEMPLATE': this.checklistTemplates = this.checklistTemplates.filter((x) => x.id !== id); break;
        case 'LEAD_SOURCE': this.leadSources = this.leadSources.filter((x) => x.id !== id); break;
        case 'RECURRING_PRESET': this.recurringPresets = this.recurringPresets.filter((x) => x.id !== id); break;
        default: break;
      }
    },
    audit: (action, details) => this.mockStarterAudit(action, details),
  });

  private starterCall<T>(fn: (tenantId: string) => T): T {
    const user = this.currentMockUser();
    if (user.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    try {
      return fn(user.tenantId);
    } catch (error) {
      if (error instanceof MockStarterError) throw mockApiError(error.status, error.message, error.code);
      throw error;
    }
  }

  async listStarterPacks(): Promise<types.StarterPackSummary[]> {
    await delay(150);
    return this.starterCall((t) => this.mockStarter.listPacks(t));
  }

  async getStarterPack(packKey: types.StarterPackKey): Promise<types.StarterPack> {
    await delay(100);
    return this.starterCall(() => this.mockStarter.getPack(packKey));
  }

  async previewStarterPack(packKey: types.StarterPackKey): Promise<types.StarterPreviewResponse> {
    await delay(150);
    return this.starterCall((t) => this.mockStarter.preview(t, packKey));
  }

  async applyStarterPack(packKey: types.StarterPackKey): Promise<types.StarterApplyResponse> {
    await delay(300);
    return this.starterCall((t) => this.mockStarter.apply(t, packKey));
  }

  async listSeededStarterRecords(): Promise<types.SeededRecordsResponse> {
    await delay(100);
    return this.starterCall((t) => this.mockStarter.listSeeded(t));
  }

  async keepStarterRecord(seededId: string): Promise<types.StarterKeepResponse> {
    await delay(100);
    return this.starterCall((t) => this.mockStarter.keep(t, seededId));
  }

  async removeUnusedStarterRecords(packKey: types.StarterPackKey): Promise<types.StarterRemoveResponse> {
    await delay(300);
    return this.starterCall((t) => this.mockStarter.removeUnused(t, packKey));
  }

  // Recurring presets: staff read, ADMIN / OFFICE_MANAGER write
  private presetReader(): MockUser {
    const user = this.currentMockUser();
    if (user.role === types.UserRole.CUSTOMER_PORTAL) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return user;
  }

  private presetWriter(): MockUser {
    const user = this.currentMockUser();
    if (user.role !== types.UserRole.ADMIN && user.role !== types.UserRole.OFFICE_MANAGER) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return user;
  }

  private toMockPreset(row: types.RecurringPreset & { tenantId: string }): types.RecurringPreset {
    const { tenantId: _tenantId, ...preset } = row;
    return { ...preset, frequencyDetails: preset.frequencyDetails ? { ...preset.frequencyDetails } : null };
  }

  private requireMockPreset(tenantId: string, id: string) {
    const row = this.recurringPresets.find((p) => p.id === id && p.tenantId === tenantId);
    if (!row) throw mockApiError(404, 'Recurring preset not found', 'NOT_FOUND');
    return row;
  }

  /** Validates the merged preset like the backend business layer (every failure is a 400). */
  private assertMockPreset(p: Pick<types.RecurringPreset, 'name' | 'frequency' | 'interval' | 'frequencyDetails' | 'billingModel' | 'billingFrequency' | 'billingInterval'>): void {
    if (!p.name.trim()) throw mockApiError(400, 'Preset name is required');
    if (p.name.trim().length > 100) throw mockApiError(400, 'Preset name must be 100 characters or less');
    if (!Number.isInteger(p.interval) || p.interval < 1) throw mockApiError(400, 'interval must be a whole number of 1 or more');
    this.assertMockRecurrence(p.frequency, p.interval, p.frequencyDetails as types.RecurringFrequencyDetails | null);
    if (p.billingModel === 'FLAT_CONTRACT') {
      if (!p.billingFrequency) throw mockApiError(400, 'billingFrequency is required for a flat contract');
    } else if (p.billingFrequency) {
      throw mockApiError(400, 'billingFrequency applies to flat contracts only');
    }
  }

  async listRecurringPresets(active?: boolean): Promise<types.RecurringPreset[]> {
    await delay(100);
    const user = this.presetReader();
    return this.recurringPresets
      .filter((p) => p.tenantId === user.tenantId && (active === undefined || p.isActive === active))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => this.toMockPreset(p));
  }

  async getRecurringPreset(id: string): Promise<types.RecurringPreset> {
    await delay(100);
    return this.toMockPreset(this.requireMockPreset(this.presetReader().tenantId, id));
  }

  async createRecurringPreset(data: types.CreateRecurringPresetRequest): Promise<types.RecurringPreset> {
    await delay(200);
    const user = this.presetWriter();
    const billingModel = data.billingModel ?? 'PER_VISIT';
    const name = (data.name ?? '').trim();
    const draft = {
      name, frequency: data.frequency, interval: data.interval ?? 1, frequencyDetails: data.frequencyDetails ?? null, billingModel,
      billingFrequency: billingModel === 'FLAT_CONTRACT' ? data.billingFrequency ?? null : data.billingFrequency ?? null,
      billingInterval: data.billingInterval ?? 1,
    };
    this.assertMockPreset(draft);
    if (this.recurringPresets.some((p) => p.tenantId === user.tenantId && p.name === name)) {
      throw mockApiError(409, `A recurring preset named '${name}' already exists`, 'DUPLICATE_RECURRING_PRESET');
    }
    const now = new Date().toISOString();
    const row = { ...draft, id: generateId('rp'), tenantId: user.tenantId, notes: data.notes?.trim() || null, isActive: data.isActive ?? true, createdAt: now, updatedAt: now };
    this.recurringPresets.push(row);
    this.mockStarterAudit('recurring_preset.create', { target: { type: 'recurring_preset', id: row.id, label: row.name } });
    return this.toMockPreset(row);
  }

  async updateRecurringPreset(id: string, data: types.UpdateRecurringPresetRequest): Promise<types.RecurringPreset> {
    await delay(200);
    const user = this.presetWriter();
    const row = this.requireMockPreset(user.tenantId, id);
    const merged = {
      name: data.name !== undefined ? data.name.trim() : row.name,
      frequency: data.frequency ?? row.frequency,
      interval: data.interval ?? row.interval,
      frequencyDetails: data.frequencyDetails !== undefined ? data.frequencyDetails : row.frequencyDetails,
      billingModel: data.billingModel ?? row.billingModel,
      billingFrequency: data.billingFrequency !== undefined ? data.billingFrequency : row.billingFrequency,
      billingInterval: data.billingInterval ?? row.billingInterval,
    };
    this.assertMockPreset(merged);
    if (merged.name !== row.name && this.recurringPresets.some((p) => p.tenantId === user.tenantId && p.name === merged.name)) {
      throw mockApiError(409, `A recurring preset named '${merged.name}' already exists`, 'DUPLICATE_RECURRING_PRESET');
    }
    Object.assign(row, merged, {
      notes: data.notes !== undefined ? data.notes?.trim() || null : row.notes,
      isActive: data.isActive ?? row.isActive,
      updatedAt: new Date().toISOString(),
    });
    this.mockStarterAudit('recurring_preset.update', { target: { type: 'recurring_preset', id: row.id, label: row.name } });
    return this.toMockPreset(row);
  }

  async deleteRecurringPreset(id: string): Promise<void> {
    await delay(200);
    const user = this.presetWriter();
    const row = this.requireMockPreset(user.tenantId, id);
    this.recurringPresets = this.recurringPresets.filter((p) => p !== row);
    this.mockStarterAudit('recurring_preset.delete', { target: { type: 'recurring_preset', id: row.id, label: row.name } });
  }

  private mockStarterAudit(action: types.AuditAction, details: Record<string, unknown>): void {
    const actor = this.currentMockUser();
    this.auditLogs.push({
      id: generateId('audit'), tenantId: actor.tenantId, actorUserId: actor.id, actorEmail: actor.email, action,
      targetUserId: null, targetEmail: null, details: details as types.AuditLogEntry['details'], createdAt: new Date().toISOString(),
    });
  }

  // ---- Users ----

  /** The signed-in mock user (from the token), or throws like a 401. */
  private currentMockUser(): MockUser {
    try {
      const decoded = JSON.parse(atob(this.token || ''));
      const user = this.users.find((u) => u.id === decoded.userId && u.tenantId === decoded.tenantId);
      if (user && user.isActive) return user;
    } catch {
      // fall through
    }
    throw new Error('Not authenticated');
  }

  /** Mirrors backend validateAdminAccess (INSUFFICIENT_PERMISSIONS). */
  private assertCanManageRole(actor: MockUser, role: string): void {
    const allowed =
      actor.role === types.UserRole.ADMIN ||
      (actor.role === types.UserRole.OFFICE_MANAGER &&
        (role === types.UserRole.TECHNICIAN || role === types.UserRole.CUSTOMER_PORTAL));
    if (allowed) return;
    throw new Error(
      actor.role === types.UserRole.OFFICE_MANAGER
        ? 'Only ADMIN users can manage ADMIN or OFFICE_MANAGER users'
        : 'Insufficient permissions to manage users'
    );
  }

  /** Mirrors backend removesActiveAdmin + validateNotLastAdmin (LAST_ADMIN). */
  private assertNotLastAdmin(target: MockUser, change: { role?: string; isActive?: boolean }): void {
    if (target.role !== types.UserRole.ADMIN || !target.isActive) return;
    const demoted = change.role !== undefined && change.role !== types.UserRole.ADMIN;
    const deactivated = change.isActive === false;
    if (!demoted && !deactivated) return;
    const otherAdmins = this.users.filter(
      (u) => u.tenantId === target.tenantId && u.id !== target.id && u.role === types.UserRole.ADMIN && u.isActive
    );
    if (otherAdmins.length < 1) throw new Error('Cannot remove the last active admin for this tenant');
  }

  private findTenantUser(tenantId: string, id: string): MockUser {
    const user = this.users.find((u) => u.id === id && u.tenantId === tenantId);
    if (!user) throw new Error('User not found');
    return user;
  }

  private replaceUser(updated: MockUser): void {
    this.users = this.users.map((u) => (u.id === updated.id ? updated : u));
  }

  /** Mirrors backend GET /users: ADMIN / OFFICE_MANAGER only. */
  async listUsers(includeInactive = false): Promise<types.UserListItem[]> {
    await delay(300);
    const actor = this.requireOfficeUser();
    return this.users
      .filter((u) => u.tenantId === actor.tenantId && (includeInactive || u.isActive))
      .map(toUserListItem);
  }

  /**
   * Mirrors backend GET /users/assignable: staff only (CUSTOMER_PORTAL and
   * legacy OFFICE get 403); active ADMIN / OFFICE_MANAGER / TECHNICIAN users
   * as { id, email, role }, ordered by email.
   */
  async listAssignableUsers(): Promise<types.AssignableUser[]> {
    await delay(200);
    const actor = this.currentMockUser();
    if (!ASSIGNABLE_MOCK_ROLES.includes(actor.role)) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return this.users
      .filter((u) => u.tenantId === actor.tenantId && u.isActive && ASSIGNABLE_MOCK_ROLES.includes(u.role))
      .sort((a, b) => a.email.localeCompare(b.email))
      .map(({ id, email, role }) => ({ id, email, role }));
  }

  /** Mirrors backend GET /users/:id: ADMIN / OFFICE_MANAGER only. */
  async getUser(id: string): Promise<types.UserDetail> {
    await delay(200);
    const actor = this.requireOfficeUser();
    return toUserDetail(this.findTenantUser(actor.tenantId, id));
  }

  async createUser(data: types.CreateUserRequest): Promise<types.UserDetail> {
    await delay(300);
    const actor = this.currentMockUser();
    const email = (data.email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Email format is invalid');
    validateMockPassword(data.password);
    this.assertCanManageRole(actor, data.role);
    if (this.users.some((u) => u.tenantId === actor.tenantId && u.email === email)) {
      throw new Error(`User with email '${email}' already exists`);
    }

    const now = new Date().toISOString();
    const created = mockUser(generateId('user'), actor.tenantId, email, data.role, {
      password: data.password,
      mustChangePassword: true,
      createdAt: now,
      updatedAt: now,
    });
    this.users = [...this.users, created];
    return toUserDetail(created);
  }

  async updateUser(id: string, data: types.UpdateUserRequest): Promise<types.UserDetail> {
    await delay(300);
    const actor = this.currentMockUser();
    const existing = this.findTenantUser(actor.tenantId, id);

    if (data.email !== undefined && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      throw new Error('Email format is invalid');
    }
    if (data.password !== undefined) validateMockPassword(data.password);

    const newRole = data.role !== undefined && data.role !== existing.role ? data.role : undefined;
    this.assertCanManageRole(actor, existing.role);
    if (newRole !== undefined) this.assertCanManageRole(actor, newRole);
    this.assertNotLastAdmin(existing, { role: newRole, isActive: data.isActive });

    if (
      data.email !== undefined &&
      data.email !== existing.email &&
      this.users.some((u) => u.tenantId === actor.tenantId && u.email === data.email)
    ) {
      throw new Error(`User with email '${data.email}' already exists`);
    }

    const updated: MockUser = {
      ...existing,
      ...(data.email !== undefined && { email: data.email }),
      ...(data.role !== undefined && { role: data.role }),
      ...(data.isActive !== undefined && { isActive: data.isActive }),
      ...(data.password !== undefined && { password: data.password, mustChangePassword: true }),
      updatedAt: new Date().toISOString(),
    };
    this.replaceUser(updated);
    return toUserDetail(updated);
  }

  async deactivateUser(id: string): Promise<void> {
    await delay(200);
    const actor = this.currentMockUser();
    const existing = this.findTenantUser(actor.tenantId, id);
    this.assertCanManageRole(actor, existing.role);
    if (!existing.isActive) return;
    this.assertNotLastAdmin(existing, { isActive: false });
    this.replaceUser({ ...existing, isActive: false, updatedAt: new Date().toISOString() });
  }

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    await delay(200);
    const actor = this.currentMockUser();
    validateMockPassword(newPassword);
    const existing = this.findTenantUser(actor.tenantId, id);
    this.assertCanManageRole(actor, existing.role);
    this.replaceUser({
      ...existing,
      password: newPassword,
      mustChangePassword: true,
      updatedAt: new Date().toISOString(),
    });
  }

  async changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
    await delay(200);
    const actor = this.currentMockUser();
    if (!currentPassword) throw new Error('Current password is required');
    validateMockPassword(newPassword);
    if (newPassword === currentPassword) {
      throw new Error('New password must be different from the current password');
    }
    if (currentPassword !== actor.password) throw new Error('Current password is incorrect');
    this.replaceUser({
      ...actor,
      password: newPassword,
      mustChangePassword: false,
      updatedAt: new Date().toISOString(),
    });
  }

  async logout(): Promise<void> {
    await delay(200);
    this.token = null;
  }

  async verifyToken(token: string): Promise<types.VerifyResponse> {
    await delay(100);
    try {
      const decoded = JSON.parse(atob(token));
      return {
        valid: true,
        user: {
          id: decoded.userId,
          email: decoded.email,
          name: decoded.email.split('@')[0],
          role: decoded.role,
        },
      };
    } catch {
      return { valid: false };
    }
  }

  async listCustomers(page = 1, limit = 20): Promise<types.PaginatedResponse<types.Customer>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const filtered = filterByTenant(this.customers, tenantId);
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginatedData = filtered.slice(start, end).map(toCustomer);

    return {
      success: true,
      data: paginatedData,
      pagination: {
        page,
        limit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / limit),
      },
    };
  }

  async getCustomer(id: string): Promise<types.Customer> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const customer = this.customers.find((c) => c.id === id && c.tenantId === tenantId);
    if (!customer) throw new Error('Customer not found');
    return toCustomer(customer);
  }

  async createCustomer(data: types.CreateCustomerRequest): Promise<types.Customer> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const leadSourceId = this.resolveMockLeadSourceId(tenantId, data.leadSourceId, null) ?? null;
    this.requireMockActiveRoute(tenantId, data.routeId);

    const newCustomer: MockCustomer = {
      id: generateId('cust'),
      tenantId,
      ...data,
      routeId: data.routeId ?? null,
      leadSourceId,
      taxExempt: data.taxExempt ?? false,
      billingMethod: data.billingMethod ?? 'PER_VISIT',
      status: data.status ?? 'LEAD',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.customers.push(newCustomer);
    if (leadSourceId) this.recordMockLeadSourceChange(tenantId, newCustomer.id, leadSourceId);
    return toCustomer(newCustomer);
  }

  async updateCustomer(id: string, data: types.UpdateCustomerRequest): Promise<types.Customer> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const customer = this.customers.find((c) => c.id === id && c.tenantId === tenantId);
    if (!customer) throw new Error('Customer not found');

    // Like the backend: only a real change to the lead source records history.
    const { leadSourceId: requested, ...fields } = data;
    const oldLeadSourceId = customer.leadSourceId ?? null;
    const newLeadSourceId = this.resolveMockLeadSourceId(tenantId, requested, oldLeadSourceId);

    Object.assign(customer, fields, { updatedAt: new Date().toISOString() });
    if (newLeadSourceId !== undefined && newLeadSourceId !== oldLeadSourceId) {
      customer.leadSourceId = newLeadSourceId;
      this.recordMockLeadSourceChange(tenantId, customer.id, newLeadSourceId);
    }
    return toCustomer(customer);
  }

  async listProperties(customerId?: string, page = 1, limit = 20): Promise<types.PaginatedResponse<types.Property>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    let filtered = this.properties.filter((p) => p.tenantId === tenantId);
    if (customerId) {
      filtered = filtered.filter((p) => p.customerId === customerId);
    }

    const start = (page - 1) * limit;
    const end = start + limit;
    const paginatedData = filtered.slice(start, end);

    return {
      success: true,
      data: paginatedData,
      pagination: {
        page,
        limit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / limit),
      },
    };
  }

  async getProperty(id: string): Promise<types.Property> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const property = this.properties.find((p) => p.id === id && p.tenantId === tenantId);
    if (!property) throw new Error('Property not found');
    return property;
  }

  /**
   * Same definition as backend PropertyBusinessLayer.computeBalance: per
   * POSTED/PARTIAL/PAID invoice, the property's lines (lineAmount -
   * lineDiscount + lineTax) and its largest-remainder share of the invoice's
   * ACTIVE payment applications, in integer cents.
   */
  async getPropertyBalance(id: string): Promise<types.PropertyBalance> {
    await this.getProperty(id);
    const cents = (value: string | undefined) => decimalToCents(value ?? '0') ?? 0;
    const propertyOfJobLine = new Map<string, string>();
    this.jobs.forEach((job) => (job.lineItems ?? []).forEach((line) => propertyOfJobLine.set(line.id, job.propertyId)));

    let totalInvoiced = 0;
    let totalPaid = 0;
    const invoices: types.PropertyBalanceInvoice[] = [];
    this.invoices
      .filter((invoice) => ['POSTED', 'PARTIAL', 'PAID'].includes(invoice.status))
      .forEach((invoice) => {
        const amounts = new Map<string, number>();
        invoice.lineItems.forEach((line) => {
          const key = (line.jobLineItemId && propertyOfJobLine.get(line.jobLineItemId)) || '';
          amounts.set(key, (amounts.get(key) ?? 0) + cents(line.lineAmount) - cents(line.lineDiscount) + cents(line.lineTax));
        });
        if (!amounts.has(id)) return;
        const total = cents(invoice.totalAmount) + cents(invoice.totalTax);
        const attributed = Array.from(amounts.values()).reduce((a, b) => a + b, 0);
        if (attributed !== total) amounts.set('', (amounts.get('') ?? 0) + total - attributed);
        const applied = this.payments
          .filter((payment) => payment.status === 'ACTIVE')
          .flatMap((payment) => payment.applications)
          .filter((app) => app.invoiceId === invoice.id)
          .reduce((sum, app) => sum + cents(app.amountApplied), 0);
        const paid = Math.min(Math.max(applied, 0), Math.max(total, 0));
        // Largest remainder over the positive buckets
        const positive = Array.from(amounts.entries()).filter(([, w]) => w > 0).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
        const weight = positive.reduce((a, [, w]) => a + w, 0);
        const shares = new Map<string, number>();
        if (paid > 0 && weight > 0) {
          const rows = positive.map(([key, w]) => ({ key, share: Math.floor((paid * w) / weight), rem: (paid * w) % weight }));
          let leftover = paid - rows.reduce((a, r) => a + r.share, 0);
          [...rows].sort((a, b) => b.rem - a.rem).forEach((row) => { if (leftover > 0) { row.share += 1; leftover -= 1; } });
          rows.forEach((row) => shares.set(row.key, row.share));
        }
        const amount = amounts.get(id) ?? 0;
        const share = shares.get(id) ?? 0;
        totalInvoiced += amount;
        totalPaid += share;
        invoices.push({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          status: invoice.status,
          invoiceDate: invoice.invoiceDate,
          dueDate: invoice.dueDate,
          invoiceTotal: centsToDecimal(total),
          invoicePaid: centsToDecimal(paid),
          propertyAmount: centsToDecimal(amount),
          propertyPaid: centsToDecimal(share),
          propertyBalance: centsToDecimal(amount - share),
          shared: Array.from(amounts.entries()).some(([key, value]) => key !== id && value !== 0),
        });
      });
    invoices.sort((a, b) => (a.invoiceDate === b.invoiceDate ? 0 : a.invoiceDate < b.invoiceDate ? 1 : -1));
    return {
      propertyId: id,
      totalInvoiced: centsToDecimal(totalInvoiced),
      totalPaid: centsToDecimal(totalPaid),
      balanceDue: centsToDecimal(totalInvoiced - totalPaid),
      invoiceCount: invoices.length,
      invoices,
    };
  }

  async createProperty(data: types.CreatePropertyRequest): Promise<types.Property> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const { sizeValue, sizeUnit, ...fields } = data;
    const size = this.mockPropertySize(sizeValue, sizeUnit);
    this.requireMockActiveRoute(tenantId, data.routeId);
    const newProperty: types.Property = {
      id: generateId('prop'),
      tenantId,
      ...fields,
      routeId: data.routeId ?? null,
      ...size,
      leadSourceId: this.resolveMockLeadSourceId(tenantId, data.leadSourceId, null) ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.properties.push(newProperty);
    return newProperty;
  }

  /** Same rules as backend PropertyBusinessLayer.validateSize: both or neither, >= 0, at most 2 decimals. */
  private mockPropertySize(
    value: string | number | null | undefined,
    unit: string | null | undefined
  ): { sizeValue: string | null; sizeUnit: types.SizeUnit | null } {
    const text = value === null || value === undefined ? '' : String(value).trim();
    const unitText = unit ?? '';
    let sizeValue: string | null = null;
    if (text) {
      const n = Number(text);
      if (!Number.isFinite(n)) throw new Error('Property size must be a valid number');
      if (n < 0) throw new Error('Property size cannot be negative');
      if (!/^\d+(\.\d{1,2})?$/.test(text)) throw new Error('Property size must have at most 2 decimal places');
      sizeValue = n.toFixed(2);
    }
    let sizeUnit: types.SizeUnit | null = null;
    if (unitText) {
      if (!isSizeUnit(unitText)) throw new Error(`Property size unit must be one of: ${types.SIZE_UNITS.join(', ')}`);
      sizeUnit = unitText;
    }
    if ((sizeValue === null) !== (sizeUnit === null)) {
      throw new Error('Property size needs both a value and a unit (or neither)');
    }
    return { sizeValue, sizeUnit };
  }

  async updateProperty(id: string, data: types.UpdatePropertyRequest): Promise<types.Property> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const property = this.properties.find((p) => p.id === id && p.tenantId === tenantId);
    if (!property) throw new Error('Property not found');

    const leadSourceId = this.resolveMockLeadSourceId(tenantId, data.leadSourceId, property.leadSourceId ?? null);
    // Like backend validateMergedSize: fields not sent keep their stored value.
    const { sizeValue, sizeUnit, ...fields } = data;
    const size =
      sizeValue !== undefined || sizeUnit !== undefined
        ? this.mockPropertySize(
            sizeValue !== undefined ? sizeValue : property.sizeValue,
            sizeUnit !== undefined ? sizeUnit : property.sizeUnit
          )
        : undefined;
    const addressOf = (p: types.Property) => [p.address, p.city, p.state, p.zip].map((v) => (v ?? '').trim()).join('|');
    const before = addressOf(property);
    Object.assign(property, fields, size ?? {}, { updatedAt: new Date().toISOString() });
    if (leadSourceId !== undefined) property.leadSourceId = leadSourceId;
    // An address change puts the property back in the geocoding queue (tracker item 41)
    if (addressOf(property) !== before) Object.assign(property, MOCK_GEOCODE_RESET);
    return property;
  }

  // ============ Geocoding (tracker item 41) ============

  private mockLocation(property: types.Property, extra: Partial<types.PropertyLocation> = {}): types.PropertyLocation {
    return {
      propertyId: property.id,
      latitude: property.latitude ?? null,
      longitude: property.longitude ?? null,
      geocodeStatus: property.geocodeStatus ?? 'PENDING',
      geocodePrecision: property.geocodePrecision ?? null,
      geocodedAt: null,
      geocodeError: null,
      ...extra,
    };
  }

  private officeProperty(id: string): types.Property {
    const { tenantId } = this.requireOfficeUser();
    const property = this.properties.find((p) => p.id === id && p.tenantId === tenantId);
    if (!property) throw mockApiError(404, 'Property not found', 'NOT_FOUND');
    return property;
  }

  async setPropertyLocation(propertyId: string, location: { latitude: number; longitude: number } | null): Promise<types.PropertyLocation> {
    await delay(150);
    const property = this.officeProperty(propertyId);
    if (location === null) {
      Object.assign(property, MOCK_GEOCODE_RESET);
      return this.mockLocation(property);
    }
    const { latitude, longitude } = location;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
      throw mockApiError(400, 'Invalid location: latitude must be a number from -90 to 90 and longitude from -180 to 180', 'VALIDATION_ERROR');
    }
    Object.assign(property, {
      latitude: Math.round(latitude * 1e6) / 1e6,
      longitude: Math.round(longitude * 1e6) / 1e6,
      geocodeStatus: 'MANUAL',
      geocodePrecision: null,
    });
    return this.mockLocation(property, { geocodedAt: new Date().toISOString() });
  }

  async requeuePropertyGeocode(propertyId: string): Promise<types.PropertyLocation> {
    await delay(150);
    const property = this.officeProperty(propertyId);
    Object.assign(property, MOCK_GEOCODE_RESET);
    return this.mockLocation(property);
  }

  async getGeocodingStatus(): Promise<types.GeocodingStatus> {
    await delay(150);
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions');
    const counts = Object.fromEntries(types.GEOCODE_STATUSES.map((s) => [s, 0])) as Record<types.GeocodeStatus, number>;
    const mine = this.properties.filter((p) => p.tenantId === actor.tenantId);
    for (const p of mine) counts[p.geocodeStatus ?? 'PENDING']++;
    const settings = this.mockCompanySettings(actor.tenantId);
    return {
      provider: 'none',
      workerEnabled: false,
      properties: { ...counts, total: mine.length },
      company: {
        geocodeStatus: settings.geocodeStatus ?? 'PENDING',
        geocodePrecision: settings.geocodePrecision ?? null,
        hasAddress: !!(settings.addressLine1 || settings.postalCode),
      },
    };
  }

  async retryFailedGeocoding(): Promise<types.RetryFailedGeocodingResult> {
    await delay(150);
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions');
    const retryable = (s: types.GeocodeStatus | undefined) => s === 'FAILED' || s === 'NOT_FOUND' || s === 'SKIPPED';
    let requeued = 0;
    for (const p of this.properties.filter((p) => p.tenantId === actor.tenantId && retryable(p.geocodeStatus))) {
      Object.assign(p, MOCK_GEOCODE_RESET);
      requeued++;
    }
    const settings = this.mockCompanySettings(actor.tenantId);
    const companyRequeued = retryable(settings.geocodeStatus);
    if (companyRequeued) Object.assign(settings, { geocodeStatus: 'PENDING', geocodePrecision: null });
    return { requeued, companyRequeued };
  }

  async deleteProperty(id: string): Promise<void> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    this.properties = this.properties.filter((p) => !(p.id === id && p.tenantId === tenantId));
  }

  async listItems(page = 1, limit = 20): Promise<types.PaginatedResponse<types.Item>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const live = filterByTenant(this.items, tenantId).filter((i) => !i.deletedAt);
    const result = paginate(live, page, limit);
    return { ...result, data: result.data.map((i) => this.toItem(i)) };
  }

  async getItem(id: string): Promise<types.Item> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return this.toItem(this.findLiveItem(id, tenantId));
  }

  async createItem(data: types.CreateItemRequest): Promise<types.Item> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const name = data.name?.trim() ?? '';
    if (!name) throw new Error('Item name is required');
    if (this.items.some((i) => i.tenantId === tenantId && !i.deletedAt && i.name === name)) {
      throw new Error('Item name already exists in tenant');
    }
    this.assertItemAccount(data.revenueAccountId, types.GLAccountType.REVENUE, 'Revenue');
    if (data.expenseAccountId) this.assertItemAccount(data.expenseAccountId, types.GLAccountType.EXPENSE, 'Expense');
    if (data.itemType === types.ItemType.PRODUCT && !data.expenseAccountId) {
      throw new Error('Expense GL account is required for PRODUCT items');
    }
    const priceChartId = data.priceChartId || null;
    if (priceChartId) this.assertLinkablePriceChart(tenantId, priceChartId);
    const reorderPoint = this.mockReorderPoint(data.reorderPoint) ?? null;
    this.assertReorderPointItemType(data.itemType, reorderPoint);
    const checklistTemplateId = data.checklistTemplateId || null;
    if (checklistTemplateId) this.assertLinkableChecklistTemplate(tenantId, checklistTemplateId);
    this.assertPortalRequestable(data.itemType, data.portalRequestable);

    const now = new Date().toISOString();
    const item: MockItem = {
      id: generateId('item'),
      tenantId,
      name,
      description: data.description ?? null,
      itemType: data.itemType,
      unitPrice: Number(data.unitPrice).toFixed(2),
      unitCost: this.mockUnitCost(data.unitCost) ?? null,
      unitOfMeasure: data.unitOfMeasure || 'each',
      revenueAccountId: data.revenueAccountId,
      expenseAccountId: data.expenseAccountId ?? null,
      categoryId: data.categoryId ?? null,
      taxable: data.taxable ?? true,
      quantityOnHand: '0',
      reorderPoint,
      priceChartId,
      routeId: data.routeId ?? null,
      checklistTemplateId,
      portalRequestable: data.portalRequestable ?? false,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    this.items.push(item);
    return this.toItem(item);
  }

  async updateItem(id: string, data: types.UpdateItemRequest): Promise<types.Item> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const item = this.findLiveItem(id, tenantId);
    if (data.revenueAccountId) this.assertItemAccount(data.revenueAccountId, types.GLAccountType.REVENUE, 'Revenue');
    if (data.expenseAccountId) this.assertItemAccount(data.expenseAccountId, types.GLAccountType.EXPENSE, 'Expense');
    const itemType = data.itemType ?? item.itemType;
    const expenseAccountId = data.expenseAccountId !== undefined ? data.expenseAccountId : item.expenseAccountId;
    if (itemType === types.ItemType.PRODUCT && !expenseAccountId) {
      throw new Error('Expense GL account is required for PRODUCT items');
    }
    const unitCost = this.mockUnitCost(data.unitCost);
    // Like the backend: the effective type and value (requested, else stored)
    const portalRequestable = data.portalRequestable;
    this.assertPortalRequestable(itemType, portalRequestable !== undefined ? portalRequestable : item.portalRequestable ?? false);
    // Like the backend: the effective type and reorder point (requested, else stored)
    const reorderPoint = this.mockReorderPoint(data.reorderPoint);
    this.assertReorderPointItemType(itemType, reorderPoint !== undefined ? reorderPoint : item.reorderPoint);
    // Like the backend: '' / null unlinks; only a newly linked chart must be active.
    const priceChartId = data.priceChartId === undefined ? undefined : data.priceChartId || null;
    if (priceChartId && priceChartId !== item.priceChartId) this.assertLinkablePriceChart(tenantId, priceChartId);
    const routeId = data.routeId === undefined ? undefined : data.routeId || null;
    if (routeId && !this.dispatchRoutes.some((route) => route.tenantId === tenantId && route.id === routeId && route.isActive !== false)) {
      throw mockApiError(400, 'Route not found or inactive', 'VALIDATION_ERROR');
    }
    // Like the backend: '' / null clears; only a changed link is validated.
    const checklistTemplateId = data.checklistTemplateId === undefined ? undefined : data.checklistTemplateId || null;
    if (checklistTemplateId && checklistTemplateId !== (item.checklistTemplateId ?? null)) {
      this.assertLinkableChecklistTemplate(tenantId, checklistTemplateId);
    }

    if (data.name !== undefined) item.name = data.name;
    if (data.description !== undefined) item.description = data.description;
    if (data.itemType !== undefined) item.itemType = data.itemType;
    if (data.unitPrice !== undefined) item.unitPrice = Number(data.unitPrice).toFixed(2);
    if (unitCost !== undefined) item.unitCost = unitCost;
    if (reorderPoint !== undefined) item.reorderPoint = reorderPoint;
    if (data.unitOfMeasure !== undefined) item.unitOfMeasure = data.unitOfMeasure;
    if (data.revenueAccountId !== undefined) item.revenueAccountId = data.revenueAccountId;
    if (data.expenseAccountId !== undefined) item.expenseAccountId = data.expenseAccountId;
    if (data.categoryId !== undefined) item.categoryId = data.categoryId;
    if (data.taxable !== undefined) item.taxable = data.taxable;
    if (priceChartId !== undefined) item.priceChartId = priceChartId;
    if (routeId !== undefined) item.routeId = routeId;
    if (checklistTemplateId !== undefined) item.checklistTemplateId = checklistTemplateId;
    if (portalRequestable !== undefined) item.portalRequestable = portalRequestable;
    item.updatedAt = new Date().toISOString();
    return this.toItem(item);
  }

  /** Backend ItemService checklist link check: in the tenant (404) and active (422). */
  private assertLinkableChecklistTemplate(tenantId: string, templateId: string): void {
    const template = this.checklistTemplates.find((t) => t.id === templateId && t.tenantId === tenantId);
    if (!template) throw mockApiError(404, 'Checklist template not found', 'NOT_FOUND');
    if (!template.isActive) {
      throw mockApiError(
        422,
        `Checklist template "${template.name}" is inactive; activate it before linking items to it`,
        'CHECKLIST_TEMPLATE_INACTIVE'
      );
    }
  }

  /** Backend ItemService.validatePriceChart: the chart is in the tenant and active. */
  private assertLinkablePriceChart(tenantId: string, priceChartId: string): void {
    const chart = this.priceCharts.find((c) => c.id === priceChartId && c.tenantId === tenantId);
    if (!chart) throw mockApiError(400, 'Price chart not found');
    if (!chart.active) {
      throw mockApiError(
        422,
        `Price chart "${chart.name}" is inactive; activate it before linking items to it`,
        'PRICE_CHART_INACTIVE'
      );
    }
  }

  async deleteItem(id: string): Promise<void> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    // Soft delete, like the backend.
    this.findLiveItem(id, tenantId).deletedAt = new Date().toISOString();
  }

  async listItemCategories(page = 1, limit = 20): Promise<types.PaginatedResponse<types.ItemCategory>> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const rows = filterByTenant(this.itemCategories, tenantId).map(({ tenantId: _tenantId, ...category }) => category);
    return paginate(rows, page, limit);
  }

  private findLiveItem(id: string, tenantId: string): MockItem {
    const item = this.items.find((i) => i.id === id && i.tenantId === tenantId && !i.deletedAt);
    if (!item) throw new Error('Item not found');
    return item;
  }

  /** undefined = leave unchanged; null / '' = clear; otherwise a 2dp string. */
  private mockUnitCost(value: string | number | null | undefined): string | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) throw new Error('Unit cost must be a valid number');
    if (n < 0) throw new Error('Unit cost cannot be negative');
    return n.toFixed(2);
  }

  /**
   * Backend ItemBusinessLayer.validateReorderPoint: undefined = leave
   * unchanged; null / '' = clear; otherwise >= 0 with at most 2 decimals.
   */
  private mockReorderPoint(value: string | number | null | undefined): string | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;
    const text = String(value).trim();
    if (!Number.isFinite(Number(text)) || text === '') throw mockApiError(400, 'Reorder point must be a valid number');
    if (Number(text) < 0) throw mockApiError(400, 'Reorder point cannot be negative');
    if (!/^\d+(\.\d{1,2})?$/.test(text)) throw mockApiError(400, 'Reorder point must have at most 2 decimal places');
    return Number(text).toFixed(2);
  }

  /** Backend: only PRODUCT items may have a reorder point (checked on the effective type and value). */
  private assertReorderPointItemType(itemType: types.ItemType, reorderPoint: string | null | undefined): void {
    if (reorderPoint !== null && reorderPoint !== undefined && itemType !== types.ItemType.PRODUCT) {
      throw mockApiError(400, `Reorder point is only allowed on PRODUCT items (this item is ${itemType}); send null to clear it`);
    }
  }

  /** Low stock (backend isLowStock): a reorder point is set and on hand is at or below it. */
  private mockLowStock(item: Pick<MockItem, 'quantityOnHand' | 'reorderPoint'>): boolean {
    if (item.reorderPoint === null) return false;
    return (decimalToCents(item.quantityOnHand) ?? 0) <= (decimalToCents(item.reorderPoint) ?? 0);
  }

  /** Mirrors the backend account checks: the account exists and has the expected type. */
  private assertItemAccount(glAccountId: string, accountType: types.GLAccountType, label: string): void {
    const account = this.glAccounts.find((a) => a.id === glAccountId);
    if (!account) throw new Error(`${label} GL account not found or access denied`);
    if (account.accountType !== accountType) throw new Error(`${label} account must be ${accountType} type`);
  }

  private toItemAccount(glAccountId: string): types.ItemGLAccountRef {
    const account = this.glAccounts.find((a) => a.id === glAccountId);
    return {
      id: glAccountId,
      accountNumber: account?.accountNumber ?? '',
      accountName: account?.accountName ?? '',
      accountType: account?.accountType ?? '',
    };
  }

  /** Build the backend ItemResponse shape (no tenantId / deletedAt). */
  private toItem(item: MockItem): types.Item {
    const category = item.categoryId ? this.itemCategories.find((c) => c.id === item.categoryId) : undefined;
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      itemType: item.itemType,
      unitPrice: item.unitPrice,
      unitCost: item.unitCost,
      unitOfMeasure: item.unitOfMeasure,
      taxable: item.taxable,
      categoryId: item.categoryId,
      ...(category ? { category: { id: category.id, name: category.name } } : {}),
      revenueAccount: this.toItemAccount(item.revenueAccountId),
      ...(item.expenseAccountId ? { expenseAccount: this.toItemAccount(item.expenseAccountId) } : {}),
      quantityOnHand: item.quantityOnHand,
      reorderPoint: item.reorderPoint,
      lowStock: this.mockLowStock(item),
      priceChartId: item.priceChartId,
      routeId: item.routeId ?? null,
      checklistTemplateId: item.checklistTemplateId ?? null,
      portalRequestable: item.portalRequestable ?? false,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  /** Backend ItemBusinessLayer.validatePortalRequestable: only SERVICE and LABOR items can be offered on the portal. */
  private assertPortalRequestable(itemType: types.ItemType, value: unknown): void {
    if (value === undefined) return;
    if (typeof value !== 'boolean') throw mockApiError(400, 'portalRequestable must be true or false', 'VALIDATION_ERROR');
    if (value && itemType !== types.ItemType.SERVICE && itemType !== types.ItemType.LABOR) {
      throw mockApiError(400, `Only SERVICE and LABOR items can be offered on the customer portal (this item is ${itemType})`, 'VALIDATION_ERROR');
    }
  }

  // ---- Price Charts ----

  private requireMockPriceChart(id: string): MockPriceChart {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const chart = this.priceCharts.find((c) => c.id === id && c.tenantId === tenantId);
    if (!chart) throw mockApiError(404, 'Price chart not found', 'NOT_FOUND');
    return chart;
  }

  /** Mirrors the backend role check on chart writes. */
  private assertCanManagePriceCharts(): void {
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN && actor.role !== types.UserRole.OFFICE_MANAGER) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
  }

  /** Usage counts like the backend: soft-deleted items count, and so does every job line priced from the chart. */
  private mockPriceChartUsage(chart: MockPriceChart): { itemCount: number; jobLineCount: number } {
    const itemCount = this.items.filter((i) => i.tenantId === chart.tenantId && i.priceChartId === chart.id).length;
    const jobLineCount = this.jobs
      .filter((j) => j.tenantId === chart.tenantId)
      .reduce((sum, j) => sum + j.lineItems.filter((l) => l.priceChartId === chart.id).length, 0);
    return { itemCount, jobLineCount };
  }

  private toMockPriceChart(chart: MockPriceChart, withTiers: boolean): types.PriceChart {
    const { itemCount, jobLineCount } = this.mockPriceChartUsage(chart);
    const dimension = SIZE_UNIT_OPTIONS.find((u) => u.unit === chart.unit)?.dimension ?? 'AREA';
    return {
      id: chart.id,
      tenantId: chart.tenantId,
      name: chart.name,
      description: chart.description,
      unit: chart.unit,
      dimension,
      pricingType: chart.pricingType,
      overageRatePerUnit: chart.overageRatePerUnit,
      active: chart.active,
      tierCount: chart.tiers.length,
      itemCount,
      jobLineCount,
      inUse: itemCount + jobLineCount > 0,
      ...(withTiers
        ? {
            tiers: chart.tiers.map((t, index) => ({
              id: t.id,
              sortOrder: index,
              fromSize: index === 0 ? '0' : chart.tiers[index - 1].upToSize,
              upToSize: t.upToSize,
              price: t.price,
            })),
          }
        : {}),
      createdAt: chart.createdAt,
      updatedAt: chart.updatedAt,
    };
  }

  /** Backend parseDecimal rules for chart amounts: a number >= 0 with at most `places` decimals. */
  private mockChartDecimal(value: string | number | undefined, label: string, places: number): string {
    const text = value === undefined || value === null ? '' : String(value).trim();
    if (!text || !Number.isFinite(Number(text))) throw mockApiError(400, `${label} must be a valid number`);
    if (Number(text) < 0) throw mockApiError(400, `${label} cannot be negative`);
    if (!new RegExp(`^\\d+(\\.\\d{1,${places}})?$`).test(text)) {
      throw mockApiError(400, `${label} must have at most ${places} decimal places`);
    }
    return String(Number(text));
  }

  /** Backend validateTiers: at least one, strictly increasing upToSize > 0, non-negative prices. */
  private mockValidateTiers(tiers: types.PriceChartTierInput[] | undefined): MockPriceChartTier[] {
    if (!Array.isArray(tiers)) throw mockApiError(400, 'Tiers must be an array');
    if (tiers.length === 0) throw mockApiError(400, 'A price chart needs at least one tier');
    if (tiers.length > 100) throw mockApiError(400, 'A price chart can have at most 100 tiers');
    let previous: number | null = null;
    return tiers.map((tier, index) => {
      const label = `Tier ${index + 1}`;
      const upToSize = this.mockChartDecimal(tier.upToSize, `${label} upToSize`, 2);
      if (Number(upToSize) <= 0) throw mockApiError(400, `${label} upToSize must be greater than 0`);
      if (previous !== null && Number(upToSize) <= previous) {
        throw mockApiError(400, `${label} upToSize must be greater than the previous tier's upToSize (${previous})`);
      }
      previous = Number(upToSize);
      const price = this.mockChartDecimal(tier.price, `${label} price`, 4);
      return { id: generateId('tier'), upToSize, price };
    });
  }

  private assertMockUniqueChartName(tenantId: string, name: string, excludeId?: string): void {
    if (this.priceCharts.some((c) => c.tenantId === tenantId && c.name === name && c.id !== excludeId)) {
      throw mockApiError(409, `Price chart with name '${name}' already exists`, 'DUPLICATE_PRICE_CHART');
    }
  }

  private assertMockChartFields(data: types.UpdatePriceChartRequest): void {
    if (data.name !== undefined) {
      const name = data.name.trim();
      if (!name) throw mockApiError(400, 'Price chart name is required');
      if (name.length > 100) throw mockApiError(400, 'Price chart name must be 100 characters or less');
    }
    if (data.unit !== undefined && !isSizeUnit(data.unit)) {
      throw mockApiError(400, `Unit must be one of: ${types.SIZE_UNITS.join(', ')}`);
    }
    if (data.pricingType !== undefined && !types.PRICE_CHART_PRICING_TYPES.includes(data.pricingType)) {
      throw mockApiError(400, `Pricing type must be one of: ${types.PRICE_CHART_PRICING_TYPES.join(', ')}`);
    }
  }

  async listPriceCharts(): Promise<types.PriceChart[]> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return filterByTenant(this.priceCharts, tenantId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => this.toMockPriceChart(c, false));
  }

  async getPriceChart(id: string): Promise<types.PriceChart> {
    await delay(200);
    return this.toMockPriceChart(this.requireMockPriceChart(id), true);
  }

  async createPriceChart(data: types.CreatePriceChartRequest): Promise<types.PriceChart> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    this.assertCanManagePriceCharts();

    if (!data.name?.trim()) throw mockApiError(400, 'Price chart name is required');
    if (!data.unit) throw mockApiError(400, 'Unit is required');
    if (!data.pricingType) throw mockApiError(400, 'Pricing type is required');
    this.assertMockChartFields(data);
    const name = data.name.trim();
    const overageRatePerUnit = this.mockChartDecimal(data.overageRatePerUnit, 'Overage rate per unit', 4);
    const tiers = data.tiers !== undefined ? this.mockValidateTiers(data.tiers) : [];
    this.assertMockUniqueChartName(tenantId, name);

    const now = new Date().toISOString();
    const chart: MockPriceChart = {
      id: generateId('chart'),
      tenantId,
      name,
      description: data.description?.trim() || null,
      unit: data.unit,
      pricingType: data.pricingType,
      overageRatePerUnit,
      active: data.active ?? true,
      tiers,
      createdAt: now,
      updatedAt: now,
    };
    this.priceCharts.push(chart);
    return this.toMockPriceChart(chart, true);
  }

  async updatePriceChart(id: string, data: types.UpdatePriceChartRequest): Promise<types.PriceChart> {
    await delay(300);
    this.assertCanManagePriceCharts();
    const chart = this.requireMockPriceChart(id);
    this.assertMockChartFields(data);
    const overage =
      data.overageRatePerUnit !== undefined
        ? this.mockChartDecimal(data.overageRatePerUnit, 'Overage rate per unit', 4)
        : undefined;
    const name = data.name?.trim();
    if (name !== undefined && name !== chart.name) this.assertMockUniqueChartName(chart.tenantId, name, id);

    if (name !== undefined) chart.name = name;
    if (data.description !== undefined) chart.description = data.description?.trim() || null;
    if (data.unit !== undefined) chart.unit = data.unit;
    if (data.pricingType !== undefined) chart.pricingType = data.pricingType;
    if (overage !== undefined) chart.overageRatePerUnit = overage;
    if (data.active !== undefined) chart.active = data.active;
    chart.updatedAt = new Date().toISOString();
    return this.toMockPriceChart(chart, true);
  }

  async replacePriceChartTiers(id: string, tiers: types.PriceChartTierInput[]): Promise<types.PriceChart> {
    await delay(300);
    this.assertCanManagePriceCharts();
    const chart = this.requireMockPriceChart(id);
    chart.tiers = this.mockValidateTiers(tiers);
    chart.updatedAt = new Date().toISOString();
    return this.toMockPriceChart(chart, true);
  }

  async deletePriceChart(id: string): Promise<void> {
    await delay(200);
    this.assertCanManagePriceCharts();
    const chart = this.requireMockPriceChart(id);
    const { itemCount, jobLineCount } = this.mockPriceChartUsage(chart);
    if (itemCount > 0) {
      throw mockApiError(
        409,
        'Cannot delete a price chart that items are linked to. Unlink it from those items first.',
        'PRICE_CHART_IN_USE'
      );
    }
    if (jobLineCount > 0) {
      throw mockApiError(
        409,
        'Cannot delete a price chart that job lines were priced from. Deactivate it instead.',
        'PRICE_CHART_IN_USE'
      );
    }
    this.priceCharts = this.priceCharts.filter((c) => c.id !== id);
  }

  async listSizeUnits(): Promise<types.SizeUnitOption[]> {
    await delay(100);
    return SIZE_UNIT_OPTIONS.map((u) => ({ ...u }));
  }

  async quotePrice(data: types.PriceQuoteRequest): Promise<types.PriceQuote> {
    await delay(150);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const item = this.items.find((i) => i.id === data.itemId && i.tenantId === tenantId && !i.deletedAt);
    if (!item) throw mockApiError(404, 'Item not found', 'NOT_FOUND');
    const property = this.properties.find((p) => p.id === data.propertyId && p.tenantId === tenantId);
    if (!property) throw mockApiError(404, 'Property not found', 'NOT_FOUND');
    return this.mockPriceItem(tenantId, item, property);
  }

  /**
   * Offline stand-in for backend PricingService.priceItem, so the mock client
   * can quote and price job lines. This is the only frontend copy of the
   * pricing rules and exists for mock mode only; the real UI always asks the
   * backend. Plain JS numbers, so cents may differ from the backend at
   * extreme sizes.
   */
  private mockPriceItem(tenantId: string, item: MockItem, property: types.Property): types.PriceQuote {
    if (!item.priceChartId) {
      return {
        unitPrice: item.unitPrice,
        source: 'CATALOG',
        chartId: null,
        tierId: null,
        size: null,
        unit: null,
        chartSize: null,
        chartUnit: null,
      };
    }
    const chart = this.priceCharts.find((c) => c.id === item.priceChartId && c.tenantId === tenantId);
    if (!chart) {
      throw mockApiError(422, 'The price chart linked to this item was not found', 'PRICE_CHART_NOT_FOUND');
    }
    if (!chart.active) {
      throw mockApiError(
        422,
        `Price chart "${chart.name}" is inactive. Activate it, unlink it from the item, or enter a unit price.`,
        'PRICE_CHART_INACTIVE'
      );
    }
    if (property.sizeValue === null || property.sizeValue === undefined || !property.sizeUnit) {
      throw mockApiError(
        422,
        `This item is priced by the "${chart.name}" chart, which needs the property's size. Set the property size or enter a unit price.`,
        'PROPERTY_SIZE_REQUIRED'
      );
    }
    const from = SIZE_UNIT_OPTIONS.find((u) => u.unit === property.sizeUnit)!;
    const to = SIZE_UNIT_OPTIONS.find((u) => u.unit === chart.unit)!;
    if (from.dimension !== to.dimension) {
      throw mockApiError(
        422,
        `Cannot convert ${from.unit} (${from.dimension.toLowerCase()}) to ${to.unit} (${to.dimension.toLowerCase()})`,
        'UNIT_MISMATCH'
      );
    }
    if (chart.tiers.length === 0) throw mockApiError(422, 'Price chart has no tiers', 'PRICE_CHART_NO_TIERS');

    const size = Number(property.sizeValue);
    const chartSize =
      from.unit === to.unit ? size : roundTo((size * MOCK_SI_FACTORS[from.unit]) / MOCK_SI_FACTORS[to.unit], 4);
    const bands = chart.tiers.map((t) => ({ id: t.id, upTo: Number(t.upToSize), price: Number(t.price) }));
    const first = bands[0];
    const top = bands[bands.length - 1];
    const overage = Number(chart.overageRatePerUnit);
    const index = bands.findIndex((b) => chartSize <= b.upTo);
    const aboveMax = index === -1;

    let price: number;
    if (chart.pricingType === 'BAND_FLAT') {
      price = aboveMax ? top.price + (chartSize - top.upTo) * overage : bands[index].price;
    } else if (aboveMax) {
      const atMax = bands.length === 1 ? first.price : Math.max(first.price, top.price * top.upTo);
      price = atMax + (chartSize - top.upTo) * overage;
    } else {
      price = index === 0 ? first.price : Math.max(first.price, bands[index].price * chartSize);
    }

    return {
      unitPrice: roundTo(price, 2).toFixed(2),
      source: 'CHART',
      chartId: chart.id,
      tierId: aboveMax ? top.id : bands[index].id,
      size: property.sizeValue,
      unit: property.sizeUnit,
      chartSize: String(chartSize),
      chartUnit: chart.unit,
    };
  }

  async listTaxRates(): Promise<types.TaxRate[]> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return filterByTenant(this.taxRates, tenantId).map((rate) => ({
      ...rate,
      propertyCount: this.properties.filter((p) => p.tenantId === tenantId && p.taxRateId === rate.id).length,
    }));
  }

  async getTaxRate(id: string): Promise<types.TaxRate> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const rate = this.taxRates.find((r) => r.id === id && r.tenantId === tenantId);
    if (!rate) throw new Error('Tax rate not found');
    return rate;
  }

  async createTaxRate(data: types.CreateTaxRateRequest): Promise<types.TaxRate> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const newRate: types.TaxRate = {
      id: generateId('tax'),
      tenantId,
      name: data.name,
      rate: String(data.rate),
      payableAccount: this.mockPayableAccount(data.payableAccountId),
      propertyCount: 0,
      createdAt: new Date().toISOString(),
    };

    this.taxRates.push(newRate);
    return newRate;
  }

  async updateTaxRate(id: string, data: types.UpdateTaxRateRequest): Promise<types.TaxRate> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const rate = this.taxRates.find((r) => r.id === id && r.tenantId === tenantId);
    if (!rate) throw new Error('Tax rate not found');

    if (data.name !== undefined) rate.name = data.name;
    if (data.rate !== undefined) rate.rate = String(data.rate);
    if (data.payableAccountId !== undefined) {
      rate.payableAccount = this.mockPayableAccount(data.payableAccountId);
    }
    return rate;
  }

  private mockPayableAccount(glAccountId: string): types.TaxRate['payableAccount'] {
    const account = this.glAccounts.find((a) => a.id === glAccountId);
    if (!account) throw new Error('GL payable account not found');
    return { id: account.id, accountNumber: account.accountNumber, accountName: account.accountName };
  }

  async deleteTaxRate(id: string): Promise<void> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    // Mirror backend validateDeletion: refuse while properties still use the rate.
    if (this.properties.some((p) => p.tenantId === tenantId && p.taxRateId === id)) {
      throw new Error('Cannot delete tax rate that is linked to properties. Remove the tax rate from properties first.');
    }
    this.taxRates = this.taxRates.filter((r) => !(r.id === id && r.tenantId === tenantId));
  }

  async listDiscounts(): Promise<types.Discount[]> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return filterByTenant(this.discounts, tenantId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((d) => this.withMockUsage(d));
  }

  async getDiscount(id: string): Promise<types.Discount> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const discount = this.discounts.find((d) => d.id === id && d.tenantId === tenantId);
    if (!discount) throw new Error('Discount not found');
    return this.withMockUsage(discount);
  }

  async createDiscount(data: types.CreateDiscountRequest): Promise<types.Discount> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const name = data.name.trim();
    if (this.discounts.some((d) => d.tenantId === tenantId && d.name === name)) {
      throw new Error(`Discount with name '${name}' already exists`);
    }
    this.assertMockDiscountValue(data.type, data.value);

    const now = new Date().toISOString();
    const newDiscount: types.Discount = {
      id: generateId('discount'),
      tenantId,
      name,
      description: data.description?.trim() || null,
      type: data.type,
      value: String(Number(data.value)),
      discountAccount: this.mockDiscountAccount(data.discountAccountId),
      active: data.active ?? true,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      customerCount: 0,
      propertyCount: 0,
      itemCount: 0,
      createdAt: now,
      updatedAt: now,
    };

    this.discounts.push(newDiscount);
    return newDiscount;
  }

  async updateDiscount(id: string, data: types.UpdateDiscountRequest): Promise<types.Discount> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const discount = this.discounts.find((d) => d.id === id && d.tenantId === tenantId);
    if (!discount) throw new Error('Discount not found');

    const name = data.name?.trim();
    if (
      name !== undefined &&
      name !== discount.name &&
      this.discounts.some((d) => d.tenantId === tenantId && d.name === name)
    ) {
      throw new Error(`Discount with name '${name}' already exists`);
    }
    // Mirror backend: validate value against the merged type/value.
    this.assertMockDiscountValue(data.type ?? discount.type, data.value ?? discount.value);

    if (name !== undefined) discount.name = name;
    if (data.description !== undefined) discount.description = data.description?.trim() || null;
    if (data.type !== undefined) discount.type = data.type;
    if (data.value !== undefined) discount.value = String(Number(data.value));
    if (data.discountAccountId !== undefined) {
      discount.discountAccount = this.mockDiscountAccount(data.discountAccountId);
    }
    if (data.active !== undefined) discount.active = data.active;
    if (data.startDate !== undefined) discount.startDate = data.startDate || null;
    if (data.endDate !== undefined) discount.endDate = data.endDate || null;
    discount.updatedAt = new Date().toISOString();
    return this.withMockUsage(discount);
  }

  private assertMockDiscountValue(type: types.DiscountType, value: string | number): void {
    const n = Number(value);
    if (value === '' || Number.isNaN(n)) throw new Error('Discount value must be a valid number');
    if (type === types.DiscountType.PERCENTAGE && (n < 0 || n > 100)) {
      throw new Error('Percentage discount must be between 0 and 100');
    }
    if (type === types.DiscountType.FIXED_AMOUNT && n < 0) {
      throw new Error('Fixed amount discount cannot be negative');
    }
  }

  private mockDiscountAccount(glAccountId: string): types.Discount['discountAccount'] {
    const account = this.glAccounts.find((a) => a.id === glAccountId);
    if (!account) throw new Error('GL discount account not found');
    if (account.accountType !== types.GLAccountType.REVENUE) {
      throw new Error('Discount account must be REVENUE type');
    }
    return { id: account.id, accountNumber: account.accountNumber, accountName: account.accountName };
  }

  async deleteDiscount(id: string): Promise<void> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    if (!this.discounts.some((d) => d.id === id && d.tenantId === tenantId)) {
      throw new Error('Discount not found');
    }
    if (
      this.customerDiscountLinks.some((l) => l.discountId === id) ||
      this.propertyDiscountLinks.some((l) => l.discountId === id)
    ) {
      throw new Error(
        'Cannot delete discount that is attached to customers or properties. Remove the discount from them first.'
      );
    }
    this.discounts = this.discounts.filter((d) => !(d.id === id && d.tenantId === tenantId));
  }

  async listCustomerDiscounts(customerId: string): Promise<types.Discount[]> {
    await delay(200);
    const tenantId = this.requireMockCustomer(customerId);
    return this.mockAttachedDiscounts(
      tenantId,
      this.customerDiscountLinks.filter((l) => l.customerId === customerId).map((l) => l.discountId)
    );
  }

  async attachCustomerDiscount(customerId: string, discountId: string): Promise<types.Discount[]> {
    await delay(300);
    const tenantId = this.requireMockCustomer(customerId);
    this.requireMockDiscount(tenantId, discountId);
    if (this.customerDiscountLinks.some((l) => l.customerId === customerId && l.discountId === discountId)) {
      throw new Error('Discount is already attached to this customer');
    }
    this.customerDiscountLinks.push({ customerId, discountId });
    return this.listCustomerDiscounts(customerId);
  }

  async detachCustomerDiscount(customerId: string, discountId: string): Promise<types.Discount[]> {
    await delay(200);
    this.requireMockCustomer(customerId);
    const before = this.customerDiscountLinks.length;
    this.customerDiscountLinks = this.customerDiscountLinks.filter(
      (l) => !(l.customerId === customerId && l.discountId === discountId)
    );
    if (this.customerDiscountLinks.length === before) {
      throw new Error('Discount is not attached to this customer');
    }
    return this.listCustomerDiscounts(customerId);
  }

  async listPropertyDiscounts(propertyId: string): Promise<types.Discount[]> {
    await delay(200);
    const tenantId = this.requireMockProperty(propertyId);
    return this.mockAttachedDiscounts(
      tenantId,
      this.propertyDiscountLinks.filter((l) => l.propertyId === propertyId).map((l) => l.discountId)
    );
  }

  async attachPropertyDiscount(propertyId: string, discountId: string): Promise<types.Discount[]> {
    await delay(300);
    const tenantId = this.requireMockProperty(propertyId);
    this.requireMockDiscount(tenantId, discountId);
    if (this.propertyDiscountLinks.some((l) => l.propertyId === propertyId && l.discountId === discountId)) {
      throw new Error('Discount is already attached to this property');
    }
    this.propertyDiscountLinks.push({ propertyId, discountId });
    return this.listPropertyDiscounts(propertyId);
  }

  async detachPropertyDiscount(propertyId: string, discountId: string): Promise<types.Discount[]> {
    await delay(200);
    this.requireMockProperty(propertyId);
    const before = this.propertyDiscountLinks.length;
    this.propertyDiscountLinks = this.propertyDiscountLinks.filter(
      (l) => !(l.propertyId === propertyId && l.discountId === discountId)
    );
    if (this.propertyDiscountLinks.length === before) {
      throw new Error('Discount is not attached to this property');
    }
    return this.listPropertyDiscounts(propertyId);
  }

  private requireMockCustomer(customerId: string): string {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    if (!this.customers.some((c) => c.id === customerId && c.tenantId === tenantId)) {
      throw new Error('Customer not found');
    }
    return tenantId;
  }

  private requireMockProperty(propertyId: string): string {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    if (!this.properties.some((p) => p.id === propertyId && p.tenantId === tenantId)) {
      throw new Error('Property not found');
    }
    return tenantId;
  }

  private requireMockDiscount(tenantId: string, discountId: string): void {
    if (!discountId) throw new Error('Discount ID is required');
    if (!this.discounts.some((d) => d.id === discountId && d.tenantId === tenantId)) {
      throw new Error('Discount not found');
    }
  }

  private mockAttachedDiscounts(tenantId: string, discountIds: string[]): types.Discount[] {
    return this.discounts
      .filter((d) => d.tenantId === tenantId && discountIds.includes(d.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((d) => this.withMockUsage(d));
  }

  private withMockUsage(discount: types.Discount): types.Discount {
    return {
      ...discount,
      customerCount: this.customerDiscountLinks.filter((l) => l.discountId === discount.id).length,
      propertyCount: this.propertyDiscountLinks.filter((l) => l.discountId === discount.id).length,
    };
  }

  // ---- Lead Sources ----

  async listLeadSources(): Promise<types.LeadSource[]> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return filterByTenant(this.leadSources, tenantId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => this.toMockLeadSource(l));
  }

  async getLeadSource(id: string): Promise<types.LeadSource> {
    await delay(200);
    return this.toMockLeadSource(this.requireMockLeadSourceNode(id));
  }

  async getLeadSourceEffectiveUtm(id: string): Promise<types.UtmValues> {
    await delay(100);
    return this.toMockLeadSource(this.requireMockLeadSourceNode(id)).effectiveUtm;
  }

  async createLeadSource(data: types.CreateLeadSourceRequest): Promise<types.LeadSource> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const name = data.name?.trim();
    if (!name) throw new Error('Lead source name is required');
    const parentId = data.parentId || null;
    if (parentId) this.requireMockLeadSourceNode(parentId);
    this.assertMockUniqueLeadSourceName(tenantId, parentId, name);

    const now = new Date().toISOString();
    const node: MockLeadSourceNode = {
      id: generateId('ls'),
      tenantId,
      parentId,
      name,
      description: toNullableText(data.description),
      active: data.active ?? true,
      utmSource: toNullableText(data.utmSource),
      utmMedium: toNullableText(data.utmMedium),
      utmCampaign: toNullableText(data.utmCampaign),
      utmTerm: toNullableText(data.utmTerm),
      utmContent: toNullableText(data.utmContent),
      createdAt: now,
      updatedAt: now,
    };
    this.leadSources.push(node);
    return this.toMockLeadSource(node);
  }

  async updateLeadSource(
    id: string,
    data: types.UpdateLeadSourceRequest,
    forceCascade = false
  ): Promise<types.LeadSource> {
    await delay(300);
    const node = this.requireMockLeadSourceNode(id);
    const siblings = filterByTenant(this.leadSources, node.tenantId);

    const name = data.name !== undefined ? data.name.trim() : node.name;
    if (!name) throw new Error('Lead source name cannot be empty');
    const parentId = data.parentId !== undefined ? data.parentId || null : node.parentId;
    if (parentId !== node.parentId && parentId) {
      this.requireMockLeadSourceNode(parentId);
      if (parentId === id || descendantIds(id, siblings).includes(parentId)) {
        throw new Error('A lead source cannot be moved under itself or one of its own descendants');
      }
    }
    if (name !== node.name || parentId !== node.parentId) {
      this.assertMockUniqueLeadSourceName(node.tenantId, parentId, name, id);
    }

    const wasActive = node.active;
    node.name = name;
    node.parentId = parentId;
    if (data.description !== undefined) node.description = toNullableText(data.description);
    for (const field of types.UTM_FIELDS) {
      if (data[field] !== undefined) node[field] = toNullableText(data[field]);
    }
    if (data.active !== undefined) node.active = data.active;
    // Deactivation (active -> inactive) cascades to every descendant;
    // reactivation does not. deactivateLeadSource always cascades.
    if (data.active === false && (wasActive || forceCascade)) {
      const ids = descendantIds(id, siblings);
      this.leadSources.forEach((l) => {
        if (ids.includes(l.id)) l.active = false;
      });
    }
    node.updatedAt = new Date().toISOString();
    return this.toMockLeadSource(node);
  }

  async deactivateLeadSource(id: string): Promise<types.LeadSource> {
    return this.updateLeadSource(id, { active: false }, true);
  }

  async deleteLeadSource(id: string): Promise<void> {
    await delay(200);
    const node = this.toMockLeadSource(this.requireMockLeadSourceNode(id));
    if (node.childCount > 0) {
      throw new Error('Cannot delete a lead source that has child sources. Delete or move its children first.');
    }
    if (node.inUse) {
      throw new Error(
        'Cannot delete a lead source that is in use by customers (including their lead source history), ' +
          'properties, or jobs. Deactivate it instead.'
      );
    }
    this.leadSources = this.leadSources.filter((l) => l.id !== id);
  }

  async listCustomerLeadSourceHistory(customerId: string): Promise<types.CustomerLeadSourceHistoryEntry[]> {
    await delay(200);
    const tenantId = this.requireMockCustomer(customerId);
    const nodes = filterByTenant(this.leadSources, tenantId);

    return this.customerLeadSourceHistory
      .filter((h) => h.tenantId === tenantId && h.customerId === customerId)
      .sort((a, b) => b.changedAt.localeCompare(a.changedAt))
      .map(({ tenantId: _tenantId, ...entry }) => {
        const node = entry.leadSourceId ? nodes.find((n) => n.id === entry.leadSourceId) : undefined;
        return {
          ...entry,
          leadSourceName: node ? node.name : null,
          leadSourcePath: node ? pathNames(node.id, nodes).join(' > ') : null,
          changedByEmail: this.users.find((u) => u.id === entry.changedByUserId)?.email ?? null,
        };
      });
  }

  private requireMockLeadSourceNode(id: string): MockLeadSourceNode {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const node = this.leadSources.find((l) => l.id === id && l.tenantId === tenantId);
    if (!node) throw new Error('Lead source not found');
    return node;
  }

  private assertMockUniqueLeadSourceName(
    tenantId: string,
    parentId: string | null,
    name: string,
    excludeId?: string
  ): void {
    if (
      this.leadSources.some(
        (l) => l.tenantId === tenantId && l.parentId === parentId && l.name === name && l.id !== excludeId
      )
    ) {
      throw new Error(`A lead source named '${name}' already exists at this level`);
    }
  }

  /** Same contract as backend resolveAssignableLeadSourceId. */
  private resolveMockLeadSourceId(
    tenantId: string,
    value: string | null | undefined,
    currentId: string | null
  ): string | null | undefined {
    if (value === undefined) return undefined;
    if (!value) return null;
    const node = this.leadSources.find((l) => l.id === value && l.tenantId === tenantId);
    if (!node) throw new Error('Lead source not found');
    if (!node.active && node.id !== currentId) throw new Error('Lead source is inactive and cannot be assigned');
    return node.id;
  }

  private recordMockLeadSourceChange(tenantId: string, customerId: string, leadSourceId: string | null): void {
    const actor = getCurrentActor(this.token || '');
    this.customerLeadSourceHistory.push({
      id: generateId('lsh'),
      tenantId,
      customerId,
      leadSourceId,
      leadSourceName: null,
      leadSourcePath: null,
      changedByUserId: actor?.userId ?? 'unknown',
      changedByEmail: null,
      changedAt: new Date().toISOString(),
    });
  }

  private toMockLeadSource(node: MockLeadSourceNode): types.LeadSource {
    const nodes = filterByTenant(this.leadSources, node.tenantId);
    const path = pathNames(node.id, nodes);
    const customerCount = this.customers.filter((c) => c.leadSourceId === node.id).length;
    const customerHistoryCount = this.customerLeadSourceHistory.filter((h) => h.leadSourceId === node.id).length;
    const propertyCount = this.properties.filter((p) => p.leadSourceId === node.id).length;
    const jobCount = this.jobs.filter((j) => j.leadSourceId === node.id).length;
    return {
      ...node,
      effectiveUtm: effectiveUtm(node.id, nodes),
      path,
      pathLabel: path.join(' > '),
      depth: Math.max(0, path.length - 1),
      childCount: nodes.filter((n) => n.parentId === node.id).length,
      customerCount,
      customerHistoryCount,
      propertyCount,
      jobCount,
      inUse: customerCount + customerHistoryCount + propertyCount + jobCount > 0,
    };
  }

  async listJobs(page = 1, limit = 20, filters: types.JobListFilters = {}): Promise<types.PaginatedResponse<types.Job>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const query = filters.search?.trim().toLowerCase();
    const filtered = filterByTenant(this.jobs, tenantId).filter((job) =>
      (!filters.status || job.status === filters.status) &&
      (!filters.customerId || job.customerId === filters.customerId) &&
      (!filters.propertyId || job.propertyId === filters.propertyId) &&
      (!filters.assignedTo || job.assignedTo === filters.assignedTo) &&
      (!query || [job.id, job.customer.name, job.property.address].some((value) => value.toLowerCase().includes(query)))
    );
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginatedData = filtered.slice(start, end);

    return {
      success: true,
      data: paginatedData,
      pagination: {
        page,
        limit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / limit),
      },
    };
  }

  async getJob(id: string): Promise<types.Job> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
    if (!job) throw new Error('Job not found');
    return job;
  }

  async createJob(data: types.CreateJobRequest): Promise<types.Job> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const property = this.properties.find((p) => p.id === data.propertyId && p.tenantId === tenantId);
    if (!property) throw new Error('Property not found');
    const customer = this.customers.find((c) => c.id === property.customerId && c.tenantId === tenantId);
    const assignee = this.users.find((u) => u.id === data.assignedTo && u.tenantId === tenantId);
    // Like the backend: an explicit routeId (even null) is MANUAL; otherwise
    // the property default, then the customer default.
    this.requireMockActiveRoute(tenantId, data.routeId);
    const routeId = data.routeId !== undefined ? data.routeId : (property.routeId ?? customer?.routeId ?? null);
    const newJob: types.Job & { tenantId: string } = {
      id: generateId('job'),
      tenantId,
      routeId,
      routeSource: data.routeId !== undefined ? 'MANUAL' : 'DEFAULT',
      customerId: property.customerId,
      customer: { id: property.customerId, name: customer?.name || '' },
      property: { id: property.id, address: property.address, city: property.city, state: property.state, customerId: property.customerId },
      propertyId: property.id,
      assignedTo: data.assignedTo || null,
      assignedToUser: assignee ? { id: assignee.id, email: assignee.email } : null,
      scheduledDate: data.scheduledDate || null,
      notes: data.notes || null,
      leadSourceId: data.leadSourceId || null,
      invoiceId: null,
      status: types.JobStatus.DRAFT,
      lineItems: [],
      subtotal: '0',
      totalTax: '0',
      total: '0',
      checklist: { total: 0, completed: 0, requiredIncomplete: 0 },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.jobs.push(newJob);
    return newJob;
  }

  async updateJob(id: string, data: types.UpdateJobRequest): Promise<types.Job> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
    if (!job) throw new Error('Job not found');
    if (data.routeId !== undefined) this.requireMockActiveRoute(tenantId, data.routeId);

    const previousDate = job.scheduledDate;
    const { shiftLaterVisits, ...jobPatch } = data;
    Object.assign(job, jobPatch, { updatedAt: new Date().toISOString() });
    // Bundle visit moved with "shift all later visits" (tracker item 85)
    if (shiftLaterVisits && job.enrollmentId && previousDate && data.scheduledDate) {
      this.mockBundles.shiftLaterVisits(tenantId, job.id, Math.round((Date.parse(`${data.scheduledDate}T00:00:00Z`) - Date.parse(`${previousDate}T00:00:00Z`)) / 86_400_000));
    }
    if (data.routeId !== undefined) job.routeSource = 'MANUAL';
    if (data.assignedTo !== undefined) {
      const assignee = this.users.find((u) => u.id === data.assignedTo && u.tenantId === tenantId);
      job.assignedToUser = assignee ? { id: assignee.id, email: assignee.email } : null;
    }
    if (data.status === types.JobStatus.COMPLETED) job.completedDate = new Date().toISOString().slice(0, 10);
    if (data.status && job.enrollmentId) this.mockBundles.jobChanged(tenantId, job.id);
    return job;
  }

  async completeJob(id: string): Promise<types.Job> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
    if (!job) throw new Error('Job not found');

    job.status = types.JobStatus.COMPLETED;
    job.completedDate = new Date().toISOString();
    job.updatedAt = new Date().toISOString();
    if (job.enrollmentId) this.mockBundles.jobChanged(tenantId, job.id);
    return job;
  }

  async addJobLineItem(jobId: string, data: types.AddJobLineItemRequest): Promise<types.JobLineItem> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
    if (!job) throw new Error('Job not found');

    const item = this.items.find((i) => i.id === data.itemId && i.tenantId === tenantId);
    if (!item) throw new Error('Item not found');
    const route = this.mockInferLineRoute(tenantId, job, [...job.lineItems.map((li) => li.itemId), item.id]);
    const priced = this.mockPriceJobLine(tenantId, job, item, data.unitPrice);
    const quantity = Number(data.quantity);
    const lineTotal = quantity * Number(priced.unitPrice);
    const lineItem: types.JobLineItem = {
      id: generateId('li'),
      itemId: data.itemId,
      item: { id: item.id, name: item.name, description: item.description, itemType: item.itemType },
      quantity: String(data.quantity),
      ...priced,
      pricingModel: data.pricingModel || 'PER_ITEM',
      taxRate: '0',
      lineTotal: String(lineTotal),
      lineTax: '0',
      lineAmount: String(lineTotal),
      createdAt: new Date().toISOString(),
    };

    job.lineItems.push(lineItem);
    this.applyMockLineChecklist(job, item.id);
    if (route) Object.assign(job, route);
    this.updateMockJobTotals(job);
    job.updatedAt = new Date().toISOString();

    return lineItem;
  }

  async updateJobLineItem(jobId: string, lineId: string, data: types.UpdateJobLineItemRequest): Promise<types.JobLineItem> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
    const line = job?.lineItems.find((li) => li.id === lineId);
    if (!job || !line) throw new Error('Line item not found');
    // Like the backend: an entered price makes the line MANUAL; a changed item
    // without one is re-priced; otherwise the stored price and source stay.
    const hasPrice = data.unitPrice !== undefined && data.unitPrice !== null && data.unitPrice !== '';
    if (data.itemId && data.itemId !== line.itemId) {
      const item = this.items.find((i) => i.id === data.itemId && i.tenantId === tenantId);
      if (!item) throw new Error('Item not found');
      const route = this.mockInferLineRoute(tenantId, job, job.lineItems.map((li) => (li.id === lineId ? item.id : li.itemId)));
      const priced = this.mockPriceJobLine(tenantId, job, item, data.unitPrice);
      if (route) Object.assign(job, route);
      const oldItemId = line.itemId;
      line.itemId = item.id;
      line.item = { id: item.id, name: item.name, description: item.description, itemType: item.itemType };
      Object.assign(line, priced);
      // Like the backend checklist hook: drop the old item's untouched group, then apply the new one.
      this.removeMockLineChecklist(job, oldItemId);
      this.applyMockLineChecklist(job, item.id);
    } else if (hasPrice) {
      // Renewal price prompt (tracker item 85): a changed visit price needs the choice, before anything is saved
      if (job.enrollmentId && job.lineItems[0]?.id === line.id) this.mockBundles.visitPriceChanging(tenantId, job.id, String(data.unitPrice), data.renewalPriceAction);
      Object.assign(line, this.mockManualPrice(data.unitPrice!));
    }
    if (data.quantity !== undefined) line.quantity = String(data.quantity);
    if (data.pricingModel !== undefined) line.pricingModel = data.pricingModel;
    line.lineTotal = String(Number(line.quantity) * Number(line.unitPrice));
    line.lineAmount = String(Number(line.lineTotal) + Number(line.lineTax));
    this.updateMockJobTotals(job);
    job.updatedAt = new Date().toISOString();
    return line;
  }

  async removeJobLineItem(jobId: string, itemId: string): Promise<void> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
    if (!job) throw new Error('Job not found');

    const route = this.mockInferLineRoute(tenantId, job, job.lineItems.filter((li) => li.id !== itemId).map((li) => li.itemId));
    const removed = job.lineItems.find((li) => li.id === itemId);
    job.lineItems = job.lineItems.filter((li) => li.id !== itemId);
    if (removed) this.removeMockLineChecklist(job, removed.itemId);
    if (route) Object.assign(job, route);
    this.updateMockJobTotals(job);
    job.updatedAt = new Date().toISOString();
  }

  /** Like backend verifyRoute: a given (non-null) route must be an active route in the tenant. */
  private requireMockActiveRoute(tenantId: string, routeId: string | null | undefined): void {
    if (routeId === undefined || routeId === null) return;
    if (!this.dispatchRoutes.some((r) => r.tenantId === tenantId && r.id === routeId && r.isActive !== false)) {
      throw mockApiError(404, 'Route not found or inactive', 'NOT_FOUND');
    }
  }

  /**
   * Like backend JobService line route inference (tracker item 57): a
   * nonmanual Job follows its Items' single route, else the property then
   * customer default; distinct Item routes are 409 ROUTE_CONFLICT. Returns
   * null for MANUAL Jobs (left alone). Call before changing the lines.
   */
  private mockInferLineRoute(tenantId: string, job: types.Job, itemIds: string[]): Pick<types.Job, 'routeId' | 'routeSource'> | null {
    if (job.routeSource === 'MANUAL') return null;
    const itemRoutes = [...new Set(itemIds
      .map((id) => this.items.find((i) => i.id === id && i.tenantId === tenantId)?.routeId)
      .filter((id): id is string => !!id))];
    if (itemRoutes.length > 1) {
      throw mockApiError(409, 'Items specify different routes; choose the Job route before saving this line', 'ROUTE_CONFLICT');
    }
    if (itemRoutes.length === 1) return { routeId: itemRoutes[0], routeSource: 'ITEM' };
    const property = this.properties.find((p) => p.id === job.propertyId && p.tenantId === tenantId);
    const customer = this.customers.find((c) => c.id === job.customerId && c.tenantId === tenantId);
    return { routeId: property?.routeId ?? customer?.routeId ?? null, routeSource: 'DEFAULT' };
  }

  private mockManualPrice(unitPrice: string | number): Pick<
    types.JobLineItem,
    'unitPrice' | 'priceSource' | 'priceChartId' | 'sizeSnapshot' | 'sizeUnitSnapshot'
  > {
    return { unitPrice: String(unitPrice), priceSource: 'MANUAL', priceChartId: null, sizeSnapshot: null, sizeUnitSnapshot: null };
  }

  /** Backend JobService.priceLine: manual price (0 included), else chart, else catalog. */
  private mockPriceJobLine(
    tenantId: string,
    job: types.Job,
    item: MockItem,
    unitPrice: string | number | undefined
  ): Pick<types.JobLineItem, 'unitPrice' | 'priceSource' | 'priceChartId' | 'sizeSnapshot' | 'sizeUnitSnapshot'> {
    if (unitPrice !== undefined && unitPrice !== null && unitPrice !== '') return this.mockManualPrice(unitPrice);
    const property = this.properties.find((p) => p.id === job.propertyId && p.tenantId === tenantId);
    if (!property) throw new Error('Property not found');
    const quote = this.mockPriceItem(tenantId, item, property);
    return {
      unitPrice: quote.unitPrice,
      priceSource: quote.source,
      priceChartId: quote.chartId,
      sizeSnapshot: quote.size,
      sizeUnitSnapshot: quote.unit,
    };
  }

  private updateMockJobTotals(job: types.Job): void {
    job.subtotal = String(job.lineItems.reduce((sum, line) => sum + Number(line.lineTotal), 0));
    job.totalTax = String(job.lineItems.reduce((sum, line) => sum + Number(line.lineTax), 0));
    job.total = String(Number(job.subtotal) + Number(job.totalTax));
  }

  async listInvoices(page = 1, limit = 20, filters: types.InvoiceListFilters = {}): Promise<types.PaginatedResponse<types.Invoice>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const filtered = filterByTenant(this.invoices, tenantId).filter((invoice) =>
      (!filters.status || invoice.status === filters.status) &&
      (!filters.billingCustomerId || invoice.billingCustomerId === filters.billingCustomerId) &&
      (!filters.invoiceDateFrom || invoice.invoiceDate >= filters.invoiceDateFrom) &&
      (!filters.invoiceDateTo || invoice.invoiceDate <= filters.invoiceDateTo));
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginatedData = filtered.slice(start, end);

    return {
      success: true,
      data: paginatedData,
      pagination: {
        page,
        limit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / limit),
      },
    };
  }

  async getInvoice(id: string): Promise<types.Invoice> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const invoice = this.invoices.find((i) => i.id === id && i.tenantId === tenantId);
    if (!invoice) throw new Error('Invoice not found');
    // An issued invoice carries its customer portal registration code (created on first use, like the backend's)
    return this.portalCodeIssued(invoice) ? { ...invoice, portalCode: this.mockPortalCode(invoice.id) } : invoice;
  }

  private portalCodeIssued(invoice: types.Invoice): boolean {
    return invoice.status !== types.InvoiceStatus.DRAFT && invoice.status !== types.InvoiceStatus.VOID;
  }

  /** POST /invoices/:id/portal-code/regenerate: office only; the old code stops working at once. */
  async regenerateInvoicePortalCode(id: string): Promise<types.Invoice> {
    await delay(200);
    const actor = this.requireOfficeUser();
    const invoice = this.invoices.find((i) => i.id === id && i.tenantId === actor.tenantId);
    if (!invoice) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    if (!this.portalCodeIssued(invoice)) {
      throw mockApiError(422, `Only an issued invoice has a portal code (this invoice is ${invoice.status})`, 'INVOICE_NOT_ISSUED');
    }
    for (const [key, invoiceId] of [...this.portalCodes]) if (invoiceId === id) this.portalCodes.delete(key);
    return { ...invoice, portalCode: this.mockPortalCode(id) };
  }

  /** Backend validateJobsNotCoveredByFlatContract: a flat contract's visits are never invoiced on their own. */
  private assertNoCoveredJobs(jobs: Array<types.Job | undefined>): void {
    const covered = jobs.filter((job): job is types.Job => !!job && !!job.coveredByFlatContract);
    if (covered.length) {
      throw mockApiError(
        400,
        `Jobs covered by a flat contract are billed by the contract's period invoices and cannot be invoiced: ${covered.map((job) => job.id).join(', ')}`,
        'JOB_COVERED_BY_FLAT_CONTRACT'
      );
    }
  }

  async createInvoice(data: types.CreateInvoiceRequest): Promise<types.Invoice> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const jobs = data.jobIds.map((id) => this.jobs.find((job) => job.id === id && job.tenantId === tenantId));
    this.assertNoCoveredJobs(jobs);
    if (!jobs.length || jobs.some((job) => !job || job.status !== types.JobStatus.COMPLETED || job.invoiceId)) throw new Error('Choose completed, uninvoiced jobs');
    if (data.billingMethod === 'PER_VISIT' && jobs.length !== 1) throw new Error('Per-visit billing requires exactly one job');
    const properties = jobs.map((job) => this.properties.find((property) => property.id === job!.propertyId && property.tenantId === tenantId));
    if (properties.some((property) => property?.billingCustomerId !== data.billingCustomerId)) throw new Error('Jobs must have the same billing customer');
    const customer = this.customers.find((entry) => entry.id === data.billingCustomerId && entry.tenantId === tenantId);
    if (!customer) throw new Error('Billing customer not found');
    const lineItems: types.InvoiceLineItem[] = jobs.flatMap((job) => job!.lineItems.map((line) => ({
      id: generateId('ili'), jobLineItemId: line.id, itemId: line.itemId, item: { id: line.item.id, name: line.item.name, itemType: line.item.itemType },
      description: line.item.description || line.item.name, quantity: line.quantity, unitPrice: line.unitPrice, taxRate: line.taxRate,
      lineAmount: line.lineTotal, lineDiscount: '0', lineTax: line.lineTax, lineCogs: '0', createdAt: new Date().toISOString(),
    })));
    const subtotal = lineItems.reduce((sum, line) => sum + Number(line.lineAmount), 0);
    const tax = lineItems.reduce((sum, line) => sum + Number(line.lineTax), 0);

    const newInvoice: types.Invoice = {
      id: generateId('inv'),
      tenantId,
      billingCustomerId: data.billingCustomerId,
      billingCustomer: { id: customer.id, name: customer.name, email: customer.email || null },
      invoiceNumber: `INV-${Date.now()}`,
      status: types.InvoiceStatus.DRAFT,
      invoiceDate: data.invoiceDate,
      // Like the backend: omitted dueDate = invoiceDate + the tenant's payment terms
      dueDate: data.dueDate || addDaysToDateOnly(
        data.invoiceDate,
        this.companySettings.get(tenantId)?.defaultPaymentTermsDays ?? DEFAULT_PAYMENT_TERMS_DAYS
      ) || data.invoiceDate,
      periodStartDate: data.periodStartDate,
      periodEndDate: data.periodEndDate,
      billingMethod: data.billingMethod,
      invoiceType: 'STANDARD',
      lineItems,
      jobs: jobs.map((job) => ({ id: job!.id, status: types.JobStatus.INVOICED })),
      // The mock is not a pricing engine: it never applies discounts
      subtotal: subtotal.toFixed(2),
      totalDiscount: '0.00',
      discounts: [],
      totalAmount: subtotal.toFixed(2),
      totalTax: tax.toFixed(2),
      totalCogs: '0',
      total: (subtotal + tax).toFixed(2),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.invoices.push(newInvoice);
    jobs.forEach((job) => { job!.status = types.JobStatus.INVOICED; job!.invoiceId = newInvoice.id; });
    return newInvoice;
  }

  async previewInvoice(data: types.CreateInvoiceRequest): Promise<types.InvoicePreview> {
    await delay(100);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const jobs = data.jobIds.map((id) => this.jobs.find((job) => job.id === id && job.tenantId === tenantId));
    this.assertNoCoveredJobs(jobs);
    if (!jobs.length || jobs.some((job) => !job || job.status !== types.JobStatus.COMPLETED || job.invoiceId)) throw new Error('Choose completed, uninvoiced jobs');
    // Static pass-through of the job lines (no discounts): the real pricing lives in the backend
    const lineItems = jobs.flatMap((job) => job!.lineItems.map((line) => ({
      jobId: job!.id, jobLineItemId: line.id, itemId: line.itemId, description: line.item.description || line.item.name,
      quantity: line.quantity, unitPrice: line.unitPrice, taxRate: line.taxRate,
      lineAmount: line.lineTotal, lineDiscount: '0', lineTax: line.lineTax,
    })));
    const subtotal = lineItems.reduce((sum, line) => sum + Number(line.lineAmount), 0);
    const tax = lineItems.reduce((sum, line) => sum + Number(line.lineTax), 0);
    return {
      billingCustomerId: data.billingCustomerId, billingMethod: data.billingMethod, invoiceDate: data.invoiceDate, lineItems,
      applicableDiscounts: [], appliedDiscounts: [],
      subtotal: subtotal.toFixed(2), totalDiscount: '0.00', totalAmount: subtotal.toFixed(2), totalTax: tax.toFixed(2), total: (subtotal + tax).toFixed(2),
    };
  }

  async updateInvoice(id: string, data: types.UpdateInvoiceRequest): Promise<types.Invoice> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const invoice = this.invoices.find((i) => i.id === id && i.tenantId === tenantId);
    if (!invoice) throw new Error('Invoice not found');

    if (invoice.status !== types.InvoiceStatus.DRAFT) throw new Error('Only draft invoices can be updated');
    Object.assign(invoice, data, { updatedAt: new Date().toISOString() });
    return invoice;
  }

  async sendInvoice(id: string): Promise<types.Invoice> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const invoice = this.invoices.find((i) => i.id === id && i.tenantId === tenantId);
    if (!invoice) throw new Error('Invoice not found');

    if (invoice.status !== types.InvoiceStatus.DRAFT) throw new Error('Only draft invoices can be sent');
    invoice.status = types.InvoiceStatus.POSTED;
    invoice.updatedAt = new Date().toISOString();
    return invoice;
  }

  async cancelInvoice(id: string): Promise<types.Invoice> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const invoice = this.invoices.find((i) => i.id === id && i.tenantId === tenantId);
    if (!invoice) throw new Error('Invoice not found');

    if (invoice.status === types.InvoiceStatus.VOID) throw new Error('Invoice already cancelled');
    invoice.status = types.InvoiceStatus.VOID;
    // A cancelled invoice loses its portal code (logins already registered through it stay linked)
    for (const [key, invoiceId] of [...this.portalCodes]) if (invoiceId === id) this.portalCodes.delete(key);
    invoice.updatedAt = new Date().toISOString();
    invoice.jobs.forEach(({ id: jobId }) => { const job = this.jobs.find((entry) => entry.id === jobId); if (job) { job.status = types.JobStatus.COMPLETED; job.invoiceId = null; } });
    // Like the backend: the VOID invoice's lines are unlinked from the job lines, so the jobs can be invoiced again
    invoice.lineItems.forEach((line) => { line.jobLineItemId = null; });
    return invoice;
  }

  async listPayments(page = 1, limit = 20, filters: types.PaymentListFilters = {}): Promise<types.PaginatedResponse<types.Payment>> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const filtered = filterByTenant(this.payments, tenantId).filter((payment) =>
      (!filters.billingCustomerId || payment.billingCustomerId === filters.billingCustomerId) &&
      (!filters.invoiceId || payment.applications.some((application) => application.invoiceId === filters.invoiceId)) &&
      (!filters.paymentDateFrom || payment.paymentDate >= filters.paymentDateFrom) &&
      (!filters.paymentDateTo || payment.paymentDate <= filters.paymentDateTo) &&
      (!filters.status || payment.status === filters.status)
    );
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginatedData = filtered.slice(start, end);

    return {
      success: true,
      data: paginatedData,
      pagination: {
        page,
        limit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / limit),
      },
    };
  }

  async getPayment(id: string): Promise<types.Payment> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const payment = this.payments.find((p) => p.id === id && p.tenantId === tenantId);
    if (!payment) throw new Error('Payment not found');
    return payment;
  }

  async recordPayment(data: types.RecordPaymentRequest): Promise<types.Payment> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const customer = this.customers.find((entry) => entry.id === data.billingCustomerId && entry.tenantId === tenantId);
    if (!customer) throw new Error('Billing customer not found');
    if (!(Number(data.amount) > 0)) throw new Error('Payment amount must be positive');
    const invoiceIds = data.invoiceIds || this.invoices
      .filter((invoice) => invoice.tenantId === tenantId && invoice.billingCustomerId === data.billingCustomerId && [types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status))
      .sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate)).map((invoice) => invoice.id);
    const invoices = invoiceIds.map((id) => this.invoices.find((invoice) => invoice.id === id && invoice.tenantId === tenantId));
    if (invoices.some((invoice) => !invoice || invoice.billingCustomerId !== data.billingCustomerId || ![types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status))) {
      throw new Error('Choose posted or partially paid invoices for this customer');
    }
    let unallocated = Number(data.amount);
    const applications: types.PaymentApplication[] = [];
    for (const invoice of invoices) {
      if (!invoice || unallocated <= 0) break;
      const paid = this.payments.filter((payment) => payment.status === 'ACTIVE')
        .flatMap((payment) => payment.applications).filter((application) => application.invoiceId === invoice.id)
        .reduce((sum, application) => sum + Number(application.amountApplied), 0);
      const remaining = Math.max(0, Number(invoice.total) - paid);
      const applied = Math.min(remaining, unallocated);
      if (!applied) continue;
      unallocated -= applied;
      invoice.status = applied >= remaining ? types.InvoiceStatus.PAID : types.InvoiceStatus.PARTIAL;
      applications.push({ id: generateId('application'), invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber,
        amountApplied: applied.toFixed(2), invoiceTotal: invoice.total, invoiceStatus: invoice.status, source: 'PAYMENT',
        appliedOn: data.paymentDate > invoice.invoiceDate.slice(0, 10) ? data.paymentDate : invoice.invoiceDate.slice(0, 10), createdAt: new Date().toISOString() });
    }
    const newPayment: types.Payment = {
      id: generateId('pay'),
      tenantId,
      billingCustomerId: data.billingCustomerId,
      billingCustomer: { id: customer.id, name: customer.name, email: customer.email || null },
      amount: String(data.amount),
      paymentMethod: data.paymentMethod,
      paymentDate: data.paymentDate,
      referenceNumber: data.referenceNumber,
      memo: data.memo,
      status: 'ACTIVE',
      applications,
      unappliedAmount: unallocated.toFixed(2),
      createdAt: new Date().toISOString(),
    };

    this.payments.push(newPayment);
    return newPayment;
  }

  async voidPayment(id: string, reason?: string): Promise<types.Payment> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const payment = this.payments.find((entry) => entry.id === id && entry.tenantId === tenantId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status === 'VOIDED') throw new Error('Payment already voided');
    payment.status = 'VOIDED';
    payment.voidedAt = new Date().toISOString();
    payment.voidReason = reason;
    for (const application of payment.applications) {
      const invoice = this.invoices.find((entry) => entry.id === application.invoiceId && entry.tenantId === tenantId);
      if (!invoice || invoice.status === types.InvoiceStatus.VOID) continue;
      const paid = this.payments.filter((entry) => entry.status === 'ACTIVE')
        .flatMap((entry) => entry.applications).filter((entry) => entry.invoiceId === invoice.id)
        .reduce((sum, entry) => sum + Number(entry.amountApplied), 0);
      invoice.status = paid === 0 ? types.InvoiceStatus.POSTED : paid >= Number(invoice.total) ? types.InvoiceStatus.PAID : types.InvoiceStatus.PARTIAL;
    }
    return payment;
  }

  async getPaymentGLEntries(id: string): Promise<types.PaymentGLEntry[]> {
    const payment = await this.getPayment(id);
    return payment ? [] : [];
  }

  // Apply existing credit (tracker item 55): same rules as the backend, no GL in the mock either.
  private mockInvoicePaid(invoiceId: string): number {
    return this.payments.filter((entry) => entry.status === 'ACTIVE').flatMap((entry) => entry.applications)
      .filter((entry) => entry.invoiceId === invoiceId).reduce((sum, entry) => sum + Number(entry.amountApplied), 0);
  }

  private mockUnapplied(payment: types.Payment): number {
    return Math.round((Number(payment.amount) - payment.applications.reduce((sum, entry) => sum + Number(entry.amountApplied), 0)) * 100) / 100;
  }

  private mockCreditInvoice(invoice: types.Invoice): types.CreditInvoiceSummary {
    return { id: invoice.id, invoiceNumber: invoice.invoiceNumber, status: invoice.status, total: invoice.total,
      remainingBalance: Math.max(0, Number(invoice.total) - this.mockInvoicePaid(invoice.id)).toFixed(2) };
  }

  async getCreditTargets(paymentId: string): Promise<types.CreditTargets> {
    const payment = await this.getPayment(paymentId);
    const unapplied = payment.status === 'ACTIVE' ? this.mockUnapplied(payment) : 0;
    const invoices = this.invoices
      .filter((invoice) => invoice.tenantId === payment.tenantId && invoice.billingCustomerId === payment.billingCustomerId
        && [types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status))
      .sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate))
      .map((invoice) => ({ ...this.mockCreditInvoice(invoice), invoiceDate: invoice.invoiceDate.slice(0, 10) }))
      .filter((invoice) => Number(invoice.remainingBalance) > 0);
    const applicable = payment.status === 'ACTIVE' && (!payment.processorStatus || payment.processorStatus === 'COMPLETED');
    return { paymentId, billingCustomerId: payment.billingCustomerId, canApply: applicable && unapplied > 0, unappliedAmount: unapplied.toFixed(2), invoices };
  }

  async applyCredit(paymentId: string, data: types.ApplyCreditRequest): Promise<types.ApplyCreditResult> {
    await delay(200);
    const payment = await this.getPayment(paymentId);
    const invoice = this.invoices.find((entry) => entry.id === data.invoiceId && entry.tenantId === payment.tenantId);
    if (!invoice) throw new Error('Invoice not found');
    if (payment.status !== 'ACTIVE') throw new Error("Only an ACTIVE payment's credit can be applied");
    if (invoice.billingCustomerId !== payment.billingCustomerId) throw new Error('The invoice belongs to a different billing customer than the payment');
    if (![types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status)) throw new Error('Credit can only be applied to a POSTED or PARTIAL invoice');
    const credit = this.mockUnapplied(payment);
    const remaining = Math.round((Number(invoice.total) - this.mockInvoicePaid(invoice.id)) * 100) / 100;
    if (credit <= 0) throw new Error('This payment has no unapplied credit');
    const amount = data.amount === undefined || data.amount === '' ? Math.min(credit, remaining) : Number(data.amount);
    if (!(amount > 0) || Math.round(amount * 100) !== amount * 100) throw new Error('amount must be a positive amount with at most 2 decimal places');
    if (amount > credit) throw new Error("Amount is more than the payment's unapplied credit");
    if (amount > remaining) throw new Error('Amount is more than the invoice balance due');
    const today = new Date().toISOString().slice(0, 10);
    const appliedOn = [today, payment.paymentDate, invoice.invoiceDate.slice(0, 10)].sort().pop() as string;
    const paidAfter = remaining - amount;
    invoice.status = paidAfter <= 0 ? types.InvoiceStatus.PAID : types.InvoiceStatus.PARTIAL;
    const application: types.PaymentApplication = { id: generateId('application'), invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber,
      amountApplied: amount.toFixed(2), invoiceTotal: invoice.total, invoiceStatus: invoice.status, source: 'CREDIT', appliedOn, createdAt: new Date().toISOString() };
    payment.applications.push(application);
    payment.unappliedAmount = this.mockUnapplied(payment).toFixed(2);
    return { payment, application, invoice: this.mockCreditInvoice(invoice) };
  }

  async unapplyCredit(paymentId: string, applicationId: string): Promise<types.UnapplyCreditResult> {
    await delay(200);
    const payment = await this.getPayment(paymentId);
    const application = payment.applications.find((entry) => entry.id === applicationId);
    if (!application) throw new Error('Payment application not found');
    if (application.source !== 'CREDIT') throw new Error('Only applied credit can be unapplied');
    if (payment.status !== 'ACTIVE') throw new Error('The payment is voided; its applications are history');
    payment.applications = payment.applications.filter((entry) => entry.id !== applicationId);
    payment.unappliedAmount = this.mockUnapplied(payment).toFixed(2);
    const invoice = this.invoices.find((entry) => entry.id === application.invoiceId && entry.tenantId === payment.tenantId);
    if (!invoice) throw new Error('Invoice not found');
    if (invoice.status !== types.InvoiceStatus.VOID) {
      const paid = this.mockInvoicePaid(invoice.id);
      invoice.status = paid <= 0 ? types.InvoiceStatus.POSTED : paid >= Number(invoice.total) ? types.InvoiceStatus.PAID : types.InvoiceStatus.PARTIAL;
    }
    return { payment, invoice: this.mockCreditInvoice(invoice) };
  }

  async getCustomerCredits(customerId: string): Promise<types.CustomerCredits> {
    await delay(150);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    if (!this.customers.some((entry) => entry.id === customerId && entry.tenantId === tenantId)) throw new Error('Customer not found');
    const payments = this.payments
      .filter((entry) => entry.tenantId === tenantId && entry.billingCustomerId === customerId && entry.status === 'ACTIVE' && this.mockUnapplied(entry) > 0)
      .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate))
      .map((entry) => ({ paymentId: entry.id, paymentDate: entry.paymentDate, paymentMethod: entry.paymentMethod, referenceNumber: entry.referenceNumber ?? null,
        amount: Number(entry.amount).toFixed(2), unappliedAmount: this.mockUnapplied(entry).toFixed(2),
        applicable: !entry.processorStatus || entry.processorStatus === 'COMPLETED' }));
    const total = payments.reduce((sum, entry) => sum + Number(entry.unappliedAmount), 0);
    return { customerId, totalUnapplied: total.toFixed(2), payments };
  }

  async listGLAccounts(): Promise<types.GLAccount[]> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    return filterByTenant(this.glAccounts, tenantId);
  }

  async getGLAccount(id: string): Promise<types.GLAccount> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const account = this.glAccounts.find((a) => a.id === id && a.tenantId === tenantId);
    if (!account) throw new Error('GL Account not found');
    return account;
  }

  async createGLAccount(data: types.CreateGLAccountRequest): Promise<types.GLAccount> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const newAccount: types.GLAccount = {
      id: generateId('gl-acc'),
      tenantId,
      ...data,
      isPostable: data.isPostable ?? true,
      hasJournalLines: false,
      createdAt: new Date().toISOString(),
    };

    this.glAccounts.push(newAccount);
    return newAccount;
  }

  async updateGLAccount(id: string, data: types.UpdateGLAccountRequest): Promise<types.GLAccount> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    const account = this.glAccounts.find((a) => a.id === id && a.tenantId === tenantId);
    if (!account) throw new Error('GL Account not found');

    const { parentId, ...rest } = data;
    Object.assign(account, rest);
    if (parentId !== undefined) account.parentId = parentId ?? undefined;
    return account;
  }

  async seedChartOfAccounts(): Promise<types.SeedChartOfAccountsResponse> {
    await delay(300);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');

    // Mock accounts are pre-seeded; return what the tenant already has.
    const accounts = filterByTenant(this.glAccounts, tenantId);
    return { count: accounts.length, accounts };
  }

  /** An entry with its reversal links, computed like the backend (ADJUSTMENT whose sourceId is the entry's id). */
  private toJournalEntry(entry: MockJournalEntry): types.JournalEntry {
    const sameTenant = this.journalEntries.filter((e) => e.tenantId === entry.tenantId);
    const reversal = sameTenant
      .filter((e) => e.sourceType === 'ADJUSTMENT' && e.sourceId === entry.id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
    const reverses =
      entry.sourceType === 'ADJUSTMENT' && sameTenant.some((e) => e.id === entry.sourceId) ? entry.sourceId : null;
    return { ...entry, lines: entry.lines.map((line) => ({ ...line })), reversedByEntryId: reversal?.id ?? null, reversesEntryId: reverses };
  }

  private requireJournalActor(): { tenantId: string; userId: string; email: string } {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const actor = getCurrentActor(this.token || '');
    return { tenantId, userId: actor?.userId ?? 'unknown', email: actor?.email ?? '' };
  }

  async listJournalEntries(
    page = 1,
    limit = 20,
    filters: types.JournalEntryListFilters = {}
  ): Promise<types.PaginatedResponse<types.JournalEntry>> {
    await delay(300);
    const { tenantId } = this.requireJournalActor();
    const { startDate, endDate, sourceType, accountId } = filters;
    if ((startDate && !isDateOnly(startDate)) || (endDate && !isDateOnly(endDate))) {
      throw mockApiError(400, 'Dates must be valid YYYY-MM-DD dates', 'INVALID_FILTER');
    }
    if (startDate && endDate && startDate > endDate) {
      throw mockApiError(400, 'startDate must be on or before endDate', 'INVALID_FILTER');
    }
    if (sourceType && !(types.JOURNAL_SOURCE_TYPES as readonly string[]).includes(sourceType)) {
      throw mockApiError(400, `sourceType must be one of ${types.JOURNAL_SOURCE_TYPES.join(', ')}`, 'INVALID_FILTER');
    }
    const cappedLimit = Math.min(Math.max(1, limit), 100);

    const filtered = this.journalEntries
      .filter((e) => e.tenantId === tenantId)
      .filter((e) => !startDate || e.entryDate >= startDate)
      .filter((e) => !endDate || e.entryDate <= endDate)
      .filter((e) => !sourceType || e.sourceType === sourceType)
      .filter((e) => !accountId || e.lines.some((line) => line.glAccountId === accountId))
      .sort((a, b) => b.entryDate.localeCompare(a.entryDate) || b.createdAt.localeCompare(a.createdAt));
    const start = (page - 1) * cappedLimit;

    return {
      success: true,
      data: filtered.slice(start, start + cappedLimit).map((e) => this.toJournalEntry(e)),
      pagination: {
        page,
        limit: cappedLimit,
        total: filtered.length,
        pages: Math.ceil(filtered.length / cappedLimit),
      },
    };
  }

  async getJournalEntry(id: string): Promise<types.JournalEntry> {
    await delay(200);
    const { tenantId } = this.requireJournalActor();
    const entry = this.journalEntries.find((e) => e.id === id && e.tenantId === tenantId);
    if (!entry) throw mockApiError(404, 'Journal entry not found', 'NOT_FOUND');
    return this.toJournalEntry(entry);
  }

  async createJournalEntry(data: types.CreateJournalEntryRequest): Promise<types.JournalEntry> {
    await delay(300);
    const actor = this.requireJournalActor();

    // Same rules as the backend posting validation (GLBusinessLayer).
    if (!isDateOnly(data.entryDate)) throw mockApiError(400, 'Entry date is required');
    if (!data.sourceId) throw mockApiError(400, 'Source ID is required');
    // Backend GLBusinessLayer.validateManualSourceId: an entry id as sourceId would fake a reversal.
    if (this.journalEntries.some((e) => e.tenantId === actor.tenantId && e.id === data.sourceId)) {
      throw mockApiError(
        400,
        `sourceId ${data.sourceId} is a journal entry id. A manual entry cannot reference another journal entry; ` +
          'to reverse an entry use POST /gl/journal-entries/:id/reverse',
        'SOURCE_ID_IS_JOURNAL_ENTRY'
      );
    }
    if (!data.lines || data.lines.length < 2) throw mockApiError(400, 'Journal entry must have at least 2 lines');
    if (data.sourceType !== 'ADJUSTMENT') {
      throw mockApiError(400, 'Manual journal entries must use sourceType ADJUSTMENT.', 'MANUAL_SOURCE_TYPE_NOT_ALLOWED');
    }
    let debits = 0;
    let credits = 0;
    const lines = data.lines.map((line, index) => {
      const debit = decimalToCents(line.debit) ?? -1;
      const credit = decimalToCents(line.credit) ?? -1;
      if (debit < 0 || credit < 0) throw mockApiError(400, `Line ${index + 1}: amounts must be non-negative numbers`);
      if ((debit > 0) === (credit > 0)) throw mockApiError(400, `Line ${index + 1}: must have either debit or credit, not both or neither`);
      const account = this.glAccounts.find((a) => a.id === line.glAccountId && a.tenantId === actor.tenantId);
      if (!account) throw mockApiError(400, `Line ${index + 1}: GL account not found`);
      if (!account.isPostable) throw mockApiError(400, `Line ${index + 1}: GL account ${account.accountNumber} is not postable`);
      debits += debit;
      credits += credit;
      return { account, debit, credit, customerId: line.customerId };
    });
    if (debits !== credits) {
      throw mockApiError(400, `Journal entry is not balanced: debits ${centsToDecimal(debits)} != credits ${centsToDecimal(credits)}`);
    }
    if (debits === 0) throw mockApiError(400, 'Journal entry total must be greater than zero');

    const id = generateId('je');
    const entry: MockJournalEntry = {
      id,
      tenantId: actor.tenantId,
      entryDate: data.entryDate,
      ...(data.memo ? { memo: data.memo } : {}),
      sourceType: data.sourceType,
      sourceId: data.sourceId,
      createdBy: actor.userId,
      createdByEmail: actor.email || null,
      createdAt: new Date().toISOString(),
      lines: lines.map(({ account, debit, credit, customerId }) => ({
        id: generateId('jel'),
        journalEntryId: id,
        glAccountId: account.id,
        accountNumber: account.accountNumber,
        accountName: account.accountName,
        accountType: account.accountType,
        debit: centsToDecimal(debit),
        credit: centsToDecimal(credit),
        ...(customerId ? { customerId } : {}),
      })),
    };
    this.journalEntries.push(entry);
    const postedIds = new Set(lines.map(({ account }) => account.id));
    this.glAccounts = this.glAccounts.map((a) => (postedIds.has(a.id) ? { ...a, hasJournalLines: true } : a));
    return this.toJournalEntry(entry);
  }

  async reverseJournalEntry(id: string, memo?: string): Promise<types.JournalEntry> {
    await delay(300);
    const actor = this.requireJournalActor();
    const stored = this.journalEntries.find((e) => e.id === id && e.tenantId === actor.tenantId);
    if (!stored) throw mockApiError(404, 'Journal entry not found', 'NOT_FOUND');
    const original = this.toJournalEntry(stored);
    if (original.sourceType !== 'ADJUSTMENT') {
      throw mockApiError(
        400,
        `Journal entry ${id} was posted by a ${original.sourceType} document. Cancel the invoice or void the payment instead.`,
        'DOCUMENT_ENTRY_NOT_REVERSIBLE'
      );
    }
    if (this.inventoryAdjustments.some((row) => row.tenantId === actor.tenantId && row.referenceType === 'MANUAL' && row.referenceId === id)) {
      throw mockApiError(
        400,
        `Journal entry ${id} was posted by an inventory receipt or count adjustment; post an inventory adjustment to correct it`,
        'INVENTORY_ENTRY_NOT_REVERSIBLE'
      );
    }
    if (original.reversesEntryId) {
      throw mockApiError(
        400,
        `Journal entry ${id} is a reversal of entry ${original.reversesEntryId} and cannot be reversed. Post a new manual entry instead.`,
        'REVERSAL_NOT_REVERSIBLE'
      );
    }
    if (original.reversedByEntryId) {
      throw mockApiError(409, `Journal entry ${id} has already been reversed by entry ${original.reversedByEntryId}`, 'ALREADY_REVERSED');
    }
    const trimmed = memo?.trim();
    const reversalId = generateId('je');
    const reversal: MockJournalEntry = {
      id: reversalId,
      tenantId: actor.tenantId,
      entryDate: new Date().toISOString().slice(0, 10),
      memo: trimmed || `Reversal of entry ${id}`,
      sourceType: 'ADJUSTMENT',
      sourceId: id,
      createdBy: actor.userId,
      createdByEmail: actor.email || null,
      createdAt: new Date().toISOString(),
      lines: stored.lines.map((line) => ({ ...line, id: generateId('jel'), journalEntryId: reversalId, debit: line.credit, credit: line.debit })),
    };
    this.journalEntries.push(reversal);
    return this.toJournalEntry(reversal);
  }

  // Reports: static fixtures in the backend shape. The mock is not a report
  // engine (out of scope for the Reports UI plan); it only keeps the contract
  // compiling and gives the pages something to render offline.

  private requireReportTenant(): string {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    return tenantId;
  }

  private emptyReportPage(query?: types.ReportPagination): types.PaginationInfo {
    return { page: query?.page || 1, limit: query?.limit || 20, total: 0, pages: 0 };
  }

  async getTrialBalance(query?: types.TrialBalanceQuery): Promise<types.TrialBalanceReport> {
    await delay(300);
    this.requireReportTenant();
    return {
      asOfDate: query?.asOfDate || new Date().toISOString().slice(0, 10),
      accounts: [
        { glAccountId: 'gl-acc-1000', accountNumber: '1000', accountName: 'Cash', accountType: 'ASSET', normalBalance: 'DEBIT', debit: '50000.00', credit: '0.00', balance: '50000.00' },
        { glAccountId: 'gl-acc-1200', accountNumber: '1200', accountName: 'Accounts Receivable', accountType: 'ASSET', normalBalance: 'DEBIT', debit: '25000.00', credit: '0.00', balance: '25000.00' },
        { glAccountId: 'gl-acc-4000', accountNumber: '4000', accountName: 'Service Revenue', accountType: 'REVENUE', normalBalance: 'CREDIT', debit: '0.00', credit: '75000.00', balance: '75000.00' },
      ],
      totalDebits: '75000.00',
      totalCredits: '75000.00',
      balanced: true,
    };
  }

  async getARAging(query?: types.ARAgingQuery): Promise<types.ARAgingReport> {
    await delay(300);
    const tenantId = this.requireReportTenant();
    const customer = this.customers.find((c) => c.tenantId === tenantId);
    const buckets: types.ARAgingBuckets = { current: '5000.00', days1to30: '3000.00', days31to60: '2000.00', days61to90: '0.00', days90plus: '0.00' };
    return {
      asOfDate: query?.asOfDate || new Date().toISOString().slice(0, 10),
      customers: customer
        ? [{ customerId: customer.id, customerName: customer.name, buckets, totalOutstanding: '10000.00', unappliedCredit: '0.00', netBalance: '10000.00', invoices: [] }]
        : [],
      totals: customer
        ? { ...buckets, totalOutstanding: '10000.00', unappliedCredit: '0.00', netBalance: '10000.00' }
        : { current: '0.00', days1to30: '0.00', days31to60: '0.00', days61to90: '0.00', days90plus: '0.00', totalOutstanding: '0.00', unappliedCredit: '0.00', netBalance: '0.00' },
    };
  }

  async getRevenueReport(query?: types.RevenueReportQuery): Promise<types.RevenueReport> {
    await delay(300);
    this.requireReportTenant();
    return { startDate: query?.startDate, endDate: query?.endDate, totalRevenue: '0.00', grossRevenue: '0.00', totalDiscounts: '0.00', totalTax: '0.00', byItemType: [], byCustomer: [] };
  }

  async getJobReport(query?: types.JobReportQuery): Promise<types.JobReport> {
    await delay(300);
    this.requireReportTenant();
    return { startDate: query?.startDate, endDate: query?.endDate, byStatus: [], byProperty: [], data: [], pagination: this.emptyReportPage(query) };
  }

  async getPaymentReport(query?: types.PaymentReportQuery): Promise<types.PaymentReport> {
    await delay(300);
    this.requireReportTenant();
    return { startDate: query?.startDate, endDate: query?.endDate, totalPayments: '0.00', byMethod: [], data: [], pagination: this.emptyReportPage(query) };
  }

  async getCustomerStatement(customerId: string, query?: types.CustomerStatementQuery): Promise<types.CustomerStatementReport> {
    await delay(300);
    const tenantId = this.requireReportTenant();
    const customer = this.customers.find((c) => c.id === customerId && c.tenantId === tenantId);
    if (!customer) throw new Error('Customer not found');
    return {
      customerId: customer.id,
      customerName: customer.name,
      startDate: query?.startDate,
      endDate: query?.endDate,
      openingBalance: '0.00',
      closingBalance: '0.00',
      lines: [],
      pagination: this.emptyReportPage(query),
    };
  }

  // ============ Finance Charges ============
  // A small in-memory version of backend FinanceChargeService: enough to
  // exercise the page offline. Money is kept in integer cents.

  private requireOfficeUser(): MockUser {
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN && actor.role !== types.UserRole.OFFICE_MANAGER) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  /** Like the backend: blank = today (UTC); otherwise a real date, not in the future. */
  private mockFinanceChargeAsOf(input: string | undefined): string {
    const today = new Date().toISOString().slice(0, 10);
    if (!input) return today;
    if (addDaysToDateOnly(input, 0) !== input) {
      throw mockApiError(400, 'asOfDate must be a valid date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    }
    if (input > today) throw mockApiError(400, 'asOfDate cannot be in the future', 'VALIDATION_ERROR');
    return input;
  }

  private mockFinanceChargeRows(tenantId: string, asOf: string, customerIds?: string[]): types.FinanceChargePreviewRow[] {
    const settings = this.mockCompanySettings(tenantId);
    const periodKey = asOf.slice(0, 7);
    const toCents = (value: string | number) => Math.round(Number(value) * 100);
    const money = (cents: number) => (cents / 100).toFixed(2);
    const activeAsOf = this.payments.filter((payment) =>
      payment.tenantId === tenantId && payment.status === 'ACTIVE' && payment.paymentDate.slice(0, 10) <= asOf);
    const appliedCents = (invoiceId: string) => activeAsOf
      .flatMap((payment) => payment.applications)
      .filter((application) => application.invoiceId === invoiceId)
      .reduce((sum, application) => sum + toCents(application.amountApplied), 0);
    const byCustomer = new Map<string, { row: types.FinanceChargePreviewRow; overdueCents: number }>();

    for (const invoice of filterByTenant(this.invoices, tenantId)) {
      if (invoice.invoiceType === 'FINANCE_CHARGE') continue;
      if (![types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL, types.InvoiceStatus.PAID].includes(invoice.status)) continue;
      if (customerIds && !customerIds.includes(invoice.billingCustomerId)) continue;
      const dueDate = invoice.dueDate.slice(0, 10);
      const graceEnd = addDaysToDateOnly(dueDate, settings.financeChargeGraceDays) || dueDate;
      if (invoice.invoiceDate.slice(0, 10) > asOf || !(asOf > graceEnd)) continue;
      const remaining = toCents(invoice.total) - appliedCents(invoice.id);
      if (remaining <= 0) continue;
      let entry = byCustomer.get(invoice.billingCustomerId);
      if (!entry) {
        entry = {
          overdueCents: 0,
          row: {
            customerId: invoice.billingCustomerId,
            customerName: invoice.billingCustomer?.name || invoice.billingCustomerId,
            overdueBalance: '0.00', unappliedCredit: '0.00', overdueBase: '0.00', charge: '0',
            chargeable: false, skipReason: null, note: null, invoices: [],
          },
        };
        byCustomer.set(invoice.billingCustomerId, entry);
      }
      entry.overdueCents += remaining;
      entry.row.invoices.push({
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        dueDate,
        daysOverdue: Math.round((Date.parse(asOf) - Date.parse(dueDate)) / 86400000),
        remainingBalance: money(remaining),
      });
    }

    const rate = Number(settings.financeChargeRate);
    const minCents = toCents(settings.financeChargeMinAmount);
    const notes: Record<types.FinanceChargeSkipReason, string> = {
      ALREADY_ASSESSED: `Already charged for ${periodKey}`,
      CREDIT_COVERS_BALANCE: 'Unapplied credit covers the overdue balance',
      BELOW_ONE_CENT: 'Charge rounds to less than one cent',
      NOTHING_OVERDUE: 'Nothing overdue',
      ERROR: 'Assessment failed',
    };
    return Array.from(byCustomer.values()).map(({ row, overdueCents }) => {
      const creditCents = activeAsOf
        .filter((payment) => payment.billingCustomerId === row.customerId)
        .reduce((sum, payment) => sum + toCents(payment.unappliedAmount || '0'), 0);
      const baseCents = Math.max(0, overdueCents - creditCents);
      let skipReason: types.FinanceChargeSkipReason | null = null;
      let chargeCents = 0;
      if (baseCents === 0) skipReason = 'CREDIT_COVERS_BALANCE';
      else if (this.financeChargePeriods.has(`${tenantId}:${row.customerId}:${periodKey}`)) skipReason = 'ALREADY_ASSESSED';
      else {
        chargeCents = Math.max(minCents, Math.round(baseCents * rate));
        if (chargeCents === 0) skipReason = 'BELOW_ONE_CENT';
      }
      return {
        ...row,
        overdueBalance: money(overdueCents),
        unappliedCredit: money(creditCents),
        overdueBase: money(baseCents),
        charge: skipReason ? '0' : money(chargeCents),
        chargeable: skipReason === null,
        skipReason,
        note: skipReason ? notes[skipReason] : null,
      };
    }).sort((a, b) => a.customerName.localeCompare(b.customerName));
  }

  async previewFinanceCharges(query?: types.FinanceChargePreviewQuery): Promise<types.FinanceChargePreview> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const asOfDate = this.mockFinanceChargeAsOf(query?.asOfDate);
    const settings = this.mockCompanySettings(tenantId);
    const enabled = Number(settings.financeChargeRate) > 0;
    const rows = enabled ? this.mockFinanceChargeRows(tenantId, asOfDate) : [];
    const chargeable = rows.filter((row) => row.chargeable);
    return {
      asOfDate,
      periodKey: asOfDate.slice(0, 7),
      enabled,
      settings: { rate: settings.financeChargeRate, graceDays: settings.financeChargeGraceDays, minAmount: settings.financeChargeMinAmount },
      rows,
      totalCharge: (chargeable.reduce((sum, row) => sum + Math.round(Number(row.charge) * 100), 0) / 100).toFixed(2),
      chargeableCount: chargeable.length,
    };
  }

  async assessFinanceCharges(data: types.AssessFinanceChargesRequest): Promise<types.AssessFinanceChargesResult> {
    await delay(300);
    const { tenantId } = this.requireOfficeUser();
    const asOfDate = this.mockFinanceChargeAsOf(data.asOfDate);
    const periodKey = asOfDate.slice(0, 7);
    const settings = this.mockCompanySettings(tenantId);
    if (!(Number(settings.financeChargeRate) > 0)) {
      throw mockApiError(
        400,
        'Finance charges are disabled: set a finance charge rate above 0 in company settings first',
        'FINANCE_CHARGES_DISABLED'
      );
    }
    let customerIds: string[] | undefined;
    if (data.customerIds !== undefined && data.customerIds !== null) {
      if (!Array.isArray(data.customerIds) || data.customerIds.length === 0) {
        throw mockApiError(400, 'customerIds must be a non-empty array of customer ids', 'VALIDATION_ERROR');
      }
      customerIds = Array.from(new Set(data.customerIds));
      const missing = customerIds.filter((id) => !this.customers.some((c) => c.id === id && c.tenantId === tenantId));
      if (missing.length) throw mockApiError(404, `Customer not found: ${missing.join(', ')}`, 'CUSTOMER_NOT_FOUND');
    }

    const rows = this.mockFinanceChargeRows(tenantId, asOfDate, customerIds);
    const assessed: types.FinanceChargeAssessed[] = [];
    const skipped: types.FinanceChargeSkipped[] = [];
    for (const id of customerIds ?? []) {
      if (!rows.some((row) => row.customerId === id)) {
        skipped.push({ customerId: id, customerName: null, reason: 'NOTHING_OVERDUE', message: `Nothing overdue as of ${asOfDate}` });
      }
    }
    const now = new Date().toISOString();
    for (const row of rows) {
      if (!row.chargeable) {
        const reason = row.skipReason ?? 'ERROR';
        skipped.push({ customerId: row.customerId, customerName: row.customerName, reason, message: row.note || reason });
        continue;
      }
      const customer = this.customers.find((c) => c.id === row.customerId && c.tenantId === tenantId);
      const invoice: types.Invoice = {
        id: generateId('inv'),
        tenantId,
        billingCustomerId: row.customerId,
        billingCustomer: { id: row.customerId, name: row.customerName, email: customer?.email || null },
        invoiceNumber: `INV-${Date.now()}-${assessed.length + 1}`,
        status: types.InvoiceStatus.POSTED,
        invoiceType: 'FINANCE_CHARGE',
        invoiceDate: asOfDate,
        dueDate: addDaysToDateOnly(asOfDate, settings.defaultPaymentTermsDays) || asOfDate,
        billingMethod: 'PER_VISIT',
        lineItems: [{
          id: generateId('ili'),
          itemId: 'item-finance-charge',
          item: { id: 'item-finance-charge', name: 'Finance Charge', itemType: 'SERVICE' },
          description: `Finance charge for ${periodKey} on overdue balance ${row.overdueBase}`,
          quantity: '1', unitPrice: row.charge, taxRate: '0', lineAmount: row.charge,
          lineDiscount: '0', lineTax: '0', lineCogs: '0', createdAt: now,
        }],
        jobs: [],
        subtotal: row.charge,
        totalDiscount: '0.00',
        discounts: [],
        totalAmount: row.charge,
        totalTax: '0.00',
        totalCogs: '0',
        total: row.charge,
        createdAt: now,
        updatedAt: now,
      };
      this.invoices.push(invoice);
      this.financeChargePeriods.add(`${tenantId}:${row.customerId}:${periodKey}`);
      assessed.push({
        customerId: row.customerId, customerName: row.customerName, invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber, overdueBase: row.overdueBase, amount: row.charge,
      });
    }
    const totalCents = assessed.reduce((sum, entry) => sum + Math.round(Number(entry.amount) * 100), 0);
    return {
      asOfDate,
      periodKey,
      runId: assessed.length ? generateId('fcrun') : null,
      assessed,
      skipped,
      totalAssessed: (totalCents / 100).toFixed(2),
    };
  }

  // ============ Flat Contract Billing ============
  // A small in-memory version of backend FlatContractBillingService (same
  // period math, eligibility and one-invoice-per-period rule), enough to
  // exercise the page offline. Money is kept in integer cents.

  private mockBillingPattern(schedule: MockRecurringSchedule): BillingPeriodPattern | null {
    if (!schedule.billingFrequency) return null;
    return { anchor: schedule.startDate, frequency: schedule.billingFrequency, interval: schedule.billingInterval || 1 };
  }

  private mockBillableFrom(schedule: MockRecurringSchedule): string {
    const billing = schedule.billingStartDate ?? schedule.startDate;
    return billing > schedule.startDate ? billing : schedule.startDate;
  }

  private mockPeriodStatus(schedule: MockRecurringSchedule, period: { start: string; end: string }): {
    status: types.FlatContractPeriodStatus;
    invoice: types.FlatContractPreviewRow['invoice'];
  } {
    const billed = this.billedPeriods.filter((p) => p.scheduleId === schedule.id);
    const same = billed.find((p) => p.start === period.start);
    if (same) {
      const invoice = this.invoices.find((i) => i.id === same.invoiceId);
      return {
        status: 'ALREADY_BILLED',
        invoice: { invoiceId: same.invoiceId, invoiceNumber: invoice?.invoiceNumber ?? '', status: invoice?.status ?? '' },
      };
    }
    if (billed.some((p) => periodsOverlap(p, period))) return { status: 'OVERLAPS_BILLED_PERIOD', invoice: null };
    return { status: 'DUE', invoice: null };
  }

  private mockNextBillingDate(schedule: MockRecurringSchedule): string | null {
    const pattern = this.mockBillingPattern(schedule);
    if (schedule.billingModel !== 'FLAT_CONTRACT' || !pattern) return null;
    const today = new Date().toISOString().slice(0, 10);
    const first = billingPeriodContaining(pattern, today)?.index ?? 0;
    for (let index = first; index < first + 500; index += 1) {
      const period = billingPeriodAt(pattern, index);
      if (schedule.endDate && period.start > schedule.endDate) return null;
      if (period.end < this.mockBillableFrom(schedule)) continue;
      if (this.mockPeriodStatus(schedule, period).status !== 'DUE') continue;
      return period.start;
    }
    return null;
  }

  private mockFlatContractRow(
    schedule: MockRecurringSchedule,
    period: { index: number; start: string; end: string },
    isCurrent: boolean
  ): types.FlatContractPreviewRow {
    const property = this.properties.find((p) => p.id === schedule.propertyId);
    const customer = this.customers.find((c) => c.id === schedule.customerId);
    const billingCustomer = this.customers.find((c) => c.id === property?.billingCustomerId) ?? customer;
    const rate = customer?.taxExempt ? 0 : Number(this.taxRates.find((t) => t.id === property?.taxRateId)?.rate ?? 0);
    const amountCents = Math.round(Number(schedule.flatContractAmount ?? 0) * 100);
    const taxCents = Math.round(amountCents * rate);
    const { status, invoice } = this.mockPeriodStatus(schedule, period);
    const notes: Record<string, string> = {
      ALREADY_BILLED: 'Already billed for this period',
      OVERLAPS_BILLED_PERIOD: 'Overlaps a period billed under an earlier billing period setting',
    };
    return {
      scheduleId: schedule.id,
      customerId: schedule.customerId,
      customerName: customer?.name ?? '',
      billingCustomerId: billingCustomer?.id ?? schedule.customerId,
      billingCustomerName: billingCustomer?.name ?? '',
      propertyId: schedule.propertyId,
      propertyAddress: [property?.address, property?.city].filter(Boolean).join(', '),
      billingFrequency: schedule.billingFrequency!,
      billingInterval: schedule.billingInterval,
      periodStartDate: period.start,
      periodEndDate: period.end,
      isCurrent,
      amount: (amountCents / 100).toFixed(2),
      taxRate: rate.toFixed(4),
      tax: (taxCents / 100).toFixed(2),
      total: ((amountCents + taxCents) / 100).toFixed(2),
      status,
      billable: status === 'DUE',
      note: notes[status] ?? null,
      invoice,
    };
  }

  private mockFlatContractRows(tenantId: string, today: string): types.FlatContractPreviewRow[] {
    const rows: types.FlatContractPreviewRow[] = [];
    const schedules = this.recurringSchedules
      .filter((s) => s.tenantId === tenantId && !s.deleted && s.status === 'ACTIVE' && s.billingModel === 'FLAT_CONTRACT');
    for (const schedule of schedules) {
      const pattern = this.mockBillingPattern(schedule);
      if (!pattern) continue;
      const through = schedule.endDate && schedule.endDate < today ? schedule.endDate : today;
      const last = billingPeriodContaining(pattern, through);
      if (!last) continue;
      const current = billingPeriodContaining(pattern, today);
      const first = billingPeriodContaining(pattern, this.mockBillableFrom(schedule))?.index ?? 0;
      for (let index = Math.max(first, last.index - 23); index <= last.index; index += 1) {
        const period = billingPeriodAt(pattern, index);
        const isCurrent = current?.index === index;
        const row = this.mockFlatContractRow(schedule, period, isCurrent);
        if (row.status === 'DUE' || isCurrent) rows.push(row);
      }
    }
    return rows.sort((a, b) => a.customerName.localeCompare(b.customerName) || a.scheduleId.localeCompare(b.scheduleId));
  }

  async previewFlatContractBilling(): Promise<types.FlatContractPreview> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const today = new Date().toISOString().slice(0, 10);
    const rows = this.mockFlatContractRows(tenantId, today);
    const due = rows.filter((r) => r.billable);
    return {
      asOfDate: today,
      mode: this.mockCompanySettings(tenantId).flatContractBillingMode,
      rows,
      dueCount: due.length,
      currentDueCount: due.filter((r) => r.isCurrent).length,
      totalDue: (due.reduce((sum, r) => sum + Math.round(Number(r.total) * 100), 0) / 100).toFixed(2),
      truncatedScheduleIds: [],
    };
  }

  async billFlatContracts(data: types.BillFlatContractsRequest): Promise<types.BillFlatContractsResult> {
    await delay(300);
    const { tenantId } = this.requireOfficeUser();
    const today = new Date().toISOString().slice(0, 10);
    let items: types.FlatContractBillItem[];
    if (data.items !== undefined && data.items !== null) {
      if (!Array.isArray(data.items) || data.items.length === 0 || data.items.some((i) => !i.scheduleId || !isDateOnly(i.periodStartDate))) {
        throw mockApiError(400, 'items must be a non-empty array of { scheduleId, periodStartDate (YYYY-MM-DD) }');
      }
      const missing = Array.from(new Set(data.items.map((i) => i.scheduleId)))
        .filter((id) => !this.recurringSchedules.some((s) => s.id === id && s.tenantId === tenantId && !s.deleted));
      if (missing.length) throw mockApiError(404, `Recurring schedule not found: ${missing.join(', ')}`, 'SCHEDULE_NOT_FOUND');
      items = data.items;
    } else {
      items = this.mockFlatContractRows(tenantId, today)
        .filter((r) => r.billable && r.isCurrent)
        .map((r) => ({ scheduleId: r.scheduleId, periodStartDate: r.periodStartDate }));
    }

    const settings = this.mockCompanySettings(tenantId);
    const result: types.BillFlatContractsResult = { invoiceDate: today, billed: [], skipped: [], totalBilled: '0.00' };
    let totalCents = 0;
    for (const item of items) {
      const schedule = this.recurringSchedules.find((s) => s.id === item.scheduleId && s.tenantId === tenantId && !s.deleted)!;
      const customerName = this.customers.find((c) => c.id === schedule.customerId)?.name ?? null;
      const skip = (reason: types.FlatContractSkipReason, message: string) =>
        result.skipped.push({ scheduleId: schedule.id, customerName, periodStartDate: item.periodStartDate, reason, message });
      const pattern = this.mockBillingPattern(schedule);
      if (schedule.billingModel !== 'FLAT_CONTRACT' || !pattern) { skip('NOT_FLAT_CONTRACT', 'Schedule is billed per visit, not as a flat contract'); continue; }
      if (schedule.status !== 'ACTIVE') { skip('SCHEDULE_NOT_ACTIVE', 'Schedule is not active'); continue; }
      const period = billingPeriodContaining(pattern, item.periodStartDate);
      if (!period || period.start !== item.periodStartDate || period.start > today
        || (schedule.endDate && period.start > schedule.endDate) || period.end < this.mockBillableFrom(schedule)) {
        skip('NOT_A_DUE_PERIOD', 'Not a due billing period of this schedule');
        continue;
      }
      const current = billingPeriodContaining(pattern, today);
      const row = this.mockFlatContractRow(schedule, period, current?.index === period.index);
      if (row.status !== 'DUE') { skip(row.status, row.note ?? row.status); continue; }

      const now = new Date().toISOString();
      const billingCustomer = this.customers.find((c) => c.id === row.billingCustomerId);
      const invoice: types.Invoice = {
        id: generateId('inv'),
        tenantId,
        billingCustomerId: row.billingCustomerId,
        billingCustomer: { id: row.billingCustomerId, name: row.billingCustomerName, email: billingCustomer?.email || null },
        invoiceNumber: `INV-${Date.now()}-${result.billed.length + 1}`,
        status: types.InvoiceStatus.POSTED,
        invoiceType: 'STANDARD',
        invoiceDate: today,
        dueDate: addDaysToDateOnly(today, settings.defaultPaymentTermsDays) || today,
        periodStartDate: row.periodStartDate,
        periodEndDate: row.periodEndDate,
        billingMethod: 'CONSOLIDATED',
        lineItems: [{
          id: generateId('ili'),
          itemId: 'item-flat-contract',
          item: { id: 'item-flat-contract', name: 'Flat Contract Service', itemType: 'SERVICE' },
          description: `Recurring service: ${row.periodStartDate} to ${row.periodEndDate}`,
          quantity: '1', unitPrice: row.amount, taxRate: row.taxRate, lineAmount: row.amount,
          lineDiscount: '0', lineTax: row.tax, lineCogs: '0', createdAt: now,
        }],
        jobs: [],
        subtotal: row.amount,
        totalDiscount: '0.00',
        discounts: [],
        totalAmount: row.amount,
        totalTax: row.tax,
        totalCogs: '0',
        total: row.total,
        createdAt: now,
        updatedAt: now,
      };
      this.invoices.push(invoice);
      this.billedPeriods.push({
        tenantId, scheduleId: schedule.id, start: row.periodStartDate, end: row.periodEndDate,
        invoiceId: invoice.id, amount: row.amount, trigger: 'MANUAL', createdAt: now,
      });
      totalCents += Math.round(Number(row.total) * 100);
      result.billed.push({
        scheduleId: schedule.id, customerName: row.customerName, billingCustomerName: row.billingCustomerName,
        periodStartDate: row.periodStartDate, periodEndDate: row.periodEndDate, invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber, amount: row.amount, tax: row.tax, total: row.total,
      });
    }
    result.totalBilled = (totalCents / 100).toFixed(2);
    return result;
  }

  // ============ Auto-invoicing (tracker item 37) ============
  // A small in-memory version of backend AutoInvoicingService: PER_VISIT
  // groups one job each, CONSOLIDATED bill-to customers one group per month
  // after the month ends; invoices go through the mock createInvoice.

  private mockAutoInvoiceGroups(tenantId: string): { groups: types.AutoInvoicePreviewGroup[]; skipped: types.AutoInvoiceSkippedJob[] } {
    const settings = this.mockCompanySettings(tenantId);
    const today = new Date().toISOString().slice(0, 10);
    const groups = new Map<string, types.AutoInvoicePreviewGroup>();
    const skipped: types.AutoInvoiceSkippedJob[] = [];
    const money = (n: number) => n.toFixed(2);
    for (const job of this.jobs.filter((j) => j.tenantId === tenantId && j.status === types.JobStatus.COMPLETED && !j.invoiceId)) {
      const property = this.properties.find((p) => p.id === job.propertyId && p.tenantId === tenantId);
      const billTo = this.customers.find((c) => c.id === (property?.billingCustomerId ?? job.customerId));
      const owner = this.customers.find((c) => c.id === job.customerId);
      const subtotal = job.lineItems.reduce((sum, l) => sum + Number(l.lineTotal), 0);
      const tax = job.lineItems.reduce((sum, l) => sum + Number(l.lineTax), 0);
      const completed = job.completedDate ? job.completedDate.slice(0, 10) : null;
      const ref: types.AutoInvoiceJobRef = {
        jobId: job.id, completedDate: completed, propertyAddress: property?.address ?? '', subtotal: money(subtotal), tax: money(tax),
      };
      let reason: types.AutoInvoiceSkipReason | null = null;
      if (job.coveredByFlatContract) reason = 'COVERED_BY_FLAT_CONTRACT';
      else if (billTo?.autoInvoiceExempt || owner?.autoInvoiceExempt) reason = 'CUSTOMER_EXCLUDED';
      else if (job.autoInvoiceHold) reason = 'ON_HOLD';
      else if (job.lineItems.length === 0) reason = 'NO_LINE_ITEMS';
      else if (subtotal + tax <= 0) reason = 'ZERO_TOTAL';
      else if (!completed) reason = 'NO_COMPLETED_DATE';
      else if (settings.autoInvoicingStartDate && completed < settings.autoInvoicingStartDate) reason = 'BEFORE_START_DATE';
      else if (billTo?.billingMethod === 'CONSOLIDATED' && completed.slice(0, 7) >= today.slice(0, 7)) reason = 'WAITING_FOR_MONTH_END';
      if (reason) {
        skipped.push({
          ...ref, customerId: job.customerId, customerName: owner?.name ?? '', billingCustomerId: billTo?.id ?? '',
          billingCustomerName: billTo?.name ?? '', reason, message: reason,
        });
        continue;
      }
      const consolidated = billTo?.billingMethod === 'CONSOLIDATED';
      const month = completed!.slice(0, 7);
      const key = consolidated ? `month:${billTo!.id}:${job.customerId}:${month}` : `job:${job.id}`;
      const group = groups.get(key) ?? {
        key,
        billingMethod: consolidated ? 'CONSOLIDATED' : 'PER_VISIT',
        billingCustomerId: billTo?.id ?? '',
        billingCustomerName: billTo?.name ?? '',
        customerId: job.customerId,
        customerName: owner?.name ?? '',
        periodStartDate: consolidated ? `${month}-01` : null,
        periodEndDate: consolidated ? new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10) : null,
        jobs: [],
        subtotal: '0.00',
        tax: '0.00',
        total: '0.00',
      } as types.AutoInvoicePreviewGroup;
      group.jobs.push(ref);
      group.subtotal = money(Number(group.subtotal) + subtotal);
      group.tax = money(Number(group.tax) + tax);
      group.total = money(Number(group.subtotal) + Number(group.tax));
      groups.set(key, group);
    }
    return { groups: [...groups.values()], skipped };
  }

  async previewAutoInvoicing(): Promise<types.AutoInvoicePreview> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const settings = this.mockCompanySettings(tenantId);
    const { groups, skipped } = this.mockAutoInvoiceGroups(tenantId);
    return {
      asOfDate: new Date().toISOString().slice(0, 10),
      mode: settings.autoInvoicingMode,
      startDate: settings.autoInvoicingStartDate,
      autoSend: settings.autoInvoicingAutoSend,
      nightlyHour: settings.autoInvoicingNightlyHour,
      groups,
      skipped,
      total: groups.reduce((sum, g) => sum + Number(g.total), 0).toFixed(2),
    };
  }

  async runAutoInvoicing(data: types.RunAutoInvoicingRequest): Promise<types.AutoInvoiceRunResult> {
    const actor = this.requireOfficeUser();
    const { tenantId } = actor;
    if (data.groups !== undefined && (!Array.isArray(data.groups) || data.groups.length === 0)) {
      throw mockApiError(400, 'groups must be a non-empty array of group keys', 'VALIDATION_ERROR');
    }
    const { groups } = this.mockAutoInvoiceGroups(tenantId);
    const runDate = new Date().toISOString().slice(0, 10);
    const results: types.AutoInvoiceGroupResult[] = [];
    const base = { invoiceId: null, invoiceNumber: null, invoiceStatus: null, total: null, reason: null, message: null, emailStatus: null, sendError: null };
    const targets = data.groups ?? groups.map((g) => g.key);
    for (const key of targets) {
      const group = groups.find((g) => g.key === key);
      if (!group) {
        results.push({ ...base, key, billingCustomerId: '', billingCustomerName: '', jobIds: [], status: 'SKIPPED', reason: 'NOT_ELIGIBLE', message: 'No eligible jobs for this group now' });
        continue;
      }
      const invoice = await this.createInvoice({
        billingCustomerId: group.billingCustomerId,
        jobIds: group.jobs.map((j) => j.jobId),
        invoiceDate: runDate,
        billingMethod: group.billingMethod,
        periodStartDate: group.periodStartDate ?? undefined,
        periodEndDate: group.periodEndDate ?? undefined,
        discountIds: [],
      });
      results.push({
        ...base, key, billingCustomerId: group.billingCustomerId, billingCustomerName: group.billingCustomerName,
        jobIds: group.jobs.map((j) => j.jobId), status: 'CREATED', invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber,
        invoiceStatus: invoice.status, total: invoice.total,
      });
    }
    const createdCount = results.filter((r) => r.status === 'CREATED').length;
    const skippedCount = results.length - createdCount;
    const runId = generateId('air');
    this.autoInvoiceRuns.unshift({
      tenantId, id: runId, trigger: 'MANUAL', runDate, startedAt: new Date().toISOString(), finishedAt: new Date().toISOString(),
      groupCount: results.length, createdCount, skippedCount, failedCount: 0, createdBy: actor.id, error: null,
      items: results.flatMap((r) => r.jobIds.map((jobId) => ({
        jobId, status: r.status, invoiceId: r.invoiceId, invoiceNumber: r.invoiceNumber, reason: r.reason, message: r.message,
      }))),
    });
    return { runId, trigger: 'MANUAL', runDate, results, createdCount, skippedCount, failedCount: 0 };
  }

  async listAutoInvoicingRuns(limit = 20): Promise<types.AutoInvoiceRunSummary[]> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    return this.autoInvoiceRuns
      .filter((r) => r.tenantId === tenantId)
      .slice(0, limit)
      .map(({ tenantId: _tenantId, ...run }) => run);
  }

  // ============ Automated dunning (tracker item 36) ============
  // A small in-memory version of backend DunningService: one reminder per
  // customer at the highest step reached, once per invoice per step, held
  // invoices and exempt customers left out. Enough to exercise the pages.

  private mockDunningStepsFor(tenantId: string): types.DunningStep[] {
    let steps = this.dunningSteps.get(tenantId);
    if (!steps) {
      const labels = ['Friendly reminder', 'Past due notice', 'Second notice', 'Seriously past due', 'Urgent', 'Final notice'];
      steps = [7, 21, 30, 60, 90, 120].map((daysOverdue, index) => ({
        id: generateId('dstep'),
        position: index + 1,
        daysOverdue,
        eventKey: `DUNNING_${index + 1}`,
        templateLabel: `Reminder ${index + 1}: ${labels[index]}`,
        templateActive: false,
        isActive: true,
      }));
      this.dunningSteps.set(tenantId, steps);
    }
    return steps;
  }

  private mockDunningStepsResponse(tenantId: string): types.DunningSteps {
    const steps = this.mockDunningStepsFor(tenantId).map((s) => ({ ...s }));
    const used = new Set(steps.map((s) => s.eventKey));
    return {
      steps,
      maxSteps: 6,
      availableEventKeys: [1, 2, 3, 4, 5, 6].map((n) => `DUNNING_${n}`).filter((k) => !used.has(k)),
    };
  }

  private mockDunningRows(tenantId: string): types.DunningPreviewRow[] {
    const settings = this.mockCompanySettings(tenantId);
    const today = new Date().toISOString().slice(0, 10);
    const active = this.mockDunningStepsFor(tenantId).filter((s) => s.isActive).sort((a, b) => a.daysOverdue - b.daysOverdue);
    const toCents = (value: string | number) => Math.round(Number(value) * 100);
    const applied = (invoiceId: string) => this.payments
      .filter((p) => p.tenantId === tenantId && p.status === 'ACTIVE')
      .flatMap((p) => p.applications)
      .filter((a) => a.invoiceId === invoiceId)
      .reduce((sum, a) => sum + toCents(a.amountApplied), 0);
    const byCustomer = new Map<string, types.DunningPreviewRow>();
    for (const invoice of filterByTenant(this.invoices, tenantId)) {
      if (![types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status)) continue;
      const due = invoice.dueDate.slice(0, 10);
      const days = Math.round((Date.parse(today) - Date.parse(due)) / 86400000);
      const balance = toCents(invoice.total) - applied(invoice.id);
      if (days < 1 || balance <= 0) continue;
      const reached = active.filter((s) => s.daysOverdue <= days);
      const highest = reached[reached.length - 1] ?? null;
      const excludedReason = invoice.dunningHold
        ? 'ON_HOLD'
        : settings.dunningStartDate && due < settings.dunningStartDate ? 'BEFORE_START_DATE' : null;
      const customer = this.customers.find((c) => c.id === invoice.billingCustomerId);
      const row = byCustomer.get(invoice.billingCustomerId) ?? {
        customerId: invoice.billingCustomerId,
        customerName: customer?.name ?? invoice.billingCustomer?.name ?? '',
        willSend: false, action: 'SKIP', reason: null, message: null, step: null,
        totalOverdue: '0.00', overdueCount: 0, oldestDaysOverdue: 0, lastRemindedDate: null, nextReminderDate: null, invoices: [],
      } as types.DunningPreviewRow;
      const noticed = highest ? this.dunningNotices.has(`${invoice.id}:${highest.id}`) : true;
      row.invoices.push({
        invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber, invoiceType: invoice.invoiceType ?? 'STANDARD', dueDate: due,
        daysOverdue: days, balance: (balance / 100).toFixed(2), stepReached: highest?.position ?? null,
        listed: excludedReason === null, excludedReason, newNotice: excludedReason === null && !!highest && !noticed,
      });
      row.oldestDaysOverdue = Math.max(row.oldestDaysOverdue, days);
      if (excludedReason === null) {
        row.totalOverdue = ((toCents(row.totalOverdue) + balance) / 100).toFixed(2);
        row.overdueCount += 1;
        if (highest && (!row.step || highest.daysOverdue > row.step.daysOverdue)) {
          row.step = { id: highest.id, position: highest.position, daysOverdue: highest.daysOverdue, eventKey: highest.eventKey };
        }
      }
      byCustomer.set(invoice.billingCustomerId, row);
    }
    for (const row of byCustomer.values()) {
      const customer = this.customers.find((c) => c.id === row.customerId);
      const reason = customer?.dunningExempt ? 'CUSTOMER_EXEMPT'
        : row.overdueCount === 0 ? (row.invoices.some((i) => i.excludedReason === 'ON_HOLD') ? 'ON_HOLD' : 'BEFORE_START_DATE')
          : !row.step ? 'NO_STEP_REACHED'
            : !row.invoices.some((i) => i.newNotice) ? 'ALREADY_REMINDED'
              : !customer?.email ? 'NO_EMAIL'
                : null;
      row.action = reason ? 'SKIP' : 'CONTACT';
      row.reason = reason;
      row.willSend = !reason && !!settings.notificationsEnabled;
      if (!reason && !settings.notificationsEnabled) row.reason = 'NOTIFICATIONS_DISABLED';
    }
    return [...byCustomer.values()].sort((a, b) => a.customerName.localeCompare(b.customerName));
  }

  async previewDunning(): Promise<types.DunningPreview> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const settings = this.mockCompanySettings(tenantId);
    const rows = this.mockDunningRows(tenantId);
    const customers = rows.filter((r) => r.action === 'CONTACT');
    return {
      asOfDate: new Date().toISOString().slice(0, 10),
      mode: settings.dunningMode ?? 'OFF',
      startDate: settings.dunningStartDate ?? null,
      hour: settings.dunningHour ?? 9,
      minDaysBetween: settings.dunningMinDaysBetween ?? 7,
      customers,
      skipped: rows.filter((r) => r.action === 'SKIP'),
      totalOverdue: (customers.reduce((sum, r) => sum + Math.round(Number(r.totalOverdue) * 100), 0) / 100).toFixed(2),
    };
  }

  async runDunning(data: types.RunDunningRequest): Promise<types.DunningRunResult> {
    const actor = this.requireOfficeUser();
    const { tenantId } = actor;
    if (data.customerIds !== undefined && (!Array.isArray(data.customerIds) || data.customerIds.length === 0)) {
      throw mockApiError(400, 'customerIds must be a non-empty array of customer ids', 'VALIDATION_ERROR');
    }
    const rows = this.mockDunningRows(tenantId);
    const targets = data.customerIds ?? rows.filter((r) => r.action === 'CONTACT').map((r) => r.customerId);
    const runId = generateId('dun');
    const runDate = new Date().toISOString().slice(0, 10);
    const results: types.DunningCustomerResult[] = targets.map((customerId) => {
      const row = rows.find((r) => r.customerId === customerId);
      const base = {
        customerId, customerName: row?.customerName ?? '', stepPosition: row?.step?.position ?? null, eventKey: row?.step?.eventKey ?? null,
        invoiceIds: (row?.invoices ?? []).filter((i) => i.listed).map((i) => i.invoiceId),
        invoiceNumbers: (row?.invoices ?? []).filter((i) => i.listed).map((i) => i.invoiceNumber),
        totalOverdue: row?.totalOverdue ?? null, outboxId: null, reason: null, message: null,
      };
      if (!row || row.action !== 'CONTACT') {
        return { ...base, status: 'SKIPPED' as const, reason: row?.reason ?? 'NOTHING_OVERDUE' };
      }
      for (const invoice of row.invoices.filter((i) => i.newNotice)) {
        const step = this.mockDunningStepsFor(tenantId).find((s) => s.position === invoice.stepReached);
        if (step) this.dunningNotices.add(`${invoice.invoiceId}:${step.id}`);
      }
      return row.willSend
        ? { ...base, status: 'QUEUED' as const, outboxId: generateId('outbox') }
        : { ...base, status: 'SKIPPED' as const, reason: row.reason };
    });
    const queuedCount = results.filter((r) => r.status === 'QUEUED').length;
    const skippedCount = results.filter((r) => r.status === 'SKIPPED').length;
    this.dunningRuns.unshift({
      tenantId, id: runId, trigger: 'MANUAL', runDate, startedAt: new Date().toISOString(), finishedAt: new Date().toISOString(),
      customerCount: results.length, queuedCount, skippedCount, failedCount: 0, createdBy: actor.id, error: null, results,
    });
    return { runId, trigger: 'MANUAL', runDate, results, queuedCount, skippedCount, failedCount: 0 };
  }

  // ---- Dispatch (tracker item 40): a small in-memory version of the backend ----

  private mockBoardJob(job: types.Job, conflicts: types.ScheduleConflict[] = []): types.BoardJob {
    const suggested = job.suggestedDurationMinutes ?? 60;
    return {
      id: job.id,
      routeId: job.routeId ?? null,
      status: job.status,
      customer: job.customer,
      property: { id: job.property.id, address: job.property.address, city: job.property.city, state: job.property.state },
      assignedTo: job.assignedTo,
      assignedToEmail: job.assignedToUser?.email ?? null,
      scheduledDate: job.scheduledDate,
      scheduledStartTime: job.scheduledStartTime ?? null,
      durationMinutes: job.estimatedDurationMinutes ?? suggested,
      estimatedDurationMinutes: job.estimatedDurationMinutes ?? null,
      suggestedDurationMinutes: suggested,
      durationSource: job.durationSource ?? 'AUTO',
      arrivalWindowMinutes: job.arrivalWindowMinutes ?? null,
      notes: job.notes ?? null,
      lineSummary: job.lineItems.map((l) => `${l.item.name} x ${Number(l.quantity)}`).join(', '),
      lineCount: job.lineItems.length,
      schedulable: ![types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(job.status),
      updatedAt: job.updatedAt,
      conflicts,
      location: this.mockBoardLocation(job.property.id),
    };
  }

  /** A property's stored coordinates as the board shows them (tracker item 41, phase 4). */
  private mockBoardLocation(propertyId: string): types.BoardLocation | null {
    const property = this.properties.find((p) => p.id === propertyId);
    if (!property || property.latitude == null || property.longitude == null) return null;
    return { lat: Number(property.latitude), lng: Number(property.longitude), precision: property.geocodePrecision ?? null };
  }

  /**
   * Mock board travel legs (tracker item 41, phase 4), the backend's model:
   * straight-line km x road factor / speed, rounded up to 5 minutes; a leg
   * with an end without coordinates costs the unknown-travel minutes. The
   * mock company address has no coordinates, so each day's first leg is
   * unknown.
   */
  private mockBoardTravel(jobs: types.BoardJob[], settings: types.CompanySettings): types.BoardTravelDay[] {
    const speed = settings.routingSpeedKph ?? 48;
    const factor = Number(settings.routingCircuityFactor ?? '1.30');
    const unknownMinutes = settings.routingUnknownTravelMinutes ?? 15;
    const rad = (d: number) => (d * Math.PI) / 180;
    const km = (a: types.BoardLocation, b: types.BoardLocation) => {
      const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
      return 2 * 6371.0088 * Math.asin(Math.min(1, Math.sqrt(h)));
    };
    const days = new Map<string, types.BoardJob[]>();
    for (const job of jobs) {
      if (!job.assignedTo || !job.scheduledDate || !job.scheduledStartTime || job.status === types.JobStatus.CANCELLED) continue;
      const key = `${job.scheduledDate}|${job.assignedTo}`;
      days.set(key, [...(days.get(key) ?? []), job]);
    }
    return [...days.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, list]) => {
      const ordered = [...list].sort((a, b) => a.scheduledStartTime!.localeCompare(b.scheduledStartTime!) || a.id.localeCompare(b.id));
      return {
        userId: ordered[0].assignedTo!,
        date: ordered[0].scheduledDate!,
        legs: ordered.map((job, i) => {
          const prev = i === 0 ? null : ordered[i - 1];
          const from = prev?.location ?? null;
          const to = job.location ?? null;
          if (!prev || !from || !to) return { fromJobId: prev?.id ?? null, toJobId: job.id, minutes: unknownMinutes, km: null, unknown: true };
          const distance = km(from, to);
          const raw = Math.round(((distance * factor) / speed) * 60 * 1e6) / 1e6;
          return { fromJobId: prev.id, toJobId: job.id, minutes: distance <= 0 ? 0 : Math.ceil(Math.ceil(raw) / 5) * 5, km: Math.round(distance * 1000) / 1000, unknown: false };
        }),
      };
    });
  }

  private mockOverlaps(
    tenantId: string,
    jobId: string,
    change: Required<Pick<types.ScheduleChange, 'assignedTo' | 'scheduledDate' | 'scheduledStartTime'>> & { duration: number },
    /** Planned placements that count as jobs (bulk preview) */
    extra: MockPlacement[] = []
  ): types.ScheduleConflict[] {
    if (!change.assignedTo || !change.scheduledDate || !change.scheduledStartTime) return [];
    const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const start = toMin(change.scheduledStartTime);
    const end = start + change.duration;
    const placed = new Set(extra.map((e) => e.id));
    const clashing = [
      ...this.jobs.filter((j) => j.tenantId === tenantId && !placed.has(j.id) && j.scheduledStartTime
        && ![types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(j.status))
        .map((j) => ({ id: j.id, assignedTo: j.assignedTo, scheduledDate: j.scheduledDate, scheduledStartTime: j.scheduledStartTime!, duration: j.estimatedDurationMinutes ?? 60 })),
      ...extra,
    ].filter((j) => j.id !== jobId && j.assignedTo === change.assignedTo && j.scheduledDate === change.scheduledDate
      && toMin(j.scheduledStartTime) < end && start < toMin(j.scheduledStartTime) + j.duration);
    return clashing.length ? [{ type: 'OVERLAP', message: 'Overlaps another job', jobIds: clashing.map((j) => j.id) }] : [];
  }

  async getDispatchBoard(requested: string | undefined, view: types.BoardView): Promise<types.DispatchBoard> {
    await delay(150);
    const date = requested || new Date().toISOString().slice(0, 10);
    const { tenantId } = this.requireOfficeUser();
    const settings = await this.getCompanySettings();
    const day = new Date(`${date}T00:00:00Z`);
    const weekday = day.getUTCDay();
    const from = view === 'week' ? new Date(day.getTime() - ((weekday + 6) % 7) * 86400000) : day;
    const to = view === 'week' ? new Date(from.getTime() + 6 * 86400000) : day;
    const fromKey = from.toISOString().slice(0, 10);
    const toKey = to.toISOString().slice(0, 10);
    const company: types.WorkHours = {
      workDays: settings.workDays ?? [1, 2, 3, 4, 5], startTime: settings.workdayStart ?? '08:00', endTime: settings.workdayEnd ?? '17:00',
    };
    const jobs = this.jobs
      .filter((j) => j.tenantId === tenantId && j.scheduledDate && j.scheduledDate >= fromKey && j.scheduledDate <= toKey)
      .map((j) => this.mockBoardJob(j, this.mockOverlaps(tenantId, j.id, {
        assignedTo: j.assignedTo, scheduledDate: j.scheduledDate, scheduledStartTime: j.scheduledStartTime ?? null, duration: j.estimatedDurationMinutes ?? 60,
      })));
    const now = new Date();
    return {
      view, date, from: fromKey, to: toKey,
      today: now.toISOString().slice(0, 10), nowMinutes: now.getHours() * 60 + now.getMinutes(),
      timezone: settings.timezone ?? 'America/New_York',
      companyWorkHours: company,
      technicians: this.users
        .filter((u) => u.tenantId === tenantId && u.isActive && [types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER, types.UserRole.TECHNICIAN].includes(u.role))
        .map((u) => {
          const override = this.workHourOverrides.get(`${tenantId}:${u.id}`);
          return {
            id: u.id, email: u.email, role: u.role,
            workHours: override ? { ...override, source: 'TECHNICIAN' as const } : { ...company, source: 'COMPANY' as const },
            usualRouteIds: [...(this.technicianRouteIds.get(`${tenantId}:${u.id}`) ?? [])],
            dayRouteIds: this.routeAssignments.filter((a) => a.tenantId === tenantId && a.userId === u.id && a.date === date).map((a) => a.routeId),
          };
        }),
      routes: this.dispatchRoutes.filter((r) => r.tenantId === tenantId).map(({ tenantId: _tenantId, ...r }) => ({ ...r })),
      routeAssignments: this.routeAssignments
        .filter((a) => a.tenantId === tenantId && a.date >= fromKey && a.date <= toKey)
        .map(({ tenantId: _tenantId, ...a }) => ({ ...a })),
      jobs,
      unassigned: jobs.filter((j) => !j.assignedTo),
      timeOff: this.timeOffEntries
        .filter((t) => t.tenantId === tenantId && t.startDate <= toKey && t.endDate >= fromKey)
        .map(({ tenantId: _tenantId, ...entry }) => entry),
      truncated: false,
      cap: 500,
      travel: this.mockBoardTravel(jobs, settings),
      distanceUnit: settings.distanceUnit ?? 'MI',
      depotKnown: false,
    };
  }

  async getDispatchBacklog(query: { search?: string; limit?: number; after?: string } = {}): Promise<types.DispatchBacklog> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const search = query.search?.trim().toLowerCase();
    const rows = this.jobs
      .filter((j) => j.tenantId === tenantId && [types.JobStatus.DRAFT, types.JobStatus.WORK_ORDER_GENERATED, types.JobStatus.SCHEDULED].includes(j.status)
        && (!j.scheduledDate || !j.assignedTo)
        && (!search || j.customer.name.toLowerCase().includes(search) || j.property.address.toLowerCase().includes(search)))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const start = query.after ? rows.findIndex((j) => j.id === query.after) + 1 : 0;
    const limit = query.limit ?? 25;
    const page = rows.slice(start, start + limit);
    return { data: page.map((j) => this.mockBoardJob(j)), nextCursor: start + limit < rows.length ? page[page.length - 1].id : null };
  }

  async checkJobSchedule(jobId: string, change: types.ScheduleChange): Promise<types.ScheduleConflict[]> {
    await delay(50);
    const { tenantId } = this.requireOfficeUser();
    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    return this.mockOverlaps(tenantId, jobId, {
      assignedTo: change.assignedTo !== undefined ? change.assignedTo : job.assignedTo,
      scheduledDate: change.scheduledDate !== undefined ? change.scheduledDate : job.scheduledDate,
      scheduledStartTime: change.scheduledStartTime !== undefined ? change.scheduledStartTime : job.scheduledStartTime ?? null,
      duration: typeof change.estimatedDurationMinutes === 'number' ? change.estimatedDurationMinutes : job.estimatedDurationMinutes ?? 60,
    });
  }

  async scheduleJob(jobId: string, data: types.ScheduleJobRequest): Promise<types.ScheduleJobResult> {
    const { tenantId } = this.requireOfficeUser();
    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    if (job.updatedAt !== data.expectedUpdatedAt) throw mockApiError(409, 'Someone else changed this job; refresh and try again', 'JOB_CHANGED');
    if ([types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(job.status)) {
      throw mockApiError(400, `A ${job.status} job cannot be rescheduled`, 'JOB_NOT_SCHEDULABLE');
    }
    const { expectedUpdatedAt: _expected, requireNoConflicts, origin: _origin, ...change } = data;
    const conflicts = await this.checkJobSchedule(jobId, change);
    if (requireNoConflicts && conflicts.length > 0) {
      throw mockApiError(409, 'This suggestion is no longer available: the slot now has a conflict. Refresh the suggestions.', 'SUGGESTION_STALE');
    }
    const update: types.UpdateJobRequest = { ...change };
    if (change.scheduledDate === null) update.scheduledStartTime = null;
    if (typeof change.estimatedDurationMinutes === 'number') update.durationSource = 'MANUAL';
    const updated = await this.updateJob(jobId, update);
    return { job: updated, conflicts };
  }

  /**
   * Mock route suggestions (tracker item 41): no coordinates offline, so no
   * travel; the earliest free 15-minute start per technician and working day,
   * ranked by day (x10) and load, with the backend's response shape.
   */
  async getJobSuggestions(request: types.SuggestionRequest): Promise<types.SuggestionResult> {
    await delay(100);
    return this.mockSuggestions(request);
  }

  /**
   * Mock bulk preview (tracker item 41, phase 4): the mock suggestions one
   * job at a time in backlog order (created, then id), each placement
   * counting as a booked job for the next ones. Same response shape as the
   * backend; writes nothing.
   */
  async getBulkSuggestions(request: types.BulkSuggestionRequest): Promise<types.BulkSuggestionResult> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const ids = [...new Set(request.jobIds ?? [])];
    if (ids.length === 0 || ids.length > 25) throw mockApiError(400, 'jobIds: Must list 1 to 25 jobs', 'VALIDATION_ERROR');
    const jobs = ids.map((id) => {
      const job = this.jobs.find((j) => j.id === id && j.tenantId === tenantId);
      if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
      return job;
    }).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    const today = this.mockCompanyToday(tenantId);
    const from = request.from ?? today;
    const to = request.to ?? new Date(new Date(`${from}T00:00:00Z`).getTime() + 6 * 86400000).toISOString().slice(0, 10);
    const placed: MockPlacement[] = [];
    const placements: types.BulkPlacement[] = [];
    for (const job of jobs) {
      const base = {
        jobId: job.id, customerName: job.customer.name, address: job.property.address,
        durationMinutes: job.estimatedDurationMinutes ?? job.suggestedDurationMinutes ?? 60, expectedUpdatedAt: job.updatedAt,
      };
      if ([types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(job.status)) {
        placements.push({ ...base, candidate: null, reason: 'NOT_SCHEDULABLE', excluded: null });
        continue;
      }
      if (job.scheduledDate && !request.anyDate && (job.scheduledDate < from || job.scheduledDate > to)) {
        placements.push({ ...base, candidate: null, reason: 'OUTSIDE_HORIZON', excluded: null });
        continue;
      }
      const result = await this.mockSuggestions({ jobId: job.id, from, to, anyTechnician: request.anyTechnician, anyDate: request.anyDate, limit: 1 }, placed);
      const candidate = result.candidates[0] ?? null;
      if (candidate) {
        candidate.reasons = candidate.reasons.filter((r) => r.code !== 'EARLIEST_AVAILABLE');
        placed.push({ id: job.id, assignedTo: candidate.technicianId, scheduledDate: candidate.date, scheduledStartTime: candidate.startTime, duration: base.durationMinutes });
      }
      placements.push({ ...base, candidate, reason: candidate ? null : 'NO_SLOT', excluded: candidate ? null : result.excluded });
    }
    const count = placements.filter((p) => p.candidate).length;
    return { from, to, placements, placed: count, unplaced: placements.length - count, truncated: false };
  }

  private async mockSuggestions(request: types.SuggestionRequest, extra: MockPlacement[] = []): Promise<types.SuggestionResult> {
    const { tenantId } = this.requireOfficeUser();
    const job = this.jobs.find((j) => j.id === request.jobId && j.tenantId === tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    if ([types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(job.status)) {
      throw mockApiError(400, `A ${job.status} job cannot be rescheduled`, 'JOB_NOT_SCHEDULABLE');
    }
    const today = this.mockCompanyToday(tenantId);
    const addDays = (date: string, days: number) => new Date(new Date(`${date}T00:00:00Z`).getTime() + days * 86400000).toISOString().slice(0, 10);
    let dates: string[];
    if (job.scheduledDate && !request.anyDate) dates = [job.scheduledDate];
    else {
      const from = request.from ?? today;
      if (from < today) throw mockApiError(400, `from: Cannot be before today (${today})`, 'VALIDATION_ERROR');
      const to = request.to ?? addDays(from, 6);
      dates = [];
      for (let d = from; d <= to && dates.length < 14; d = addDays(d, 1)) dates.push(d);
    }
    const settings = await this.getCompanySettings();
    const company: types.WorkHours = { workDays: settings.workDays ?? [1, 2, 3, 4, 5], startTime: settings.workdayStart ?? '08:00', endTime: settings.workdayEnd ?? '17:00' };
    const staff = this.users.filter((u) => u.tenantId === tenantId && u.isActive);
    const technicians = job.assignedTo && !request.anyTechnician
      ? staff.filter((u) => u.id === job.assignedTo)
      : request.technicianIds?.length
        ? staff.filter((u) => request.technicianIds!.includes(u.id))
        : staff.filter((u) => u.role === types.UserRole.TECHNICIAN);
    const duration = job.estimatedDurationMinutes ?? job.suggestedDurationMinutes ?? 60;
    const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const toTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const now = new Date();
    const excluded: types.SuggestionExcluded = { notWorking: 0, timeOff: 0, pastDate: 0, noGapFits: 0, unavailable: job.assignedTo && !request.anyTechnician && technicians.length === 0 ? dates.length : 0, conflicts: 0 };
    const found: types.SuggestionCandidate[] = [];
    for (const tech of [...technicians].sort((a, b) => a.email.localeCompare(b.email))) {
      const hours = this.workHourOverrides.get(`${tenantId}:${tech.id}`) ?? company;
      dates.forEach((date, index) => {
        if (date < today) { excluded.pastDate += 1; return; }
        if (!hours.workDays.includes(new Date(`${date}T00:00:00Z`).getUTCDay())) { excluded.notWorking += 1; return; }
        if (this.timeOffEntries.some((t) => t.tenantId === tenantId && t.userId === tech.id && t.startDate <= date && t.endDate >= date && !t.startTime)) { excluded.timeOff += 1; return; }
        const lower = Math.max(toMin(hours.startTime), date === today ? now.getHours() * 60 + now.getMinutes() : 0);
        let start = Math.ceil(lower / 15) * 15;
        while (start + duration <= toMin(hours.endTime)
          && this.mockOverlaps(tenantId, job.id, { assignedTo: tech.id, scheduledDate: date, scheduledStartTime: toTime(start), duration }, extra).length > 0) start += 15;
        if (start + duration > toMin(hours.endTime)) { excluded.noGapFits += 1; return; }
        const booked = this.jobs.filter((j) => j.tenantId === tenantId && j.id !== job.id && j.assignedTo === tech.id && j.scheduledDate === date)
          .reduce((sum, j) => sum + (j.estimatedDurationMinutes ?? 60), 0)
          + extra.filter((e) => e.id !== job.id && e.assignedTo === tech.id && e.scheduledDate === date).reduce((sum, e) => sum + e.duration, 0);
        const available = toMin(hours.endTime) - toMin(hours.startTime);
        const dayLoad = Math.round((booked * 30) / available);
        found.push({
          technicianId: tech.id, technicianEmail: tech.email, date, startTime: toTime(start), endTime: toTime(start + duration),
          score: dayLoad + index * 10,
          scoreBreakdown: { addedTravel: 0, route: 0, dayLoad, daysOut: index * 10, unknownLocation: 0 },
          travelInMinutes: 0, travelOutMinutes: null, addedTravelMinutes: 0, distanceUnit: settings.distanceUnit ?? 'MI',
          reasons: [
            booked / available < 0.5 ? { code: 'LIGHT_DAY', params: { bookedMinutes: booked, availableMinutes: available } } : { code: 'BUSY_DAY', params: { bookedMinutes: booked, availableMinutes: available } },
            { code: 'LOCATION_UNKNOWN', params: { end: 'JOB' } },
          ],
        });
      });
    }
    found.sort((a, b) => a.score - b.score || a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime) || a.technicianEmail.localeCompare(b.technicianEmail));
    const candidates = found.slice(0, Math.min(10, Math.max(1, request.limit ?? 5)));
    const earliest = [...candidates].sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))[0];
    if (earliest) earliest.reasons.splice(1, 0, { code: 'EARLIEST_AVAILABLE', params: {} });
    return {
      job: { id: job.id, durationMinutes: duration, location: null },
      from: dates[0], to: dates[dates.length - 1], candidates, excluded, truncated: false,
    };
  }

  /**
   * The technician's day for the mock optimizer: hours, whether it is a day
   * off, the timed / untimed jobs, today and now.
   */
  private async mockTechnicianDay(tenantId: string, userId: string, date: string) {
    const user = this.users.find((u) => u.id === userId && u.tenantId === tenantId);
    if (!user) throw mockApiError(404, 'User not found', 'NOT_FOUND');
    if (![types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER, types.UserRole.TECHNICIAN].includes(user.role)) {
      throw mockApiError(400, 'Only administrators, office managers and technicians have a day to optimize', 'USER_NOT_SCHEDULABLE');
    }
    const today = this.mockCompanyToday(tenantId);
    if (date < today) throw mockApiError(400, `date: Cannot be before today (${today})`, 'VALIDATION_ERROR');
    const settings = await this.getCompanySettings();
    const company: types.WorkHours = { workDays: settings.workDays ?? [1, 2, 3, 4, 5], startTime: settings.workdayStart ?? '08:00', endTime: settings.workdayEnd ?? '17:00' };
    const hours = this.workHourOverrides.get(`${tenantId}:${user.id}`) ?? company;
    const dayOff = !hours.workDays.includes(new Date(`${date}T00:00:00Z`).getUTCDay())
      || this.timeOffEntries.some((t) => t.tenantId === tenantId && t.userId === user.id && t.startDate <= date && t.endDate >= date && !t.startTime);
    const all = this.jobs.filter((j) => j.tenantId === tenantId && j.assignedTo === user.id && j.scheduledDate === date && j.status !== types.JobStatus.CANCELLED);
    const timed = all.filter((j) => j.scheduledStartTime)
      .sort((a, b) => a.scheduledStartTime!.localeCompare(b.scheduledStartTime!) || a.id.localeCompare(b.id));
    const now = new Date();
    return { user, hours, dayOff, all, timed, today, nowMinutes: date === today ? now.getHours() * 60 + now.getMinutes() : 0, distanceUnit: settings.distanceUnit ?? 'MI' };
  }

  /**
   * Mock day optimizer (tracker item 41, phase 3). No coordinates offline:
   * a leg costs 0 between jobs at the same property and 15 minutes otherwise
   * (the backend's unknown-travel default), so grouping visits to one
   * property saves travel. Movable jobs are grouped by property (current
   * order otherwise), packed from the work start (or now, today) around the
   * pinned ones; arrival windows are respected; time off is ignored. Same
   * response shape and outcomes as the backend.
   */
  async optimizeDay(request: types.OptimizeDayRequest): Promise<types.OptimizeDayResult> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const day = await this.mockTechnicianDay(tenantId, request.userId, request.date);
    const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const toTime = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const duration = (j: types.Job) => j.estimatedDurationMinutes ?? j.suggestedDurationMinutes ?? 60;
    const leg = (from: types.Job | null, to: types.Job) => (from && from.propertyId === to.propertyId ? 0 : 15);
    const pinned = new Set(request.pinnedJobIds ?? []);
    const pinReason = (j: types.Job): types.OptimizerPinReason | null => {
      if (j.status === types.JobStatus.IN_PROGRESS) return 'IN_PROGRESS';
      if (j.status === types.JobStatus.COMPLETED || j.status === types.JobStatus.INVOICED || j.invoiceId) return 'DONE';
      if (pinned.has(j.id)) return 'KEPT';
      if (request.date === day.today && toMin(j.scheduledStartTime!) < day.nowMinutes) return 'PASSED';
      return null;
    };
    const pins = day.timed.filter((j) => pinReason(j));
    const movable = day.timed.filter((j) => !pinReason(j));
    const travelOf = (order: types.Job[]) => order.reduce((sum, j, i) => sum + leg(i === 0 ? null : order[i - 1], j), 0);
    const currentTravel = travelOf(day.timed);
    const workStart = toMin(day.hours.startTime);
    const workEnd = toMin(day.hours.endTime);
    const lower = request.date === day.today ? Math.max(workStart, day.nowMinutes) : workStart;

    // Group by property, keeping the first appearance order.
    const groups = new Map<string, types.Job[]>();
    for (const j of movable) groups.set(j.propertyId, [...(groups.get(j.propertyId) ?? []), j]);
    const order = [...groups.values()].flat();

    // Pack, interleaving pins (a pin goes first when the next job would make it late).
    let plan: { job: types.Job; start: number }[] | null = [];
    let t = lower;
    let prev: types.Job | null = null;
    const pinQueue = [...pins];
    const place = (job: types.Job, start: number) => { plan!.push({ job, start }); t = Math.max(t, start + duration(job)); prev = job; };
    const startFor = (job: types.Job, from: number, previous: types.Job | null) =>
      Math.ceil(Math.max(from + leg(previous, job), job.arrivalWindowMinutes ? toMin(job.scheduledStartTime!) : 0, lower) / 5) * 5;
    for (const job of order) {
      while (pinQueue.length && startFor(job, t, prev) + duration(job) + leg(job, pinQueue[0]) > toMin(pinQueue[0].scheduledStartTime!)) {
        const pin = pinQueue.shift()!;
        place(pin, toMin(pin.scheduledStartTime!));
      }
      const start = startFor(job, t, prev);
      const old = toMin(job.scheduledStartTime!);
      if (start + duration(job) > workEnd || (job.arrivalWindowMinutes && start > old + job.arrivalWindowMinutes)) { plan = null; break; }
      place(job, start);
    }
    if (plan) for (const pin of pinQueue) place(pin, toMin(pin.scheduledStartTime!));

    const proposedTravel = plan ? travelOf(plan.map((p) => p.job)) : currentTravel;
    let outcome: types.OptimizerOutcome;
    if (day.dayOff) outcome = 'DAY_OFF';
    else if (movable.length === 0) outcome = 'NOTHING_TO_MOVE';
    else if (!plan) outcome = 'NO_FEASIBLE_ORDER';
    else outcome = proposedTravel < currentTravel ? 'IMPROVED' : 'ALREADY_BEST';
    const improved = outcome === 'IMPROVED';
    const final = improved ? plan! : day.timed.map((job) => ({ job, start: toMin(job.scheduledStartTime!) }));
    const oldPosition = new Map(day.timed.map((j, i) => [j.id, i + 1]));
    const jobs: types.OptimizeDayJob[] = final.map(({ job, start }, i) => {
      const old = toMin(job.scheduledStartTime!);
      const travel = leg(i === 0 ? null : final[i - 1].job, job);
      return {
        jobId: job.id, customerName: job.customer.name, address: job.property.address, status: job.status,
        pinned: !!pinReason(job), pinReason: pinReason(job),
        oldPosition: oldPosition.get(job.id)!, newPosition: i + 1,
        oldStartTime: job.scheduledStartTime!, newStartTime: toTime(start), newEndTime: toTime(start + duration(job)),
        durationMinutes: duration(job), travelInMinutes: travel, travelInUnknown: travel > 0, travelInDistance: null,
        arrivalWindow: job.arrivalWindowMinutes ? { earliest: job.scheduledStartTime!, latest: toTime(old + job.arrivalWindowMinutes) } : null,
        locationKnown: false, expectedUpdatedAt: job.updatedAt, changed: start !== old,
      };
    });
    const dayEnd = (rows: { job: types.Job; start: number }[]) => rows.length ? toTime(Math.max(...rows.map((r) => r.start + duration(r.job)))) : null;
    return {
      userId: day.user.id, technicianEmail: day.user.email, date: request.date,
      workHours: { startTime: day.hours.startTime, endTime: day.hours.endTime },
      outcome, improved, algorithm: movable.length === 0 || day.dayOff ? null : movable.length <= 10 ? 'EXACT' : 'HEURISTIC',
      movableCount: movable.length,
      current: { order: day.timed.map((j) => j.id), totalTravelMinutes: currentTravel, dayEnd: dayEnd(day.timed.map((job) => ({ job, start: toMin(job.scheduledStartTime!) }))) },
      proposed: { order: final.map((p) => p.job.id), totalTravelMinutes: improved ? proposedTravel : currentTravel, dayEnd: dayEnd(final) },
      minutesSaved: improved ? currentTravel - proposedTravel : 0,
      jobs,
      leftOut: day.all.filter((j) => !j.scheduledStartTime).map((j) => ({ jobId: j.id, customerName: j.customer.name, reason: 'NO_TIME' as const })),
      changes: jobs.filter((j) => j.changed).map((j) => ({ jobId: j.jobId, expectedUpdatedAt: j.expectedUpdatedAt, scheduledStartTime: j.newStartTime }))
        .sort((a, b) => a.jobId.localeCompare(b.jobId)),
      distanceUnit: day.distanceUnit, depotKnown: false, unknownLocations: day.timed.length, conflicts: [],
    };
  }

  /**
   * Mock day reorder apply (tracker item 41): all or nothing like the
   * backend. 404 for a job not in the tenant, 409 JOB_CHANGED with every
   * stale id, 400 for a job not on that technician's day, 409
   * SUGGESTION_STALE when a new time overlaps another job; then every job.
   */
  async applyDayOptimization(request: types.ApplyDayOptimizationRequest): Promise<types.ApplyDayOptimizationResult> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    if (!request.changes?.length) throw mockApiError(400, 'changes: Must be a non-empty list', 'VALIDATION_ERROR');
    const day = await this.mockTechnicianDay(tenantId, request.userId, request.date);
    const jobs = request.changes.map((change) => {
      const job = this.jobs.find((j) => j.id === change.jobId && j.tenantId === tenantId);
      if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
      return job;
    });
    const staleJobIds = request.changes.filter((c, i) => jobs[i].updatedAt !== c.expectedUpdatedAt).map((c) => c.jobId).sort();
    if (staleJobIds.length) throw mockApiError(409, 'Someone else changed this day; refresh and try again', 'JOB_CHANGED', { staleJobIds });
    const elsewhere = jobs.filter((j) => !day.timed.includes(j)).map((j) => j.id);
    if (elsewhere.length) throw mockApiError(400, "Every job must be this technician's on this date", 'VALIDATION_ERROR', { jobIds: elsewhere });
    const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const startOf = (j: types.Job) => request.changes.find((c) => c.jobId === j.id)?.scheduledStartTime ?? j.scheduledStartTime!;
    const duration = (j: types.Job) => j.estimatedDurationMinutes ?? j.suggestedDurationMinutes ?? 60;
    const problems = request.changes.filter((c) => day.timed.some((other) => other.id !== c.jobId
      && ![types.JobStatus.COMPLETED, types.JobStatus.INVOICED].includes(other.status)
      && toMin(startOf(other)) < toMin(c.scheduledStartTime) + duration(jobs.find((j) => j.id === c.jobId)!)
      && toMin(c.scheduledStartTime) < toMin(startOf(other)) + duration(other)))
      .map((c) => ({ jobId: c.jobId, problem: 'CONFLICT', message: 'Overlaps another job' }));
    if (problems.length) throw mockApiError(409, 'This plan no longer fits the day; optimize again', 'SUGGESTION_STALE', { problems });
    const applied: string[] = [];
    for (const change of request.changes) {
      const job = jobs.find((j) => j.id === change.jobId)!;
      if (job.scheduledStartTime === change.scheduledStartTime) continue;
      await this.updateJob(job.id, { scheduledStartTime: change.scheduledStartTime });
      applied.push(job.id);
    }
    return { runId: generateId('run'), userId: request.userId, date: request.date, applied: applied.length, jobIds: applied.sort() };
  }

  async getTechnicianWorkHours(userId: string): Promise<types.TechnicianWorkHours> {
    await delay(50);
    const { tenantId } = this.requireOfficeUser();
    const settings = await this.getCompanySettings();
    const company = { workDays: settings.workDays ?? [1, 2, 3, 4, 5], startTime: settings.workdayStart ?? '08:00', endTime: settings.workdayEnd ?? '17:00' };
    const override = this.workHourOverrides.get(`${tenantId}:${userId}`);
    return { userId, source: override ? 'TECHNICIAN' : 'COMPANY', ...(override ?? company), companyDefault: company };
  }

  async setTechnicianWorkHours(userId: string, hours: types.WorkHours): Promise<types.TechnicianWorkHours> {
    const { tenantId } = this.requireOfficeUser();
    if (!hours.workDays.length || hours.endTime <= hours.startTime) throw mockApiError(400, 'Invalid working hours', 'VALIDATION_ERROR');
    this.workHourOverrides.set(`${tenantId}:${userId}`, { ...hours, workDays: [...hours.workDays].sort() });
    return this.getTechnicianWorkHours(userId);
  }

  async clearTechnicianWorkHours(userId: string): Promise<types.TechnicianWorkHours> {
    const { tenantId } = this.requireOfficeUser();
    this.workHourOverrides.delete(`${tenantId}:${userId}`);
    return this.getTechnicianWorkHours(userId);
  }

  async listTimeOff(query: types.TimeOffQuery = {}): Promise<types.TimeOff[]> {
    await delay(50);
    const { tenantId } = this.requireOfficeUser();
    const from = query.from ?? (query.to ? undefined : new Date().toISOString().slice(0, 10));
    return this.timeOffEntries
      .filter((t) => t.tenantId === tenantId && (!query.userId || t.userId === query.userId)
        && (!from || t.endDate >= from) && (!query.to || t.startDate <= query.to))
      .map(({ tenantId: _tenantId, ...entry }) => entry);
  }

  async createTimeOff(data: types.CreateTimeOffRequest): Promise<types.TimeOff> {
    const actor = this.requireOfficeUser();
    const { tenantId } = actor;
    const endDate = data.endDate || data.startDate;
    if (endDate < data.startDate) throw mockApiError(400, 'Invalid time off: endDate: Must be on or after startDate', 'VALIDATION_ERROR');
    const now = new Date().toISOString();
    const entry = {
      id: `timeoff-${Date.now()}-${this.timeOffEntries.length}`, tenantId, userId: data.userId, startDate: data.startDate, endDate,
      startTime: data.startTime ?? null, endTime: data.endTime ?? null, reason: data.reason ?? null, createdBy: actor.id, createdAt: now, updatedAt: now,
    };
    this.timeOffEntries.push(entry);
    const { tenantId: _tenantId, ...result } = entry;
    return result;
  }

  async updateTimeOff(id: string, data: types.UpdateTimeOffRequest): Promise<types.TimeOff> {
    const { tenantId } = this.requireOfficeUser();
    const entry = this.timeOffEntries.find((t) => t.id === id && t.tenantId === tenantId);
    if (!entry) throw mockApiError(404, 'Time off not found', 'NOT_FOUND');
    Object.assign(entry, data, { updatedAt: new Date().toISOString() });
    const { tenantId: _tenantId, ...result } = entry;
    return result;
  }

  async deleteTimeOff(id: string): Promise<void> {
    const { tenantId } = this.requireOfficeUser();
    const index = this.timeOffEntries.findIndex((t) => t.id === id && t.tenantId === tenantId);
    if (index < 0) throw mockApiError(404, 'Time off not found', 'NOT_FOUND');
    this.timeOffEntries.splice(index, 1);
  }

  // ---- Work orders (tracker item 58): a small in-memory version of the backend ----

  private mockWorkOrderCandidates(tenantId: string, date: string): types.WorkOrderCandidate[] {
    const confirmed = this.mockConfirmedWorkOrderJobs(tenantId);
    const routeName = (id: string | null | undefined) => (id ? this.dispatchRoutes.find((r) => r.id === id)?.name ?? null : null);
    const rows = this.jobs
      .filter((j) => j.tenantId === tenantId && j.scheduledDate === date && !j.invoiceId
        && (j.status === types.JobStatus.SCHEDULED || j.status === types.JobStatus.WORK_ORDER_GENERATED))
      .map((j): types.WorkOrderCandidate => ({
        id: j.id, status: j.status, customer: j.customer,
        property: { id: j.property.id, address: j.property.address, city: j.property.city ?? null, state: j.property.state ?? null },
        scheduledDate: date, scheduledStartTime: j.scheduledStartTime ?? null,
        routeId: j.routeId ?? null, routeName: routeName(j.routeId),
        assignedTo: j.assignedTo, assignedToEmail: j.assignedTo ? j.assignedToUser?.email ?? null : null,
        lineCount: j.lineItems.length, hasWorkItems: j.lineItems.length > 0,
        previouslyConfirmed: confirmed.has(j.id), updatedAt: j.updatedAt,
      }));
    const last = (v: string | null) => (v === null ? '￿' : v.toLowerCase());
    return rows.sort((a, b) => last(a.routeName).localeCompare(last(b.routeName)) || last(a.assignedToEmail).localeCompare(last(b.assignedToEmail))
      || last(a.scheduledStartTime).localeCompare(last(b.scheduledStartTime)) || a.id.localeCompare(b.id));
  }

  private mockConfirmedWorkOrderJobs(tenantId: string): Set<string> {
    return new Set(this.workOrderBatches.filter((b) => b.tenantId === tenantId)
      .flatMap((b) => b.members.filter((m) => m.confirmedAt).map((m) => m.jobId)));
  }

  private mockWorkOrderFacets(
    rows: types.WorkOrderCandidate[],
    idOf: (c: types.WorkOrderCandidate) => string,
    labelOf: (c: types.WorkOrderCandidate) => string,
  ): types.WorkOrderFacet[] {
    const facets = new Map<string, types.WorkOrderFacet>();
    rows.forEach((c) => {
      const facet = facets.get(idOf(c)) ?? { id: idOf(c), label: labelOf(c), count: 0 };
      facet.count += 1;
      facets.set(facet.id, facet);
    });
    return Array.from(facets.values());
  }

  async listWorkOrderCandidates(query: types.WorkOrderCandidateQuery = {}): Promise<types.WorkOrderCandidatePage> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const today = new Date().toISOString().slice(0, 10);
    const date = query.date || addDaysToDateOnly(today, 1) || today;
    const day = this.mockWorkOrderCandidates(tenantId, date);
    const search = query.search?.trim().toLowerCase();
    const matches = day.filter((c) => (!query.routeId || (query.routeId === types.WORK_ORDER_UNROUTED ? c.routeId === null : c.routeId === query.routeId))
      && (!query.assignedTo || (query.assignedTo === types.WORK_ORDER_UNASSIGNED ? c.assignedTo === null : c.assignedTo === query.assignedTo))
      && (!query.status || c.status === query.status)
      && (!search || `${c.customer.name} ${c.property.address} ${c.id}`.toLowerCase().includes(search)));
    const offset = Number(query.cursor ?? 0);
    const limit = query.limit ?? 50;
    return {
      date, today, timezone: (await this.getCompanySettings()).timezone ?? 'America/New_York',
      data: matches.slice(offset, offset + limit), total: matches.length, dayTotal: day.length,
      nextCursor: offset + limit < matches.length ? String(offset + limit) : null,
      routes: this.mockWorkOrderFacets(day, (c) => c.routeId ?? types.WORK_ORDER_UNROUTED, (c) => c.routeName ?? 'Unrouted'),
      technicians: this.mockWorkOrderFacets(day, (c) => c.assignedTo ?? types.WORK_ORDER_UNASSIGNED, (c) => c.assignedToEmail ?? 'Unassigned'),
    };
  }

  async resolveWorkOrderSelection(date: string, scope: 'DAY' | 'TECHNICIAN', assignedTo?: string): Promise<types.WorkOrderSelection> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const day = this.mockWorkOrderCandidates(tenantId, date);
    const jobs = scope === 'DAY' ? day : day.filter((c) => (assignedTo === types.WORK_ORDER_UNASSIGNED ? c.assignedTo === null : c.assignedTo === assignedTo));
    return { date, scope, assignedTo: scope === 'DAY' ? null : assignedTo ?? null, total: jobs.length, jobs };
  }

  async prepareWorkOrderBatch(data: types.PrepareWorkOrderBatchRequest): Promise<types.WorkOrderBatchSummary> {
    await delay(150);
    const actor = this.requireOfficeUser();
    const day = this.mockWorkOrderCandidates(actor.tenantId, data.date);
    const problems: types.WorkOrderJobProblem[] = data.jobs.flatMap((j): types.WorkOrderJobProblem[] => {
      const candidate = day.find((c) => c.id === j.id);
      if (!candidate) return [{ jobId: j.id, reason: 'NOT_ELIGIBLE', message: `Not a scheduled job on ${data.date}` }];
      if (!candidate.hasWorkItems) return [{ jobId: j.id, reason: 'NO_WORK_ITEMS', message: 'Add at least one work item before printing its work order' }];
      return [];
    });
    if (problems.length) throw mockWorkOrderError(409, `${problems.length} selected job(s) cannot be prepared; nothing was saved`, 'WORK_ORDER_SELECTION_INVALID', problems);
    const copies = new Map(data.jobs.map((j) => [j.id, j]));
    const now = new Date().toISOString();
    const members: MockWorkOrderMember[] = day.filter((c) => copies.has(c.id)).map((c, index) => ({
      jobId: c.id, position: index + 1, serviceCopies: copies.get(c.id)!.serviceCopies, billingCopies: copies.get(c.id)!.billingCopies,
      originalStatus: c.status, printCount: 0, lastPrintedAt: null, confirmedAt: null, confirmedByEmail: null, printedVersion: null, transitioned: false,
    }));
    const batch: MockWorkOrderBatch = {
      id: `wob-${Date.now()}-${this.workOrderBatches.length}`, tenantId: actor.tenantId, scheduledDate: data.date, status: 'PREPARED',
      preparedBy: actor.id, preparedByEmail: actor.email, jobCount: members.length, confirmedJobCount: 0, unconfirmedJobCount: members.length,
      serviceCopyCount: members.reduce((n, m) => n + m.serviceCopies, 0), billingCopyCount: members.reduce((n, m) => n + m.billingCopies, 0),
      printAttempts: 0, lastPrintedAt: null, lastConfirmedAt: null, lastConfirmedByEmail: null, createdAt: now, updatedAt: now, members,
    };
    this.workOrderBatches.push(batch);
    return this.mockWorkOrderSummary(batch);
  }

  private mockWorkOrderSummary(batch: MockWorkOrderBatch): types.WorkOrderBatchSummary {
    const { tenantId: _tenantId, members: _members, ...summary } = batch;
    return { ...summary };
  }

  private mockWorkOrderBatch(id: string): MockWorkOrderBatch {
    const { tenantId } = this.requireOfficeUser();
    const batch = this.workOrderBatches.find((b) => b.id === id && b.tenantId === tenantId);
    if (!batch) throw mockApiError(404, 'Work order batch not found', 'NOT_FOUND');
    return batch;
  }

  async listWorkOrderBatches(query: types.WorkOrderBatchListQuery = {}): Promise<types.WorkOrderBatchList> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const search = query.search?.trim().toLowerCase();
    const rows = this.workOrderBatches
      .filter((b) => b.tenantId === tenantId && (!query.date || b.scheduledDate === query.date) && (!query.status || b.status === query.status)
        && (!search || b.preparedByEmail.toLowerCase().includes(search) || b.members.some((m) => {
          const job = this.jobs.find((j) => j.id === m.jobId);
          return !!job && `${job.customer.name} ${job.property.address}`.toLowerCase().includes(search);
        })))
      .slice()
      .reverse();
    const page = query.page ?? 1;
    const limit = query.limit ?? 20;
    return {
      data: rows.slice((page - 1) * limit, page * limit).map((b) => this.mockWorkOrderSummary(b)),
      pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) },
    };
  }

  async getWorkOrderBatch(id: string): Promise<types.WorkOrderBatchDetail> {
    await delay(100);
    const batch = this.mockWorkOrderBatch(id);
    const confirmed = this.mockConfirmedWorkOrderJobs(batch.tenantId);
    const company = await this.getCompanySettings();
    return {
      ...this.mockWorkOrderSummary(batch),
      company,
      members: batch.members.map((m): types.WorkOrderBatchMember => {
        const stored = this.jobs.find((j) => j.id === m.jobId && j.tenantId === batch.tenantId);
        let job: types.Job | null = null;
        if (stored) {
          const { tenantId: _tenantId, ...rest } = stored;
          job = rest;
        }
        const eligible = !!job && (job.status === types.JobStatus.SCHEDULED || job.status === types.JobStatus.WORK_ORDER_GENERATED);
        let confirmBlock: types.WorkOrderConfirmBlock | null = null;
        if (!m.confirmedAt) {
          if (!job) confirmBlock = 'JOB_MISSING';
          else if (!eligible) confirmBlock = 'STATUS_CHANGED';
          else if (job.scheduledDate !== batch.scheduledDate) confirmBlock = 'DATE_CHANGED';
        }
        return {
          ...m,
          reprint: confirmed.has(m.jobId),
          routeName: job?.routeId ? this.dispatchRoutes.find((r) => r.id === job!.routeId)?.name ?? null : null,
          job,
          checklist: stored ? this.toJobChecklist(stored) : null,
          confirmBlock,
        };
      }),
    };
  }

  async recordWorkOrderPrint(id: string, jobIds: string[]): Promise<types.RecordWorkOrderPrintResult> {
    await delay(50);
    const batch = this.mockWorkOrderBatch(id);
    const now = new Date().toISOString();
    const confirmed = this.mockConfirmedWorkOrderJobs(batch.tenantId);
    batch.printAttempts += 1;
    batch.lastPrintedAt = now;
    batch.members.filter((m) => jobIds.includes(m.jobId)).forEach((m) => {
      m.printCount += 1;
      m.lastPrintedAt = now;
    });
    return { batch: this.mockWorkOrderSummary(batch), reprintJobIds: jobIds.filter((jobId) => confirmed.has(jobId)) };
  }

  async confirmWorkOrderJobs(id: string, jobs: Array<{ id: string; printedVersion: string }>): Promise<types.ConfirmWorkOrderJobsResult> {
    await delay(150);
    const actor = this.requireOfficeUser();
    const batch = this.mockWorkOrderBatch(id);
    const pending = jobs.filter((j) => batch.members.some((m) => m.jobId === j.id && !m.confirmedAt));
    const problems: types.WorkOrderJobProblem[] = pending.flatMap((entry): types.WorkOrderJobProblem[] => {
      const job = this.jobs.find((j) => j.id === entry.id && j.tenantId === batch.tenantId);
      if (!job) return [{ jobId: entry.id, reason: 'NOT_FOUND', message: 'Job no longer exists' }];
      if (job.status !== types.JobStatus.SCHEDULED && job.status !== types.JobStatus.WORK_ORDER_GENERATED) {
        return [{ jobId: entry.id, reason: 'STATUS_CHANGED', label: job.customer.name, message: `Now ${job.status}` }];
      }
      if (job.scheduledDate !== batch.scheduledDate) return [{ jobId: entry.id, reason: 'DATE_CHANGED', label: job.customer.name, message: 'Rescheduled' }];
      if (job.updatedAt !== entry.printedVersion) {
        return [{ jobId: entry.id, reason: 'JOB_CHANGED', label: job.customer.name, message: 'Changed after this work order was printed; reprint it before confirming' }];
      }
      return [];
    });
    if (problems.length) throw mockWorkOrderError(409, `${problems.length} job(s) need review; nothing was confirmed`, 'WORK_ORDER_CONFIRM_CONFLICT', problems);
    const now = new Date().toISOString();
    const newlyGenerated: string[] = [];
    const alreadyGenerated: string[] = [];
    pending.forEach((entry) => {
      const job = this.jobs.find((j) => j.id === entry.id)!;
      const member = batch.members.find((m) => m.jobId === entry.id)!;
      if (job.status === types.JobStatus.SCHEDULED) {
        job.status = types.JobStatus.WORK_ORDER_GENERATED;
        job.updatedAt = now;
        newlyGenerated.push(job.id);
        member.transitioned = true;
      } else {
        alreadyGenerated.push(job.id);
      }
      member.confirmedAt = now;
      member.confirmedByEmail = actor.email;
      member.printedVersion = entry.printedVersion;
    });
    batch.confirmedJobCount = batch.members.filter((m) => m.confirmedAt).length;
    batch.unconfirmedJobCount = batch.jobCount - batch.confirmedJobCount;
    batch.status = batch.confirmedJobCount === 0 ? 'PREPARED' : batch.confirmedJobCount === batch.jobCount ? 'CONFIRMED' : 'PARTIALLY_CONFIRMED';
    if (pending.length) {
      batch.lastConfirmedAt = now;
      batch.lastConfirmedByEmail = actor.email;
    }
    return {
      batch: this.mockWorkOrderSummary(batch),
      newlyGenerated,
      alreadyGenerated,
      alreadyConfirmed: jobs.filter((j) => !pending.includes(j)).map((j) => j.id),
      stillUnconfirmed: batch.members.filter((m) => !m.confirmedAt).map((m) => m.jobId),
    };
  }

  async listDispatchRoutes(): Promise<types.DispatchRoute[]> {
    const { tenantId } = this.requireOfficeUser();
    return this.dispatchRoutes.filter((r) => r.tenantId === tenantId).map(({ tenantId: _tenantId, ...r }) => ({ ...r }));
  }

  async createDispatchRoute(data: types.CreateDispatchRouteRequest): Promise<types.DispatchRoute> {
    const { tenantId } = this.requireOfficeUser();
    const name = data.name.trim();
    if (!name) throw mockApiError(400, 'Route name is required', 'VALIDATION_ERROR');
    const route = { id: `route-${Date.now()}-${this.dispatchRoutes.length}`, tenantId, name, isActive: true };
    this.dispatchRoutes.push(route);
    const { tenantId: _tenantId, ...result } = route;
    return result;
  }

  async updateDispatchRoute(id: string, data: types.UpdateDispatchRouteRequest): Promise<types.DispatchRoute> {
    const { tenantId } = this.requireOfficeUser();
    const route = this.dispatchRoutes.find((r) => r.id === id && r.tenantId === tenantId);
    if (!route) throw mockApiError(404, 'Route not found', 'NOT_FOUND');
    if (data.name !== undefined) {
      if (!data.name.trim()) throw mockApiError(400, 'Route name is required', 'VALIDATION_ERROR');
      route.name = data.name.trim();
    }
    if (data.isActive !== undefined) route.isActive = data.isActive;
    const { tenantId: _tenantId, ...result } = route;
    return { ...result };
  }

  async deleteDispatchRoute(id: string): Promise<void> {
    const { tenantId } = this.requireOfficeUser();
    const index = this.dispatchRoutes.findIndex((r) => r.id === id && r.tenantId === tenantId);
    if (index < 0) throw mockApiError(404, 'Route not found', 'NOT_FOUND');
    this.dispatchRoutes.splice(index, 1);
    this.technicianRouteIds.forEach((routeIds, key) => this.technicianRouteIds.set(key, routeIds.filter((routeId) => routeId !== id)));
    this.routeAssignments = this.routeAssignments.filter((assignment) => assignment.tenantId !== tenantId || assignment.routeId !== id);
  }

  async getTechnicianRoutes(userId: string): Promise<string[]> {
    const { tenantId } = this.requireOfficeUser();
    this.findTenantUser(tenantId, userId);
    return [...(this.technicianRouteIds.get(`${tenantId}:${userId}`) ?? [])];
  }

  async setTechnicianRoutes(userId: string, routeIds: string[]): Promise<string[]> {
    const { tenantId } = this.requireOfficeUser();
    this.findTenantUser(tenantId, userId);
    const valid = new Set(this.dispatchRoutes.filter((r) => r.tenantId === tenantId).map((r) => r.id));
    if (routeIds.some((id) => !valid.has(id))) throw mockApiError(404, 'Route not found', 'NOT_FOUND');
    const unique = [...new Set(routeIds)];
    this.technicianRouteIds.set(`${tenantId}:${userId}`, unique);
    return [...unique];
  }

  async listRouteAssignments(query: types.RouteAssignmentQuery): Promise<types.RouteAssignment[]> {
    const { tenantId } = this.requireOfficeUser();
    return this.routeAssignments.filter((a) => a.tenantId === tenantId && a.date === query.date && (!query.userId || a.userId === query.userId))
      .map(({ tenantId: _tenantId, ...a }) => ({ ...a }));
  }

  async createRouteAssignment(data: types.CreateRouteAssignmentRequest): Promise<types.RouteAssignment> {
    const { tenantId } = this.requireOfficeUser();
    this.findTenantUser(tenantId, data.userId);
    if (!this.dispatchRoutes.some((r) => r.tenantId === tenantId && r.id === data.routeId)) throw mockApiError(404, 'Route not found', 'NOT_FOUND');
    const assignment = { ...data, id: `route-assignment-${Date.now()}-${this.routeAssignments.length}`, tenantId };
    this.routeAssignments.push(assignment);
    const { tenantId: _tenantId, ...result } = assignment;
    return result;
  }

  async deleteRouteAssignment(id: string): Promise<void> {
    const { tenantId } = this.requireOfficeUser();
    const index = this.routeAssignments.findIndex((a) => a.tenantId === tenantId && a.id === id);
    if (index < 0) throw mockApiError(404, 'Route assignment not found', 'NOT_FOUND');
    this.routeAssignments.splice(index, 1);
  }

  /** Like backend RouteService.setDefault: default and eligible Jobs change together. */
  async setRouteDefault(entity: types.RouteDefaultEntity, id: string, data: types.SetRouteDefaultRequest): Promise<types.SetRouteDefaultResult> {
    const { tenantId } = this.requireOfficeUser();
    const propagation = data.routePropagation ?? 'NONE';
    if (!['ALL', 'NONE', 'MATCH_OLD'].includes(propagation)) throw mockApiError(400, 'Invalid routePropagation', 'VALIDATION_ERROR');
    if (entity === 'item' && propagation !== 'NONE') throw mockApiError(400, 'Item defaults do not propagate to existing jobs', 'VALIDATION_ERROR');
    const target: { routeId?: string | null } | undefined = entity === 'customer'
      ? this.customers.find((c) => c.tenantId === tenantId && c.id === id)
      : entity === 'property'
        ? this.properties.find((p) => p.tenantId === tenantId && p.id === id)
        : this.items.find((i) => i.tenantId === tenantId && i.id === id);
    if (!target) throw mockApiError(404, 'Default target not found', 'NOT_FOUND');
    if (data.routeId && !this.dispatchRoutes.some((r) => r.tenantId === tenantId && r.id === data.routeId && r.isActive !== false)) {
      throw mockApiError(404, 'Route not found or inactive', 'NOT_FOUND');
    }
    const oldRouteId = target.routeId ?? null;
    target.routeId = data.routeId;
    let updatedJobs = 0;
    if (entity !== 'item' && propagation !== 'NONE' && oldRouteId !== data.routeId) {
      const terminal = [types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED];
      this.jobs
        .filter((j) => j.tenantId === tenantId && (entity === 'customer' ? j.customerId === id : j.propertyId === id)
          && !terminal.includes(j.status) && (propagation === 'ALL' || (j.routeId ?? null) === oldRouteId))
        .forEach((j) => { j.routeId = data.routeId; j.routeSource = 'DEFAULT'; j.updatedAt = new Date().toISOString(); updatedJobs += 1; });
    }
    return { entityId: id, routeId: data.routeId, updatedJobs };
  }

  async listDunningRuns(limit = 20): Promise<types.DunningRunSummary[]> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    return this.dunningRuns
      .filter((r) => r.tenantId === tenantId)
      .slice(0, limit)
      .map(({ tenantId: _tenantId, ...run }) => run);
  }

  async getDunningSteps(): Promise<types.DunningSteps> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    return this.mockDunningStepsResponse(tenantId);
  }

  async replaceDunningSteps(data: types.ReplaceDunningStepsRequest): Promise<types.DunningSteps> {
    await delay(200);
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions');
    const input = data.steps ?? [];
    if (input.length > 6) throw mockApiError(400, 'Invalid schedule: steps: At most 6 steps', 'VALIDATION_ERROR');
    input.forEach((s, i) => {
      if (!Number.isInteger(s.daysOverdue) || s.daysOverdue < 1 || s.daysOverdue > 365) {
        throw mockApiError(400, `Invalid schedule: steps[${i}].daysOverdue: Must be a whole number of days from 1 to 365`, 'VALIDATION_ERROR');
      }
      if (i > 0 && s.daysOverdue <= input[i - 1].daysOverdue) {
        throw mockApiError(400, `Invalid schedule: steps[${i}].daysOverdue: Steps must be in ascending order of days, each a different number of days`, 'VALIDATION_ERROR');
      }
    });
    const current = this.mockDunningStepsFor(actor.tenantId);
    const used = new Set(input.map((s) => s.eventKey).filter(Boolean));
    const free = [1, 2, 3, 4, 5, 6].map((n) => `DUNNING_${n}`).filter((k) => !used.has(k));
    const mode = this.mockCompanySettings(actor.tenantId).dunningMode ?? 'OFF';
    const next = input.map((s, i) => {
      const existing = current.find((c) => c.eventKey === s.eventKey);
      const eventKey = s.eventKey ?? free.shift()!;
      return {
        id: existing?.id ?? generateId('dstep'),
        position: i + 1,
        daysOverdue: s.daysOverdue,
        eventKey,
        templateLabel: existing?.templateLabel ?? `Reminder ${eventKey.slice(-1)}`,
        templateActive: existing ? existing.templateActive : mode !== 'OFF',
        isActive: s.isActive,
      };
    });
    this.dunningSteps.set(actor.tenantId, next);
    return this.mockDunningStepsResponse(actor.tenantId);
  }

  async listInvoiceDunningNotices(invoiceId: string): Promise<types.DunningNotice[]> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    if (!this.invoices.some((i) => i.id === invoiceId && i.tenantId === tenantId)) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    return [];
  }

  async setInvoiceDunningHold(invoiceId: string, hold: boolean): Promise<types.Invoice> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    const invoice = this.invoices.find((i) => i.id === invoiceId && i.tenantId === tenantId);
    if (!invoice) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    if (typeof hold !== 'boolean') throw mockApiError(400, 'hold must be true or false', 'VALIDATION_ERROR');
    invoice.dunningHold = hold;
    return { ...invoice };
  }

  // ============ Company Settings ============

  async getCompanySettings(): Promise<types.CompanySettings> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    return { ...this.mockCompanySettings(tenantId) };
  }

  async updateCompanySettings(data: types.UpdateCompanySettingsRequest): Promise<types.CompanySettings> {
    await delay(300);
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) {
      throw mockApiError(403, 'Insufficient permissions');
    }
    if (!data.companyName || !data.companyName.trim()) {
      throw mockApiError(400, 'Invalid company settings: companyName: Company name is required', 'VALIDATION_ERROR');
    }
    const current = this.mockCompanySettings(actor.tenantId);
    // PUT semantics as the backend: omitted fields unchanged, '' clears text
    const updated: types.CompanySettings = { ...current, updatedAt: new Date().toISOString() };
    for (const [field, value] of Object.entries(data)) {
      if (value === undefined) continue;
      (updated as unknown as Record<string, unknown>)[field] =
        typeof value === 'string' ? (value.trim() === '' ? null : value.trim()) : value;
    }
    updated.companyName = data.companyName.trim();
    if (data.financeChargeRate !== undefined) updated.financeChargeRate = Number(data.financeChargeRate).toFixed(4);
    if (data.financeChargeMinAmount !== undefined) updated.financeChargeMinAmount = Number(data.financeChargeMinAmount).toFixed(2);
    if (data.flatContractBillingMode !== undefined
      && !(types.FLAT_CONTRACT_BILLING_MODES as readonly string[]).includes(data.flatContractBillingMode)) {
      throw mockApiError(400, 'Invalid company settings: flatContractBillingMode: Must be one of: MANUAL, AUTOMATIC', 'VALIDATION_ERROR');
    }
    if (data.autoInvoicingMode !== undefined
      && !(types.AUTO_INVOICING_MODES as readonly string[]).includes(data.autoInvoicingMode)) {
      throw mockApiError(400, 'Invalid company settings: autoInvoicingMode: Must be one of: OFF, ON_COMPLETION, NIGHTLY', 'VALIDATION_ERROR');
    }
    if (data.autoInvoicingNightlyHour !== undefined
      && (!Number.isInteger(data.autoInvoicingNightlyHour) || data.autoInvoicingNightlyHour < 0 || data.autoInvoicingNightlyHour > 23)) {
      throw mockApiError(400, 'Invalid company settings: autoInvoicingNightlyHour: Must be a whole number from 0 to 23', 'VALIDATION_ERROR');
    }
    if (data.dunningMode !== undefined && !(types.DUNNING_MODES as readonly string[]).includes(data.dunningMode)) {
      throw mockApiError(400, 'Invalid company settings: dunningMode: Must be one of: OFF, ON', 'VALIDATION_ERROR');
    }
    if (data.dunningHour !== undefined && (!Number.isInteger(data.dunningHour) || data.dunningHour < 0 || data.dunningHour > 23)) {
      throw mockApiError(400, 'Invalid company settings: dunningHour: Must be a whole number from 0 to 23 (hour of the day, UTC)', 'VALIDATION_ERROR');
    }
    if (data.dunningMinDaysBetween !== undefined
      && (!Number.isInteger(data.dunningMinDaysBetween) || data.dunningMinDaysBetween < 1 || data.dunningMinDaysBetween > 365)) {
      throw mockApiError(400, 'Invalid company settings: dunningMinDaysBetween: Must be a whole number from 1 to 365', 'VALIDATION_ERROR');
    }
    // Customer portal (tracker item 45): booleans and whole-number ranges, like the backend
    for (const field of ['portalEnabled', 'portalRescheduleAutoApprove'] as const) {
      if (data[field] !== undefined && typeof data[field] !== 'boolean') {
        throw mockApiError(400, `Invalid company settings: ${field}: Must be true or false`, 'VALIDATION_ERROR');
      }
    }
    for (const [field, min, max] of [['portalCodeValidMonths', 1, 120], ['portalMaxLoginsPerCustomer', 1, 20], ['portalRescheduleLeadHours', 0, 720]] as const) {
      const value = data[field];
      if (value !== undefined && (!Number.isInteger(value) || value < min || value > max)) {
        throw mockApiError(400, `Invalid company settings: ${field}: Must be a whole number from ${min} to ${max}`, 'VALIDATION_ERROR');
      }
    }
    // Conversation inbox (tracker item 56)
    if (data.inboxAccess !== undefined && !(types.INBOX_ACCESS_VALUES as readonly string[]).includes(data.inboxAccess)) {
      throw mockApiError(400, 'Invalid company settings: inboxAccess: Must be one of: STAFF, EVERYONE', 'VALIDATION_ERROR');
    }
    // Like the backend: turning dunning on without a start date starts it today, and turns its templates on
    if (updated.dunningMode === 'ON' && !updated.dunningStartDate) {
      if (data.dunningStartDate === null || data.dunningStartDate === '') {
        throw mockApiError(400, 'Invalid company settings: dunningStartDate: Required while dunning is on', 'VALIDATION_ERROR');
      }
      updated.dunningStartDate = new Date().toISOString().slice(0, 10);
    }
    if ((current.dunningMode ?? 'OFF') === 'OFF' && updated.dunningMode === 'ON') {
      for (const step of this.mockDunningStepsFor(actor.tenantId)) if (step.isActive) step.templateActive = true;
    }
    // Like the backend: turning auto-invoicing on without a start date starts it today
    if (updated.autoInvoicingMode !== 'OFF' && !updated.autoInvoicingStartDate) {
      if (data.autoInvoicingStartDate === null || data.autoInvoicingStartDate === '') {
        throw mockApiError(400, 'Invalid company settings: autoInvoicingStartDate: Required while auto-invoicing is on', 'VALIDATION_ERROR');
      }
      updated.autoInvoicingStartDate = new Date().toISOString().slice(0, 10);
    }
    // Routing (tracker item 41): same ranges as the backend
    const routingChecks: [keyof types.UpdateCompanySettingsRequest, boolean, string][] = [
      ['routingSpeedKph', data.routingSpeedKph === undefined || (Number.isInteger(data.routingSpeedKph) && data.routingSpeedKph >= 5 && data.routingSpeedKph <= 130), 'Must be a whole number from 5 to 130'],
      ['routingCircuityFactor', data.routingCircuityFactor === undefined || (/^\d(\.\d{1,2})?$/.test(String(data.routingCircuityFactor)) && Number(data.routingCircuityFactor) >= 1 && Number(data.routingCircuityFactor) <= 3), 'Must be a number from 1.00 to 3.00 with at most 2 decimal places'],
      ['routingUnknownTravelMinutes', data.routingUnknownTravelMinutes === undefined || (Number.isInteger(data.routingUnknownTravelMinutes) && data.routingUnknownTravelMinutes >= 0 && data.routingUnknownTravelMinutes <= 120), 'Must be a whole number from 0 to 120'],
      ['distanceUnit', data.distanceUnit === undefined || data.distanceUnit === 'MI' || data.distanceUnit === 'KM', 'Must be one of: MI, KM'],
    ];
    for (const [field, ok, message] of routingChecks) {
      if (!ok) throw mockApiError(400, `Invalid company settings: ${field}: ${message}`, 'VALIDATION_ERROR');
    }
    if (data.routingCircuityFactor !== undefined) updated.routingCircuityFactor = Number(data.routingCircuityFactor).toFixed(2);
    // A changed company address goes back in the geocoding queue
    const addressOf = (s: types.CompanySettings) => [s.addressLine1, s.city, s.state, s.postalCode].map((v) => (v ?? '').trim()).join('|');
    if (addressOf(updated) !== addressOf(current)) {
      updated.geocodeStatus = 'PENDING';
      updated.geocodePrecision = null;
    }
    this.companySettings.set(actor.tenantId, updated);
    return { ...updated };
  }

  private mockCompanySettings(tenantId: string): types.CompanySettings {
    let settings = this.companySettings.get(tenantId);
    if (!settings) {
      const now = new Date().toISOString();
      settings = {
        id: generateId('settings'),
        tenantId,
        companyName: MOCK_TENANTS.find((t) => t.id === tenantId)?.displayName ?? 'My Company',
        logoUrl: null,
        taxId: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
        phone: null,
        email: null,
        website: null,
        invoiceFooterNote: null,
        defaultPaymentTermsDays: DEFAULT_PAYMENT_TERMS_DAYS,
        financeChargeRate: '0.0000',
        financeChargeGraceDays: 0,
        financeChargeMinAmount: '0.00',
        flatContractBillingMode: 'MANUAL',
        autoInvoicingMode: 'OFF',
        autoInvoicingStartDate: null,
        autoInvoicingAutoSend: false,
        autoInvoicingNightlyHour: 2,
        dunningMode: 'OFF',
        dunningStartDate: null,
        dunningHour: 9,
        dunningMinDaysBetween: 7,
        portalEnabled: true,
        portalCodeValidMonths: 12,
        portalMaxLoginsPerCustomer: 3,
        portalRescheduleLeadHours: 48,
        portalRescheduleAutoApprove: true,
        inboxAccess: 'STAFF',
        routingSpeedKph: 48,
        routingCircuityFactor: '1.30',
        routingUnknownTravelMinutes: 15,
        distanceUnit: 'MI',
        geocodeStatus: 'PENDING',
        geocodePrecision: null,
        createdAt: now,
        updatedAt: now,
      };
      this.companySettings.set(tenantId, settings);
    }
    return settings;
  }

  // ============ Recurring Jobs ============
  // A small in-memory version of backend RecurringJobsService (same date
  // engine, markers and idempotency), enough to exercise the pages offline.

  private requireMockSchedule(id: string): MockRecurringSchedule {
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const schedule = this.recurringSchedules.find((s) => s.id === id && s.tenantId === tenantId && !s.deleted);
    if (!schedule) throw mockApiError(404, 'Schedule not found');
    return schedule;
  }

  private toMockRecurringSchedule(schedule: MockRecurringSchedule): types.RecurringSchedule {
    const { occurrences, deleted, ...rest } = schedule;
    const property = this.properties.find((p) => p.id === schedule.propertyId);
    const customer = this.customers.find((c) => c.id === schedule.customerId);
    const template = this.jobs.find((j) => j.id === schedule.templateJobId);
    return {
      ...rest,
      skippedDates: [...rest.skippedDates],
      property: { id: schedule.propertyId, address: property?.address ?? '', city: property?.city ?? '', state: property?.state ?? '' },
      customer: { id: schedule.customerId, name: customer?.name ?? '' },
      templateJob: template
        ? { id: template.id, status: template.status, scheduledDate: template.scheduledDate ? template.scheduledDate.slice(0, 10) : null }
        : { id: '', status: '', scheduledDate: null },
    };
  }

  private assertMockRecurrence(
    frequency: string,
    interval: number,
    details: types.RecurringFrequencyDetails | null | undefined
  ): void {
    if (!(types.RECURRING_FREQUENCIES as readonly string[]).includes(frequency)) {
      throw mockApiError(400, `frequency must be one of: ${types.RECURRING_FREQUENCIES.join(', ')}`);
    }
    const problem = frequencyConfigError(frequency as types.RecurringFrequency, interval, details);
    if (problem) throw mockApiError(400, `Invalid frequency configuration: ${problem}`);
  }

  /** Backend generationStartDate: an on-pattern date to walk from. */
  private mockGenerationStart(schedule: MockRecurringSchedule): string {
    const { startDate, lastGeneratedDate, nextScheduledDate } = schedule;
    if (lastGeneratedDate && (!nextScheduledDate || nextScheduledDate > lastGeneratedDate)) return lastGeneratedDate;
    return nextScheduledDate ?? startDate;
  }

  private mockScheduleDates(schedule: MockRecurringSchedule, limit: number): string[] {
    return listOccurrences(
      this.mockGenerationStart(schedule),
      schedule.frequency as types.RecurringFrequency,
      schedule.interval,
      schedule.frequencyDetails,
      schedule.endDate ?? null,
      limit
    );
  }

  async listRecurringSchedules(
    page = 1,
    limit = 20,
    filters: types.RecurringScheduleListFilters = {}
  ): Promise<types.PaginatedResponse<types.RecurringSchedule>> {
    await delay(200);
    const tenantId = getCurrentTenantId(this.token || '');
    if (!tenantId) throw new Error('Not authenticated');
    const rows = this.recurringSchedules
      .filter((s) => s.tenantId === tenantId && !s.deleted)
      .filter((s) => (!filters.status || s.status === filters.status)
        && (!filters.customerId || s.customerId === filters.customerId)
        && (!filters.propertyId || s.propertyId === filters.propertyId))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((s) => this.toMockRecurringSchedule(s));
    return paginate(rows, page, limit);
  }

  async getRecurringSchedule(id: string): Promise<types.RecurringScheduleDetail> {
    await delay(200);
    const schedule = this.requireMockSchedule(id);
    const preview = await this.previewRecurringOccurrences(id, 5);
    const billed = this.billedPeriods
      .filter((p) => p.scheduleId === schedule.id)
      .sort((a, b) => b.start.localeCompare(a.start));
    return {
      ...this.toMockRecurringSchedule(schedule),
      nextOccurrences: preview.occurrences.map((o) => ({ date: o.date, status: o.willGenerate ? 'PENDING' : 'SKIPPED' })),
      nextBillingDate: this.mockNextBillingDate(schedule),
      billingPeriods: billed.map((p) => {
        const invoice = this.invoices.find((i) => i.id === p.invoiceId);
        return {
          periodStartDate: p.start,
          periodEndDate: p.end,
          invoiceId: p.invoiceId,
          invoiceNumber: invoice?.invoiceNumber ?? '',
          invoiceStatus: invoice?.status ?? '',
          amount: p.amount,
          trigger: p.trigger,
          createdAt: p.createdAt,
        };
      }),
    };
  }

  /** Backend resolveCreateBilling / resolveUpdateBilling (merged terms, same messages). */
  private mockBillingTerms(
    data: types.RecurringBillingTerms,
    current: Pick<MockRecurringSchedule, 'billingModel' | 'flatContractAmount' | 'billingFrequency' | 'billingInterval' | 'billingStartDate'> | null,
    billableFrom: string
  ): Pick<MockRecurringSchedule, 'billingModel' | 'flatContractAmount' | 'billingFrequency' | 'billingInterval' | 'billingStartDate'> {
    const model = data.billingModel ?? current?.billingModel ?? 'PER_VISIT';
    if (!['PER_VISIT', 'FLAT_CONTRACT'].includes(model)) throw mockApiError(400, 'billingModel must be one of: PER_VISIT, FLAT_CONTRACT');
    if (model === 'PER_VISIT') {
      const hasTerms = (data.flatContractAmount !== undefined && data.flatContractAmount !== null && data.flatContractAmount !== '')
        || (data.billingFrequency !== undefined && data.billingFrequency !== null)
        || (data.billingInterval !== undefined && data.billingInterval !== 1);
      if (hasTerms) throw mockApiError(400, 'flatContractAmount, billingFrequency and billingInterval apply only to FLAT_CONTRACT schedules');
      return { billingModel: 'PER_VISIT', flatContractAmount: null, billingFrequency: null, billingInterval: 1, billingStartDate: null };
    }
    const rawAmount = data.flatContractAmount !== undefined ? data.flatContractAmount : current?.flatContractAmount ?? null;
    if (rawAmount === null || rawAmount === '') throw mockApiError(400, 'flatContractAmount is required for a FLAT_CONTRACT schedule');
    if (!/^\d+(\.\d{1,2})?$/.test(String(rawAmount).trim()) || !(Number(rawAmount) > 0)) {
      throw mockApiError(400, 'flatContractAmount must be an amount greater than 0 with at most 2 decimal places');
    }
    const frequency = data.billingFrequency !== undefined ? data.billingFrequency : current?.billingFrequency ?? null;
    if (!frequency) throw mockApiError(400, 'billingFrequency is required for a FLAT_CONTRACT schedule');
    if (!(types.FLAT_CONTRACT_BILLING_FREQUENCIES as readonly string[]).includes(frequency)) {
      throw mockApiError(400, `billingFrequency must be one of: ${types.FLAT_CONTRACT_BILLING_FREQUENCIES.join(', ')}`);
    }
    const interval = data.billingInterval ?? current?.billingInterval ?? 1;
    if (!Number.isInteger(interval) || interval < 1 || interval > 120) throw mockApiError(400, 'billingInterval must be a whole number from 1 to 120');
    const switching = !current || current.billingModel !== 'FLAT_CONTRACT' || !current.billingStartDate;
    return {
      billingModel: 'FLAT_CONTRACT',
      flatContractAmount: Number(rawAmount).toFixed(2),
      billingFrequency: frequency,
      billingInterval: interval,
      billingStartDate: switching ? billableFrom : current!.billingStartDate,
    };
  }

  async createRecurringSchedule(data: types.CreateRecurringScheduleRequest): Promise<types.RecurringSchedule> {
    await delay(300);
    const actor = this.requireOfficeUser();
    const tenantId = actor.tenantId;
    const interval = data.interval ?? 1;
    this.assertMockRecurrence(data.frequency, interval, data.frequencyDetails);
    if (!isDateOnly(data.startDate)) throw mockApiError(400, `Date must be in ISO format (YYYY-MM-DD): ${data.startDate}`);
    if (data.endDate) {
      if (!isDateOnly(data.endDate)) throw mockApiError(400, `Date must be in ISO format (YYYY-MM-DD): ${data.endDate}`);
      if (data.endDate < data.startDate) throw mockApiError(400, 'endDate must be after or equal to startDate');
    }
    const property = this.properties.find((p) => p.id === data.propertyId && p.tenantId === tenantId);
    if (!property) throw mockApiError(400, 'Property not found');
    const template = this.jobs.find((j) => j.id === data.templateJobId && j.tenantId === tenantId);
    if (!template) throw mockApiError(400, 'Template job not found');
    if (template.status !== types.JobStatus.DRAFT) throw mockApiError(400, 'Template job must be in DRAFT status');
    if (!template.assignedTo) throw mockApiError(400, 'Template job must have an assigned user');
    if (template.lineItems.length === 0) throw mockApiError(400, 'Template job must have at least one line item');
    const now = new Date().toISOString();
    const billingTerms = this.mockBillingTerms(data, null, data.startDate);
    const schedule: MockRecurringSchedule = {
      ...billingTerms,
      id: generateId('rs'),
      tenantId,
      propertyId: property.id,
      customerId: property.customerId,
      templateJobId: template.id,
      frequency: data.frequency,
      interval,
      frequencyDetails: data.frequencyDetails ?? null,
      startDate: data.startDate,
      endDate: data.endDate || undefined,
      nextScheduledDate: data.startDate,
      skippedDates: Array.from(new Set(data.skippedDates ?? [])),
      notes: data.notes ?? null,
      status: 'ACTIVE',
      createdAt: now,
      updatedAt: now,
      occurrences: [],
      deleted: false,
    };
    this.recurringSchedules.push(schedule);
    return this.toMockRecurringSchedule(schedule);
  }

  async updateRecurringSchedule(id: string, data: types.UpdateRecurringScheduleRequest): Promise<types.RecurringSchedule> {
    await delay(200);
    this.requireOfficeUser();
    const schedule = this.requireMockSchedule(id);
    if (data.status !== undefined && !(types.RECURRING_SCHEDULE_STATUSES as readonly string[]).includes(data.status)) {
      throw mockApiError(400, `status must be one of: ${types.RECURRING_SCHEDULE_STATUSES.join(', ')}`);
    }
    if (data.frequency || data.frequencyDetails || data.interval) {
      this.assertMockRecurrence(
        data.frequency ?? schedule.frequency,
        data.interval ?? schedule.interval,
        data.frequencyDetails ?? schedule.frequencyDetails
      );
    }
    const billingTouched = data.billingModel !== undefined || data.flatContractAmount !== undefined
      || data.billingFrequency !== undefined || data.billingInterval !== undefined;
    if (billingTouched) {
      const today = new Date().toISOString().slice(0, 10);
      const start = data.startDate ?? schedule.startDate;
      Object.assign(schedule, this.mockBillingTerms(data, schedule, start > today ? start : today));
    }
    // Only the fields the backend applies; anything else is ignored.
    if (data.status !== undefined) schedule.status = data.status;
    if (data.frequency !== undefined) schedule.frequency = data.frequency;
    if (data.interval !== undefined) schedule.interval = data.interval;
    if (data.frequencyDetails !== undefined) schedule.frequencyDetails = data.frequencyDetails;
    if (data.startDate !== undefined) schedule.startDate = data.startDate;
    if (data.endDate !== undefined) schedule.endDate = data.endDate || undefined;
    if (data.notes !== undefined) schedule.notes = data.notes;
    schedule.updatedAt = new Date().toISOString();
    return this.toMockRecurringSchedule(schedule);
  }

  async deleteRecurringSchedule(id: string): Promise<void> {
    await delay(200);
    this.requireOfficeUser();
    this.requireMockSchedule(id).deleted = true;
  }

  async generateRecurringJobs(id: string): Promise<types.RecurringGenerationResult> {
    await delay(300);
    this.requireOfficeUser();
    const schedule = this.requireMockSchedule(id);
    if (schedule.status !== 'ACTIVE') throw mockApiError(400, 'Schedule is not active');
    const template = this.jobs.find((j) => j.id === schedule.templateJobId && j.tenantId === schedule.tenantId);
    if (!template) throw mockApiError(400, 'Schedule has no template job');
    if (template.status !== types.JobStatus.DRAFT) throw mockApiError(400, 'Template job is no longer in DRAFT status');

    const today = new Date().toISOString().slice(0, 10);
    const existing = new Set(schedule.occurrences.map((o) => o.date));
    const skipped = new Set(schedule.skippedDates);
    const due = this.mockScheduleDates(schedule, 100).filter((date) => !skipped.has(date) && !existing.has(date) && date <= today);
    const result: types.RecurringGenerationResult = { scheduleId: schedule.id, generatedCount: 0, failedCount: 0, errors: [] };
    const generated: string[] = [];
    const failed: string[] = [];
    const property = this.properties.find((p) => p.id === schedule.propertyId);

    for (const date of due) {
      try {
        // CHART lines are re-priced from the current chart and property size;
        // a pricing failure fails the whole occurrence (nothing is written).
        const lines = template.lineItems.map((line) => {
          const item = this.items.find((i) => i.id === line.itemId);
          if (line.priceSource !== 'CHART' || !item || !item.priceChartId) return { ...line, id: generateId('li') };
          if (!property) throw new Error('Property not found');
          try {
            const quote = this.mockPriceItem(schedule.tenantId, item, property);
            const lineTotal = String(Number(line.quantity) * Number(quote.unitPrice));
            return {
              ...line,
              id: generateId('li'),
              unitPrice: quote.unitPrice,
              priceChartId: quote.chartId,
              sizeSnapshot: quote.size,
              sizeUnitSnapshot: quote.unit,
              lineTotal,
              lineAmount: String(Number(lineTotal) + Number(line.lineTax)),
            };
          } catch (err) {
            const data = (err as { response?: { data?: { code?: string; message?: string } } }).response?.data;
            throw new Error(`${item.name}: ${data?.code ?? 'PRICING_ERROR'} — ${data?.message ?? (err as Error).message}`);
          }
        });
        const now = new Date().toISOString();
        const job: types.Job & { tenantId: string } = {
          ...template,
          id: generateId('job'),
          status: types.JobStatus.SCHEDULED,
          scheduledDate: date,
          notes: `Auto-generated from recurring schedule ${schedule.id}: ${template.notes ?? ''}`,
          lineItems: lines,
          invoiceId: null,
          coveredByFlatContract: schedule.billingModel === 'FLAT_CONTRACT',
          createdAt: now,
          updatedAt: now,
        };
        this.updateMockJobTotals(job);
        this.jobs.push(job);
        schedule.occurrences.push({ date, jobId: job.id });
        generated.push(date);
        result.generatedCount += 1;
      } catch (err) {
        failed.push(date);
        result.failedCount += 1;
        result.errors!.push({ date, reason: (err as Error).message || 'Unknown error' });
      }
    }

    // Backend markersAfterRun: the marker never moves past a failed occurrence.
    const latest = [schedule.lastGeneratedDate, ...generated].filter((d): d is string => !!d).sort().pop();
    const earliestFailed = [...failed].sort()[0];
    const next = earliestFailed ?? (latest ? addDays(latest, 1) : schedule.nextScheduledDate);
    if (next && next !== schedule.nextScheduledDate) result.nextScheduledDate = next;
    schedule.lastGeneratedDate = latest;
    schedule.nextScheduledDate = next;
    return result;
  }

  async previewRecurringOccurrences(id: string, limit?: number): Promise<types.RecurringPreview> {
    await delay(150);
    const schedule = this.requireMockSchedule(id);
    const size = limit || 12;
    const existing = new Set(schedule.occurrences.map((o) => o.date));
    const skipped = new Set(schedule.skippedDates);
    const dates = this.mockScheduleDates(schedule, size + 1)
      .filter((date) => !schedule.nextScheduledDate || date >= schedule.nextScheduledDate)
      .slice(0, size);
    return {
      scheduleId: schedule.id,
      occurrences: dates.map((date) => ({
        date,
        willGenerate: !skipped.has(date) && !existing.has(date),
        reason: skipped.has(date) ? 'Date is skipped' : existing.has(date) ? 'Occurrence already exists' : undefined,
      })),
    };
  }

  async addRecurringSkippedDate(id: string, date: string): Promise<types.RecurringSchedule> {
    await delay(150);
    this.requireOfficeUser();
    if (!isDateOnly(date)) throw mockApiError(400, `Date must be in ISO format (YYYY-MM-DD): ${date}`);
    const schedule = this.requireMockSchedule(id);
    if (!schedule.skippedDates.includes(date)) schedule.skippedDates.push(date);
    return this.toMockRecurringSchedule(schedule);
  }

  async removeRecurringSkippedDate(id: string, date: string): Promise<types.RecurringSchedule> {
    await delay(150);
    this.requireOfficeUser();
    if (!isDateOnly(date)) throw mockApiError(400, `Date must be in ISO format (YYYY-MM-DD): ${date}`);
    const schedule = this.requireMockSchedule(id);
    schedule.skippedDates = schedule.skippedDates.filter((d) => d !== date);
    return this.toMockRecurringSchedule(schedule);
  }

  // ============ Audit log (read-only, ADMIN only) ============

  private requireAuditAdmin(): MockUser {
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return actor;
  }

  async listAuditLogs(
    page = 1,
    limit = 50,
    filters: types.AuditLogListFilters = {}
  ): Promise<types.PaginatedResponse<types.AuditLogEntry>> {
    await delay(200);
    const actor = this.requireAuditAdmin();
    const { actorUserId, action, targetUserId, startDate, endDate } = filters;
    if (action && !(types.AUDIT_ACTIONS as readonly string[]).includes(action)) {
      throw mockApiError(400, `Unknown action: ${action}`, 'INVALID_FILTER');
    }
    if ((startDate && !isDateOnly(startDate)) || (endDate && !isDateOnly(endDate))) {
      throw mockApiError(400, 'Dates must be valid YYYY-MM-DD dates', 'INVALID_FILTER');
    }
    if (startDate && endDate && startDate > endDate) {
      throw mockApiError(400, 'startDate must be on or before endDate', 'INVALID_FILTER');
    }
    const cappedLimit = Math.min(Math.max(1, limit), 100);
    const currentPage = Math.max(1, page);

    // createdAt is an ISO UTC timestamp, so its first 10 characters are the UTC day.
    const filtered = this.auditLogs
      .filter((row) => row.tenantId === actor.tenantId)
      .filter((row) => !actorUserId || row.actorUserId === actorUserId)
      .filter((row) => !action || row.action === action)
      .filter((row) => !targetUserId || row.targetUserId === targetUserId)
      .filter((row) => !startDate || row.createdAt.slice(0, 10) >= startDate)
      .filter((row) => !endDate || row.createdAt.slice(0, 10) <= endDate)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    const start = (currentPage - 1) * cappedLimit;

    return {
      success: true,
      data: filtered.slice(start, start + cappedLimit).map(({ tenantId, ...row }) => ({ ...row })),
      pagination: { page: currentPage, limit: cappedLimit, total: filtered.length, pages: Math.ceil(filtered.length / cappedLimit) },
    };
  }

  async listAuditLogActions(): Promise<string[]> {
    await delay(100);
    this.requireAuditAdmin();
    return [...types.AUDIT_ACTIONS];
  }

  // ============ Inventory (tracker item 30) ============
  // Same rules and messages as backend inventory-adjustments.service.ts, in
  // integer hundredths so quantities and values never drift.

  private requireInventoryOffice(): MockUser {
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN && actor.role !== types.UserRole.OFFICE_MANAGER) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  private parseStockAmount(value: unknown, field: string): number {
    const parsed = mockHundredths(value);
    if (parsed === null) throw mockApiError(400, `${field} must be a number with at most 2 decimal places`, 'VALIDATION_ERROR');
    return parsed;
  }

  private parseStockText(value: unknown, field: string, required: boolean): string | null {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) {
      if (required) throw mockApiError(400, `${field} is required`, 'VALIDATION_ERROR');
      return null;
    }
    if (trimmed.length > 500) throw mockApiError(400, `${field} must be at most 500 characters`, 'VALIDATION_ERROR');
    return trimmed;
  }

  /** An active PRODUCT item of the tenant (404 / 400 like the backend). */
  private stockItem(tenantId: string, itemId: string): MockItem {
    const item = this.items.find((i) => i.id === itemId && i.tenantId === tenantId && !i.deletedAt);
    if (!item) throw mockApiError(404, 'Item not found', 'NOT_FOUND');
    if (item.itemType !== types.ItemType.PRODUCT) {
      throw mockApiError(400, `Item "${item.name}" is a ${item.itemType} item; only PRODUCT items carry stock`, 'ITEM_NOT_INVENTORY_TRACKED');
    }
    return item;
  }

  private accountByNumber(tenantId: string, accountNumber: string, name: string): types.GLAccount {
    const account = this.glAccounts.find((a) => a.tenantId === tenantId && a.accountNumber === accountNumber);
    if (!account) throw mockApiError(400, `Required GL account (${accountNumber} ${name}) not found`, 'GL_ACCOUNT_MISSING');
    return account;
  }

  /** Post an inventory ADJUSTMENT entry (sourceId = the item id, like the backend). */
  private postInventoryEntry(
    actor: MockUser,
    itemId: string,
    entryDate: string,
    memo: string,
    debit: types.GLAccount,
    credit: types.GLAccount,
    valueCents: number
  ): MockJournalEntry {
    const id = generateId('je');
    const amount = centsToDecimal(valueCents);
    const entry: MockJournalEntry = {
      id,
      tenantId: actor.tenantId,
      entryDate,
      memo,
      sourceType: 'ADJUSTMENT',
      sourceId: itemId,
      createdBy: actor.id,
      createdByEmail: actor.email,
      createdAt: new Date().toISOString(),
      lines: [mockJournalLine(id, 1, debit, amount, '0.00'), mockJournalLine(id, 2, credit, '0.00', amount)],
    };
    this.journalEntries.push(entry);
    this.glAccounts = this.glAccounts.map((a) => (a.id === debit.id || a.id === credit.id ? { ...a, hasJournalLines: true } : a));
    return entry;
  }

  /** Apply the change, write the MANUAL row and build the response. */
  private recordStockChange(
    actor: MockUser,
    item: MockItem,
    changeHundredths: number,
    adjustmentDate: string,
    memo: string,
    entry: MockJournalEntry | null,
    valueCents: number
  ): types.InventoryChangeResponse {
    const onHand = decimalToCents(item.quantityOnHand) ?? 0;
    item.quantityOnHand = centsToDecimal(onHand + changeHundredths);
    item.updatedAt = new Date().toISOString();
    this.inventoryAdjustments.push({
      id: generateId('adj'),
      tenantId: actor.tenantId,
      itemId: item.id,
      itemName: item.name,
      adjustmentDate,
      qtyChange: centsToDecimal(changeHundredths),
      referenceType: 'MANUAL',
      referenceId: entry?.id ?? null,
      memo,
      createdAt: new Date().toISOString(),
    });
    return {
      itemId: item.id,
      itemName: item.name,
      qtyChange: centsToDecimal(changeHundredths),
      quantityOnHand: item.quantityOnHand,
      unitCost: item.unitCost,
      value: centsToDecimal(entry ? (changeHundredths < 0 ? -valueCents : valueCents) : 0),
      journalEntry: entry
        ? {
            id: entry.id,
            entryDate: entry.entryDate,
            memo: entry.memo ?? null,
            lines: entry.lines.map((line) => ({ glAccountId: line.glAccountId, debit: line.debit, credit: line.credit })),
          }
        : null,
    };
  }

  async receiveStock(data: types.ReceiveStockRequest): Promise<types.InventoryChangeResponse> {
    await delay(300);
    const actor = this.requireInventoryOffice();
    const quantity = this.parseStockAmount(data.quantity, 'quantity');
    if (quantity <= 0) throw mockApiError(400, 'quantity must be greater than 0', 'VALIDATION_ERROR');
    const hasCost = data.unitCost !== undefined && data.unitCost !== null && data.unitCost !== '';
    const requestCost = hasCost ? this.parseStockAmount(data.unitCost, 'unitCost') : null;
    if (requestCost !== null && requestCost < 0) throw mockApiError(400, 'unitCost must not be negative', 'VALIDATION_ERROR');
    if (data.updateItemCost && requestCost === null) throw mockApiError(400, 'updateItemCost requires a unitCost', 'VALIDATION_ERROR');
    if (!data.itemId) throw mockApiError(400, 'itemId is required', 'VALIDATION_ERROR');
    if (!data.offsetAccountId) throw mockApiError(400, 'offsetAccountId is required', 'VALIDATION_ERROR');
    if (!isDateOnly(data.adjustmentDate)) throw mockApiError(400, 'adjustmentDate is required as YYYY-MM-DD', 'VALIDATION_ERROR');
    const note = this.parseStockText(data.memo, 'memo', false);

    const item = this.stockItem(actor.tenantId, data.itemId);
    const offset = this.glAccounts.find((a) => a.id === data.offsetAccountId && a.tenantId === actor.tenantId);
    if (!offset) throw mockApiError(404, 'Offset account not found', 'NOT_FOUND');
    if (!offset.isPostable) throw mockApiError(400, `Offset account ${offset.accountNumber} is not postable`, 'ACCOUNT_NOT_POSTABLE');
    if (offset.accountNumber === '1300') {
      throw mockApiError(400, 'The offset account cannot be 1300 Inventory itself', 'INVALID_OFFSET_ACCOUNT');
    }
    const cost = requestCost ?? decimalToCents(item.unitCost);
    if (cost === null) {
      throw mockApiError(400, 'unitCost is required: the item has no unit cost to value the receipt at', 'UNIT_COST_REQUIRED');
    }
    if (cost <= 0) throw mockApiError(400, 'A receipt must be valued at a unitCost greater than 0', 'UNIT_COST_REQUIRED');
    const inventory = this.accountByNumber(actor.tenantId, '1300', 'Inventory - Materials');

    const base = `Stock received: ${centsToDecimal(quantity)} x ${item.name} @ ${centsToDecimal(cost)}`;
    const memo = note ? `${base} - ${note}` : base;
    const value = mockStockValueCents(quantity, cost);
    const entry = value > 0 ? this.postInventoryEntry(actor, item.id, data.adjustmentDate, memo, inventory, offset, value) : null;
    if (data.updateItemCost && requestCost !== null) item.unitCost = centsToDecimal(requestCost);
    return this.recordStockChange(actor, item, quantity, data.adjustmentDate, memo, entry, value);
  }

  async adjustStock(data: types.AdjustStockRequest): Promise<types.InventoryChangeResponse> {
    await delay(300);
    const actor = this.requireInventoryOffice();
    const hasNew = data.newQuantity !== undefined && data.newQuantity !== null && data.newQuantity !== '';
    const hasChange = data.quantityChange !== undefined && data.quantityChange !== null && data.quantityChange !== '';
    if (hasNew === hasChange) throw mockApiError(400, 'Provide exactly one of newQuantity or quantityChange', 'VALIDATION_ERROR');
    const newQuantity = hasNew ? this.parseStockAmount(data.newQuantity, 'newQuantity') : null;
    const quantityChange = hasChange ? this.parseStockAmount(data.quantityChange, 'quantityChange') : null;
    if (quantityChange === 0) throw mockApiError(400, 'quantityChange must not be 0', 'VALIDATION_ERROR');
    if (!data.itemId) throw mockApiError(400, 'itemId is required', 'VALIDATION_ERROR');
    const reason = this.parseStockText(data.reason, 'reason', true) as string;
    if (!isDateOnly(data.adjustmentDate)) throw mockApiError(400, 'adjustmentDate is required as YYYY-MM-DD', 'VALIDATION_ERROR');

    const item = this.stockItem(actor.tenantId, data.itemId);
    const onHand = decimalToCents(item.quantityOnHand) ?? 0;
    const change = newQuantity !== null ? newQuantity - onHand : (quantityChange as number);
    if (change === 0) {
      throw mockApiError(400, `The quantity on hand is already ${centsToDecimal(onHand)}; nothing to adjust`, 'NO_CHANGE');
    }
    const result = onHand + change;
    if (result < 0 && !data.allowNegative) {
      throw mockApiError(
        400,
        `The adjustment would leave ${centsToDecimal(result)} on hand; set allowNegative to allow a negative quantity`,
        'NEGATIVE_STOCK'
      );
    }
    const signed = change < 0 ? centsToDecimal(change) : `+${centsToDecimal(change)}`;
    const memo = `Count adjustment: ${item.name} ${signed} - ${reason}`;
    const cost = decimalToCents(item.unitCost);
    const value = cost ? mockStockValueCents(change, cost) : 0;
    let entry: MockJournalEntry | null = null;
    if (value > 0) {
      const inventory = this.accountByNumber(actor.tenantId, '1300', 'Inventory - Materials');
      const cogs = this.accountByNumber(actor.tenantId, '5000', 'Cost of Goods Sold');
      entry = change < 0
        ? this.postInventoryEntry(actor, item.id, data.adjustmentDate, memo, cogs, inventory, value)
        : this.postInventoryEntry(actor, item.id, data.adjustmentDate, memo, inventory, cogs, value);
    }
    return this.recordStockChange(actor, item, change, data.adjustmentDate, memo, entry, value);
  }

  async listInventoryAdjustments(
    page = 1,
    limit = 50,
    filters: types.InventoryAdjustmentListFilters = {}
  ): Promise<types.PaginatedResponse<types.InventoryAdjustment>> {
    await delay(200);
    const actor = this.currentMockUser();
    const { itemId } = filters;
    // Soft-deleted items keep their history.
    if (itemId && !this.items.some((i) => i.id === itemId && i.tenantId === actor.tenantId)) {
      throw mockApiError(404, 'Item not found', 'NOT_FOUND');
    }
    const cappedLimit = Math.min(Math.max(1, limit), 100);
    const currentPage = Math.max(1, page);
    const rows = this.inventoryAdjustments
      .filter((row) => row.tenantId === actor.tenantId && (!itemId || row.itemId === itemId))
      .sort((a, b) => b.adjustmentDate.localeCompare(a.adjustmentDate) || b.createdAt.localeCompare(a.createdAt));
    const start = (currentPage - 1) * cappedLimit;
    return {
      success: true,
      data: rows.slice(start, start + cappedLimit).map(({ tenantId, ...row }) => ({ ...row })),
      pagination: { page: currentPage, limit: cappedLimit, total: rows.length, pages: Math.ceil(rows.length / cappedLimit) },
    };
  }

  async getInventoryValuation(filters: types.InventoryValuationFilters = {}): Promise<types.InventoryValuation> {
    await delay(200);
    const actor = this.requireInventoryOffice();
    let totalCents = 0;
    let unvaluedItemCount = 0;
    const items = this.items
      .filter((i) => i.tenantId === actor.tenantId && i.itemType === types.ItemType.PRODUCT)
      .filter((i) => !i.deletedAt || (decimalToCents(i.quantityOnHand) ?? 0) !== 0)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((i): types.InventoryValuationItem => {
        const qty = decimalToCents(i.quantityOnHand) ?? 0;
        const cost = decimalToCents(i.unitCost);
        let extendedValue: string | null = null;
        if (cost !== null) {
          const cents = Math.sign(qty) * mockStockValueCents(qty, cost);
          totalCents += cents;
          extendedValue = centsToDecimal(cents);
        } else if (qty !== 0) {
          unvaluedItemCount += 1;
        }
        return {
          itemId: i.id,
          name: i.name,
          unitOfMeasure: i.unitOfMeasure,
          quantityOnHand: centsToDecimal(qty),
          unitCost: cost === null ? null : centsToDecimal(cost),
          extendedValue,
          deleted: !!i.deletedAt,
          reorderPoint: i.reorderPoint,
          lowStock: !i.deletedAt && this.mockLowStock(i),
        };
      });
    const lowStockCount = items.filter((row) => row.lowStock).length;
    const account = this.glAccounts.find((a) => a.tenantId === actor.tenantId && a.accountNumber === '1300');
    let balanceCents = 0;
    if (account) {
      this.journalEntries
        .filter((e) => e.tenantId === actor.tenantId)
        .forEach((e) => e.lines.filter((line) => line.glAccountId === account.id).forEach((line) => {
          balanceCents += (decimalToCents(line.debit) ?? 0) - (decimalToCents(line.credit) ?? 0);
        }));
    }
    return {
      // The filter narrows the rows only; totals and lowStockCount cover every item
      items: filters.lowStock ? items.filter((row) => row.lowStock) : items,
      totalValue: centsToDecimal(totalCents),
      unvaluedItemCount,
      glAccount: account
        ? { id: account.id, accountNumber: account.accountNumber, accountName: account.accountName, balance: centsToDecimal(balanceCents) }
        : null,
      difference: centsToDecimal(totalCents - balanceCents),
      lowStockCount,
    };
  }

  // ============ Notifications (tracker item 35) ============
  // A small in-memory model of /api/notifications: templates, a provider
  // connection (log-mode verify), the master switch and an outbox that never
  // sends (messages stay PENDING or SKIPPED, as with the worker off).

  private notificationTemplates = new Map<string, types.NotificationTemplate[]>();
  private notificationOutbox: Array<types.OutboxMessage & { tenantId: string }> = [];
  private notificationProviders = new Map<string, types.NotificationProviderConfig>();
  private notificationSettings = new Map<string, { enabled: boolean; fromName: string | null }>();
  private publicViewTokens = new Map<string, { tenantId: string; type: 'INVOICE' | 'STATEMENT'; id: string }>();

  private requireNotificationRole(adminOnly = false): MockUser {
    const actor = this.currentMockUser();
    const allowed = adminOnly ? [types.UserRole.ADMIN] : [types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER];
    if (!allowed.includes(actor.role)) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return actor;
  }

  private mockTemplates(tenantId: string): types.NotificationTemplate[] {
    let list = this.notificationTemplates.get(tenantId);
    if (!list) {
      const common = [{ name: 'customerName', description: 'Customer name' }, { name: 'companyName', description: 'Your company name' }];
      list = [
        {
          eventKey: 'INVOICE_SENT', channel: 'EMAIL', label: 'Invoice', transactional: true, isActive: true, updatedAt: null,
          description: 'Sent when the office emails an invoice. Contains a link to view it online.',
          variables: [...common, { name: 'invoiceNumber', description: 'Invoice number' }, { name: 'dueDate', description: 'Due date' }, { name: 'amountDue', description: 'Balance still due' }, { name: 'viewLink', description: 'Link to view the invoice online' }],
          subject: 'Invoice {{invoiceNumber}} from {{companyName}}',
          body: 'Hello {{customerName}},\n\nYour invoice {{invoiceNumber}} is ready.\n\nAmount due: {{amountDue}}\n\nView your invoice: {{viewLink}}',
        },
        {
          eventKey: 'STATEMENT_SENT', channel: 'EMAIL', label: 'Statement', transactional: true, isActive: true, updatedAt: null,
          description: 'Sent when the office emails a customer statement.',
          variables: [...common, { name: 'balanceDue', description: 'Account balance' }, { name: 'viewLink', description: 'Link to view the statement online' }],
          subject: 'Your statement from {{companyName}}',
          body: 'Hello {{customerName}},\n\nBalance due: {{balanceDue}}\n\nView your statement: {{viewLink}}',
        },
        {
          eventKey: 'PAYMENT_RECEIPT', channel: 'EMAIL', label: 'Payment receipt', transactional: true, isActive: false, updatedAt: null,
          description: 'Sent automatically when a payment is recorded (off until you turn it on).',
          variables: [...common, { name: 'paymentAmount', description: 'Payment amount' }, { name: 'paymentDate', description: 'Payment date' }],
          subject: 'Payment received - thank you',
          body: 'Hello {{customerName}},\n\nWe received your payment of {{paymentAmount}} on {{paymentDate}}.',
        },
        // Day-of customer messages (tracker item 43): off until turned on
        {
          eventKey: 'JOB_ON_MY_WAY', channel: 'EMAIL', label: 'On my way', transactional: false, isActive: false, updatedAt: null,
          description: 'Sent when the technician taps "On my way" on their phone, or the office clicks "Notify customer" on the job. Off until you turn it on.',
          variables: [...common, { name: 'technicianName', description: "The technician's first name" }, { name: 'propertyAddress', description: 'The address of the visit' }, { name: 'arrivalWindow', description: 'The arrival window' }, { name: 'arrivalWindowLine', description: 'A line with the arrival window (blank without one)' }],
          subject: '{{technicianName}} from {{companyName}} is on the way',
          body: 'Hello {{customerName}},\n\n{{technicianName}} from {{companyName}} is on the way to {{propertyAddress}}.\n{{arrivalWindowLine}}',
        },
        {
          eventKey: 'JOB_COMPLETED', channel: 'EMAIL', label: 'Job complete', transactional: false, isActive: false, updatedAt: null,
          description: 'Sent automatically once a job is completed; the office can send it again from the job. Off until you turn it on.',
          variables: [...common, { name: 'technicianName', description: "The technician's first name" }, { name: 'propertyAddress', description: 'The address of the visit' }, { name: 'serviceList', description: 'The work done' }],
          subject: 'Your service from {{companyName}} is complete',
          body: 'Hello {{customerName}},\n\n{{technicianName}} finished the visit at {{propertyAddress}}.\n\nWork done: {{serviceList}}',
        },
        // Portal rollout welcome (tracker item 81): on by default, only ever sent by a rollout campaign
        {
          eventKey: 'PORTAL_ROLLOUT_WELCOME', channel: 'EMAIL', label: 'Customer portal welcome', transactional: false, isActive: true, updatedAt: null,
          description: 'The "welcome to our new system" email a portal rollout campaign sends. It carries the set-password link (7 days, single use).',
          variables: [...common, { name: 'inviteLink', description: 'Link to set a password' }, { name: 'portalLink', description: 'The portal sign-in page' }, { name: 'expiresDays', description: 'Days the link works' }, { name: 'expiresDate', description: 'Date the link stops working' }],
          subject: 'Welcome to the new {{companyName}} customer portal',
          body: 'Hello {{customerName}},\n\n{{companyName}} has a new customer portal where you can see your invoices and jobs and pay online.\n\nSet your password here: {{inviteLink}}\n\nThe link works once and expires on {{expiresDate}}. After that, sign in at {{portalLink}}.',
        },
        // Customer satisfaction survey (tracker item 69): off until surveys are turned on
        {
          eventKey: 'SURVEY_REQUEST', channel: 'EMAIL', label: 'Customer survey', transactional: false, isActive: false, updatedAt: null,
          description: 'Asks the customer to rate a completed visit. Sent automatically a set time after a job is completed when surveys are turned on (Settings > Surveys), or by the office from the job. Contains a personal link.',
          variables: [...common, { name: 'jobDate', description: 'Date the job was completed' }, { name: 'serviceList', description: 'The work done' }, { name: 'technicianName', description: "The technician's first name" }, { name: 'surveyLink', description: 'Link to the survey page' }, { name: 'ratingLinks', description: 'Five lines, one per score, each opening the survey with that score selected' }],
          subject: 'How did we do, {{customerName}}?',
          body: 'Hello {{customerName}},\n\n{{technicianName}} from {{companyName}} completed your service on {{jobDate}} ({{serviceList}}).\n\nHow would you rate your visit? Choose a score:\n{{ratingLinks}}\n\nOr open the survey: {{surveyLink}}',
        },
        // Service bundle renewed (tracker item 85): off until a company turns it on
        {
          eventKey: 'ENROLLMENT_RENEWED', channel: 'EMAIL', label: 'Service bundle renewed', transactional: false, isActive: false, updatedAt: null,
          description: 'Sent to the customer when a renewal of their service bundle is approved: the visit dates and the program total.',
          variables: [...common, { name: 'bundleName', description: 'Name of the service bundle' }, { name: 'propertyAddress', description: 'Service address' }, { name: 'startDate', description: 'Date of the first visit (YYYY-MM-DD)' }, { name: 'visitCount', description: 'Number of visits' }, { name: 'visitList', description: 'The visits, one per line: date and service' }, { name: 'total', description: 'Program total including tax' }],
          subject: 'Your {{bundleName}} is renewed',
          body: 'Hello {{customerName}},\n\nYour {{bundleName}} at {{propertyAddress}} has been renewed with {{companyName}}. {{visitCount}} visits are scheduled, starting {{startDate}}:\n\n{{visitList}}\n\nProgram total: {{total}}\n\nThank you,\n{{companyName}}',
        },
      ];
      this.notificationTemplates.set(tenantId, list);
    }
    return list;
  }

  private mockTemplateCheck(template: types.NotificationTemplate, subject: string | null, body: string): void {
    const allowed = new Set(template.variables.map((v) => v.name));
    const used = [...`${subject ?? ''} ${body}`.matchAll(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g)].map((m) => m[1]);
    const unknown = used.filter((name) => !allowed.has(name));
    if (unknown.length) throw mockApiError(400, `Invalid template: Unknown variable {{${unknown[0]}}}`, 'VALIDATION_ERROR');
    if (subject !== null && /[\r\n]/.test(subject)) throw mockApiError(400, 'Invalid template: subject: Must be a single line', 'VALIDATION_ERROR');
    if (!body.trim()) throw mockApiError(400, 'Invalid template: body: Message body is required', 'VALIDATION_ERROR');
  }

  private mockRender(text: string, vars: Record<string, string>): string {
    const escape = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return text.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (_m, name: string) => escape(vars[name] ?? ''));
  }

  async listNotificationTemplates(): Promise<types.NotificationTemplate[]> {
    await delay(100);
    const actor = this.requireNotificationRole();
    return this.mockTemplates(actor.tenantId).map((t) => ({ ...t }));
  }

  async updateNotificationTemplate(eventKey: string, data: types.UpdateNotificationTemplateRequest): Promise<types.NotificationTemplate> {
    await delay(150);
    const actor = this.requireNotificationRole(true);
    const template = this.mockTemplates(actor.tenantId).find((t) => t.eventKey === eventKey);
    if (!template) throw mockApiError(404, 'Unknown notification event', 'NOT_FOUND');
    const subject = data.subject ?? template.subject;
    const body = data.body ?? template.body;
    this.mockTemplateCheck(template, subject, body);
    Object.assign(template, { subject, body, isActive: data.isActive ?? template.isActive, updatedAt: new Date().toISOString() });
    return { ...template };
  }

  async previewNotificationTemplate(eventKey: string, data: { subject?: string; body?: string }): Promise<types.NotificationTemplatePreview> {
    await delay(100);
    const actor = this.requireNotificationRole();
    const template = this.mockTemplates(actor.tenantId).find((t) => t.eventKey === eventKey);
    if (!template) throw mockApiError(404, 'Unknown notification event', 'NOT_FOUND');
    const subject = data.subject ?? template.subject;
    const body = data.body ?? template.body;
    this.mockTemplateCheck(template, subject, body);
    const sample: Record<string, string> = {
      customerName: 'Jane Smith', companyName: this.mockCompanySettings(actor.tenantId).companyName, invoiceNumber: '1042',
      dueDate: '2026-10-31', amountDue: '$125.00', balanceDue: '$310.50', paymentAmount: '$125.00', paymentDate: '2026-10-15',
      viewLink: 'https://example.com/view/invoice/sample',
    };
    const renderedSubject = subject === null ? null : this.mockRender(subject, sample);
    const renderedBody = this.mockRender(body, sample);
    // The HTML part escapes everything itself, so it starts from the unescaped text
    const rawBody = body.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (_m, name: string) => sample[name] ?? '');
    return { subject: renderedSubject, body: renderedBody, html: this.mockEmailHtml(renderedSubject, rawBody, sample.companyName) };
  }

  /** A simplified stand-in for the server's fixed HTML email layout (the server escapes and formats the same way). */
  private mockEmailHtml(subject: string | null, body: string, companyName: string): string {
    const escape = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const paragraphs = body
      .split(/\n\s*\n/)
      .filter((block) => block.trim())
      .map((block) => `<p style="margin:0 0 16px;font-size:16px;line-height:24px;color:#1f2937;">${block.split('\n').map(escape).join('<br>')}</p>`)
      .join('');
    return (
      `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${escape(subject ?? '')}</title></head>` +
      `<body style="margin:0;background:#f3f4f6;font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;">` +
      `<div style="max-width:600px;margin:0 auto;padding:32px 12px;">` +
      `<div style="background:#1e3a8a;color:#fff;font-size:20px;font-weight:700;padding:20px 32px;border-radius:12px 12px 0 0;">${escape(companyName)}</div>` +
      `<div style="background:#fff;border:1px solid #e5e7eb;border-radius:0 0 12px 12px;padding:32px;">${paragraphs}</div>` +
      `<p style="text-align:center;font-size:12px;color:#6b7280;">This message was sent by ${escape(companyName)}.</p>` +
      `</div></body></html>`
    );
  }

  async listOutbox(filters: types.OutboxListFilters = {}, page = 1, limit = 50): Promise<types.PaginatedResponse<types.OutboxMessage>> {
    await delay(100);
    const actor = this.requireNotificationRole();
    const cappedLimit = Math.min(Math.max(1, limit), 100);
    const rows = this.notificationOutbox
      .filter((m) => m.tenantId === actor.tenantId)
      .filter((m) => !filters.customerId || m.customerId === filters.customerId)
      .filter((m) => !filters.status || m.status === filters.status)
      .filter((m) => !filters.eventKey || m.eventKey === filters.eventKey)
      .filter((m) => !filters.sourceType || m.sourceType === filters.sourceType)
      .filter((m) => !filters.sourceId || m.sourceId === filters.sourceId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const start = (Math.max(1, page) - 1) * cappedLimit;
    return {
      success: true,
      data: rows.slice(start, start + cappedLimit).map(({ tenantId, ...m }) => ({ ...m })),
      pagination: { page, limit: cappedLimit, total: rows.length, pages: Math.max(1, Math.ceil(rows.length / cappedLimit)) },
    };
  }

  private mockOutboxTransition(id: string, from: types.OutboxStatus, apply: (m: types.OutboxMessage) => void): types.OutboxMessage {
    const actor = this.requireNotificationRole();
    const message = this.notificationOutbox.find((m) => m.id === id && m.tenantId === actor.tenantId);
    if (!message) throw mockApiError(404, 'Message not found', 'NOT_FOUND');
    if (message.status !== from) throw mockApiError(409, `Only ${from} messages can be changed (this one is ${message.status})`, 'INVALID_STATUS');
    apply(message);
    const { tenantId, ...rest } = message;
    return { ...rest };
  }

  async retryOutboxMessage(id: string): Promise<types.OutboxMessage> {
    await delay(100);
    return this.mockOutboxTransition(id, 'FAILED', (m) => Object.assign(m, { status: 'PENDING', attempts: 0, lastError: null }));
  }

  async cancelOutboxMessage(id: string): Promise<types.OutboxMessage> {
    await delay(100);
    return this.mockOutboxTransition(id, 'PENDING', (m) =>
      Object.assign(m, { status: 'SKIPPED', skipReason: 'CANCELLED', skipReasonText: 'Cancelled before sending' })
    );
  }

  private mockQueue(
    actor: MockUser,
    input: { eventKey: string; customerId: string | null; toAddress: string | null; subject: string; body: string; sourceType: string; sourceId: string | null; bypassSwitch?: boolean; dedupeKey: string }
  ): types.NotificationSendResult {
    const existing = this.notificationOutbox.find((m) => m.tenantId === actor.tenantId && (m as { dedupeKey?: string }).dedupeKey === input.dedupeKey);
    if (existing) {
      const { tenantId, ...rest } = existing;
      return { message: { ...rest }, deduplicated: true };
    }
    const settings = this.notificationSettings.get(actor.tenantId);
    const provider = this.notificationProviders.get(actor.tenantId);
    const skip: [string, string] | null =
      !input.bypassSwitch && !settings?.enabled ? ['NOTIFICATIONS_DISABLED', 'Notifications are turned off for this company']
        : provider?.status !== 'VERIFIED' ? ['NOT_CONFIGURED', 'No verified sending connection']
          : !input.toAddress ? ['NO_EMAIL', 'The customer has no email address'] : null;
    const now = new Date().toISOString();
    const message = {
      id: generateId('msg'), tenantId: actor.tenantId, customerId: input.customerId,
      customerName: input.customerId ? this.customers.find((c) => c.id === input.customerId)?.name ?? null : null,
      eventKey: input.eventKey, channel: 'EMAIL', toAddress: input.toAddress, subject: input.subject, body: input.body,
      status: (skip ? 'SKIPPED' : 'PENDING') as types.OutboxStatus, skipReason: skip?.[0] ?? null, skipReasonText: skip?.[1] ?? null,
      attempts: 0, nextAttemptAt: now, lastError: null, sentAt: null, deliveryStatus: null, deliveryUpdatedAt: null,
      sourceType: input.sourceType, sourceId: input.sourceId, createdBy: actor.id, createdAt: now, dedupeKey: input.dedupeKey,
    };
    this.notificationOutbox.push(message);
    const { tenantId, dedupeKey, ...rest } = message;
    return { message: rest, deduplicated: false };
  }

  private mockViewToken(tenantId: string, type: 'INVOICE' | 'STATEMENT', id: string): string {
    const token = `${generateId('tok')}${'x'.repeat(43)}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 43);
    this.publicViewTokens.set(token, { tenantId, type, id });
    return token;
  }

  async emailInvoice(invoiceId: string, idempotencyKey?: string): Promise<types.NotificationSendResult> {
    await delay(200);
    const actor = this.requireNotificationRole();
    const invoice = this.invoices.find((i) => i.id === invoiceId && i.tenantId === actor.tenantId);
    if (!invoice) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    if (invoice.status === types.InvoiceStatus.DRAFT || invoice.status === types.InvoiceStatus.VOID) {
      throw mockApiError(400, `Only sent invoices can be emailed (this invoice is ${invoice.status})`, 'INVOICE_NOT_SENDABLE');
    }
    const customer = this.customers.find((c) => c.id === invoice.billingCustomerId);
    const dedupeKey = `INVOICE_SENT:${invoiceId}:${idempotencyKey ?? Math.floor(Date.now() / 60000)}`;
    const token = this.mockViewToken(actor.tenantId, 'INVOICE', invoiceId);
    const template = this.mockTemplates(actor.tenantId)[0];
    const vars = { customerName: customer?.name ?? '', companyName: this.mockCompanySettings(actor.tenantId).companyName, invoiceNumber: invoice.invoiceNumber, amountDue: `$${invoice.total}`, viewLink: `${window.location.origin}/view/invoice/${token}` };
    return this.mockQueue(actor, {
      eventKey: 'INVOICE_SENT', customerId: invoice.billingCustomerId, toAddress: customer?.email || null,
      subject: this.mockRender(template.subject ?? '', vars), body: this.mockRender(template.body, vars),
      sourceType: 'INVOICE', sourceId: invoiceId, dedupeKey,
    });
  }

  async emailStatement(customerId: string, idempotencyKey?: string): Promise<types.NotificationSendResult> {
    await delay(200);
    const actor = this.requireNotificationRole();
    const customer = this.customers.find((c) => c.id === customerId && c.tenantId === actor.tenantId);
    if (!customer) throw mockApiError(404, 'Customer not found', 'NOT_FOUND');
    const token = this.mockViewToken(actor.tenantId, 'STATEMENT', customerId);
    const template = this.mockTemplates(actor.tenantId)[1];
    const vars = { customerName: customer.name, companyName: this.mockCompanySettings(actor.tenantId).companyName, balanceDue: '$0.00', viewLink: `${window.location.origin}/view/statement/${token}` };
    return this.mockQueue(actor, {
      eventKey: 'STATEMENT_SENT', customerId, toAddress: customer.email || null,
      subject: this.mockRender(template.subject ?? '', vars), body: this.mockRender(template.body, vars),
      sourceType: 'STATEMENT', sourceId: customerId, dedupeKey: `STATEMENT_SENT:${customerId}:${idempotencyKey ?? Math.floor(Date.now() / 60000)}`,
    });
  }

  async sendTestNotification(to?: string): Promise<types.NotificationSendResult> {
    await delay(150);
    const actor = this.requireNotificationRole(true);
    const address = (to ?? '').trim() || actor.email;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) throw mockApiError(400, 'to: Enter a valid email address', 'VALIDATION_ERROR');
    return this.mockQueue(actor, {
      eventKey: 'TEST', customerId: null, toAddress: address, subject: '[Test] Test email',
      body: 'This is a TEST message. If you received it, the email connection is working.', sourceType: 'TEST', sourceId: null, bypassSwitch: true,
      dedupeKey: `TEST:${actor.id}:${address.toLowerCase()}:${Math.floor(Date.now() / 60000)}`,
    });
  }

  async sendTestNotificationTemplate(eventKey: string, data: { subject?: string; body?: string; to?: string }): Promise<types.NotificationSendResult> {
    const actor = this.requireNotificationRole(true);
    const address = (data.to ?? '').trim() || actor.email;
    if (!this.mockTemplates(actor.tenantId).some((t) => t.eventKey === eventKey)) throw mockApiError(404, 'Unknown notification event', 'NOT_FOUND');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) throw mockApiError(400, 'to: Enter a valid email address', 'VALIDATION_ERROR');
    // Same validation and sample-data render as the preview
    const rendered = await this.previewNotificationTemplate(eventKey, { subject: data.subject, body: data.body });
    await delay(50);
    const subject = `[Test] ${rendered.subject ?? ''}`.slice(0, 500);
    let hash = 0;
    for (const ch of `${subject}\n${rendered.body}`) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return this.mockQueue(actor, {
      eventKey: 'TEST', customerId: null, toAddress: address, subject, body: rendered.body,
      sourceType: 'TEST', sourceId: eventKey, bypassSwitch: true,
      dedupeKey: `TEST:${eventKey}:${hash.toString(16)}:${actor.id}:${address.toLowerCase()}:${Math.floor(Date.now() / 60000)}`,
    });
  }

  // ---- Day-of customer messages on a job (tracker item 43) ----

  /** Why a day-of message cannot be sent for this job by this user now, or null. */
  private mockDayOfProblem(actor: MockUser, job: types.Job, eventKey: types.DayOfEventKey): { status: number; code: string; message: string } | null {
    if (actor.role === types.UserRole.TECHNICIAN && eventKey !== 'JOB_ON_MY_WAY') {
      return { status: 403, code: 'FORBIDDEN', message: 'Only the office can send this message' };
    }
    const active = [types.JobStatus.SCHEDULED, types.JobStatus.WORK_ORDER_GENERATED, types.JobStatus.IN_PROGRESS];
    const done = [types.JobStatus.COMPLETED, types.JobStatus.INVOICED];
    if (eventKey === 'JOB_ON_MY_WAY' && !active.includes(job.status)) {
      return { status: 409, code: 'JOB_NOT_ACTIVE', message: `"On my way" cannot be sent for a ${job.status} job` };
    }
    if (eventKey === 'JOB_COMPLETED' && !done.includes(job.status)) {
      return { status: 409, code: 'JOB_NOT_COMPLETED', message: `"Job complete" can only be sent once the job is completed (this job is ${job.status})` };
    }
    return null;
  }

  private mockDayOfEnabled(tenantId: string, eventKey: string): { notificationsEnabled: boolean; eventActive: boolean } {
    return {
      notificationsEnabled: !!this.notificationSettings.get(tenantId)?.enabled,
      eventActive: !!this.mockTemplates(tenantId).find((t) => t.eventKey === eventKey)?.isActive,
    };
  }

  private mockDayOfSummary(m: types.OutboxMessage, technician: boolean): types.DayOfMessageSummary {
    return {
      id: m.id, eventKey: m.eventKey, status: m.status, skipReason: m.skipReason, skipReasonText: m.skipReasonText,
      toAddress: technician ? null : m.toAddress, createdAt: m.createdAt, sentAt: m.sentAt,
    };
  }

  async getJobCustomerNotifications(jobId: string): Promise<types.JobCustomerNotifications> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const job = this.technicianJob(actor, jobId);
    const technician = actor.role === types.UserRole.TECHNICIAN;
    const messages = this.notificationOutbox
      .filter((m) => m.tenantId === actor.tenantId && m.sourceType === 'JOB' && m.sourceId === job.id)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 20)
      .map(({ tenantId: _t, ...m }) => this.mockDayOfSummary(m, technician));
    const customer = this.customers.find((c) => c.id === job.customerId);
    const keys: types.DayOfEventKey[] = ['JOB_ON_MY_WAY', 'JOB_COMPLETED'];
    const events = keys.map((eventKey): types.DayOfEventState => {
      const status = this.mockDayOfEnabled(actor.tenantId, eventKey);
      const enabled = status.notificationsEnabled && status.eventActive;
      const problem = this.mockDayOfProblem(actor, job, eventKey)?.message
        ?? (enabled ? null : status.notificationsEnabled ? 'This message type is turned off (Settings > Notifications)' : 'Notifications are turned off for this company (Settings > Notifications)');
      const wouldSkip: [string, string] | null = !enabled ? null
        : !customer?.email ? ['NO_EMAIL', 'The customer has no email address']
          : customer.notificationOptOut ? ['OPTED_OUT', 'The customer opted out of notifications'] : null;
      return {
        eventKey, label: eventKey === 'JOB_ON_MY_WAY' ? 'On my way' : 'Job complete', enabled, canSend: problem === null,
        unavailableReason: problem, wouldSkip: wouldSkip?.[0] ?? null, wouldSkipText: wouldSkip?.[1] ?? null,
        lastMessage: messages.find((m) => m.eventKey === eventKey) ?? null,
      };
    });
    return { jobId: job.id, notificationsEnabled: !!this.notificationSettings.get(actor.tenantId)?.enabled, events, messages: technician ? null : messages };
  }

  async sendJobCustomerNotification(jobId: string, eventKey: types.DayOfEventKey, idempotencyKey?: string): Promise<types.SendJobCustomerNotificationResult> {
    await delay(150);
    const actor = this.requireTechnicianStaff();
    if (eventKey !== 'JOB_ON_MY_WAY' && eventKey !== 'JOB_COMPLETED') {
      throw mockApiError(400, 'eventKey: Must be one of JOB_ON_MY_WAY, JOB_COMPLETED', 'VALIDATION_ERROR');
    }
    const job = this.technicianJob(actor, jobId);
    const problem = this.mockDayOfProblem(actor, job, eventKey);
    if (problem) throw mockApiError(problem.status, problem.message, problem.code);
    const status = this.mockDayOfEnabled(actor.tenantId, eventKey);
    if (!status.notificationsEnabled || !status.eventActive) {
      throw mockApiError(409, status.notificationsEnabled ? 'This message type is turned off (Settings > Notifications)' : 'Notifications are turned off for this company (Settings > Notifications)', 'NOTIFICATION_OFF');
    }
    const technician = actor.role === types.UserRole.TECHNICIAN;
    const customer = this.customers.find((c) => c.id === job.customerId);
    const techUser = this.users.find((u) => u.id === (technician ? actor.id : job.assignedTo)) as MockUser | undefined;
    const template = this.mockTemplates(actor.tenantId).find((t) => t.eventKey === eventKey)!;
    const window = job.scheduledStartTime && job.arrivalWindowMinutes ? formatArrivalWindow(job.scheduledStartTime, job.arrivalWindowMinutes) : '';
    const vars: Record<string, string> = {
      customerName: customer?.name ?? '', companyName: this.mockCompanySettings(actor.tenantId).companyName,
      technicianName: techUser?.firstName?.trim() || 'Your technician',
      propertyAddress: [job.property.address, job.property.city, job.property.state].filter(Boolean).join(', '),
      arrivalWindow: window, arrivalWindowLine: window ? `Your arrival window: ${window}` : '',
      serviceList: [...new Set(job.lineItems.map((l) => l.item.name))].join(', ') || 'Your scheduled service',
    };
    const dedupeKey = technician
      ? `${eventKey}:${job.id}`
      : `${eventKey}:${job.id}:office:${idempotencyKey ? `k:${idempotencyKey}` : `w:${Math.floor(Date.now() / 60000)}`}`;
    const result = this.mockQueue(actor, {
      eventKey, customerId: job.customerId, toAddress: customer?.email || null,
      subject: this.mockRender(template.subject ?? '', vars), body: this.mockRender(template.body, vars),
      sourceType: 'JOB', sourceId: job.id, dedupeKey,
    });
    if (!result.deduplicated && result.message.status === 'PENDING' && customer?.notificationOptOut) {
      const stored = this.notificationOutbox.find((m) => m.id === result.message.id);
      if (stored) Object.assign(stored, { status: 'SKIPPED', skipReason: 'OPTED_OUT', skipReasonText: 'The customer opted out of notifications' });
      Object.assign(result.message, { status: 'SKIPPED', skipReason: 'OPTED_OUT', skipReasonText: 'The customer opted out of notifications' });
    }
    return { message: this.mockDayOfSummary(result.message, technician), deduplicated: result.deduplicated };
  }

  async listNotificationProviders(): Promise<types.NotificationProviderDescriptor[]> {
    await delay(100);
    this.requireNotificationRole(true);
    return [{
      id: 'MAILGUN', channel: 'EMAIL', displayName: 'Mailgun',
      setupHelp: 'In Mailgun, verify your sending domain (SPF and DKIM), then create an API key.',
      configSchema: [
        { key: 'domain', label: 'Sending domain', type: 'text', required: true, secret: false, help: 'e.g. mg.example.com', options: null },
        { key: 'region', label: 'Region', type: 'select', required: true, secret: false, help: null, options: [{ value: 'US', label: 'US' }, { value: 'EU', label: 'EU' }] },
        { key: 'fromAddress', label: 'From address', type: 'email', required: true, secret: false, help: 'An address on the sending domain', options: null },
        { key: 'apiKey', label: 'API key', type: 'password', required: true, secret: true, help: null, options: null },
        { key: 'webhookSigningKey', label: 'Webhook signing key', type: 'password', required: false, secret: true, help: 'Optional', options: null },
      ],
    }];
  }

  async getNotificationProvider(): Promise<types.NotificationProviderConfig | null> {
    await delay(100);
    const actor = this.requireNotificationRole(true);
    const config = this.notificationProviders.get(actor.tenantId);
    return config ? { ...config, settings: { ...config.settings } } : null;
  }

  async saveNotificationProvider(data: types.SaveNotificationProviderRequest): Promise<types.NotificationProviderConfig> {
    await delay(200);
    const actor = this.requireNotificationRole(true);
    const existing = this.notificationProviders.get(actor.tenantId);
    const apiKey = data.credentials?.apiKey;
    if (!apiKey && !existing?.hasCredentials) throw mockApiError(400, 'Invalid provider settings: apiKey: API key is required', 'VALIDATION_ERROR');
    const config: types.NotificationProviderConfig = {
      channel: 'EMAIL', provider: data.provider, settings: { ...data.settings },
      credentialsHint: apiKey ? `****${apiKey.slice(-4)}` : existing?.credentialsHint ?? null,
      hasCredentials: true, status: 'UNVERIFIED', lastVerifiedAt: null, lastError: null, updatedAt: new Date().toISOString(),
    };
    this.notificationProviders.set(actor.tenantId, config);
    return { ...config };
  }

  async verifyNotificationProvider(): Promise<types.VerifyNotificationProviderResult> {
    await delay(200);
    const actor = this.requireNotificationRole(true);
    const config = this.notificationProviders.get(actor.tenantId);
    if (!config) throw mockApiError(400, 'No sending connection is configured yet', 'NOT_CONFIGURED');
    Object.assign(config, { status: 'VERIFIED', lastVerifiedAt: new Date().toISOString(), lastError: null });
    return { config: { ...config }, ok: true, message: 'Log delivery mode: messages are written to the server log, not sent.' };
  }

  async getNotificationSettings(): Promise<types.NotificationSettings> {
    await delay(100);
    const actor = this.requireNotificationRole();
    const s = this.notificationSettings.get(actor.tenantId) ?? { enabled: false, fromName: null };
    const company = this.mockCompanySettings(actor.tenantId);
    return {
      enabled: s.enabled, fromName: s.fromName, effectiveFromName: s.fromName || company.companyName, replyTo: company.email,
      providerStatus: this.notificationProviders.get(actor.tenantId)?.status ?? null, deliveryMode: 'log',
    };
  }

  async updateNotificationSettings(data: types.UpdateNotificationSettingsRequest): Promise<types.NotificationSettings> {
    await delay(150);
    const actor = this.requireNotificationRole(true);
    const current = this.notificationSettings.get(actor.tenantId) ?? { enabled: false, fromName: null };
    if (data.enabled && this.notificationProviders.get(actor.tenantId)?.status !== 'VERIFIED') {
      throw mockApiError(400, 'Verify the sending connection before turning notifications on', 'PROVIDER_NOT_VERIFIED');
    }
    this.notificationSettings.set(actor.tenantId, {
      enabled: data.enabled ?? current.enabled,
      fromName: data.fromName === undefined ? current.fromName : data.fromName?.trim() || null,
    });
    return this.getNotificationSettings();
  }

  private mockLetterhead(tenantId: string): types.PublicLetterhead {
    const c = this.mockCompanySettings(tenantId);
    return {
      companyName: c.companyName, logoUrl: c.logoUrl, addressLine1: c.addressLine1, addressLine2: c.addressLine2, city: c.city,
      state: c.state, postalCode: c.postalCode, phone: c.phone, email: c.email, website: c.website, footerNote: c.invoiceFooterNote,
    };
  }

  async getPublicInvoiceView(token: string): Promise<types.PublicInvoiceView> {
    await delay(100);
    const entry = this.publicViewTokens.get(token);
    const invoice = entry?.type === 'INVOICE' ? this.invoices.find((i) => i.id === entry.id && i.tenantId === entry.tenantId) : undefined;
    if (!entry || !invoice || invoice.status === types.InvoiceStatus.VOID) throw mockApiError(404, 'Not found', 'NOT_FOUND');
    return {
      company: this.mockLetterhead(entry.tenantId),
      customerName: this.customers.find((c) => c.id === invoice.billingCustomerId)?.name ?? '',
      invoiceNumber: invoice.invoiceNumber, invoiceDate: invoice.invoiceDate, dueDate: invoice.dueDate,
      periodStartDate: invoice.periodStartDate ?? null, periodEndDate: invoice.periodEndDate ?? null, status: invoice.status,
      lines: invoice.lineItems.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, amount: l.lineAmount, discount: l.lineDiscount ?? '0.00', tax: l.lineTax })),
      subtotal: invoice.subtotal ?? invoice.totalAmount, totalDiscount: invoice.totalDiscount ?? '0.00', totalTax: invoice.totalTax,
      total: invoice.total, amountPaid: '0.00', balanceDue: invoice.total,
    };
  }

  async getPublicStatementView(token: string): Promise<types.PublicStatementView> {
    await delay(100);
    const entry = this.publicViewTokens.get(token);
    const customer = entry?.type === 'STATEMENT' ? this.customers.find((c) => c.id === entry.id && c.tenantId === entry.tenantId) : undefined;
    if (!entry || !customer) throw mockApiError(404, 'Not found', 'NOT_FOUND');
    return { company: this.mockLetterhead(entry.tenantId), customerName: customer.name, statementDate: new Date().toISOString().slice(0, 10), closingBalance: '0.00', lines: [] };
  }

  // ============ Online payments (tracker item 38) ============
  // A small in-memory model of /api/online-payments with the test processor
  // ("FAKE"): connecting succeeds at once, card tokens decide the outcome
  // (fake-tok-ok-*, fake-tok-decline-*, fake-tok-insufficient-*,
  // fake-tok-error-* = no answer), charges become CC payments. No money moves.

  private onlineConnections = new Map<string, types.ProcessorConnection>();
  private onlineSettings = new Map<string, { enabled: boolean; minAmount: string; autopayRetryDays: number[]; autopayMaxFailures: number }>();
  private onlineMethods: Array<types.CustomerPaymentMethod & { tenantId: string }> = [];
  private onlineAttempts: Array<types.OnlinePaymentAttempt & { tenantId: string; key: string }> = [];

  private requireOnlineRole(adminOnly = false): MockUser {
    return this.requireNotificationRole(adminOnly);
  }

  private mockOnlineSettings(tenantId: string) {
    let s = this.onlineSettings.get(tenantId);
    if (!s) {
      s = { enabled: false, minAmount: '1.00', autopayRetryDays: [0, 3, 7], autopayMaxFailures: 3 };
      this.onlineSettings.set(tenantId, s);
    }
    return s;
  }

  private mockConnection(tenantId: string): types.ProcessorConnection {
    return (
      this.onlineConnections.get(tenantId) ?? {
        connected: false, provider: null, providerName: null, environment: null, status: null, accountId: null, accountName: null,
        locationId: null, locationName: null, locations: [], accessTokenExpiresAt: null, lastRefreshedAt: null, lastError: null,
        connectedAt: null, clientConfig: null, availableProvider: { id: 'FAKE', name: 'Test processor', environment: 'SANDBOX' },
      }
    );
  }

  async getProcessorConnection(): Promise<types.ProcessorConnection> {
    await delay(100);
    return { ...this.mockConnection(this.requireOnlineRole().tenantId) };
  }

  async startProcessorConnect(): Promise<{ url: string; provider: string }> {
    await delay(150);
    const actor = this.requireOnlineRole(true);
    this.onlineConnections.set(actor.tenantId, {
      ...this.mockConnection(actor.tenantId),
      connected: true, provider: 'FAKE', providerName: 'Test processor', environment: 'SANDBOX', status: 'CONNECTED',
      accountId: `FAKE-ACCT-${actor.tenantId}`, accountName: 'Mock merchant', locationId: 'FKLOC-1', locationName: 'Main office',
      locations: [{ id: 'FKLOC-1', name: 'Main office' }], connectedAt: new Date().toISOString(),
      accessTokenExpiresAt: new Date(Date.now() + 30 * 86400000).toISOString(),
      clientConfig: { provider: 'FAKE', environment: 'SANDBOX', siteId: 'FKLOC-1' },
    });
    return { url: '/settings/payments?connect=success', provider: 'FAKE' };
  }

  async chooseProcessorLocation(locationId: string): Promise<types.ProcessorConnection> {
    await delay(100);
    const actor = this.requireOnlineRole(true);
    const conn = this.mockConnection(actor.tenantId);
    const location = conn.locations.find((l) => l.id === locationId);
    if (!conn.connected || !location) throw mockApiError(400, 'locationId: not one of the account locations', 'VALIDATION_ERROR');
    Object.assign(conn, { locationId: location.id, locationName: location.name });
    return { ...conn };
  }

  async disconnectProcessor(): Promise<types.ProcessorConnection & { revokedAtProcessor: boolean }> {
    await delay(150);
    const actor = this.requireOnlineRole(true);
    const conn = this.onlineConnections.get(actor.tenantId);
    if (!conn || !conn.connected) throw mockApiError(409, 'No payment processor is connected', 'NOT_CONNECTED');
    Object.assign(conn, { connected: false, status: 'REVOKED', clientConfig: null });
    this.mockOnlineSettings(actor.tenantId).enabled = false;
    this.onlineMethods.filter((m) => m.tenantId === actor.tenantId).forEach((m) => { m.autopayEnabled = false; });
    return { ...conn, revokedAtProcessor: true };
  }

  async getOnlinePaymentSettings(): Promise<types.OnlinePaymentSettings> {
    await delay(100);
    const tenantId = this.requireOnlineRole().tenantId;
    const s = this.mockOnlineSettings(tenantId);
    return {
      ...s, connectionStatus: this.mockConnection(tenantId).status, autopayWorkerEnabled: false, autopayDryRun: false,
      autopayConsent: { version: 'v1', text: this.mockConsentText(tenantId) },
    };
  }

  async updateOnlinePaymentSettings(data: types.UpdateOnlinePaymentSettingsRequest): Promise<types.OnlinePaymentSettings> {
    await delay(150);
    const actor = this.requireOnlineRole(true);
    const s = this.mockOnlineSettings(actor.tenantId);
    if (data.enabled && this.mockConnection(actor.tenantId).status !== 'CONNECTED') {
      throw mockApiError(409, 'Online payments cannot be turned on: no payment processor is connected', 'NOT_CONNECTED');
    }
    if (data.minAmount !== undefined && !(Number(data.minAmount) >= 0.01)) throw mockApiError(400, 'minAmount: 0.01 or more', 'VALIDATION_ERROR');
    Object.assign(s, {
      enabled: data.enabled ?? s.enabled,
      minAmount: data.minAmount !== undefined ? Number(data.minAmount).toFixed(2) : s.minAmount,
      autopayRetryDays: data.autopayRetryDays ?? s.autopayRetryDays,
      autopayMaxFailures: data.autopayMaxFailures ?? s.autopayMaxFailures,
    });
    return this.getOnlinePaymentSettings();
  }

  private mockInvoiceBalance(invoice: types.Invoice): number {
    const paid = this.payments
      .filter((p) => p.status === 'ACTIVE')
      .flatMap((p) => p.applications)
      .filter((a) => a.invoiceId === invoice.id)
      .reduce((sum, a) => sum + Number(a.amountApplied), 0);
    return Math.max(0, Math.round((Number(invoice.total) - paid) * 100) / 100);
  }

  private mockTokenOutcome(token: string): 'ok' | 'decline' | 'insufficient' | 'error' {
    const m = /^fake-tok-(ok|decline|insufficient|error)/.exec(token);
    return (m?.[1] as 'ok' | 'decline' | 'insufficient' | 'error') ?? 'decline';
  }

  /** One charge: an attempt, and on success a CC payment applied to the invoice. */
  private mockOnlineCharge(
    tenantId: string,
    invoice: types.Invoice,
    amount: number,
    source: types.OnlinePaymentSource,
    outcome: 'ok' | 'decline' | 'insufficient' | 'error',
    key: string,
    methodId: string | null,
    createdBy: string | null
  ): types.OnlinePaymentAttempt & { tenantId: string; key: string } {
    const existing = this.onlineAttempts.find((a) => a.tenantId === tenantId && a.key === key);
    if (existing) return existing;
    const now = new Date().toISOString();
    const customer = this.customers.find((c) => c.id === invoice.billingCustomerId);
    const attempt: types.OnlinePaymentAttempt & { tenantId: string; key: string } = {
      id: generateId('attempt'), tenantId, key, invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber,
      customerId: invoice.billingCustomerId, customerName: customer?.name ?? '', paymentMethodId: methodId, provider: 'FAKE',
      amount: amount.toFixed(2), source, status: 'PENDING', processorPaymentId: null, paymentId: null, paymentStatus: null,
      paymentProcessorStatus: null, processingFee: null, receiptUrl: null, cardBrand: 'FAKECARD', cardLast4: '4242',
      failureCode: null, failureMessage: null, attemptNo: null, needsReview: false, reviewNote: null, needsAttention: false,
      createdBy, createdAt: now, updatedAt: now,
    };
    this.onlineAttempts.unshift(attempt);
    if (outcome === 'error') {
      Object.assign(attempt, {
        status: 'ERROR', failureCode: 'PROVIDER_ERROR', needsAttention: true,
        failureMessage: 'No answer from the payment processor: the card may have been charged. Its webhook records it; check before charging again.',
      });
      return attempt;
    }
    if (outcome !== 'ok') {
      Object.assign(attempt, {
        status: 'DECLINED',
        failureCode: outcome === 'insufficient' ? 'INSUFFICIENT_FUNDS' : 'CARD_DECLINED',
        failureMessage: outcome === 'insufficient' ? 'The card has insufficient funds.' : 'The card was declined.',
      });
      return attempt;
    }
    const remaining = this.mockInvoiceBalance(invoice);
    const applied = Math.min(remaining, amount);
    invoice.status = applied >= remaining ? types.InvoiceStatus.PAID : types.InvoiceStatus.PARTIAL;
    const processorPaymentId = `FK-PAY-${attempt.id.slice(-6)}`;
    const fee = (Math.round((amount * 0.029 + 0.3) * 100) / 100).toFixed(2);
    const payment: types.Payment = {
      id: generateId('pay'), tenantId, billingCustomerId: invoice.billingCustomerId,
      billingCustomer: { id: invoice.billingCustomerId, name: customer?.name ?? '', email: customer?.email || null },
      amount: amount.toFixed(2), paymentMethod: types.PaymentMethod.CC, paymentDate: now.slice(0, 10),
      referenceNumber: processorPaymentId, memo: `Online payment for invoice ${invoice.invoiceNumber}`, status: 'ACTIVE',
      processor: 'FAKE', processorPaymentId, processorStatus: 'COMPLETED', processingFee: fee,
      receiptUrl: `https://fake-processor.test/r/${processorPaymentId}`, cardBrand: 'FAKECARD', cardLast4: '4242',
      applications: applied > 0 ? [{
        id: generateId('application'), invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber, amountApplied: applied.toFixed(2),
        invoiceTotal: invoice.total, invoiceStatus: invoice.status, createdAt: now,
      }] : [],
      unappliedAmount: (amount - applied).toFixed(2), createdAt: now,
    };
    this.payments.push(payment);
    Object.assign(attempt, {
      status: 'RECORDED', processorPaymentId, paymentId: payment.id, paymentStatus: 'ACTIVE', paymentProcessorStatus: 'COMPLETED',
      processingFee: fee, receiptUrl: payment.receiptUrl,
    });
    return attempt;
  }

  private mockConsentText(tenantId: string): string {
    const name = this.mockCompanySettings(tenantId).companyName;
    return `I authorize ${name} to charge this card automatically for the balance of each invoice on or after its due date, ` +
      `and to retry a declined charge on the following days, until I turn automatic payments off by contacting ${name}.`;
  }

  async getPublicPayConfig(token: string): Promise<types.PublicPayConfig> {
    await delay(100);
    const entry = this.publicViewTokens.get(token);
    const invoice = entry?.type === 'INVOICE' ? this.invoices.find((i) => i.id === entry.id && i.tenantId === entry.tenantId) : undefined;
    if (!entry || !invoice || invoice.status === types.InvoiceStatus.VOID) throw mockApiError(404, 'Not found', 'NOT_FOUND');
    return this.mockPayConfig(entry.tenantId, invoice);
  }

  /** What the public link and the portal answer for an invoice's Pay now. */
  private mockPayConfig(tenantId: string, invoice: types.Invoice): types.PublicPayConfig {
    const s = this.mockOnlineSettings(tenantId);
    const connected = this.mockConnection(tenantId).status === 'CONNECTED';
    const balance = this.mockInvoiceBalance(invoice);
    const available = s.enabled && connected && balance > 0;
    return {
      available,
      unavailableReason: !s.enabled ? 'Online payment is not available for this invoice.' : !connected ? 'Online payment is temporarily unavailable.' : balance <= 0 ? 'This invoice has no balance due.' : null,
      invoiceNumber: invoice.invoiceNumber, balanceDue: balance.toFixed(2), minAmount: Math.min(balance, Number(s.minAmount)).toFixed(2),
      currency: 'USD', clientConfig: available ? { provider: 'FAKE', environment: 'SANDBOX', siteId: 'FKLOC-1' } : null, cardsOnFile: true,
      autopayConsent: available ? { version: 'v1', text: this.mockConsentText(tenantId) } : null,
    };
  }

  async payPublicInvoice(token: string, data: types.PublicPayRequest): Promise<types.PublicPayResult> {
    await delay(300);
    const config = await this.getPublicPayConfig(token);
    const entry = this.publicViewTokens.get(token)!;
    const invoice = this.invoices.find((i) => i.id === entry.id)!;
    const key = `link:${token}:${data.sourceId}`;
    let attempt = this.onlineAttempts.find((a) => a.key === key);
    if (!attempt) {
      if (!config.available) throw mockApiError(409, 'Online payment is not available for this invoice', 'ONLINE_PAYMENTS_UNAVAILABLE');
      const amount = Number(data.amount);
      if (!(amount > 0) || amount > Number(config.balanceDue) || amount < Number(config.minAmount)) {
        throw mockApiError(400, `amount: between ${config.minAmount} and ${config.balanceDue}`, 'VALIDATION_ERROR');
      }
      let methodId: string | null = null;
      if (data.saveCard && this.mockTokenOutcome(data.sourceId) === 'ok') {
        methodId = this.mockAddMethod(entry.tenantId, invoice.billingCustomerId, data.autopayConsent ? 'CUSTOMER' : null, null).id;
      }
      attempt = this.mockOnlineCharge(entry.tenantId, invoice, amount, 'PUBLIC_LINK', this.mockTokenOutcome(data.sourceId), key, methodId, null);
    }
    const method = attempt.paymentMethodId ? this.onlineMethods.find((m) => m.id === attempt!.paymentMethodId) : undefined;
    const paid = ['RECORDED', 'CHARGED', 'RECORD_FAILED'].includes(attempt.status);
    const status: types.PublicPayResult['status'] = paid ? 'PAID' : attempt.status === 'ERROR' || attempt.status === 'PENDING' ? 'PROCESSING' : 'DECLINED';
    return {
      status, amount: attempt.amount,
      message: status === 'PAID' ? 'Thank you, your payment was received.'
        : status === 'PROCESSING' ? 'We could not confirm your payment yet. Please do not pay again: you will receive a receipt if it went through.'
          : `${attempt.failureMessage ?? 'The card was declined.'} Please try another card.`,
      receiptUrl: paid ? attempt.receiptUrl : null, card: { brand: attempt.cardBrand, last4: attempt.cardLast4 },
      balanceDue: this.mockInvoiceBalance(invoice).toFixed(2), cardSaved: !!method, autopayEnabled: !!method?.autopayEnabled,
    };
  }

  private mockAddMethod(tenantId: string, customerId: string, consentBy: string | null, createdBy: string | null): types.CustomerPaymentMethod & { tenantId: string } {
    const existing = this.onlineMethods.filter((m) => m.tenantId === tenantId && m.customerId === customerId && m.status === 'ACTIVE');
    const makeDefault = !!consentBy || !existing.some((m) => m.isDefault);
    if (makeDefault) existing.forEach((m) => { m.isDefault = false; m.autopayEnabled = false; });
    const method = {
      id: generateId('card'), tenantId, customerId, provider: 'FAKE', brand: 'FAKECARD', last4: '4242', expMonth: 12,
      expYear: new Date().getUTCFullYear() + 3, isDefault: makeDefault, autopayEnabled: !!consentBy,
      autopayConsentAt: consentBy ? new Date().toISOString() : null, autopayConsentVersion: consentBy ? 'v1:mock' : null,
      autopayConsentBy: consentBy, status: 'ACTIVE' as const, createdAt: new Date().toISOString(),
    };
    void createdBy;
    this.onlineMethods.push(method);
    return method;
  }

  private findMockMethod(customerId: string, methodId: string) {
    const actor = this.requireOnlineRole();
    const method = this.onlineMethods.find((m) => m.id === methodId && m.customerId === customerId && m.tenantId === actor.tenantId);
    if (!method) throw mockApiError(404, 'Payment method not found', 'NOT_FOUND');
    return method;
  }

  private publicMethod(m: types.CustomerPaymentMethod & { tenantId: string }): types.CustomerPaymentMethod {
    const { tenantId: _tenantId, ...rest } = m;
    void _tenantId;
    return { ...rest };
  }

  async listPaymentMethods(customerId: string): Promise<types.CustomerPaymentMethod[]> {
    await delay(100);
    const actor = this.requireOnlineRole();
    return this.onlineMethods
      .filter((m) => m.tenantId === actor.tenantId && m.customerId === customerId && m.status !== 'REMOVED')
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
      .map((m) => this.publicMethod(m));
  }

  async addPaymentMethod(customerId: string, data: { sourceId: string; makeDefault?: boolean; autopayConsent?: types.AutopayConsentInput }): Promise<types.CustomerPaymentMethod> {
    await delay(200);
    const actor = this.requireOnlineRole();
    if (this.mockConnection(actor.tenantId).status !== 'CONNECTED') throw mockApiError(409, 'Online payments are not available: no payment processor is connected', 'ONLINE_PAYMENTS_UNAVAILABLE');
    if (/^[\d -]{12,23}$/.test(data.sourceId)) throw mockApiError(400, 'sourceId: card numbers are never accepted', 'VALIDATION_ERROR');
    if (this.mockTokenOutcome(data.sourceId) !== 'ok') throw mockApiError(400, 'The card was declined.', 'CARD_DECLINED');
    const method = this.mockAddMethod(actor.tenantId, customerId, data.autopayConsent ? 'STAFF' : null, actor.id);
    if (data.makeDefault && !method.isDefault) {
      this.onlineMethods.filter((m) => m.tenantId === actor.tenantId && m.customerId === customerId).forEach((m) => { m.isDefault = m.id === method.id; });
    }
    return this.publicMethod(method);
  }

  async setPaymentMethodAutopay(customerId: string, methodId: string, data: { enabled: boolean; autopayConsent?: types.AutopayConsentInput }): Promise<types.CustomerPaymentMethod> {
    await delay(150);
    const method = this.findMockMethod(customerId, methodId);
    if (data.enabled && !data.autopayConsent && !method.autopayConsentAt) {
      throw mockApiError(400, 'Autopay needs the customer consent', 'AUTOPAY_CONSENT_REQUIRED');
    }
    if (data.enabled) {
      this.onlineMethods.filter((m) => m.tenantId === method.tenantId && m.customerId === customerId && m.id !== methodId)
        .forEach((m) => { m.isDefault = false; m.autopayEnabled = false; });
      method.isDefault = true;
      if (data.autopayConsent) Object.assign(method, { autopayConsentAt: new Date().toISOString(), autopayConsentVersion: 'v1:mock', autopayConsentBy: 'STAFF' });
    }
    method.autopayEnabled = data.enabled;
    return this.publicMethod(method);
  }

  async setDefaultPaymentMethod(customerId: string, methodId: string): Promise<types.CustomerPaymentMethod> {
    await delay(100);
    const method = this.findMockMethod(customerId, methodId);
    this.onlineMethods.filter((m) => m.tenantId === method.tenantId && m.customerId === customerId && m.id !== methodId)
      .forEach((m) => { m.isDefault = false; m.autopayEnabled = false; });
    method.isDefault = true;
    return this.publicMethod(method);
  }

  async removePaymentMethod(customerId: string, methodId: string): Promise<types.CustomerPaymentMethod> {
    await delay(100);
    const method = this.findMockMethod(customerId, methodId);
    Object.assign(method, { status: 'REMOVED', isDefault: false, autopayEnabled: false });
    return this.publicMethod(method);
  }

  async chargeInvoiceCard(invoiceId: string, data: types.StaffChargeRequest): Promise<types.ChargeOutcome> {
    await delay(300);
    const actor = this.requireOnlineRole();
    const invoice = this.invoices.find((i) => i.id === invoiceId && i.tenantId === actor.tenantId);
    if (!invoice) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    const key = `staff:${invoiceId}:${data.idempotencyKey ?? generateId('k')}`;
    const existing = this.onlineAttempts.find((a) => a.key === key);
    if (existing) return { attempt: { ...existing }, deduplicated: true };
    if (!this.mockOnlineSettings(actor.tenantId).enabled) throw mockApiError(409, 'Online payments are turned off', 'ONLINE_PAYMENTS_UNAVAILABLE');
    const method = this.onlineMethods.find((m) => m.id === data.paymentMethodId && m.tenantId === actor.tenantId);
    if (!method || method.customerId !== invoice.billingCustomerId || method.status !== 'ACTIVE') {
      throw mockApiError(400, "paymentMethodId: not a card of the invoice's billing customer", 'VALIDATION_ERROR');
    }
    const balance = this.mockInvoiceBalance(invoice);
    const amount = data.amount ? Number(data.amount) : balance;
    if (!(amount > 0) || amount > balance) throw mockApiError(400, `amount: at most the balance due (${balance.toFixed(2)})`, 'VALIDATION_ERROR');
    const attempt = this.mockOnlineCharge(actor.tenantId, invoice, amount, 'STAFF', 'ok', key, method.id, actor.id);
    return { attempt: { ...attempt }, deduplicated: false };
  }

  async refundPayment(paymentId: string, reason: string): Promise<types.RefundResult> {
    await delay(300);
    const actor = this.requireOnlineRole(true);
    if (!reason?.trim()) throw mockApiError(400, 'reason: is required', 'VALIDATION_ERROR');
    const payment = this.payments.find((p) => p.id === paymentId && p.tenantId === actor.tenantId);
    if (!payment) throw mockApiError(404, 'Payment not found', 'NOT_FOUND');
    if (!payment.processor) throw mockApiError(400, 'Only online card payments can be refunded; void manually recorded payments instead', 'NOT_REFUNDABLE');
    if (payment.status === 'VOIDED') throw mockApiError(409, 'This payment is already refunded', 'ALREADY_REFUNDED');
    const voided = await this.voidPayment(paymentId, `Refund: ${reason}`);
    voided.processorStatus = 'REFUNDED';
    this.onlineAttempts.filter((a) => a.paymentId === paymentId).forEach((a) => { a.paymentStatus = 'VOIDED'; a.paymentProcessorStatus = 'REFUNDED'; });
    return { payment: voided, processorRefundId: `FK-RFD-${paymentId.slice(-6)}` };
  }

  async listOnlinePaymentAttempts(filters: types.OnlinePaymentAttemptFilters = {}, page = 1, limit = 50): Promise<types.OnlinePaymentAttemptList> {
    await delay(100);
    const actor = this.requireOnlineRole();
    const mine = this.onlineAttempts.filter((a) => a.tenantId === actor.tenantId);
    const rows = mine.filter((a) =>
      (!filters.status || a.status === filters.status) &&
      (!filters.source || a.source === filters.source) &&
      (!filters.invoiceId || a.invoiceId === filters.invoiceId) &&
      (!filters.customerId || a.customerId === filters.customerId) &&
      (!filters.needsAttention || a.needsAttention));
    const total = rows.length;
    return {
      success: true,
      data: rows.slice((page - 1) * limit, page * limit).map(({ tenantId: _t, key: _k, ...rest }) => { void _t; void _k; return rest; }),
      pagination: { page, limit, total, pages: Math.ceil(total / limit) },
      summary: {
        recordFailed: mine.filter((a) => a.status === 'RECORD_FAILED').length,
        outcomeUnknown: mine.filter((a) => a.status === 'ERROR').length,
        needsReview: mine.filter((a) => a.needsReview).length,
        refundedAtProcessor: mine.filter((a) => a.paymentStatus === 'ACTIVE' && a.paymentProcessorStatus === 'REFUNDED_EXTERNALLY').length,
        voidPending: mine.filter((a) => a.paymentStatus === 'ACTIVE' && a.paymentProcessorStatus === 'REFUNDED').length,
      },
    };
  }

  async reconcileOnlinePaymentAttempt(id: string, resolution?: 'NOT_CHARGED'): Promise<types.OnlinePaymentAttempt> {
    await delay(150);
    const actor = this.requireOnlineRole();
    const attempt = this.onlineAttempts.find((a) => a.id === id && a.tenantId === actor.tenantId);
    if (!attempt) throw mockApiError(404, 'Attempt not found', 'NOT_FOUND');
    if (resolution === 'NOT_CHARGED') {
      if (attempt.status !== 'ERROR') throw mockApiError(409, 'Only an attempt whose outcome is unknown can be closed as not charged', 'CONFLICT');
      Object.assign(attempt, { status: 'DECLINED', needsAttention: false, failureMessage: 'Closed as not charged after checking the payment processor.' });
    } else if (attempt.status === 'DECLINED') {
      throw mockApiError(409, 'The charge was declined; there is nothing to record', 'NOTHING_TO_RECONCILE');
    }
    const { tenantId: _t, key: _k, ...rest } = attempt;
    void _t;
    void _k;
    return { ...rest };
  }

  // ============ Checklists (tracker item 44) ============
  // In-memory version of backend ChecklistTemplateService / JobChecklistService.

  /** Reads: ADMIN, OFFICE_MANAGER, TECHNICIAN (CUSTOMER_PORTAL and OFFICE get 403). */
  private requireChecklistReader(): MockUser {
    const actor = this.currentMockUser();
    if (![types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER, types.UserRole.TECHNICIAN].includes(actor.role as types.UserRole)) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  private toChecklistTemplate(template: MockChecklistTemplate): types.ChecklistTemplate {
    const itemCount = this.items.filter((i) => i.tenantId === template.tenantId && i.checklistTemplateId === template.id).length;
    const jobStepCount = this.jobChecklistSteps.filter((s) => s.tenantId === template.tenantId && s.sourceTemplateId === template.id).length;
    return {
      id: template.id,
      name: template.name,
      isActive: template.isActive,
      stepCount: template.steps.length,
      itemCount,
      jobStepCount,
      inUse: itemCount > 0 || jobStepCount > 0,
      steps: template.steps.map((step) => ({ ...step })),
      createdAt: template.createdAt,
      updatedAt: template.updatedAt,
    };
  }

  private findChecklistTemplate(tenantId: string, id: string): MockChecklistTemplate {
    const template = this.checklistTemplates.find((t) => t.id === id && t.tenantId === tenantId);
    if (!template) throw mockApiError(404, 'Checklist template not found', 'NOT_FOUND');
    return template;
  }

  /** Validates a create / replace body (400) and the per-tenant unique name (409). */
  private mockChecklistTemplateBody(
    tenantId: string,
    data: types.CreateChecklistTemplateRequest,
    exceptId?: string
  ): { name: string; steps: types.ChecklistTemplateStep[] } {
    const error = checklistTemplateBodyError(data?.name, data?.steps);
    if (error) throw mockApiError(400, error, 'VALIDATION_ERROR');
    if (data.isActive !== undefined && typeof data.isActive !== 'boolean') {
      throw mockApiError(400, 'isActive must be true or false', 'VALIDATION_ERROR');
    }
    const name = data.name.trim();
    if (this.checklistTemplates.some((t) => t.tenantId === tenantId && t.id !== exceptId && t.name === name)) {
      throw mockApiError(409, `A checklist template named '${name}' already exists`, 'DUPLICATE_CHECKLIST_TEMPLATE');
    }
    const steps = data.steps.map((step, index) => ({
      id: generateId('cts'),
      text: step.text.trim(),
      required: step.required ?? false,
      sortOrder: index,
    }));
    return { name, steps };
  }

  async listChecklistTemplates(filters: types.ChecklistTemplateListFilters = {}): Promise<types.ChecklistTemplate[]> {
    await delay(150);
    const { tenantId } = this.requireChecklistReader();
    return this.checklistTemplates
      .filter((t) => t.tenantId === tenantId && (filters.active === undefined || t.isActive === filters.active))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => this.toChecklistTemplate(t));
  }

  async getChecklistTemplate(id: string): Promise<types.ChecklistTemplate> {
    await delay(100);
    const { tenantId } = this.requireChecklistReader();
    return this.toChecklistTemplate(this.findChecklistTemplate(tenantId, id));
  }

  async createChecklistTemplate(data: types.CreateChecklistTemplateRequest): Promise<types.ChecklistTemplate> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const body = this.mockChecklistTemplateBody(tenantId, data);
    const now = new Date().toISOString();
    const template: MockChecklistTemplate = {
      id: generateId('ctpl'),
      tenantId,
      ...body,
      isActive: data.isActive ?? true,
      createdAt: now,
      updatedAt: now,
    };
    this.checklistTemplates.push(template);
    return this.toChecklistTemplate(template);
  }

  async replaceChecklistTemplate(id: string, data: types.ReplaceChecklistTemplateRequest): Promise<types.ChecklistTemplate> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const template = this.findChecklistTemplate(tenantId, id);
    const body = this.mockChecklistTemplateBody(tenantId, data, id);
    template.name = body.name;
    template.steps = body.steps;
    if (data.isActive !== undefined) template.isActive = data.isActive;
    template.updatedAt = new Date().toISOString();
    return this.toChecklistTemplate(template);
  }

  async deleteChecklistTemplate(id: string): Promise<types.ChecklistTemplateDeleteResult> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const template = this.findChecklistTemplate(tenantId, id);
    // Like the backend: soft-deleted items count as references too.
    if (this.toChecklistTemplate(template).inUse) {
      if (template.isActive) {
        template.isActive = false;
        template.updatedAt = new Date().toISOString();
      }
      return { result: 'DEACTIVATED', template: this.toChecklistTemplate(template) };
    }
    this.checklistTemplates = this.checklistTemplates.filter((t) => t !== template);
    return { result: 'DELETED', template: null };
  }

  /** The job for a checklist call: 404 outside the tenant; TECHNICIAN only on assigned jobs (403). */
  private requireChecklistJob(jobId: string, officeOnly: boolean): { actor: MockUser; job: types.Job & { tenantId: string } } {
    const actor = officeOnly ? this.requireOfficeUser() : this.requireChecklistReader();
    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === actor.tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    if (actor.role === types.UserRole.TECHNICIAN && job.assignedTo !== actor.id) {
      throw mockApiError(403, 'You can only view checklists on jobs assigned to you', 'FORBIDDEN');
    }
    return { actor, job };
  }

  private findJobChecklistStep(job: types.Job & { tenantId: string }, stepId: string): types.JobChecklistStep & { tenantId: string } {
    const step = this.jobChecklistSteps.find((s) => s.id === stepId && s.jobId === job.id && s.tenantId === job.tenantId);
    if (!step) throw mockApiError(404, 'Checklist step not found', 'NOT_FOUND');
    return step;
  }

  private stepsForJob(job: types.Job & { tenantId: string }): Array<types.JobChecklistStep & { tenantId: string }> {
    return this.jobChecklistSteps
      .filter((s) => s.jobId === job.id && s.tenantId === job.tenantId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  private toJobChecklistStep(step: types.JobChecklistStep & { tenantId: string }): types.JobChecklistStep {
    const { tenantId: _tenantId, ...rest } = step;
    return { ...rest, completed: step.completedAt !== null };
  }

  private mockChecklistProgress(steps: types.JobChecklistStep[]): types.JobChecklistProgress {
    return {
      total: steps.length,
      completed: steps.filter((s) => s.completedAt !== null).length,
      requiredIncomplete: steps.filter((s) => s.required && s.completedAt === null).length,
    };
  }

  /** Keeps the progress summary on the stored Job (mock jobs are returned by reference). */
  private refreshMockJobChecklist(job: types.Job & { tenantId: string }): void {
    job.checklist = this.mockChecklistProgress(this.stepsForJob(job));
  }

  private toJobChecklist(job: types.Job & { tenantId: string }): types.JobChecklist {
    const steps = this.stepsForJob(job).map((s) => this.toJobChecklistStep(s));
    const groups: types.JobChecklistGroup[] = [];
    steps.forEach((step) => {
      let group = groups.find((g) => g.sourceTemplateId === step.sourceTemplateId && g.groupName === step.groupName);
      if (!group) {
        group = { sourceTemplateId: step.sourceTemplateId, groupName: step.groupName, total: 0, completed: 0, steps: [] };
        groups.push(group);
      }
      group.steps.push(step);
      group.total += 1;
      if (step.completed) group.completed += 1;
    });
    return { jobId: job.id, ...this.mockChecklistProgress(steps), groups };
  }

  private nextJobChecklistSortOrder(job: types.Job & { tenantId: string }): number {
    const steps = this.stepsForJob(job);
    return steps.length ? steps[steps.length - 1].sortOrder + 1 : 0;
  }

  /** Copies a template's steps onto the job as a new group, unless the job already has that template's group. */
  private copyMockChecklistTemplate(job: types.Job & { tenantId: string }, template: MockChecklistTemplate): boolean {
    if (this.jobChecklistSteps.some((s) => s.jobId === job.id && s.sourceTemplateId === template.id)) return false;
    const start = this.nextJobChecklistSortOrder(job);
    const now = new Date().toISOString();
    template.steps.forEach((step, index) => {
      this.jobChecklistSteps.push({
        id: generateId('jcs'),
        tenantId: job.tenantId,
        jobId: job.id,
        sourceTemplateId: template.id,
        groupName: template.name,
        text: step.text,
        sortOrder: start + index,
        required: step.required,
        completed: false,
        completedAt: null,
        completedById: null,
        createdAt: now,
        updatedAt: now,
      });
    });
    this.refreshMockJobChecklist(job);
    return true;
  }

  /** Line hook: a line for an item linking an active template copies it (missing / inactive skipped). */
  private applyMockLineChecklist(job: types.Job & { tenantId: string }, itemId: string): void {
    const templateId = this.items.find((i) => i.id === itemId && i.tenantId === job.tenantId)?.checklistTemplateId;
    const template = templateId ? this.checklistTemplates.find((t) => t.id === templateId && t.tenantId === job.tenantId) : undefined;
    if (template?.isActive) this.copyMockChecklistTemplate(job, template);
  }

  /** Line hook: drop the old item's template group when no remaining line uses it and nothing in it is ticked. */
  private removeMockLineChecklist(job: types.Job & { tenantId: string }, oldItemId: string): void {
    const templateOf = (itemId: string) => this.items.find((i) => i.id === itemId && i.tenantId === job.tenantId)?.checklistTemplateId ?? null;
    const templateId = templateOf(oldItemId);
    if (!templateId) return;
    if (job.lineItems.some((li) => templateOf(li.itemId) === templateId)) return;
    const group = this.jobChecklistSteps.filter((s) => s.jobId === job.id && s.sourceTemplateId === templateId);
    if (group.length === 0 || group.some((s) => s.completedAt !== null)) return;
    this.jobChecklistSteps = this.jobChecklistSteps.filter((s) => !group.includes(s));
    this.refreshMockJobChecklist(job);
  }

  async getJobChecklist(jobId: string): Promise<types.JobChecklist> {
    await delay(100);
    const { job } = this.requireChecklistJob(jobId, false);
    return this.toJobChecklist(job);
  }

  async addJobChecklistStep(jobId: string, data: types.AddJobChecklistStepRequest): Promise<types.JobChecklistStep> {
    await delay(150);
    const { job } = this.requireChecklistJob(jobId, true);
    const text = typeof data?.text === 'string' ? data.text.trim() : '';
    if (!text) throw mockApiError(400, 'Step text is required', 'VALIDATION_ERROR');
    if (text.length > types.CHECKLIST_STEP_TEXT_MAX) {
      throw mockApiError(400, `Step text cannot exceed ${types.CHECKLIST_STEP_TEXT_MAX} characters`, 'VALIDATION_ERROR');
    }
    const groupName = data.groupName?.trim() || types.JOB_CHECKLIST_DEFAULT_GROUP;
    const existing = this.stepsForJob(job).find((s) => s.groupName === groupName);
    const now = new Date().toISOString();
    const step = {
      id: generateId('jcs'),
      tenantId: job.tenantId,
      jobId: job.id,
      sourceTemplateId: existing?.sourceTemplateId ?? null,
      groupName,
      text,
      sortOrder: data.sortOrder ?? this.nextJobChecklistSortOrder(job),
      required: data.required ?? false,
      completed: false,
      completedAt: null,
      completedById: null,
      createdAt: now,
      updatedAt: now,
    };
    this.jobChecklistSteps.push(step);
    this.refreshMockJobChecklist(job);
    return this.toJobChecklistStep(step);
  }

  async updateJobChecklistStep(jobId: string, stepId: string, data: types.UpdateJobChecklistStepRequest): Promise<types.JobChecklistStep> {
    await delay(150);
    const { job } = this.requireChecklistJob(jobId, true);
    const step = this.findJobChecklistStep(job, stepId);
    if (data.text === undefined && data.required === undefined && data.sortOrder === undefined) {
      throw mockApiError(400, 'Nothing to update', 'VALIDATION_ERROR');
    }
    if (data.text !== undefined) {
      const text = data.text.trim();
      if (!text) throw mockApiError(400, 'Step text is required', 'VALIDATION_ERROR');
      step.text = text;
    }
    if (data.required !== undefined) step.required = data.required;
    if (data.sortOrder !== undefined) step.sortOrder = data.sortOrder;
    step.updatedAt = new Date().toISOString();
    this.refreshMockJobChecklist(job);
    return this.toJobChecklistStep(step);
  }

  async deleteJobChecklistStep(jobId: string, stepId: string): Promise<{ id: string }> {
    await delay(150);
    const { job } = this.requireChecklistJob(jobId, true);
    const step = this.findJobChecklistStep(job, stepId);
    this.jobChecklistSteps = this.jobChecklistSteps.filter((s) => s !== step);
    this.refreshMockJobChecklist(job);
    return { id: step.id };
  }

  async completeJobChecklistStep(jobId: string, stepId: string): Promise<types.JobChecklistStep> {
    await delay(100);
    const { actor, job } = this.requireChecklistJob(jobId, false);
    const step = this.findJobChecklistStep(job, stepId);
    // Repeating a tick keeps the first actor and time.
    if (step.completedAt === null) {
      step.completedAt = new Date().toISOString();
      step.completedById = actor.id;
      step.updatedAt = step.completedAt;
    }
    this.refreshMockJobChecklist(job);
    return this.toJobChecklistStep(step);
  }

  async uncompleteJobChecklistStep(jobId: string, stepId: string): Promise<types.JobChecklistStep> {
    await delay(100);
    const { job } = this.requireChecklistJob(jobId, false);
    const step = this.findJobChecklistStep(job, stepId);
    if (step.completedAt !== null) {
      step.completedAt = null;
      step.completedById = null;
      step.updatedAt = new Date().toISOString();
    }
    this.refreshMockJobChecklist(job);
    return this.toJobChecklistStep(step);
  }

  async applyJobChecklistTemplate(jobId: string, templateId: string): Promise<types.ApplyJobChecklistTemplateResult> {
    await delay(150);
    const { job } = this.requireChecklistJob(jobId, true);
    if (!templateId) throw mockApiError(400, 'templateId is required', 'VALIDATION_ERROR');
    const template = this.findChecklistTemplate(job.tenantId, templateId);
    if (!template.isActive) {
      throw mockApiError(422, `Checklist template '${template.name}' is inactive and cannot be applied`, 'CHECKLIST_TEMPLATE_INACTIVE');
    }
    const applied = this.copyMockChecklistTemplate(job, template);
    return { applied, checklist: this.toJobChecklist(job) };
  }

  // ============ Attachments (tracker item 47) ============
  // In-memory version of backend AttachmentService. The bytes are kept as a
  // Blob; only the size and extension are checked (no content sniffing).

  /** List, upload, metadata, download: ADMIN, OFFICE_MANAGER, TECHNICIAN (OFFICE and CUSTOMER_PORTAL get 403). */
  private requireAttachmentReader(): MockUser {
    const actor = this.currentMockUser();
    if (![types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER, types.UserRole.TECHNICIAN].includes(actor.role as types.UserRole)) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  /** The parent must exist in the caller's tenant (404 like the backend). */
  private requireAttachmentParent(tenantId: string, parentType: types.AttachmentParentType, parentId: string): void {
    const found = parentType === 'CUSTOMER'
      ? this.customers.some((c) => c.id === parentId && c.tenantId === tenantId)
      : parentType === 'PROPERTY'
        ? this.properties.some((p) => p.id === parentId && p.tenantId === tenantId)
        : this.jobs.some((j) => j.id === parentId && j.tenantId === tenantId);
    if (!found) {
      const name = parentType === 'CUSTOMER' ? 'Customer' : parentType === 'PROPERTY' ? 'Property' : 'Job';
      throw mockApiError(404, `${name} not found`, 'NOT_FOUND');
    }
  }

  private findMockAttachment(tenantId: string, id: string): MockAttachment {
    const row = this.attachments.find((a) => a.id === id && a.tenantId === tenantId && !a.deletedAt);
    if (!row) throw mockApiError(404, 'Attachment not found', 'NOT_FOUND');
    // Like the backend: an attachment whose parent is gone is not visible
    try {
      this.requireAttachmentParent(tenantId, row.parentType, row.parentId);
    } catch {
      throw mockApiError(404, 'Attachment not found', 'NOT_FOUND');
    }
    return row;
  }

  private toAttachment({ tenantId, blob, deletedAt, ...attachment }: MockAttachment): types.Attachment {
    return { ...attachment };
  }

  /** Backend cleanFilename, simplified: last path segment, no control characters, trimmed, at most 255 characters. */
  private mockAttachmentFilename(raw: unknown): string {
    if (typeof raw !== 'string') throw mockApiError(400, 'A filename is required', 'FILENAME_REQUIRED');
    // eslint-disable-next-line no-control-regex
    let name = (raw.normalize('NFC').split(/[\\/]/).pop() ?? '').replace(/[\u0000-\u001f\u007f]/g, '').trim();
    if (name === '' || name === '.' || name === '..') throw mockApiError(400, 'A filename is required', 'FILENAME_REQUIRED');
    if (name.length > ATTACHMENT_FILENAME_MAX) {
      const ext = extensionOf(name);
      const suffix = ext ? `.${ext}` : '';
      name = name.slice(0, ATTACHMENT_FILENAME_MAX - suffix.length).trimEnd() + suffix;
    }
    return name;
  }

  private mockAttachmentDescription(value: unknown): string | null | undefined {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (typeof value !== 'string') throw mockApiError(400, 'description must be a string', 'VALIDATION_ERROR');
    const text = value.trim();
    if (text === '') return null;
    if (text.length > ATTACHMENT_DESCRIPTION_MAX) {
      throw mockApiError(400, `description must be at most ${ATTACHMENT_DESCRIPTION_MAX} characters`, 'VALIDATION_ERROR');
    }
    return text;
  }

  async listAttachments(
    parentType: types.AttachmentParentType,
    parentId: string,
    page = 1,
    limit = 25
  ): Promise<types.PaginatedResponse<types.Attachment>> {
    await delay(150);
    const { tenantId } = this.requireAttachmentReader();
    this.requireAttachmentParent(tenantId, parentType, parentId);
    if (!Number.isInteger(page) || page < 1) throw mockApiError(400, 'page must be a positive whole number', 'VALIDATION_ERROR');
    if (!Number.isInteger(limit) || limit < 1) throw mockApiError(400, 'limit must be a positive whole number', 'VALIDATION_ERROR');
    // Newest first; ties keep the later upload first
    const rows = this.attachments
      .map((a, index) => ({ a, index }))
      .filter(({ a }) => a.tenantId === tenantId && a.parentType === parentType && a.parentId === parentId && !a.deletedAt)
      .sort((x, y) => (x.a.createdAt === y.a.createdAt ? y.index - x.index : x.a.createdAt < y.a.createdAt ? 1 : -1))
      .map(({ a }) => a);
    const result = paginate(rows, page, limit);
    return { ...result, data: result.data.map((a) => this.toAttachment(a)) };
  }

  async uploadAttachment(
    parentType: types.AttachmentParentType,
    parentId: string,
    file: File,
    options: types.UploadAttachmentOptions = {}
  ): Promise<types.Attachment> {
    const actor = this.requireAttachmentReader();
    this.requireAttachmentParent(actor.tenantId, parentType, parentId);
    if (!file) throw mockApiError(400, "A file is required (multipart/form-data field 'file')", 'FILE_REQUIRED');
    for (const fraction of [0.25, 0.5, 0.75, 1]) {
      if (options.signal?.aborted) throw new Error('Upload cancelled');
      await delay(60);
      options.onProgress?.(fraction);
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      throw mockApiError(413, `The file is larger than the ${ATTACHMENT_MAX_BYTES / (1024 * 1024)} MB limit`, 'FILE_TOO_LARGE');
    }
    if (file.size === 0) throw mockApiError(400, 'The file is empty', 'FILE_EMPTY');
    const filename = this.mockAttachmentFilename(file.name);
    const type = fileTypeForExtension(extensionOf(filename));
    if (!type) {
      throw mockApiError(
        415,
        `Files of this type are not allowed. Allowed: ${ATTACHMENT_EXTENSIONS.map((e) => '.' + e).join(', ')}`,
        'UNSUPPORTED_FILE_TYPE'
      );
    }
    const description = this.mockAttachmentDescription(options.description) ?? null;
    const now = new Date().toISOString();
    const row: MockAttachment = {
      id: generateId('att'),
      tenantId: actor.tenantId,
      parentType,
      parentId,
      filename,
      contentType: type.contentType,
      sizeBytes: file.size,
      description,
      isImage: type.isImage,
      inline: type.inline,
      uploadedById: actor.id,
      uploadedByEmail: actor.email,
      createdAt: now,
      updatedAt: now,
      blob: new Blob([file], { type: type.contentType }),
      deletedAt: null,
    };
    this.attachments.push(row);
    return this.toAttachment(row);
  }

  async getAttachment(id: string): Promise<types.Attachment> {
    await delay(100);
    const { tenantId } = this.requireAttachmentReader();
    return this.toAttachment(this.findMockAttachment(tenantId, id));
  }

  async downloadAttachment(id: string, _options: types.DownloadAttachmentOptions = {}): Promise<Blob> {
    await delay(150);
    const { tenantId } = this.requireAttachmentReader();
    return this.findMockAttachment(tenantId, id).blob;
  }

  async updateAttachment(id: string, data: types.UpdateAttachmentRequest): Promise<types.Attachment> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    const row = this.findMockAttachment(tenantId, id);
    if (!data || typeof data !== 'object') throw mockApiError(400, 'A JSON object body is required', 'VALIDATION_ERROR');
    let filename: string | undefined;
    if (data.filename !== undefined) {
      filename = this.mockAttachmentFilename(data.filename);
      const current = fileTypeForContentType(row.contentType);
      if (!current || fileTypeForExtension(extensionOf(filename)) !== current) {
        throw mockApiError(
          400,
          `The filename must keep its file type (${current ? current.extensions.map((e) => '.' + e).join(' or ') : 'unchanged'})`,
          'FILE_EXTENSION_CHANGE'
        );
      }
    }
    const description = this.mockAttachmentDescription(data.description);
    if (filename === undefined && description === undefined) {
      throw mockApiError(400, 'Nothing to update: send filename and/or description', 'VALIDATION_ERROR');
    }
    if (filename !== undefined) row.filename = filename;
    if (description !== undefined) row.description = description;
    row.updatedAt = new Date().toISOString();
    return this.toAttachment(row);
  }

  async deleteAttachment(id: string): Promise<{ id: string }> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    const row = this.findMockAttachment(tenantId, id);
    row.deletedAt = new Date().toISOString();
    return { id: row.id };
  }

  // ============ Technician view (tracker item 42) ============
  // In-memory version of backend TechnicianService: today list, job card,
  // Start / Stop / Finish with the same 409 codes. No prices are exposed.

  /** Staff = ADMIN, OFFICE_MANAGER, TECHNICIAN (OFFICE and CUSTOMER_PORTAL get 403). */
  private requireTechnicianStaff(): MockUser {
    const actor = this.currentMockUser();
    if (![types.UserRole.ADMIN, types.UserRole.OFFICE_MANAGER, types.UserRole.TECHNICIAN].includes(actor.role as types.UserRole)) {
      throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  /** Job in the caller's tenant (404); a TECHNICIAN reaches assigned jobs only (403 FORBIDDEN). */
  private technicianJob(actor: MockUser, jobId: string): types.Job & { tenantId: string } {
    const job = this.jobs.find((j) => j.id === jobId && j.tenantId === actor.tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    if (actor.role === types.UserRole.TECHNICIAN && job.assignedTo !== actor.id) {
      throw mockApiError(403, 'You can only work on jobs assigned to you', 'FORBIDDEN');
    }
    return job;
  }

  private mockTechnicianToday(tenantId: string): string {
    return todayInTimeZone(this.mockCompanySettings(tenantId).timezone ?? 'America/New_York');
  }

  private toTechnicianEntry({ tenantId: _tenantId, ...entry }: types.TechnicianTimeEntry & { tenantId: string }): types.TechnicianTimeEntry {
    return { ...entry };
  }

  private runningEntryFor(tenantId: string, userId: string): (types.TechnicianTimeEntry & { tenantId: string }) | undefined {
    return this.technicianTimeEntries.find((e) => e.tenantId === tenantId && e.userId === userId && e.endedAt === null);
  }

  private toTechnicianCard(job: types.Job & { tenantId: string }): types.TechnicianJobCard {
    const customer = this.customers.find((c) => c.id === job.customerId && c.tenantId === job.tenantId);
    const property = this.properties.find((p) => p.id === job.propertyId && p.tenantId === job.tenantId);
    const route = job.routeId ? this.dispatchRoutes.find((r) => r.id === job.routeId) : undefined;
    const duration = job.estimatedDurationMinutes ?? null;
    let scheduledEndTime: string | null = null;
    if (job.scheduledStartTime && duration !== null) {
      const [h, m] = job.scheduledStartTime.split(':').map(Number);
      const end = (h * 60 + m + duration) % (24 * 60);
      scheduledEndTime = `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
    }
    return {
      id: job.id,
      status: job.status,
      assignedTo: job.assignedTo,
      scheduledDate: job.scheduledDate,
      scheduledStartTime: job.scheduledStartTime ?? null,
      scheduledEndTime,
      estimatedDurationMinutes: duration,
      arrivalWindowMinutes: job.arrivalWindowMinutes ?? null,
      completedDate: job.completedDate ?? null,
      route: route ? { id: route.id, name: route.name } : null,
      customer: { id: job.customerId, name: job.customer.name, phone: customer?.phone ?? null },
      property: {
        id: job.propertyId,
        address: job.property.address,
        city: job.property.city ?? '',
        state: job.property.state ?? '',
        zip: property?.zip ?? '',
      },
      notes: job.notes ?? null,
      workItems: job.lineItems.map((li) => ({
        id: li.id,
        itemId: li.itemId,
        name: li.item.name,
        description: li.item.description ?? null,
        quantity: li.quantity,
      })),
      checklist: this.mockChecklistProgress(this.stepsForJob(job)),
      openTimeEntries: this.technicianTimeEntries
        .filter((e) => e.tenantId === job.tenantId && e.jobId === job.id && e.endedAt === null)
        .map((e) => ({ id: e.id, userId: e.userId, startedAt: e.startedAt })),
      updatedAt: job.updatedAt,
    };
  }

  async getTechnicianToday(query: types.TechnicianTodayQuery = {}): Promise<types.TechnicianToday> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const { tenantId } = actor;
    let target = actor;
    if (query.userId && query.userId !== actor.id) {
      if (actor.role === types.UserRole.TECHNICIAN) {
        throw mockApiError(403, 'You can only view your own jobs', 'FORBIDDEN');
      }
      const other = this.users.find((u) => u.id === query.userId && u.tenantId === tenantId);
      if (!other) throw mockApiError(404, 'User not found', 'NOT_FOUND');
      target = other;
    }
    const today = this.mockTechnicianToday(tenantId);
    if (query.date !== undefined && addDaysToDateOnly(query.date, 0) !== query.date) {
      throw mockApiError(400, 'date must be a valid date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    }
    const date = query.date ?? today;
    const mine = this.jobs.filter((j) => j.tenantId === tenantId && j.assignedTo === target.id);
    const routeName = (j: types.Job) => (j.routeId ? this.dispatchRoutes.find((r) => r.id === j.routeId)?.name ?? null : null);
    const last = (v: string | null) => (v === null ? '￿' : v.toLowerCase());
    const dayJobs = mine
      .filter((j) => j.scheduledDate === date && j.status !== types.JobStatus.CANCELLED && j.status !== types.JobStatus.INVOICED)
      .sort((a, b) => last(a.scheduledStartTime ?? null).localeCompare(last(b.scheduledStartTime ?? null))
        || last(routeName(a)).localeCompare(last(routeName(b)))
        || a.customer.name.localeCompare(b.customer.name)
        || a.id.localeCompare(b.id));
    const dayIds = new Set(dayJobs.map((j) => j.id));
    const open = [types.JobStatus.WORK_ORDER_GENERATED, types.JobStatus.SCHEDULED, types.JobStatus.IN_PROGRESS];
    const unfinishedAll = mine
      .filter((j) => open.includes(j.status) && !!j.scheduledDate && j.scheduledDate < today && !dayIds.has(j.id))
      .sort((a, b) => (a.scheduledDate as string).localeCompare(b.scheduledDate as string) || a.id.localeCompare(b.id));
    const running = this.runningEntryFor(tenantId, target.id);
    return {
      date,
      today,
      timezone: this.mockCompanySettings(tenantId).timezone ?? 'America/New_York',
      user: { id: target.id, email: target.email },
      jobs: dayJobs.map((j) => this.toTechnicianCard(j)),
      unfinished: unfinishedAll.slice(0, 50).map((j) => this.toTechnicianCard(j)),
      unfinishedTruncated: unfinishedAll.length > 50,
      runningEntry: running ? this.toTechnicianEntry(running) : null,
    };
  }

  async getTechnicianJob(jobId: string): Promise<types.TechnicianJobCard> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    return this.toTechnicianCard(this.technicianJob(actor, jobId));
  }

  async startTechnicianJob(jobId: string, options: { confirmOffSchedule?: boolean } = {}): Promise<types.StartTechnicianJobResult> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const job = this.technicianJob(actor, jobId);
    if (
      [types.JobStatus.DRAFT, types.JobStatus.COMPLETED, types.JobStatus.INVOICED, types.JobStatus.CANCELLED].includes(job.status)
      || job.invoiceId
    ) {
      throw mockApiError(409, `A ${job.status} job cannot be started`, 'JOB_NOT_STARTABLE');
    }
    const running = this.runningEntryFor(actor.tenantId, actor.id);
    if (running && running.jobId === job.id) {
      return {
        job: { id: job.id, status: job.status, completedDate: job.completedDate ?? null },
        entry: this.toTechnicianEntry(running),
        alreadyRunning: true,
        offSchedule: false,
      };
    }
    if (running) {
      throw mockApiError(409, 'You have a timer running on another job; stop it first', 'TIMER_RUNNING_ELSEWHERE', {
        jobId: running.jobId,
        entryId: running.id,
        startedAt: running.startedAt,
      });
    }
    const today = this.mockTechnicianToday(actor.tenantId);
    const offSchedule = job.scheduledDate !== today;
    if (offSchedule && options.confirmOffSchedule !== true) {
      throw mockApiError(
        409,
        job.scheduledDate ? `This job is scheduled for ${job.scheduledDate}, not today` : 'This job has no scheduled date',
        'NOT_SCHEDULED_TODAY',
        { scheduledDate: job.scheduledDate, today },
      );
    }
    const now = new Date().toISOString();
    if (job.status === types.JobStatus.SCHEDULED || job.status === types.JobStatus.WORK_ORDER_GENERATED) {
      job.status = types.JobStatus.IN_PROGRESS;
      job.updatedAt = now;
    }
    const entry: types.TechnicianTimeEntry & { tenantId: string } = {
      id: generateId('te'),
      tenantId: actor.tenantId,
      jobId: job.id,
      userId: actor.id,
      userEmail: actor.email,
      startedAt: now,
      endedAt: null,
      running: true,
      durationSeconds: null,
      source: 'TIMER',
      createdById: actor.id,
      createdAt: now,
      updatedAt: now,
    };
    this.technicianTimeEntries.push(entry);
    return {
      job: { id: job.id, status: job.status, completedDate: job.completedDate ?? null },
      entry: this.toTechnicianEntry(entry),
      alreadyRunning: false,
      offSchedule,
    };
  }

  /** Ends a running entry; an end equal to the start is stored 1 ms later (endedAt > startedAt). */
  private closeTechnicianEntry(entry: types.TechnicianTimeEntry): void {
    const started = Date.parse(entry.startedAt);
    const ended = Math.max(Date.now(), started + 1);
    entry.endedAt = new Date(ended).toISOString();
    entry.running = false;
    entry.durationSeconds = Math.floor((ended - started) / 1000);
    entry.updatedAt = entry.endedAt;
  }

  async stopTechnicianJob(jobId: string): Promise<types.StopTechnicianJobResult> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const job = this.technicianJob(actor, jobId);
    const entry = this.technicianTimeEntries.find(
      (e) => e.tenantId === actor.tenantId && e.jobId === job.id && e.userId === actor.id && e.endedAt === null,
    );
    if (!entry) throw mockApiError(409, 'You have no running timer on this job', 'NO_RUNNING_TIMER');
    this.closeTechnicianEntry(entry);
    return {
      job: { id: job.id, status: job.status, completedDate: job.completedDate ?? null },
      entry: this.toTechnicianEntry(entry),
    };
  }

  /** The bytes of a Blob (FileReader works in browsers and in jsdom). */
  private readBlobBytes(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * Same checks as the backend: empty 400 SIGNATURE_EMPTY, over 1 MB 413
   * SIGNATURE_TOO_LARGE, not a PNG (8-byte signature, IHDR first, IEND) 415
   * SIGNATURE_NOT_PNG, a side over 4096 px 413, then the printed name
   * (400 SIGNER_NAME_REQUIRED when blank). Returns the cleaned name.
   */
  private async validateMockSignature(signature: types.TechnicianSignatureInput | undefined): Promise<string> {
    if (!signature || !signature.blob) {
      throw mockApiError(400, 'Attach the signature image as a PNG file', 'SIGNATURE_REQUIRED');
    }
    if (signature.blob.size === 0) throw mockApiError(400, 'The signature image is empty', 'SIGNATURE_EMPTY');
    const tooLarge = () => mockApiError(413, `The signature image is larger than the ${types.SIGNATURE_MAX_BYTES / 1024} KB limit`, 'SIGNATURE_TOO_LARGE');
    if (signature.blob.size > types.SIGNATURE_MAX_BYTES) throw tooLarge();
    const bytes = await this.readBlobBytes(signature.blob);
    const magic = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    const tag = (at: number) => String.fromCharCode(...Array.from(bytes.subarray(at, at + 4)));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const hasIend = () => {
      for (let i = bytes.length - 4; i >= 33; i -= 1) if (tag(i) === 'IEND') return true;
      return false;
    };
    const isPng = bytes.length >= 45
      && magic.every((b, i) => bytes[i] === b)
      && view.getUint32(8) === 13 && tag(12) === 'IHDR'
      && view.getUint32(16) > 0 && view.getUint32(20) > 0
      && hasIend();
    if (!isPng) throw mockApiError(415, 'The signature must be a PNG image', 'SIGNATURE_NOT_PNG');
    if (view.getUint32(16) > types.SIGNATURE_MAX_DIMENSION || view.getUint32(20) > types.SIGNATURE_MAX_DIMENSION) {
      throw mockApiError(413, `The signature image may be at most ${types.SIGNATURE_MAX_DIMENSION} pixels wide and high`, 'SIGNATURE_TOO_LARGE');
    }
    const name = typeof signature.signerName === 'string' ? cleanTechText(signature.signerName) : '';
    if (!name) throw mockApiError(400, "Enter the signer's printed name", 'SIGNER_NAME_REQUIRED');
    if (name.length > types.SIGNER_NAME_MAX) {
      throw mockApiError(400, `signerName may be at most ${types.SIGNER_NAME_MAX} characters`, 'VALIDATION_ERROR');
    }
    return name;
  }

  private toJobSignature({ tenantId: _tenantId, blob: _blob, ...signature }: types.JobSignature & { tenantId: string; blob: Blob }): types.JobSignature {
    return { ...signature };
  }

  /** Stores a new current signature and supersedes the previous one (kept as history). */
  private recordMockSignature(actor: MockUser, job: types.Job & { tenantId: string }, signerName: string, blob: Blob): types.JobSignature & { tenantId: string; blob: Blob } {
    const now = new Date().toISOString();
    const row = {
      id: generateId('sig'),
      tenantId: job.tenantId,
      jobId: job.id,
      signerName,
      contentType: 'image/png',
      sizeBytes: blob.size,
      signedAt: now,
      capturedById: actor.id,
      capturedByEmail: actor.email,
      current: true,
      supersededAt: null,
      supersededById: null,
      blob: new Blob([blob], { type: 'image/png' }),
    };
    this.technicianSignatures
      .filter((sig) => sig.tenantId === job.tenantId && sig.jobId === job.id && sig.current)
      .forEach((sig) => {
        sig.current = false;
        sig.supersededAt = now;
        sig.supersededById = row.id;
      });
    this.technicianSignatures.push(row);
    return row;
  }

  async finishTechnicianJob(jobId: string, options: types.FinishTechnicianJobOptions = {}): Promise<types.FinishTechnicianJobResult> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const signerName = options.signature ? await this.validateMockSignature(options.signature) : null;
    const job = this.technicianJob(actor, jobId);
    if (job.status === types.JobStatus.COMPLETED || job.status === types.JobStatus.INVOICED || job.invoiceId) {
      throw mockApiError(409, 'This job is already completed', 'JOB_ALREADY_COMPLETED');
    }
    if (job.status === types.JobStatus.DRAFT || job.status === types.JobStatus.CANCELLED) {
      throw mockApiError(409, `A ${job.status} job cannot be finished`, 'JOB_NOT_FINISHABLE');
    }
    const closed = this.technicianTimeEntries.filter(
      (e) => e.tenantId === actor.tenantId && e.jobId === job.id && e.endedAt === null,
    );
    closed.forEach((e) => this.closeTechnicianEntry(e));
    job.status = types.JobStatus.COMPLETED;
    job.completedDate = this.mockTechnicianToday(actor.tenantId);
    job.updatedAt = new Date().toISOString();
    const signature = options.signature && signerName !== null
      ? this.recordMockSignature(actor, job, signerName, options.signature.blob)
      : null;
    return {
      job: { id: job.id, status: job.status, completedDate: job.completedDate },
      closedEntries: closed.map((e) => this.toTechnicianEntry(e)),
      signature: signature ? this.toJobSignature(signature) : null,
      requiredIncomplete: this.mockChecklistProgress(this.stepsForJob(job)).requiredIncomplete,
    };
  }

  async addTechnicianNote(jobId: string, text: string): Promise<types.TechnicianNoteResult> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    if (typeof text !== 'string') throw mockApiError(400, 'text is required', 'VALIDATION_ERROR');
    const clean = cleanTechText(text);
    if (!clean) throw mockApiError(400, 'text is required', 'VALIDATION_ERROR');
    if (clean.length > types.TECH_NOTE_MAX) {
      throw mockApiError(400, `text may be at most ${types.TECH_NOTE_MAX} characters`, 'VALIDATION_ERROR');
    }
    const job = this.technicianJob(actor, jobId);
    const timezone = this.mockCompanySettings(actor.tenantId).timezone ?? 'America/New_York';
    const line = formatTechNoteLine(new Date(), timezone, actor.email, clean);
    job.notes = job.notes ? `${job.notes}\n${line}` : line;
    job.updatedAt = new Date().toISOString();
    return { jobId: job.id, line, notes: job.notes };
  }

  async uploadTechnicianSignature(jobId: string, signature: types.TechnicianSignatureInput): Promise<types.JobSignature> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const signerName = await this.validateMockSignature(signature);
    const job = this.technicianJob(actor, jobId);
    if (job.status === types.JobStatus.CANCELLED) {
      throw mockApiError(409, 'A cancelled job cannot be signed', 'JOB_CANCELLED');
    }
    return this.toJobSignature(this.recordMockSignature(actor, job, signerName, signature.blob));
  }

  async listTechnicianSignatures(jobId: string): Promise<types.JobSignatureList> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const job = this.technicianJob(actor, jobId);
    const signatures = this.technicianSignatures
      .filter((sig) => sig.tenantId === job.tenantId && sig.jobId === job.id)
      .map((sig) => this.toJobSignature(sig))
      .sort((a, b) => b.signedAt.localeCompare(a.signedAt) || b.id.localeCompare(a.id));
    return { jobId: job.id, current: signatures.find((sig) => sig.current) ?? null, signatures };
  }

  async downloadTechnicianSignatureImage(jobId: string, signatureId: string): Promise<Blob> {
    await delay(100);
    const actor = this.requireTechnicianStaff();
    const job = this.technicianJob(actor, jobId);
    const row = this.technicianSignatures.find((sig) => sig.id === signatureId && sig.jobId === job.id && sig.tenantId === job.tenantId);
    if (!row) throw mockApiError(404, 'Signature not found', 'NOT_FOUND');
    return row.blob;
  }

  // ============ Customer portal (tracker item 45, phase 3) ============
  //
  // In-memory twin of /api/portal/*. Same scoping rules as the backend: the
  // customer comes only from the login's link (a login with no link, e.g. one
  // that has not matched yet, sees nothing but `getPortalMe`), the property
  // filter must name one of the customer's properties (else 404), jobs show
  // no prices and the technician's first name only, and registration answers
  // the same whatever exists. There is no email: the single-use links the
  // backend would send are kept in `portalTokens` (read them with
  // `mockPortalToken`, and in development they are logged to the console).
  // Not simulated: the failed-attempt lockouts and the per-IP flood limits;
  // the per-login pay / card limits are (429).

  /** Portal state next to `users`: registration status, link, contact details. */
  private portalAccounts = new Map<string, MockPortalAccount>();
  private portalTokens: MockPortalToken[] = [];
  /** `${tenantId}:${CODE}` (no hyphen) -> invoice id */
  private portalCodes = new Map<string, string>();
  private portalDisabledTenants = new Set<string>();
  /** Calls made per login in the current "hour" (pay, cards) for the 429 limit */
  private portalCalls = new Map<string, number>();
  private portalAccessRequests: MockAccessRequest[] = [];
  private serviceRequests: MockServiceRequest[] = [];
  private rescheduleRequests: MockRescheduleRequest[] = [];

  /** Development / test helper: the portal switch of Settings > Customer portal. */
  setMockPortalEnabled(tenantId: string, enabled: boolean): void {
    if (enabled) this.portalDisabledTenants.delete(tenantId);
    else this.portalDisabledTenants.add(tenantId);
    this.mockCompanySettings(tenantId).portalEnabled = enabled;
  }

  /** The portal is off for the company: the test helper above, or Settings > Customer portal. */
  private portalOff(tenantId: string): boolean {
    return this.portalDisabledTenants.has(tenantId) || this.mockCompanySettings(tenantId).portalEnabled === false;
  }

  /** Development / test helper: the technician first name the portal shows for a staff user. */
  setMockUserFirstName(userId: string, firstName: string | null): void {
    const user = this.users.find((u) => u.id === userId);
    if (user) user.firstName = firstName;
  }

  /** Development / test helper: the registration code of an issued invoice (created on first use, like the backend's). */
  mockPortalCode(invoiceId: string): string {
    const invoice = this.invoices.find((i) => i.id === invoiceId);
    if (!invoice) throw new Error('Invoice not found');
    for (const [key, id] of this.portalCodes) {
      if (id === invoiceId) return formatMockCode(key.split(':')[1]);
    }
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const code = Array.from({ length: 8 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
    this.portalCodes.set(`${invoice.tenantId}:${code}`, invoiceId);
    return formatMockCode(code);
  }

  /** Development / test helper: the newest unspent single-use link token for an address. */
  mockPortalToken(email: string, purpose: 'VERIFY' | 'RESET' | 'INVITE' = 'VERIFY'): string | null {
    const user = this.users.find((u) => u.email === email.trim().toLowerCase());
    if (!user) return null;
    const row = [...this.portalTokens].reverse().find((t) => t.userId === user.id && t.purpose === purpose && !t.usedAt && t.expiresAt > Date.now());
    return row?.token ?? null;
  }

  /** Development / test helper: staff "Invite to portal" (a linked, pending login and its 7-day token). */
  mockInvitePortalUser(customerId: string, email: string, name = ''): string {
    const customer = this.customers.find((c) => c.id === customerId);
    if (!customer) throw new Error('Customer not found');
    const user = this.createPendingPortalUser(customer.tenantId, email.trim().toLowerCase(), name || customer.name, customer.phone ?? null);
    Object.assign(this.portalAccounts.get(user.id)!, { customerId, linkedVia: 'INVITE', linkedAt: new Date().toISOString() });
    return this.issuePortalToken(user, 'INVITE', 7 * 24 * 3600_000, null);
  }

  /** Development / test helper: a linked, active login (what a verified registration or accepted invite leaves). */
  mockLinkedPortalLogin(customerId: string, email: string, password = 'Portal-Pass1'): types.UserDetail {
    const customer = this.customers.find((c) => c.id === customerId);
    if (!customer) throw new Error('Customer not found');
    const user = this.createPendingPortalUser(customer.tenantId, email.trim().toLowerCase(), customer.name, customer.phone ?? null);
    user.password = password;
    const account = this.portalAccounts.get(user.id)!;
    account.status = 'ACTIVE';
    account.customerId = customerId;
    return toUserDetail(user);
  }

  private createPendingPortalUser(tenantId: string, email: string, name: string, phone: string | null): MockUser {
    const user = mockUser(generateId('user'), tenantId, email, types.UserRole.CUSTOMER_PORTAL, {
      // No usable password until the customer chooses one on the verification page
      password: `locked-${generateId('pw')}-${Math.random()}`,
    });
    this.users.push(user);
    this.portalAccounts.set(user.id, { userId: user.id, tenantId, status: 'PENDING_EMAIL', customerId: null, name, phone, accessRequest: null });
    return user;
  }

  private issuePortalToken(user: MockUser, purpose: MockPortalToken['purpose'], ttlMs: number, payload: MockPortalPayload | null): string {
    this.portalTokens.filter((t) => t.userId === user.id && t.purpose === purpose && !t.usedAt).forEach((t) => { t.usedAt = Date.now(); });
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
    const token = Array.from({ length: 43 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
    this.portalTokens.push({ token, purpose, userId: user.id, tenantId: user.tenantId, expiresAt: Date.now() + ttlMs, usedAt: null, payload });
    if (process.env.NODE_ENV !== 'test') {
      console.info(`[mock portal] ${purpose} link for ${user.email}: /portal/${purpose === 'VERIFY' ? 'verify' : purpose === 'RESET' ? 'reset-password' : 'accept-invite'}?token=${token}`);
    }
    return token;
  }

  /** A usable, unspent token of the purpose, or the one INVALID_TOKEN answer. */
  private findPortalToken(raw: unknown, purpose: MockPortalToken['purpose']): MockPortalToken {
    const invalid = () => mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    if (typeof raw !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(raw)) throw invalid();
    const row = this.portalTokens.find((t) => t.token === raw && t.purpose === purpose);
    if (!row || row.usedAt || row.expiresAt <= Date.now()) throw invalid();
    return row;
  }

  private portalPasswordOrThrow(password: unknown): string {
    try {
      validateMockPassword(typeof password === 'string' ? password : undefined);
    } catch (error) {
      throw mockApiError(400, `password: ${(error as Error).message}`, 'VALIDATION_ERROR');
    }
    return password as string;
  }

  async registerPortalAccount(data: types.PortalRegisterRequest): Promise<types.PortalRegisterResult> {
    await delay(150);
    const body = (data ?? {}) as Partial<types.PortalRegisterRequest>;
    const email = typeof body.email === 'string' ? body.email.trim().toLowerCase() : '';
    const name = typeof body.name === 'string' ? body.name.trim().replace(/\s+/g, ' ') : '';
    const phone = typeof body.phone === 'string' ? body.phone.trim() : '';
    const slug = typeof body.tenantSlug === 'string' ? body.tenantSlug.trim().toLowerCase() : '';
    // Shape problems are the only 400s, and say nothing about any account
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(slug)) throw mockApiError(400, 'tenantSlug: A company is required', 'VALIDATION_ERROR');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw mockApiError(400, 'email: Email format is invalid', 'VALIDATION_ERROR');
    if (!name) throw mockApiError(400, 'name: Name is required', 'VALIDATION_ERROR');
    const digits = phone.replace(/\D/g, '');
    if (!phone || digits.length < 7 || digits.length > 15 || !/^[0-9+().\-\s]+$/.test(phone)) {
      throw mockApiError(400, 'phone: Must be a phone number', 'VALIDATION_ERROR');
    }
    if (body.phoneLast4 && String(body.phoneLast4).replace(/\D/g, '').length < 4) {
      throw mockApiError(400, 'phoneLast4: Must be the last 4 digits of the phone number', 'VALIDATION_ERROR');
    }
    if (body.invoiceAmount && !/^\d+(\.\d{1,2})?$/.test(String(body.invoiceAmount).trim())) {
      throw mockApiError(400, 'invoiceAmount: Must be an amount such as 125.50', 'VALIDATION_ERROR');
    }

    const uniform: types.PortalRegisterResult = { registered: true, message: MOCK_PORTAL_REGISTER_MESSAGE };
    const tenant = MOCK_TENANTS.find((t) => t.slug === slug);
    if (!tenant || this.portalOff(tenant.id)) return uniform;

    const existing = this.users.find((u) => u.tenantId === tenant.id && u.email.toLowerCase() === email);
    let user: MockUser | null = null;
    if (!existing) {
      user = this.createPendingPortalUser(tenant.id, email, name, phone);
    } else if (existing.role === types.UserRole.CUSTOMER_PORTAL && existing.isActive && this.portalAccounts.get(existing.id)?.status === 'PENDING_EMAIL') {
      // A newer registration for a still-unconfirmed address replaces the contact details only: never a password
      const account = this.portalAccounts.get(existing.id)!;
      account.name = name;
      account.phone = phone;
      user = existing;
    }
    if (user) {
      const code = typeof body.invoiceCode === 'string' ? body.invoiceCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : '';
      this.issuePortalToken(user, 'VERIFY', 24 * 3600_000, {
        code: code || null,
        address: body.address?.trim() || null,
        phoneLast4: body.phoneLast4 ? String(body.phoneLast4).replace(/\D/g, '').slice(-4) : null,
        invoiceNumber: body.invoiceNumber?.trim().toUpperCase() || null,
        invoiceAmount: body.invoiceAmount?.trim() || null,
      });
    }
    return uniform;
  }

  async verifyPortalEmail(data: types.PortalTokenPasswordRequest): Promise<types.PortalVerifyResult> {
    await delay(150);
    const body = (data ?? {}) as Partial<types.PortalTokenPasswordRequest>;
    if (typeof body.token !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(body.token)) throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    // The password is checked before the link is spent, so a weak one does not burn it
    const password = this.portalPasswordOrThrow(body.password);
    const row = this.findPortalToken(body.token, 'VERIFY');
    const user = this.users.find((u) => u.id === row.userId);
    const account = this.portalAccounts.get(row.userId);
    if (!user || !account || !user.isActive || account.status !== 'PENDING_EMAIL') throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    row.usedAt = Date.now();
    user.password = password;
    account.status = 'ACTIVE';
    if (!account.customerId) this.linkVerifiedPortalUser(user, account, row.payload);
    return { verified: true };
  }

  /** After the email is proven: an invoice code, or exactly one customer with this email and two proof fields; else staff review. */
  private linkVerifiedPortalUser(user: MockUser, account: MockPortalAccount, payload: MockPortalPayload | null): void {
    const tenantId = user.tenantId;
    const loginCount = (customerId: string) => [...this.portalAccounts.values()].filter((a) => a.customerId === customerId).length;
    const maxLogins = this.mockCompanySettings(tenantId).portalMaxLoginsPerCustomer ?? 3;
    const link = (customerId: string, via: types.CustomerPortalLogin['linkedVia']) => {
      account.customerId = customerId;
      account.linkedVia = via;
      account.linkedAt = new Date().toISOString();
    };
    // Files the staff review with what was tried and which proof fields matched which candidate (never the typed values)
    const review = (reason: types.PortalReviewReason, candidates: types.PortalAccessRequest['candidates']) => {
      const createdAt = new Date().toISOString();
      account.accessRequest = { status: 'PENDING', createdAt };
      this.portalAccessRequests.push({
        id: generateId('access'), tenantId, userId: user.id, status: 'PENDING', flagged: false, createdAt, reason,
        codeGiven: !!payload?.code, candidates, decidedAt: null, linkedCustomerId: null, declineReason: null,
      });
    };
    const named = (customerId: string, matched: types.PortalAccessRequest['candidates'][number]['matched']) => ({
      customerId, customerName: this.customers.find((c) => c.id === customerId)?.name ?? null, matched,
    });

    const codeInvoiceId = payload?.code ? this.portalCodes.get(`${tenantId}:${payload.code}`) : undefined;
    const invoice = codeInvoiceId ? this.invoices.find((i) => i.id === codeInvoiceId) : undefined;
    const codeUsable = !!invoice && invoice.tenantId === tenantId && invoice.status !== types.InvoiceStatus.VOID && invoice.status !== types.InvoiceStatus.DRAFT;
    if (codeUsable && invoice) {
      if (loginCount(invoice.billingCustomerId) < maxLogins) link(invoice.billingCustomerId, 'INVOICE_CODE');
      else review('LOGIN_LIMIT', [named(invoice.billingCustomerId, null)]);
      return;
    }

    const matches = this.customers.filter((c) => c.tenantId === tenantId && (c.email ?? '').toLowerCase() === user.email.toLowerCase());
    if (matches.length !== 1 || loginCount(matches[0].id) > 0) {
      const reason: types.PortalReviewReason = matches.length === 0 ? (payload?.code ? 'CODE_NOT_USABLE' : 'NO_EMAIL_MATCH')
        : matches.length > 1 ? 'AMBIGUOUS_EMAIL' : 'CUSTOMER_HAS_LOGIN';
      review(reason, matches.map((c) => named(c.id, null)));
      return;
    }
    const customer = matches[0];
    const own = this.properties.filter((p) => p.tenantId === tenantId && p.customerId === customer.id);
    const invoices = this.invoices
      .filter((i) => i.tenantId === tenantId && i.billingCustomerId === customer.id && i.status !== types.InvoiceStatus.DRAFT && i.status !== types.InvoiceStatus.VOID)
      .sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate));
    const matched = {
      address: !!payload?.address && own.some((p) => normalizeMockAddress(p.address) === normalizeMockAddress(payload.address ?? '')),
      phoneLast4: !!payload?.phoneLast4 && (customer.phone ?? '').replace(/\D/g, '').slice(-4) === payload.phoneLast4,
      invoice: (!!payload?.invoiceNumber && invoices.slice(0, 10).some((i) => i.invoiceNumber.toUpperCase() === payload.invoiceNumber))
        || (!!payload?.invoiceAmount && !!invoices[0] && Math.round(Number(invoices[0].total) * 100) === Math.round(Number(payload.invoiceAmount) * 100)),
    };
    if (Object.values(matched).filter(Boolean).length >= 2) link(customer.id, 'AUTO_EMAIL');
    else review(payload?.code ? 'CODE_NOT_USABLE' : 'TOO_FEW_PROOF_FIELDS', [named(customer.id, matched)]);
  }

  async acceptPortalInvite(data: types.PortalTokenPasswordRequest): Promise<types.PortalOkResult> {
    await delay(150);
    const body = (data ?? {}) as Partial<types.PortalTokenPasswordRequest>;
    if (typeof body.token !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(body.token)) throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    const password = this.portalPasswordOrThrow(body.password);
    const row = this.findPortalToken(body.token, 'INVITE');
    const user = this.users.find((u) => u.id === row.userId);
    const account = this.portalAccounts.get(row.userId);
    if (!user || !account || !account.customerId || this.portalOff(user.tenantId)) throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    row.usedAt = Date.now();
    user.password = password;
    account.status = 'ACTIVE';
    return { ok: true };
  }

  async requestPortalPasswordReset(data: { tenantSlug: string; email: string }): Promise<types.PortalResetRequestResult> {
    await delay(150);
    const slug = typeof data?.tenantSlug === 'string' ? data.tenantSlug.trim().toLowerCase() : '';
    const email = typeof data?.email === 'string' ? data.email.trim().toLowerCase() : '';
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(slug)) throw mockApiError(400, 'tenantSlug: A company is required', 'VALIDATION_ERROR');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw mockApiError(400, 'email: Email format is invalid', 'VALIDATION_ERROR');
    const tenant = MOCK_TENANTS.find((t) => t.slug === slug);
    const user = tenant ? this.users.find((u) => u.tenantId === tenant.id && u.email.toLowerCase() === email) : undefined;
    const account = user ? this.portalAccounts.get(user.id) : undefined;
    // Portal users only (never staff), confirmed and active
    if (tenant && user && user.isActive && user.role === types.UserRole.CUSTOMER_PORTAL && account?.status === 'ACTIVE' && !this.portalOff(tenant.id)) {
      this.issuePortalToken(user, 'RESET', 3600_000, null);
    }
    return { requested: true, message: MOCK_PORTAL_RESET_MESSAGE };
  }

  async confirmPortalPasswordReset(data: types.PortalTokenPasswordRequest): Promise<types.PortalOkResult> {
    await delay(150);
    const body = (data ?? {}) as Partial<types.PortalTokenPasswordRequest>;
    if (typeof body.token !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(body.token)) throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    const password = this.portalPasswordOrThrow(body.password);
    const row = this.findPortalToken(body.token, 'RESET');
    const user = this.users.find((u) => u.id === row.userId);
    if (!user || !user.isActive) throw mockApiError(400, 'This link is not valid or has expired', 'INVALID_TOKEN');
    row.usedAt = Date.now();
    user.password = password;
    return { ok: true };
  }

  // ---- the signed-in customer

  /** CUSTOMER_PORTAL only (every staff role is 403); the portal must be on. */
  private portalScope(): { user: MockUser; account: MockPortalAccount; tenantId: string } {
    const user = this.currentMockUser();
    if (user.role !== types.UserRole.CUSTOMER_PORTAL) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    if (this.portalOff(user.tenantId)) throw mockApiError(403, 'The customer portal is not available', 'PORTAL_DISABLED');
    // A login staff created the old way has no link and no access request
    const account = this.portalAccounts.get(user.id)
      ?? { userId: user.id, tenantId: user.tenantId, status: 'ACTIVE' as const, customerId: null, name: '', phone: null, accessRequest: null };
    if (!this.portalAccounts.has(user.id)) this.portalAccounts.set(user.id, account);
    if (account.status !== 'ACTIVE') throw mockApiError(403, 'Confirm your email address first', 'PORTAL_NOT_LINKED');
    return { user, account, tenantId: user.tenantId };
  }

  private portalCustomerScope(): { user: MockUser; account: MockPortalAccount; tenantId: string; customerId: string } {
    const scope = this.portalScope();
    if (!scope.account.customerId) throw mockApiError(403, 'Your account is not linked to a customer yet', 'PORTAL_NOT_LINKED');
    return { ...scope, customerId: scope.account.customerId };
  }

  /** The customer's own sites and those billed to them. */
  private portalProperties(tenantId: string, customerId: string): types.PortalProperty[] {
    return this.properties
      .filter((p) => p.tenantId === tenantId && (p.customerId === customerId || p.billingCustomerId === customerId))
      .map((p) => ({ id: p.id, address: p.address, city: p.city, state: p.state, zip: p.zip, relation: (p.customerId === customerId ? 'OWNED' : 'BILLED_TO_YOU') as types.PortalRelation }));
  }

  /** A property filter value: one of the customer's (jobs: owned only), else the same 404 as an unknown id. */
  private portalPropertyFilter(tenantId: string, customerId: string, propertyId: string | undefined, ownedOnly: boolean): void {
    if (!propertyId) return;
    const found = this.portalProperties(tenantId, customerId).find((p) => p.id === propertyId && (!ownedOnly || p.relation === 'OWNED'));
    if (!found) throw mockApiError(404, 'Property not found', 'NOT_FOUND');
  }

  private portalListQuery(query: types.PortalListQuery | undefined): { page: number; limit: number; propertyId?: string } {
    const page = query?.page && query.page > 0 ? Math.floor(query.page) : 1;
    const limit = Math.min(query?.limit && query.limit > 0 ? Math.floor(query.limit) : 20, 100);
    return { page, limit, propertyId: query?.propertyId || undefined };
  }

  private portalPage<T>(rows: T[], page: number, limit: number): types.PortalPage<T> {
    return { data: rows.slice((page - 1) * limit, page * limit), pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) } };
  }

  async getPortalMe(): Promise<types.PortalMe> {
    await delay(100);
    const { user, account, tenantId } = this.portalScope();
    const customer = account.customerId ? this.customers.find((c) => c.id === account.customerId && c.tenantId === tenantId) : undefined;
    const [firstName, ...rest] = account.name.split(' ');
    return {
      user: { id: user.id, email: user.email, name: account.name, firstName: firstName || null, lastName: rest.join(' ') || null, phone: account.phone },
      status: customer ? 'LINKED' : 'UNLINKED',
      customerName: customer?.name ?? null,
      companyName: this.mockCompanySettings(tenantId).companyName,
      rescheduleLeadHours: this.mockCompanySettings(tenantId).portalRescheduleLeadHours ?? 48,
      accessRequest: customer ? null : account.accessRequest,
      properties: customer ? this.portalProperties(tenantId, customer.id) : [],
    };
  }

  /** The customer's invoices: not DRAFT or VOID, newest first. */
  private portalInvoiceRows(tenantId: string, customerId: string, propertyId?: string): types.Invoice[] {
    return this.invoices
      .filter((i) => i.tenantId === tenantId && i.billingCustomerId === customerId && i.status !== types.InvoiceStatus.DRAFT && i.status !== types.InvoiceStatus.VOID)
      .filter((i) => !propertyId || i.jobs.some((j) => this.jobs.find((job) => job.id === j.id)?.propertyId === propertyId))
      .sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate) || b.invoiceNumber.localeCompare(a.invoiceNumber));
  }

  /** The one property the invoice's jobs were at (may be owned by someone else but billed to the customer); null for none or several. */
  private portalInvoiceProperty(invoice: types.Invoice): types.PortalAddress | null {
    const ids = new Set(invoice.jobs.map((j) => this.jobs.find((job) => job.id === j.id)?.propertyId).filter((id): id is string => !!id));
    if (ids.size !== 1) return null;
    const property = this.properties.find((p) => p.id === [...ids][0]);
    return property ? { id: property.id, address: property.address, city: property.city, state: property.state, zip: property.zip } : null;
  }

  private portalInvoiceSummary(invoice: types.Invoice): types.PortalInvoiceSummary {
    const balance = this.mockInvoiceBalance(invoice);
    return {
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      status: invoice.status,
      invoiceType: invoice.invoiceType ?? 'STANDARD',
      invoiceDate: invoice.invoiceDate.slice(0, 10),
      dueDate: invoice.dueDate.slice(0, 10),
      periodStartDate: invoice.periodStartDate?.slice(0, 10) ?? null,
      periodEndDate: invoice.periodEndDate?.slice(0, 10) ?? null,
      total: Number(invoice.total).toFixed(2),
      amountPaid: (Number(invoice.total) - balance).toFixed(2),
      balanceDue: balance.toFixed(2),
      property: this.portalInvoiceProperty(invoice),
    };
  }

  async listPortalInvoices(query?: types.PortalListQuery): Promise<types.PortalPage<types.PortalInvoiceSummary>> {
    await delay(150);
    const { tenantId, customerId } = this.portalCustomerScope();
    const { page, limit, propertyId } = this.portalListQuery(query);
    this.portalPropertyFilter(tenantId, customerId, propertyId, false);
    return this.portalPage(this.portalInvoiceRows(tenantId, customerId, propertyId).map((i) => this.portalInvoiceSummary(i)), page, limit);
  }

  private portalInvoiceOr404(tenantId: string, customerId: string, id: string): types.Invoice {
    const invoice = this.portalInvoiceRows(tenantId, customerId).find((i) => i.id === id);
    if (!invoice) throw mockApiError(404, 'Invoice not found', 'NOT_FOUND');
    return invoice;
  }

  async getPortalInvoice(id: string): Promise<types.PortalInvoiceDetail> {
    await delay(150);
    const { tenantId, customerId } = this.portalCustomerScope();
    const invoice = this.portalInvoiceOr404(tenantId, customerId, id);
    const payments = this.payments
      .filter((p) => p.status === 'ACTIVE')
      .flatMap((p) => p.applications.filter((a) => a.invoiceId === invoice.id).map((a) => ({
        date: p.paymentDate.slice(0, 10), method: String(p.paymentMethod), cardBrand: p.cardBrand ?? null, cardLast4: p.cardLast4 ?? null, amount: Number(a.amountApplied).toFixed(2),
      })));
    return {
      ...this.portalInvoiceSummary(invoice),
      subtotal: Number(invoice.subtotal ?? invoice.totalAmount).toFixed(2),
      totalDiscount: Number(invoice.totalDiscount ?? 0).toFixed(2),
      totalTax: Number(invoice.totalTax).toFixed(2),
      lines: invoice.lineItems.map((l) => ({
        description: l.description, quantity: l.quantity, unitPrice: Number(l.unitPrice).toFixed(2), taxRate: l.taxRate,
        amount: Number(l.lineAmount).toFixed(2), discount: Number(l.lineDiscount ?? 0).toFixed(2), tax: Number(l.lineTax).toFixed(2),
      })),
      discounts: (invoice.discounts ?? []).map((d) => ({ name: d.name, type: d.type, amount: Number(d.amount).toFixed(2) })),
      payments,
    };
  }

  private portalJobRows(tenantId: string, customerId: string, propertyId?: string): Array<types.Job & { tenantId: string }> {
    return this.jobs
      .filter((j) => j.tenantId === tenantId && j.status !== types.JobStatus.DRAFT)
      .filter((j) => this.properties.find((p) => p.id === j.propertyId)?.customerId === customerId)
      .filter((j) => !propertyId || j.propertyId === propertyId)
      .sort((a, b) => (b.scheduledDate ?? '9999').localeCompare(a.scheduledDate ?? '9999') || (b.scheduledStartTime ?? '').localeCompare(a.scheduledStartTime ?? ''));
  }

  /** No prices, notes, checklists or internal ids; the technician's first name only. */
  private portalJob(job: types.Job & { tenantId: string }): types.PortalJob {
    const property = this.properties.find((p) => p.id === job.propertyId)!;
    const tech = job.assignedTo ? this.users.find((u) => u.id === job.assignedTo) : undefined;
    const status: types.PortalJobStatus =
      job.status === types.JobStatus.INVOICED || job.status === types.JobStatus.COMPLETED ? 'COMPLETED'
        : job.status === types.JobStatus.IN_PROGRESS ? 'IN_PROGRESS'
          : job.status === types.JobStatus.CANCELLED ? 'CANCELLED' : 'SCHEDULED';
    return {
      id: job.id,
      status,
      scheduledDate: job.scheduledDate ?? null,
      scheduledStartTime: job.scheduledStartTime ?? null,
      arrivalWindowMinutes: job.arrivalWindowMinutes ?? null,
      completedDate: job.completedDate ?? null,
      property: { id: property.id, address: property.address, city: property.city, state: property.state, zip: property.zip },
      technician: tech?.firstName?.trim() || 'Your technician',
      items: job.lineItems.map((l) => ({ name: l.item.name, quantity: l.quantity })),
    };
  }

  async listPortalJobs(query?: types.PortalListQuery): Promise<types.PortalPage<types.PortalJob>> {
    await delay(150);
    const { tenantId, customerId } = this.portalCustomerScope();
    const { page, limit, propertyId } = this.portalListQuery(query);
    this.portalPropertyFilter(tenantId, customerId, propertyId, true);
    return this.portalPage(this.portalJobRows(tenantId, customerId, propertyId).map((j) => this.portalJob(j)), page, limit);
  }

  async getPortalJob(id: string): Promise<types.PortalJob> {
    await delay(150);
    const { tenantId, customerId } = this.portalCustomerScope();
    const job = this.portalJobRows(tenantId, customerId).find((j) => j.id === id);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    return this.portalJob(job);
  }

  async getPortalStatement(query?: { page?: number; limit?: number }): Promise<types.PortalStatement> {
    await delay(150);
    const { tenantId, customerId } = this.portalCustomerScope();
    const { page, limit } = this.portalListQuery(query);
    const entries: Array<{ date: string; type: string; reference: string; description: string; charge: number; credit: number }> = [];
    for (const invoice of this.portalInvoiceRows(tenantId, customerId)) {
      entries.push({ date: invoice.invoiceDate.slice(0, 10), type: 'INVOICE', reference: invoice.invoiceNumber, description: `Invoice ${invoice.invoiceNumber}`, charge: Number(invoice.total), credit: 0 });
    }
    for (const payment of this.payments.filter((p) => p.tenantId === tenantId && p.billingCustomerId === customerId && p.status === 'ACTIVE')) {
      entries.push({ date: payment.paymentDate.slice(0, 10), type: 'PAYMENT', reference: payment.referenceNumber ?? '', description: `Payment (${payment.paymentMethod})`, charge: 0, credit: Number(payment.amount) });
    }
    entries.sort((a, b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type));
    let running = 0;
    const lines: types.PortalStatementLine[] = entries.map((e) => {
      running = Math.round((running + e.charge - e.credit) * 100) / 100;
      return { date: e.date, type: e.type, reference: e.reference, description: e.description, charge: e.charge.toFixed(2), credit: e.credit.toFixed(2), runningBalance: running.toFixed(2) };
    });
    const customer = this.customers.find((c) => c.id === customerId)!;
    return {
      customerName: customer.name,
      statementDate: new Date().toISOString().slice(0, 10),
      openingBalance: '0.00',
      closingBalance: running.toFixed(2),
      lines: lines.slice((page - 1) * limit, page * limit),
      pagination: { page, limit, total: lines.length, pages: Math.ceil(lines.length / limit) },
    };
  }

  private portalProfile(user: MockUser, account: MockPortalAccount, customerId: string): types.PortalProfile {
    const customer = this.customers.find((c) => c.id === customerId)!;
    const [firstName, ...rest] = account.name.split(' ');
    return {
      email: user.email, name: account.name, firstName: firstName || null, lastName: rest.join(' ') || null,
      phone: account.phone, notificationOptOut: customer.notificationOptOut === true, customerName: customer.name,
    };
  }

  async getPortalProfile(): Promise<types.PortalProfile> {
    await delay(100);
    const { user, account, customerId } = this.portalCustomerScope();
    return this.portalProfile(user, account, customerId);
  }

  async updatePortalProfile(data: types.PortalProfileUpdate): Promise<types.PortalProfile> {
    await delay(150);
    const { user, account, customerId } = this.portalCustomerScope();
    const body = (data ?? {}) as types.PortalProfileUpdate;
    if (body.name === undefined && body.phone === undefined && body.notificationOptOut === undefined) {
      throw mockApiError(400, 'Nothing to update', 'VALIDATION_ERROR');
    }
    if (body.name !== undefined && (typeof body.name !== 'string' || !body.name.trim() || body.name.trim().length > 200)) {
      throw mockApiError(400, 'name: Name is required', 'VALIDATION_ERROR');
    }
    if (body.phone !== undefined && body.phone !== null && body.phone !== '') {
      const phone = String(body.phone).trim();
      const digits = phone.replace(/\D/g, '');
      if (!/^[0-9+().\-\s]+$/.test(phone) || digits.length < 7 || digits.length > 15) throw mockApiError(400, 'phone: Must be a phone number', 'VALIDATION_ERROR');
    }
    if (body.notificationOptOut !== undefined && typeof body.notificationOptOut !== 'boolean') {
      throw mockApiError(400, 'notificationOptOut: Must be true or false', 'VALIDATION_ERROR');
    }
    if (body.name !== undefined) account.name = body.name.trim().replace(/\s+/g, ' ');
    if (body.phone !== undefined) account.phone = body.phone ? String(body.phone).trim() : null;
    if (body.notificationOptOut !== undefined) {
      const customer = this.customers.find((c) => c.id === customerId)!;
      customer.notificationOptOut = body.notificationOptOut;
    }
    return this.portalProfile(user, account, customerId);
  }

  /** Per-login hourly limits of the write routes (the backend's are in memory too): pay 20, cards 10. */
  private portalLimit(userId: string, bucket: 'pay' | 'cards' | 'request' | 'reschedule', max: number): void {
    const key = `${bucket}:${userId}`;
    const used = (this.portalCalls.get(key) ?? 0) + 1;
    this.portalCalls.set(key, used);
    if (used > max) throw mockApiError(429, 'Too many requests', 'RATE_LIMITED');
  }

  /** Development / test helper: starts a fresh "hour" for the per-login limits. */
  resetMockPortalLimits(): void {
    this.portalCalls.clear();
  }

  /** Development / test helper: online payments on and a (fake) processor connected, so Pay now and cards work. */
  enableMockOnlinePayments(tenantId: string): void {
    this.mockOnlineSettings(tenantId).enabled = true;
    this.onlineConnections.set(tenantId, {
      ...this.mockConnection(tenantId),
      connected: true, provider: 'FAKE', providerName: 'Test processor', environment: 'SANDBOX', status: 'CONNECTED',
      accountId: 'FAKE-ACCT-1', accountName: 'Mock merchant', locationId: 'FKLOC-1', locationName: 'Main',
      locations: [{ id: 'FKLOC-1', name: 'Main' }], clientConfig: { provider: 'FAKE', environment: 'SANDBOX', siteId: 'FKLOC-1' },
    });
  }

  async getPortalPayConfig(invoiceId: string): Promise<types.PublicPayConfig> {
    await delay(100);
    const { tenantId, customerId } = this.portalCustomerScope();
    return this.mockPayConfig(tenantId, this.portalInvoiceOr404(tenantId, customerId, invoiceId));
  }

  async payPortalInvoice(invoiceId: string, data: types.PortalPayRequest): Promise<types.PublicPayResult> {
    await delay(300);
    const { user, tenantId, customerId } = this.portalCustomerScope();
    this.portalLimit(user.id, 'pay', 20);
    const invoice = this.portalInvoiceOr404(tenantId, customerId, invoiceId);
    if (typeof data?.sourceId !== 'string' || !data.sourceId) throw mockApiError(400, 'sourceId: A card token is required', 'VALIDATION_ERROR');
    // The key follows the card token: the same token again is the same attempt (one charge)
    const key = `portal:${user.id}:${invoice.id}:${data.sourceId}`;
    let attempt = this.onlineAttempts.find((a) => a.key === key);
    if (!attempt) {
      const config = this.mockPayConfig(tenantId, invoice);
      if (!config.available) throw mockApiError(409, 'Online payment is not available for this invoice', 'ONLINE_PAYMENTS_UNAVAILABLE');
      const amount = Number(data.amount);
      if (!/^\d+(\.\d{1,2})?$/.test(String(data.amount)) || !(amount > 0) || amount > Number(config.balanceDue) || amount < Number(config.minAmount)) {
        throw mockApiError(400, `amount: between ${config.minAmount} and ${config.balanceDue}`, 'VALIDATION_ERROR');
      }
      if (data.autopayConsent !== undefined && !(data.autopayConsent?.accepted === true && data.autopayConsent.version === 'v1')) {
        throw mockApiError(400, 'autopayConsent: Agree to the current wording', 'CONSENT_REQUIRED');
      }
      let methodId: string | null = null;
      if (data.saveCard && this.mockTokenOutcome(data.sourceId) === 'ok') {
        methodId = this.mockAddMethod(tenantId, customerId, data.autopayConsent ? `PORTAL:${user.id}` : null, user.id).id;
      }
      attempt = this.mockOnlineCharge(tenantId, invoice, amount, 'PORTAL', this.mockTokenOutcome(data.sourceId), key, methodId, user.id);
    }
    const method = attempt.paymentMethodId ? this.onlineMethods.find((m) => m.id === attempt!.paymentMethodId) : undefined;
    const paid = ['RECORDED', 'CHARGED', 'RECORD_FAILED'].includes(attempt.status);
    const status: types.PublicPayResult['status'] = paid ? 'PAID' : attempt.status === 'ERROR' || attempt.status === 'PENDING' ? 'PROCESSING' : 'DECLINED';
    return {
      status, amount: attempt.amount,
      message: status === 'PAID' ? 'Thank you, your payment was received.'
        : status === 'PROCESSING' ? 'We could not confirm your payment yet. Please do not pay again: you will receive a receipt if it went through.'
          : `${attempt.failureMessage ?? 'The card was declined.'} Please try another card.`,
      receiptUrl: paid ? attempt.receiptUrl : null, card: { brand: attempt.cardBrand, last4: attempt.cardLast4 },
      balanceDue: this.mockInvoiceBalance(invoice).toFixed(2), cardSaved: !!method, autopayEnabled: !!method?.autopayEnabled,
    };
  }

  private portalMethod(m: types.CustomerPaymentMethod): types.PortalPaymentMethod {
    return {
      id: m.id, brand: m.brand, last4: m.last4, expMonth: m.expMonth, expYear: m.expYear, isDefault: m.isDefault,
      autopayEnabled: m.autopayEnabled, autopayConsentAt: m.autopayConsentAt, autopayConsentVersion: m.autopayConsentVersion, status: m.status,
    };
  }

  private portalCardsAvailable(tenantId: string): boolean {
    return this.mockOnlineSettings(tenantId).enabled && this.mockConnection(tenantId).status === 'CONNECTED';
  }

  async getPortalPaymentMethods(): Promise<types.PortalCards> {
    await delay(100);
    const { tenantId, customerId } = this.portalCustomerScope();
    const available = this.portalCardsAvailable(tenantId);
    return {
      available,
      unavailableReason: available ? null : 'Saving a card is not available right now.',
      clientConfig: available ? { provider: 'FAKE', environment: 'SANDBOX', siteId: 'FKLOC-1' } : null,
      autopayConsent: available ? { version: 'v1', text: this.mockConsentText(tenantId) } : null,
      methods: this.onlineMethods.filter((m) => m.tenantId === tenantId && m.customerId === customerId && m.status !== 'REMOVED').map((m) => this.portalMethod(m)),
    };
  }

  private portalMethodOr404(tenantId: string, customerId: string, id: string) {
    const method = this.onlineMethods.find((m) => m.id === id && m.tenantId === tenantId && m.customerId === customerId && m.status !== 'REMOVED');
    if (!method) throw mockApiError(404, 'Card not found', 'NOT_FOUND');
    return method;
  }

  private portalConsentOrThrow(consent: types.AutopayConsentInput | undefined): void {
    if (!(consent?.accepted === true && consent.version === 'v1')) {
      throw mockApiError(400, 'autopayConsent: Agree to the current wording to turn autopay on', 'CONSENT_REQUIRED');
    }
  }

  async addPortalPaymentMethod(data: types.PortalAddCardRequest): Promise<types.PortalPaymentMethod> {
    await delay(300);
    const { user, tenantId, customerId } = this.portalCustomerScope();
    this.portalLimit(user.id, 'cards', 10);
    if (!this.portalCardsAvailable(tenantId)) throw mockApiError(409, 'Saving a card is not available right now', 'ONLINE_PAYMENTS_UNAVAILABLE');
    if (typeof data?.sourceId !== 'string' || !data.sourceId) throw mockApiError(400, 'sourceId: A card token is required', 'VALIDATION_ERROR');
    if (data.autopayConsent !== undefined) this.portalConsentOrThrow(data.autopayConsent);
    if (this.mockTokenOutcome(data.sourceId) !== 'ok') throw mockApiError(422, 'The card could not be saved. Please try another card.', 'CARD_DECLINED');
    const method = this.mockAddMethod(tenantId, customerId, data.autopayConsent ? `PORTAL:${user.id}` : null, user.id);
    if (data.makeDefault && !method.isDefault) {
      this.onlineMethods.filter((m) => m.tenantId === tenantId && m.customerId === customerId).forEach((m) => { m.isDefault = m.id === method.id; });
    }
    return this.portalMethod(method);
  }

  async removePortalPaymentMethod(methodId: string): Promise<types.PortalPaymentMethod> {
    await delay(200);
    const { tenantId, customerId } = this.portalCustomerScope();
    const method = this.portalMethodOr404(tenantId, customerId, methodId);
    method.status = 'REMOVED';
    method.autopayEnabled = false;
    method.isDefault = false;
    return this.portalMethod(method);
  }

  async setPortalAutopay(methodId: string, data: types.PortalAutopayRequest): Promise<types.PortalPaymentMethod> {
    await delay(200);
    const { user, tenantId, customerId } = this.portalCustomerScope();
    this.portalLimit(user.id, 'cards', 10);
    const method = this.portalMethodOr404(tenantId, customerId, methodId);
    if (typeof data?.enabled !== 'boolean') throw mockApiError(400, 'enabled: Must be true or false', 'VALIDATION_ERROR');
    if (data.enabled) {
      if (!this.portalCardsAvailable(tenantId)) throw mockApiError(409, 'Autopay is not available right now', 'ONLINE_PAYMENTS_UNAVAILABLE');
      // Always asked for again, even when a consent is on record
      this.portalConsentOrThrow(data.autopayConsent);
      this.onlineMethods.filter((m) => m.tenantId === tenantId && m.customerId === customerId && m.id !== method.id).forEach((m) => { m.autopayEnabled = false; m.isDefault = false; });
      Object.assign(method, {
        autopayEnabled: true, isDefault: true, autopayConsentAt: new Date().toISOString(),
        autopayConsentVersion: 'v1:mock', autopayConsentBy: `PORTAL:${user.id}`,
      });
    } else {
      // Off keeps the consent record as evidence
      method.autopayEnabled = false;
    }
    return this.portalMethod(method);
  }

  // ============ Customer portal, phase 4: requests (tracker item 45) ============
  //
  // In-memory twin of the service request, reschedule request and portal
  // access request routes (portal and office). Same rules as the backend:
  // visibility (a customer's requests plus the login's own), the 20 open
  // requests / 30 per hour limits, the reschedule eligibility order
  // (JOB_NOT_RESCHEDULABLE, JOB_NOT_SCHEDULED, RESCHEDULE_TOO_LATE,
  // RESCHEDULE_DATE_NOT_FUTURE, RESCHEDULE_NO_CHANGE, RESCHEDULE_REQUEST_OPEN),
  // auto-approve when nothing is in the way, and office convert / decline /
  // approve with RESCHEDULE_CONFLICTS and overrideConflicts. Approximations:
  // the lead time reads the job's date and time in the browser's timezone (the
  // backend uses the company timezone), the conflict check is the overlap and
  // working-hours check of the mock dispatch board, and no emails are sent.

  /** The company's "today" (YYYY-MM-DD) in its timezone. */
  private mockCompanyToday(tenantId: string): string {
    const timeZone = this.mockCompanySettings(tenantId).timezone ?? 'America/New_York';
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  }

  private mockPropertyAddress(propertyId: string | null): types.PortalAddress | null {
    const property = propertyId ? this.properties.find((p) => p.id === propertyId) : undefined;
    return property ? { id: property.id, address: property.address, city: property.city, state: property.state, zip: property.zip } : null;
  }

  private mockPage<T>(rows: T[], query: types.RequestPageQuery | undefined): types.PortalPage<T> {
    const page = query?.page && query.page > 0 ? Math.floor(query.page) : 1;
    const limit = Math.min(query?.limit && query.limit > 0 ? Math.floor(query.limit) : 20, 100);
    return { data: rows.slice((page - 1) * limit, page * limit), pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) } };
  }

  private portalServiceRequestView(r: MockServiceRequest): types.PortalServiceRequest {
    return {
      id: r.id,
      status: r.status,
      requestedItem: r.requestedItemName,
      property: this.mockPropertyAddress(r.propertyId),
      serviceAddress: r.serviceAddress,
      preferredDates: [...r.preferredDates],
      preferredTimeOfDay: r.preferredTimeOfDay,
      notes: r.notes,
      createdAt: r.createdAt,
      decidedAt: r.decidedAt,
      declineReason: r.declineReason,
    };
  }

  async listPortalRequestableItems(): Promise<types.PortalRequestableItem[]> {
    await delay(100);
    const { tenantId } = this.portalScope();
    return this.items
      .filter((i) => i.tenantId === tenantId && !i.deletedAt && i.portalRequestable && (i.itemType === types.ItemType.SERVICE || i.itemType === types.ItemType.LABOR))
      .map((i) => ({ id: i.id, name: i.name, description: i.description }));
  }

  async createPortalServiceRequest(data: types.PortalServiceRequestInput): Promise<types.PortalServiceRequest> {
    await delay(200);
    const { user, account, tenantId } = this.portalScope();
    this.portalLimit(user.id, 'request', 30);
    const body = (data ?? {}) as Record<string, unknown>;
    const invalid = (message: string) => mockApiError(400, message, 'VALIDATION_ERROR');
    const text = (raw: unknown, field: string, max: number): string | null => {
      if (raw === undefined || raw === null) return null;
      if (typeof raw !== 'string') throw invalid(`${field}: must be text`);
      const value = raw.trim();
      if (value.length > max) throw invalid(`${field}: at most ${max} characters`);
      return value || null;
    };
    const requestedItemId = text(body.requestedItemId, 'requestedItemId', 64);
    const propertyId = text(body.propertyId, 'propertyId', 64);
    const serviceAddress = text(body.serviceAddress, 'serviceAddress', 300);
    const notes = text(body.notes, 'notes', 2000);
    const contactName = text(body.contactName, 'contactName', 200);
    const contactPhone = text(body.contactPhone, 'contactPhone', 50);
    if (!requestedItemId && !notes) throw invalid('Choose a service or describe what you need in the notes');
    if (propertyId && serviceAddress) throw invalid('Give either one of your properties or a new address, not both');

    const today = this.mockCompanyToday(tenantId);
    let preferredDates: string[] = [];
    if (body.preferredDates !== undefined && body.preferredDates !== null) {
      if (!Array.isArray(body.preferredDates)) throw invalid('preferredDates: must be a list of dates');
      if (body.preferredDates.length > 5) throw invalid('preferredDates: at most 5 dates');
      for (const date of body.preferredDates) {
        if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date) || new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date) {
          throw invalid('preferredDates: dates must be YYYY-MM-DD');
        }
        if (date < today) throw invalid('preferredDates: a date cannot be in the past');
      }
      preferredDates = [...new Set(body.preferredDates as string[])].sort();
    }
    let preferredTimeOfDay: types.ServiceRequestTimeOfDay | null = null;
    if (body.preferredTimeOfDay !== undefined && body.preferredTimeOfDay !== null && body.preferredTimeOfDay !== '') {
      if (!types.SERVICE_REQUEST_TIMES_OF_DAY.includes(body.preferredTimeOfDay as types.ServiceRequestTimeOfDay)) {
        throw invalid('preferredTimeOfDay: one of ANY, MORNING, AFTERNOON, EVENING');
      }
      preferredTimeOfDay = body.preferredTimeOfDay as types.ServiceRequestTimeOfDay;
    }

    if (this.serviceRequests.filter((r) => r.tenantId === tenantId && r.createdByUserId === user.id && r.status === 'NEW').length >= 20) {
      throw mockApiError(409, 'You have too many open requests; please wait for a reply', 'TOO_MANY_OPEN_REQUESTS');
    }
    let ownedPropertyId: string | null = null;
    if (propertyId) {
      // One of the customer's OWN properties; anything else (billed only, someone else's, unknown, or no link) is a 404
      const own = account.customerId
        ? this.properties.find((p) => p.id === propertyId && p.tenantId === tenantId && p.customerId === account.customerId)
        : undefined;
      if (!own) throw mockApiError(404, 'Property not found', 'NOT_FOUND');
      ownedPropertyId = own.id;
    } else if (!serviceAddress) {
      throw invalid(account.customerId ? 'Choose one of your properties or give the address of the new one' : 'Give the address where you need the service');
    }
    let itemName: string | null = null;
    if (requestedItemId) {
      const item = this.items.find((i) => i.id === requestedItemId && i.tenantId === tenantId && !i.deletedAt && i.portalRequestable);
      if (!item) throw mockApiError(404, 'Service not found', 'NOT_FOUND');
      itemName = item.name;
    }
    const name = contactName ?? (account.name.trim() || null);
    if (!name) throw invalid('contactName: your name is required');

    const request: MockServiceRequest = {
      id: generateId('sreq'), tenantId, customerId: account.customerId, propertyId: ownedPropertyId, source: 'PORTAL', status: 'NEW',
      requestedItemId, requestedItemName: itemName, serviceAddress, preferredDates, preferredTimeOfDay, notes,
      contactName: name, contactEmail: user.email, contactPhone: contactPhone ?? account.phone, convertedJobId: null,
      decidedById: null, decidedAt: null, declineReason: null, createdByUserId: user.id, createdAt: new Date().toISOString(),
    };
    this.serviceRequests.push(request);
    return this.portalServiceRequestView(request);
  }

  /** A customer's requests plus the ones the login made itself (so a prospect keeps seeing theirs). */
  private portalVisibleRequests(user: MockUser, account: MockPortalAccount, tenantId: string): MockServiceRequest[] {
    return this.serviceRequests
      .filter((r) => r.tenantId === tenantId && ((!!account.customerId && r.customerId === account.customerId) || r.createdByUserId === user.id))
      .reverse() // requests made in the same millisecond stay newest first
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async listPortalServiceRequests(query?: types.RequestPageQuery): Promise<types.PortalPage<types.PortalServiceRequest>> {
    await delay(150);
    const { user, account, tenantId } = this.portalScope();
    return this.mockPage(this.portalVisibleRequests(user, account, tenantId).map((r) => this.portalServiceRequestView(r)), query);
  }

  async getPortalServiceRequest(id: string): Promise<types.PortalServiceRequest> {
    await delay(100);
    const { user, account, tenantId } = this.portalScope();
    const found = this.portalVisibleRequests(user, account, tenantId).find((r) => r.id === id);
    if (!found) throw mockApiError(404, 'Service request not found', 'NOT_FOUND');
    return this.portalServiceRequestView(found);
  }

  private portalRescheduleView(r: MockRescheduleRequest): types.PortalRescheduleRequest {
    return {
      id: r.id, jobId: r.jobId, status: r.status, requestedDate: r.requestedDate, requestedStartTime: r.requestedStartTime,
      requestedWindow: r.requestedWindow, reason: r.reason, approvedDate: r.approvedDate, approvedStartTime: r.approvedStartTime,
      declineReason: r.declineReason, createdAt: r.createdAt, decidedAt: r.decidedAt,
    };
  }

  /** The customer's own job (owned property, not a draft) for a reschedule call; any other id is a 404. */
  private portalRescheduleJob(tenantId: string, customerId: string, jobId: string): types.Job & { tenantId: string } {
    const job = this.portalJobRows(tenantId, customerId).find((j) => j.id === jobId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    return job;
  }

  async createPortalRescheduleRequest(jobId: string, data: types.PortalRescheduleInput): Promise<types.PortalRescheduleRequest> {
    await delay(200);
    const { user, tenantId, customerId } = this.portalCustomerScope();
    this.portalLimit(user.id, 'reschedule', 30);
    const job = this.portalRescheduleJob(tenantId, customerId, jobId);
    const body = (data ?? {}) as unknown as Record<string, unknown>;
    const invalid = (message: string) => mockApiError(400, message, 'VALIDATION_ERROR');
    const requestedDate = body.requestedDate;
    if (typeof requestedDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(requestedDate) || new Date(`${requestedDate}T00:00:00Z`).toISOString().slice(0, 10) !== requestedDate) {
      throw invalid('requestedDate: a date (YYYY-MM-DD) is required');
    }
    let requestedStartTime: string | null = null;
    if (body.requestedStartTime !== undefined && body.requestedStartTime !== null && body.requestedStartTime !== '') {
      if (typeof body.requestedStartTime !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(body.requestedStartTime)) {
        throw invalid('requestedStartTime: must be HH:MM (00:00 to 23:59)');
      }
      requestedStartTime = body.requestedStartTime;
    }
    const optionalText = (raw: unknown, field: string, max: number): string | null => {
      if (raw === undefined || raw === null) return null;
      if (typeof raw !== 'string') throw invalid(`${field}: must be text`);
      if (raw.trim().length > max) throw invalid(`${field}: at most ${max} characters`);
      return raw.trim() || null;
    };
    const requestedWindow = optionalText(body.requestedWindow, 'requestedWindow', 100);
    const reason = optionalText(body.reason, 'reason', 500);

    // The backend's order of rules
    const settings = this.mockCompanySettings(tenantId);
    if (job.status !== types.JobStatus.SCHEDULED && job.status !== types.JobStatus.WORK_ORDER_GENERATED) {
      throw mockApiError(409, 'Only a scheduled visit can be rescheduled', 'JOB_NOT_RESCHEDULABLE');
    }
    if (!job.scheduledDate) throw mockApiError(409, 'This visit has no date yet, so there is nothing to reschedule', 'JOB_NOT_SCHEDULED');
    const leadHours = settings.portalRescheduleLeadHours ?? 48;
    const startsAt = new Date(`${job.scheduledDate}T${job.scheduledStartTime ?? '00:00'}:00`).getTime();
    if ((startsAt - Date.now()) / 3_600_000 < leadHours) {
      throw mockApiError(409, `Visits can only be rescheduled online at least ${leadHours} hours ahead; please call the office`, 'RESCHEDULE_TOO_LATE');
    }
    if (requestedDate <= this.mockCompanyToday(tenantId)) throw mockApiError(409, 'Choose a date in the future', 'RESCHEDULE_DATE_NOT_FUTURE');
    if (requestedDate === job.scheduledDate && (requestedStartTime === null || requestedStartTime === (job.scheduledStartTime ?? null))) {
      throw mockApiError(409, 'The visit is already booked for that date and time', 'RESCHEDULE_NO_CHANGE');
    }
    if (this.rescheduleRequests.some((r) => r.jobId === job.id && r.status === 'OPEN')) {
      throw mockApiError(409, 'This visit already has an open reschedule request', 'RESCHEDULE_REQUEST_OPEN');
    }

    const request: MockRescheduleRequest = {
      id: generateId('rreq'), tenantId, jobId: job.id, customerId, requestedByUserId: user.id,
      originalDate: job.scheduledDate, originalStartTime: job.scheduledStartTime ?? null, requestedDate, requestedStartTime,
      requestedWindow, reason, status: 'OPEN', decidedById: null, decidedAt: null, declineReason: null,
      approvedDate: null, approvedStartTime: null, createdAt: new Date().toISOString(),
    };
    this.rescheduleRequests.push(request);

    // Auto-approve: an assignee, a start time, and no conflict (never an error for the customer)
    const startTime = requestedStartTime ?? job.scheduledStartTime ?? null;
    if ((settings.portalRescheduleAutoApprove ?? true) && job.assignedTo && startTime
      && this.mockRescheduleConflicts(job, requestedDate, startTime).length === 0) {
      this.mockApplyReschedule(job, requestedDate, startTime);
      Object.assign(request, { status: 'AUTO_APPROVED', decidedAt: new Date().toISOString(), approvedDate: requestedDate, approvedStartTime: startTime });
    }
    return this.portalRescheduleView(request);
  }

  async listPortalRescheduleRequests(jobId: string): Promise<types.PortalRescheduleRequest[]> {
    await delay(100);
    const { tenantId, customerId } = this.portalCustomerScope();
    const job = this.portalRescheduleJob(tenantId, customerId, jobId);
    return this.rescheduleRequests
      .filter((r) => r.tenantId === tenantId && r.jobId === job.id && r.customerId === customerId)
      .reverse()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((r) => this.portalRescheduleView(r));
  }

  async cancelPortalRescheduleRequest(jobId: string, requestId: string): Promise<types.PortalRescheduleRequest> {
    await delay(100);
    const { tenantId, customerId } = this.portalCustomerScope();
    const job = this.portalRescheduleJob(tenantId, customerId, jobId);
    const request = this.rescheduleRequests.find((r) => r.id === requestId && r.jobId === job.id && r.customerId === customerId);
    if (!request) throw mockApiError(404, 'Reschedule request not found', 'NOT_FOUND');
    if (request.status !== 'OPEN') throw mockApiError(409, 'This request has already been decided or cancelled', 'RESCHEDULE_REQUEST_NOT_OPEN');
    Object.assign(request, { status: 'CANCELLED', decidedAt: new Date().toISOString() });
    return this.portalRescheduleView(request);
  }

  /** Overlaps and working hours for the job's assignee on a date and time (the mock dispatch board's checks). */
  private mockRescheduleConflicts(job: types.Job & { tenantId: string }, date: string, startTime: string | null): types.ScheduleConflict[] {
    const conflicts = this.mockOverlaps(job.tenantId, job.id, {
      assignedTo: job.assignedTo ?? null, scheduledDate: date, scheduledStartTime: startTime, duration: job.estimatedDurationMinutes ?? 60,
    });
    const settings = this.mockCompanySettings(job.tenantId);
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    const workDays = settings.workDays ?? [1, 2, 3, 4, 5];
    const start = settings.workdayStart ?? '08:00';
    const end = settings.workdayEnd ?? '17:00';
    if (!workDays.includes(weekday) || (startTime !== null && (startTime < start || startTime >= end))) {
      conflicts.push({ type: 'OUTSIDE_WORKING_HOURS', message: `${date}${startTime ? ` ${startTime}` : ''} is outside working hours` });
    }
    return conflicts;
  }

  private mockApplyReschedule(job: types.Job & { tenantId: string }, date: string, startTime: string | null): void {
    job.scheduledDate = date;
    job.scheduledStartTime = startTime;
    job.updatedAt = new Date().toISOString();
  }

  // ---- office: service requests

  private serviceRequestOffice(r: MockServiceRequest): types.ServiceRequest {
    const customer = r.customerId ? this.customers.find((c) => c.id === r.customerId) : undefined;
    return {
      id: r.id, source: r.source, status: r.status,
      customer: customer ? { id: customer.id, name: customer.name } : null,
      property: this.mockPropertyAddress(r.propertyId),
      serviceAddress: r.serviceAddress,
      requestedItem: r.requestedItemId || r.requestedItemName ? { id: r.requestedItemId, name: r.requestedItemName } : null,
      preferredDates: [...r.preferredDates], preferredTimeOfDay: r.preferredTimeOfDay, notes: r.notes,
      contact: { name: r.contactName, email: r.contactEmail, phone: r.contactPhone },
      convertedJobId: r.convertedJobId, decidedById: r.decidedById, decidedAt: r.decidedAt, declineReason: r.declineReason,
      createdByUserId: r.createdByUserId, createdAt: r.createdAt,
    };
  }

  private serviceRequestOr404(tenantId: string, id: string): MockServiceRequest {
    const found = this.serviceRequests.find((r) => r.id === id && r.tenantId === tenantId);
    if (!found) throw mockApiError(404, 'Service request not found', 'NOT_FOUND');
    return found;
  }

  async listServiceRequests(query: types.ServiceRequestListQuery = {}): Promise<types.PortalPage<types.ServiceRequest>> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    const rows = this.serviceRequests
      .filter((r) => r.tenantId === tenantId && (!query.status || r.status === query.status) && (!query.source || r.source === query.source))
      .reverse()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return this.mockPage(rows.map((r) => this.serviceRequestOffice(r)), query);
  }

  async getServiceRequest(id: string): Promise<types.ServiceRequest> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    return this.serviceRequestOffice(this.serviceRequestOr404(tenantId, id));
  }

  async convertServiceRequest(id: string, data: types.ConvertServiceRequestInput = {}): Promise<types.ServiceRequest> {
    await delay(250);
    const actor = this.requireOfficeUser();
    const request = this.serviceRequestOr404(actor.tenantId, id);
    if (request.status !== 'NEW') throw mockApiError(409, 'This request has already been decided', 'SERVICE_REQUEST_DECIDED');
    const invalid = (message: string) => mockApiError(400, message, 'VALIDATION_ERROR');

    if (request.customerId && data.customerId && data.customerId !== request.customerId) throw invalid('customerId: does not match the request\'s customer');
    const customerId = request.customerId ?? data.customerId ?? null;
    if (!customerId) throw mockApiError(400, 'Choose the customer this request is for', 'CUSTOMER_REQUIRED');
    if (!this.customers.some((c) => c.id === customerId && c.tenantId === actor.tenantId)) throw mockApiError(404, 'Customer not found', 'NOT_FOUND');

    if (request.propertyId && data.propertyId && data.propertyId !== request.propertyId) throw invalid('propertyId: does not match the request\'s property');
    const propertyId = request.propertyId ?? data.propertyId ?? null;
    if (!propertyId) throw mockApiError(400, 'Choose the property this request is for', 'PROPERTY_REQUIRED');
    const property = this.properties.find((p) => p.id === propertyId && p.tenantId === actor.tenantId);
    if (!property) throw mockApiError(404, 'Property not found', 'NOT_FOUND');
    if (property.customerId !== customerId) throw invalid('propertyId: that property belongs to another customer');

    const notes = [
      `Service request (${request.source === 'PORTAL' ? 'customer portal' : 'web form'})`,
      request.requestedItemName ? `Service: ${request.requestedItemName}` : null,
      request.preferredDates.length ? `Preferred dates: ${request.preferredDates.join(', ')}` : null,
      request.preferredTimeOfDay ? `Preferred time: ${request.preferredTimeOfDay}` : null,
      request.serviceAddress ? `Address given: ${request.serviceAddress}` : null,
      `Contact: ${request.contactName}${request.contactEmail ? `, ${request.contactEmail}` : ''}${request.contactPhone ? `, ${request.contactPhone}` : ''}`,
      request.notes ? `Customer notes: ${request.notes}` : null,
    ].filter(Boolean).join('\n');
    const job = await this.createJob({ propertyId, notes });
    const item = request.requestedItemId ? this.items.find((i) => i.id === request.requestedItemId && !i.deletedAt) : undefined;
    if (item) await this.addJobLineItem(job.id, { itemId: item.id, quantity: 1 } as types.AddJobLineItemRequest);

    Object.assign(request, {
      status: 'CONVERTED', customerId, propertyId, convertedJobId: job.id, decidedById: actor.id, decidedAt: new Date().toISOString(),
    });
    return this.serviceRequestOffice(request);
  }

  async declineServiceRequest(id: string, reason: string): Promise<types.ServiceRequest> {
    await delay(150);
    const actor = this.requireOfficeUser();
    const request = this.serviceRequestOr404(actor.tenantId, id);
    const text = typeof reason === 'string' ? reason.trim() : '';
    if (!text) throw mockApiError(400, 'reason: a reason for declining is required', 'VALIDATION_ERROR');
    if (text.length > 500) throw mockApiError(400, 'reason: at most 500 characters', 'VALIDATION_ERROR');
    if (request.status !== 'NEW') throw mockApiError(409, 'This request has already been decided', 'SERVICE_REQUEST_DECIDED');
    Object.assign(request, { status: 'DECLINED', declineReason: text, decidedById: actor.id, decidedAt: new Date().toISOString() });
    return this.serviceRequestOffice(request);
  }

  // ---- office: reschedule requests

  private rescheduleOffice(r: MockRescheduleRequest): types.RescheduleRequest {
    const job = this.jobs.find((j) => j.id === r.jobId);
    const customer = this.customers.find((c) => c.id === r.customerId);
    return {
      id: r.id, jobId: r.jobId, status: r.status,
      customer: { id: r.customerId, name: customer?.name ?? '' },
      property: this.mockPropertyAddress(job?.propertyId ?? null) ?? { id: '', address: '', city: '', state: '', zip: '' },
      jobStatus: job?.status ?? '',
      requestedByUserId: r.requestedByUserId, originalDate: r.originalDate, originalStartTime: r.originalStartTime,
      requestedDate: r.requestedDate, requestedStartTime: r.requestedStartTime, requestedWindow: r.requestedWindow, reason: r.reason,
      decidedById: r.decidedById, decidedAt: r.decidedAt, declineReason: r.declineReason,
      approvedDate: r.approvedDate, approvedStartTime: r.approvedStartTime, createdAt: r.createdAt,
    };
  }

  private rescheduleOr404(tenantId: string, id: string): MockRescheduleRequest {
    const found = this.rescheduleRequests.find((r) => r.id === id && r.tenantId === tenantId);
    if (!found) throw mockApiError(404, 'Reschedule request not found', 'NOT_FOUND');
    return found;
  }

  async listRescheduleRequests(query: types.RescheduleRequestListQuery = {}): Promise<types.PortalPage<types.RescheduleRequest>> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    const rows = this.rescheduleRequests
      .filter((r) => r.tenantId === tenantId && (!query.status || r.status === query.status))
      .reverse()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return this.mockPage(rows.map((r) => this.rescheduleOffice(r)), query);
  }

  async approveRescheduleRequest(id: string, data: types.ApproveRescheduleInput = {}): Promise<types.RescheduleRequest> {
    await delay(250);
    const actor = this.requireOfficeUser();
    const request = this.rescheduleOr404(actor.tenantId, id);
    if (request.status !== 'OPEN') throw mockApiError(409, 'This request has already been decided or cancelled', 'RESCHEDULE_REQUEST_NOT_OPEN');
    const job = this.jobs.find((j) => j.id === request.jobId && j.tenantId === actor.tenantId);
    if (!job) throw mockApiError(404, 'Job not found', 'NOT_FOUND');
    if (job.status !== types.JobStatus.SCHEDULED && job.status !== types.JobStatus.WORK_ORDER_GENERATED) {
      throw mockApiError(409, `The job is ${job.status} and can no longer be rescheduled`, 'JOB_NOT_RESCHEDULABLE');
    }
    if (data.scheduledDate !== undefined && !/^\d{4}-\d{2}-\d{2}$/.test(data.scheduledDate)) {
      throw mockApiError(400, 'scheduledDate: must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    }
    if (typeof data.scheduledStartTime === 'string' && !/^([01]\d|2[0-3]):[0-5]\d$/.test(data.scheduledStartTime)) {
      throw mockApiError(400, 'scheduledStartTime: must be HH:MM (00:00 to 23:59) or null', 'VALIDATION_ERROR');
    }
    const date = data.scheduledDate ?? request.requestedDate;
    const startTime = data.scheduledStartTime !== undefined ? data.scheduledStartTime : (request.requestedStartTime ?? job.scheduledStartTime ?? null);
    const conflicts = this.mockRescheduleConflicts(job, date, startTime);
    if (conflicts.length > 0 && !data.overrideConflicts) {
      throw mockApiError(
        409,
        "The new time conflicts with the technician's schedule; approve with overrideConflicts to apply it anyway",
        'RESCHEDULE_CONFLICTS',
        { conflicts }
      );
    }
    this.mockApplyReschedule(job, date, startTime);
    Object.assign(request, { status: 'APPROVED', decidedById: actor.id, decidedAt: new Date().toISOString(), approvedDate: date, approvedStartTime: startTime });
    return this.rescheduleOffice(request);
  }

  async declineRescheduleRequest(id: string, message: string): Promise<types.RescheduleRequest> {
    await delay(150);
    const actor = this.requireOfficeUser();
    const request = this.rescheduleOr404(actor.tenantId, id);
    const text = typeof message === 'string' ? message.trim() : '';
    if (!text) throw mockApiError(400, 'message: a message for the customer is required', 'VALIDATION_ERROR');
    if (text.length > 500) throw mockApiError(400, 'message: at most 500 characters', 'VALIDATION_ERROR');
    if (request.status !== 'OPEN') throw mockApiError(409, 'This request has already been decided or cancelled', 'RESCHEDULE_REQUEST_NOT_OPEN');
    Object.assign(request, { status: 'DECLINED', declineReason: text, decidedById: actor.id, decidedAt: new Date().toISOString() });
    return this.rescheduleOffice(request);
  }

  // ---- office: portal access requests and a customer's portal logins

  private accessRequestOffice(r: MockAccessRequest): types.PortalAccessRequest {
    const user = this.users.find((u) => u.id === r.userId);
    const account = this.portalAccounts.get(r.userId);
    return {
      id: r.id, status: r.status, flagged: r.flagged, createdAt: r.createdAt,
      user: { id: r.userId, email: user?.email ?? '', name: account?.name ?? '', phone: account?.phone ?? null },
      reason: r.reason, codeGiven: r.codeGiven, candidates: r.candidates.map((c) => ({ ...c, matched: c.matched ? { ...c.matched } : null })),
      decidedAt: r.decidedAt, linkedCustomerId: r.linkedCustomerId, declineReason: r.declineReason,
    };
  }

  async listPortalAccessRequests(query: types.PortalAccessRequestListQuery = {}): Promise<types.PortalPage<types.PortalAccessRequest>> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    const status = query.status ?? 'PENDING';
    const rows = this.portalAccessRequests
      .filter((r) => r.tenantId === tenantId && (status === 'ALL' || r.status === status))
      .reverse()
      .sort((a, b) => Number(b.flagged) - Number(a.flagged) || b.createdAt.localeCompare(a.createdAt));
    return this.mockPage(rows.map((r) => this.accessRequestOffice(r)), query);
  }

  async decidePortalAccessRequest(id: string, decision: types.PortalAccessRequestDecision): Promise<types.PortalAccessRequest> {
    await delay(200);
    const actor = this.requireOfficeUser();
    const request = this.portalAccessRequests.find((r) => r.id === id && r.tenantId === actor.tenantId);
    if (!request) throw mockApiError(404, 'Access request not found', 'NOT_FOUND');
    if (decision?.action !== 'link' && decision?.action !== 'decline') throw mockApiError(400, 'action: Must be link or decline', 'VALIDATION_ERROR');
    if (request.status !== 'PENDING') throw mockApiError(409, 'This request has already been decided', 'ALREADY_DECIDED');
    const account = this.portalAccounts.get(request.userId);
    const now = new Date().toISOString();
    if (decision.action === 'decline') {
      const reason = decision.reason?.trim() || null;
      Object.assign(request, { status: 'DECLINED', decidedAt: now, declineReason: reason });
      if (account) account.accessRequest = { status: 'DECLINED', createdAt: request.createdAt };
      return this.accessRequestOffice(request);
    }
    if (!decision.customerId || typeof decision.customerId !== 'string') throw mockApiError(400, 'customerId: A customer is required', 'VALIDATION_ERROR');
    const customer = this.customers.find((c) => c.id === decision.customerId && c.tenantId === actor.tenantId);
    if (!customer) throw mockApiError(404, 'Customer not found', 'NOT_FOUND');
    if (!account || account.status !== 'ACTIVE') throw mockApiError(409, 'This login has not confirmed its email address', 'EMAIL_NOT_VERIFIED');
    if (account.customerId) throw mockApiError(409, 'This login is already linked to a customer', 'ALREADY_LINKED');
    const max = this.mockCompanySettings(actor.tenantId).portalMaxLoginsPerCustomer ?? 3;
    const logins = [...this.portalAccounts.values()].filter((a) => a.customerId === customer.id).length;
    if (logins >= max) {
      throw mockApiError(422, `This customer already has ${logins} portal logins (the limit is ${max}; it can be raised in the company settings)`, 'PORTAL_LOGIN_LIMIT');
    }
    Object.assign(account, { customerId: customer.id, linkedVia: 'STAFF', linkedAt: now, accessRequest: { status: 'LINKED', createdAt: request.createdAt } });
    Object.assign(request, { status: 'LINKED', decidedAt: now, linkedCustomerId: customer.id });
    return this.accessRequestOffice(request);
  }

  private portalLoginsOf(tenantId: string, customerId: string): types.CustomerPortalLogin[] {
    return [...this.portalAccounts.values()]
      .filter((a) => a.tenantId === tenantId && a.customerId === customerId)
      .map((a) => {
        const user = this.users.find((u) => u.id === a.userId);
        return user ? {
          userId: user.id, email: user.email, name: a.name, status: a.status, isActive: user.isActive,
          linkedVia: a.linkedVia ?? 'STAFF', linkedAt: a.linkedAt ?? new Date().toISOString(),
        } : null;
      })
      .filter((l): l is types.CustomerPortalLogin => !!l)
      .sort((a, b) => a.linkedAt.localeCompare(b.linkedAt));
  }

  private requirePortalCustomer(tenantId: string, customerId: string): types.Customer {
    const customer = this.customers.find((c) => c.id === customerId && c.tenantId === tenantId);
    if (!customer) throw mockApiError(404, 'Customer not found', 'NOT_FOUND');
    return customer;
  }

  private requireLinkedPortalLogin(tenantId: string, customerId: string, userId: string): { user: MockUser; account: MockPortalAccount } {
    const account = this.portalAccounts.get(userId);
    const user = this.users.find((u) => u.id === userId && u.tenantId === tenantId);
    if (!account || !user || account.customerId !== customerId) throw mockApiError(404, 'Portal login not found', 'NOT_FOUND');
    return { user, account };
  }

  async listCustomerPortalLogins(customerId: string): Promise<types.CustomerPortalLogin[]> {
    await delay(100);
    const { tenantId } = this.requireOfficeUser();
    this.requirePortalCustomer(tenantId, customerId);
    return this.portalLoginsOf(tenantId, customerId);
  }

  async inviteCustomerPortalUser(customerId: string, data: types.PortalInviteInput = {}): Promise<types.PortalInviteResult> {
    await delay(200);
    const { tenantId } = this.requireOfficeUser();
    const customer = this.requirePortalCustomer(tenantId, customerId);
    const settings = this.mockCompanySettings(tenantId);
    if (this.portalOff(tenantId)) throw mockApiError(422, 'The customer portal is turned off', 'PORTAL_DISABLED');
    const email = (data?.email ?? customer.email ?? '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw mockApiError(400, 'email: Email format is invalid', 'VALIDATION_ERROR');
    const name = data?.name?.trim() || customer.name;
    const existing = this.users.find((u) => u.tenantId === tenantId && u.email.toLowerCase() === email);
    let user: MockUser;
    if (existing) {
      const account = this.portalAccounts.get(existing.id);
      const pendingForThisCustomer = existing.role === types.UserRole.CUSTOMER_PORTAL && existing.isActive
        && account?.status === 'PENDING_EMAIL' && account.customerId === customerId;
      if (!pendingForThisCustomer) throw mockApiError(409, 'A user with this email address already exists', 'PORTAL_USER_EXISTS');
      user = existing; // inviting the same pending login again is a resend
    } else {
      const logins = this.portalLoginsOf(tenantId, customerId).length;
      const max = settings.portalMaxLoginsPerCustomer ?? 3;
      if (logins >= max) {
        throw mockApiError(422, `This customer already has ${logins} portal logins (the limit is ${max}; it can be raised in the company settings)`, 'PORTAL_LOGIN_LIMIT');
      }
      user = this.createPendingPortalUser(tenantId, email, name, customer.phone ?? null);
      Object.assign(this.portalAccounts.get(user.id)!, { customerId, linkedVia: 'INVITE', linkedAt: new Date().toISOString() });
    }
    this.issuePortalToken(user, 'INVITE', 7 * 24 * 3600_000, null);
    return { userId: user.id, email, status: 'PENDING_EMAIL', delivery: 'QUEUED', skipReason: null };
  }

  async resendCustomerPortalInvite(customerId: string, userId: string): Promise<types.PortalInviteResult> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    this.requirePortalCustomer(tenantId, customerId);
    if (this.portalOff(tenantId)) throw mockApiError(422, 'The customer portal is turned off', 'PORTAL_DISABLED');
    const { user, account } = this.requireLinkedPortalLogin(tenantId, customerId, userId);
    if (account.status !== 'PENDING_EMAIL' || !user.isActive) throw mockApiError(409, 'This login has already set a password', 'ALREADY_ACCEPTED');
    this.issuePortalToken(user, 'INVITE', 7 * 24 * 3600_000, null);
    return { userId: user.id, email: user.email, status: 'PENDING_EMAIL', delivery: 'QUEUED', skipReason: null };
  }

  async disableCustomerPortalUser(customerId: string, userId: string): Promise<types.CustomerPortalLogin[]> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    this.requirePortalCustomer(tenantId, customerId);
    const { user } = this.requireLinkedPortalLogin(tenantId, customerId, userId);
    user.isActive = false;
    this.portalTokens.filter((t) => t.userId === userId && !t.usedAt).forEach((t) => { t.usedAt = Date.now(); });
    return this.portalLoginsOf(tenantId, customerId);
  }

  async unlinkCustomerPortalUser(customerId: string, userId: string): Promise<types.CustomerPortalLogin[]> {
    await delay(150);
    const { tenantId } = this.requireOfficeUser();
    this.requirePortalCustomer(tenantId, customerId);
    const { account } = this.requireLinkedPortalLogin(tenantId, customerId, userId);
    account.customerId = null;
    account.linkedVia = undefined;
    account.linkedAt = undefined;
    return this.portalLoginsOf(tenantId, customerId);
  }

  // ---- Web form intake (tracker item 49)

  private webFormIntakes = new Map<string, types.WebFormIntakeSettings>();

  private requireAdminUser(): MockUser {
    const actor = this.currentMockUser();
    if (actor.role !== types.UserRole.ADMIN) throw mockApiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return actor;
  }

  private newWebFormKey(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    let key = '';
    for (let i = 0; i < 32; i++) key += chars[Math.floor(Math.random() * chars.length)];
    return key;
  }

  async getWebFormIntake(): Promise<types.WebFormIntakeSettings> {
    await delay(100);
    const { tenantId } = this.requireAdminUser();
    return { ...(this.webFormIntakes.get(tenantId) ?? { enabled: false, publicKey: null, keyCreatedAt: null }) };
  }

  async updateWebFormIntake(enabled: boolean): Promise<types.WebFormIntakeSettings> {
    await delay(150);
    const { tenantId } = this.requireAdminUser();
    if (typeof enabled !== 'boolean') throw mockApiError(400, 'enabled: must be true or false', 'VALIDATION_ERROR');
    const current = this.webFormIntakes.get(tenantId) ?? { enabled: false, publicKey: null, keyCreatedAt: null };
    const next = enabled && !current.publicKey
      ? { enabled, publicKey: this.newWebFormKey(), keyCreatedAt: new Date().toISOString() }
      : { ...current, enabled };
    this.webFormIntakes.set(tenantId, next);
    return { ...next };
  }

  async regenerateWebFormKey(): Promise<types.WebFormIntakeSettings> {
    await delay(150);
    const { tenantId } = this.requireAdminUser();
    const current = this.webFormIntakes.get(tenantId) ?? { enabled: false, publicKey: null, keyCreatedAt: null };
    const next = { enabled: current.enabled, publicKey: this.newWebFormKey(), keyCreatedAt: new Date().toISOString() };
    this.webFormIntakes.set(tenantId, next);
    return { ...next };
  }

  /** The tenant whose enabled form has this key; anything else is the uniform 404 (no sign-in needed). */
  private webFormTenant(key: string): string {
    for (const [tenantId, intake] of this.webFormIntakes) {
      if (intake.enabled && intake.publicKey === key) return tenantId;
    }
    throw mockApiError(404, 'Not found');
  }

  async getWebFormItems(key: string): Promise<types.WebFormItem[]> {
    await delay(100);
    const tenantId = this.webFormTenant(key);
    return this.items
      .filter((i) => i.tenantId === tenantId && !i.deletedAt && i.portalRequestable)
      .map((i) => ({ id: i.id, name: i.name }));
  }

  async submitWebFormRequest(key: string, data: types.WebFormRequestInput): Promise<void> {
    await delay(200);
    const tenantId = this.webFormTenant(key);
    if (data.website && data.website.trim()) return; // honeypot: answered like success, nothing stored
    const invalid = (message: string) => mockApiError(400, message, 'VALIDATION_ERROR');
    const text = (value: unknown, field: string, max: number): string | null => {
      if (value === undefined || value === null) return null;
      if (typeof value !== 'string') throw invalid(`${field}: must be text`);
      const trimmed = value.trim();
      if (trimmed.length > max) throw invalid(`${field}: at most ${max} characters`);
      return trimmed || null;
    };
    const contactName = text(data.contactName, 'contactName', 200);
    const contactEmail = text(data.contactEmail, 'contactEmail', 320);
    const contactPhone = text(data.contactPhone, 'contactPhone', 50);
    const serviceAddress = text(data.serviceAddress, 'serviceAddress', 300);
    const notes = text(data.notes, 'notes', 2000);
    if (!contactName) throw invalid('contactName: your name is required');
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) throw invalid('contactEmail: not a valid email address');
    if (!contactEmail && !contactPhone) throw invalid('Give an email address or a phone number so we can reach you');
    if (!serviceAddress) throw invalid('serviceAddress: the address where you need the service is required');
    const requestedItemId = data.requestedItemId || null;
    if (!requestedItemId && !notes) throw invalid('Choose a service or describe what you need in the notes');
    let itemName: string | null = null;
    if (requestedItemId) {
      const item = this.items.find((i) => i.id === requestedItemId && i.tenantId === tenantId && !i.deletedAt && i.portalRequestable);
      if (!item) throw invalid('requestedItemId: not a service offered on this form');
      itemName = item.name;
    }
    if (this.serviceRequests.filter((r) => r.tenantId === tenantId && r.source === 'WEB_FORM' && r.status === 'NEW').length >= 200) {
      throw mockApiError(409, 'We cannot take more online requests right now; please contact us directly', 'TOO_MANY_OPEN_REQUESTS');
    }
    const today = new Date().toISOString().slice(0, 10);
    const dates = [...new Set(data.preferredDates ?? [])].sort();
    if (dates.length > 5) throw invalid('preferredDates: at most 5 dates');
    if (dates.some((d) => !/^\d{4}-\d{2}-\d{2}$/.test(d))) throw invalid('preferredDates: dates must be YYYY-MM-DD');
    if (dates.some((d) => d < today)) throw invalid('preferredDates: a date cannot be in the past');
    this.serviceRequests.push({
      id: generateId('sreq'), tenantId, customerId: null, propertyId: null, source: 'WEB_FORM', status: 'NEW',
      requestedItemId, requestedItemName: itemName, serviceAddress, preferredDates: dates, preferredTimeOfDay: data.preferredTimeOfDay ?? null, notes,
      contactName, contactEmail, contactPhone, convertedJobId: null, decidedById: null, decidedAt: null, declineReason: null,
      createdByUserId: null, createdAt: new Date().toISOString(),
    });
  }

  // ============ Import / export (tracker item 52): rules in mockImportExport.ts ============
  private importExportMock = createMockImportExport({
    actor: () => this.currentMockUser(),
    error: (status, message, code, details) => mockApiError(status, message, code, details),
    customers: (tenantId) => this.customers.filter((c) => c.tenantId === tenantId) as Array<MockCustomer & { externalRef?: string | null }>,
    properties: (tenantId) => this.properties.filter((p) => p.tenantId === tenantId),
    items: (tenantId) => this.items.filter((i) => i.tenantId === tenantId)
      .map((i) => ({ name: i.name, itemType: i.itemType, unitPrice: i.unitPrice, deleted: !!i.deletedAt })),
    taxRates: (tenantId) => this.taxRates.filter((t) => t.tenantId === tenantId),
    categories: (tenantId) => this.itemCategories.filter((c) => c.tenantId === tenantId),
    accountNumbers: (tenantId) => this.glAccounts.filter((a) => a.tenantId === tenantId),
    invoices: (tenantId) => this.invoices.filter((i) => (i as { tenantId?: string }).tenantId === tenantId).map((i) => ({
      invoiceNumber: i.invoiceNumber, invoiceDate: i.invoiceDate, status: i.status, customerName: i.billingCustomer.name,
      total: (Number(i.totalAmount) + Number(i.totalTax)).toFixed(2),
    })),
    payments: (tenantId) => this.payments.filter((p) => p.tenantId === tenantId).map((p) => ({
      paymentDate: p.paymentDate, amount: p.amount, status: p.status, customerName: p.billingCustomer.name,
    })),
    addCustomer: (tenantId, row) => {
      const now = new Date().toISOString();
      this.customers.push({ id: generateId('cust'), tenantId, ...row, createdAt: now, updatedAt: now } as unknown as MockCustomer);
    },
    addProperty: (tenantId, row) => {
      const now = new Date().toISOString();
      this.properties.push({ id: generateId('prop'), tenantId, ...row, sizeValue: null, sizeUnit: null, createdAt: now, updatedAt: now });
    },
    addCategory: (tenantId, name) => {
      const now = new Date().toISOString();
      const id = generateId('cat');
      this.itemCategories.push({ id, tenantId, name, createdAt: now, updatedAt: now } as (typeof this.itemCategories)[number]);
      return id;
    },
    addItem: (tenantId, row) => {
      const now = new Date().toISOString();
      this.items.push({
        id: generateId('item'), tenantId, ...row, unitCost: null, quantityOnHand: '0', reorderPoint: null, priceChartId: null,
        createdAt: now, updatedAt: now, deletedAt: null,
      });
    },
    audit: (action, details) => {
      const actor = this.currentMockUser();
      this.auditLogs.push({
        id: generateId('audit'), tenantId: actor.tenantId, actorUserId: actor.id, actorEmail: actor.email, action,
        targetUserId: null, targetEmail: null, details: details as types.AuditLogEntry['details'], createdAt: new Date().toISOString(),
      });
    },
  });

  listImportSpecs: IAPIClient['listImportSpecs'] = async (...args) => { await delay(100); return this.importExportMock.listImportSpecs(...args); };
  downloadImportTemplate: IAPIClient['downloadImportTemplate'] = async (...args) => this.importExportMock.downloadImportTemplate(...args);
  validateImport: IAPIClient['validateImport'] = async (...args) => { await delay(300); return this.importExportMock.validateImport(...args); };
  commitImport: IAPIClient['commitImport'] = async (...args) => { await delay(300); return this.importExportMock.commitImport(...args); };
  downloadExport: IAPIClient['downloadExport'] = async (...args) => { await delay(200); return this.importExportMock.downloadExport(...args); };

  setAuthToken(token: string): void {
    this.token = token;
  }

  getAuthToken(): string | null {
    return this.token;
  }

  clearAuthToken(): void {
    this.token = null;
  }

  // ---- Global search (tracker item 61): see mockSearch.ts

  async globalSearch(query: string, options: types.GlobalSearchOptions = {}): Promise<types.GlobalSearchResponse> {
    await delay(120);
    const user = this.currentMockUser();
    const tenantId = user.tenantId;
    const office = (mockSearchTypes(user.role) ?? []).includes('estimate');
    try {
      return mockGlobalSearch(user.role, query, options, {
        customers: this.customers.filter((c) => c.tenantId === tenantId),
        properties: this.properties.filter((p) => p.tenantId === tenantId),
        jobs: this.jobs.filter((j) => j.tenantId === tenantId),
        invoices: this.invoices.filter((i) => i.tenantId === tenantId),
        payments: this.payments.filter((p) => p.tenantId === tenantId),
        estimates: office ? (await this.mockEstimates.list({ limit: 1000 })).data : [],
      });
    } catch (error) {
      if (error instanceof MockSearchError) throw mockApiError(error.status, error.message, error.status === 403 ? 'INSUFFICIENT_PERMISSIONS' : 'VALIDATION_ERROR');
      throw error;
    }
  }

  // ---- Estimates (tracker item 39): see mockEstimates.ts

  async listEstimates(query?: types.EstimateListQuery) { await delay(100); return this.mockEstimates.list(query); }
  async getEstimate(id: string) { await delay(50); return this.mockEstimates.get(id); }
  async previewEstimate(data: types.EstimateInput) { await delay(50); return this.mockEstimates.preview(data); }
  async createEstimate(data: types.EstimateInput) { await delay(100); return this.mockEstimates.create(data); }
  async updateEstimate(id: string, data: types.EstimateUpdateInput) { await delay(100); return this.mockEstimates.update(id, data); }
  async sendEstimate(id: string, data?: types.EstimateSendInput) { await delay(100); return this.mockEstimates.send(id, data); }
  async recordEstimateResponse(id: string, decision: types.EstimateDecision, data?: types.EstimateResponseInput) { await delay(100); return this.mockEstimates.record(id, decision, data); }
  async cancelEstimate(id: string, reason?: string | null) { await delay(100); return this.mockEstimates.cancel(id, reason); }
  async convertEstimate(id: string, data: types.EstimateConvertInput) { await delay(100); return this.mockEstimates.convert(id, data); }
  async addEstimateBundle(id: string, data: { bundleId: string; startDate?: string | null }) { await delay(100); return this.mockEstimates.addBundle(id, data); }
  async updateEstimateBundle(id: string, bundleId: string, data: { startDate: string | null }) { await delay(100); return this.mockEstimates.updateBundle(id, bundleId, data); }
  async removeEstimateBundle(id: string, bundleId: string) { await delay(100); return this.mockEstimates.removeBundle(id, bundleId); }
  async listPortalEstimates(query?: { page?: number; limit?: number }) { await delay(100); return this.mockEstimates.listPortal(query); }

  // ---- Conversation inbox (tracker item 56): see mockConversations.ts
  async listConversations(query?: types.ConversationListQuery) { await delay(100); return this.mockConversations.list(query); }
  async getConversationUnreadCount() { await delay(50); return this.mockConversations.unreadCount(); }
  async getConversation(id: string) { await delay(50); return this.mockConversations.get(id); }
  async createConversation(data: types.CreateConversationInput) { await delay(100); return this.mockConversations.create(data); }
  async replyToConversation(id: string, body: string, idempotencyKey?: string) { await delay(100); return this.mockConversations.reply(id, body, idempotencyKey); }
  async addConversationNote(id: string, body: string) { await delay(100); return this.mockConversations.note(id, body); }
  async updateConversation(id: string, data: types.UpdateConversationInput) { await delay(100); return this.mockConversations.update(id, data); }
  async markConversationRead(id: string) { await delay(50); return this.mockConversations.markRead(id); }
  async listUnmatchedConversations(query?: { page?: number; limit?: number }) { await delay(100); return this.mockConversations.listUnmatched(query); }
  async assignConversationCustomer(id: string, data: types.AssignCustomerInput) { await delay(100); return this.mockConversations.assignCustomer(id, data); }
  async discardConversation(id: string) { await delay(100); return this.mockConversations.discard(id); }
  async getPortalMessages() { await delay(100); return this.mockConversations.portalMessages(); }
  async sendPortalMessage(body: string) { await delay(100); return this.mockConversations.sendPortalMessage(body); }
  async markPortalMessagesRead() { await delay(50); return this.mockConversations.markPortalRead(); }
  async getPortalEstimate(id: string) { await delay(50); return this.mockEstimates.getPortal(id); }
  async respondPortalEstimate(id: string, decision: types.EstimateDecision, comment?: string | null) { await delay(100); return this.mockEstimates.respondPortal(id, decision, comment); }
  async getPublicEstimate(token: string) { await delay(50); return this.mockEstimates.getPublic(token); }
  async respondPublicEstimate(token: string, decision: types.EstimateDecision, data: types.EstimateResponseInput) { await delay(100); return this.mockEstimates.respondPublic(token, decision, data); }
}

/**
 * Factory function for mock client
 */
export function createMockAPIClient(): IAPIClient {
  return new MockAPIClient();
}
